import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react';

const BRANDS = { RN:'Roofing Network', L:'Letchworth', S:'Stevenage', W:'Welwyn', R:'Royston', SN:'St Neots', SI:'St Ives', SW:'Saffron Walden', HR:'Hertfordshire', BS:"Bishop's Stortford", BR:'Braintree', BU:'Bury', CA:'Cambourne', E:'Ely', GD:'Great Dunmow', H:'Haverhill', N:'Newmarket', P:'Peterborough' };
const BRAND_KEYS = Object.keys(BRANDS);
const BRAND_COLORS = {
  RN: { bg:'#e2e8f0', border:'#94a3b8', text:'#334155' },
  L:  { bg:'#ecfccb', border:'#bef264', text:'#4d7c0f' },
  S:  { bg:'#dcfce7', border:'#86efac', text:'#15803d' },
  W:  { bg:'#fef9c3', border:'#fde047', text:'#a16207' },
  R:  { bg:'#dbeafe', border:'#93c5fd', text:'#1d4ed8' },
  SN: { bg:'#ffedd5', border:'#fdba74', text:'#c2410c' },
  SI: { bg:'#cffafe', border:'#67e8f9', text:'#0e7490' },
  SW: { bg:'#d1fae5', border:'#6ee7b7', text:'#065f46' },
  HR: { bg:'#fee2e2', border:'#fca5a5', text:'#b91c1c' },
  BS: { bg:'#ede9fe', border:'#c4b5fd', text:'#6d28d9' },
  BR: { bg:'#fce7f3', border:'#f9a8d4', text:'#be185d' },
  BU: { bg:'#fef3c7', border:'#fcd34d', text:'#b45309' },
  CA: { bg:'#ccfbf1', border:'#5eead4', text:'#0f766e' },
  E:  { bg:'#e0f2fe', border:'#7dd3fc', text:'#0369a1' },
  GD: { bg:'#fae8ff', border:'#f0abfc', text:'#a21caf' },
  H:  { bg:'#ffe4e6', border:'#fda4af', text:'#be123c' },
  N:  { bg:'#e0e7ff', border:'#a5b4fc', text:'#4338ca' },
  P:  { bg:'#f5f5f4', border:'#a8a29e', text:'#57534e' },
};
// Filled once from brand settings when the app loads. Until then, and for any brand with
// no colour entered, BRAND_COLORS above is what shows.
let BRAND_COLOUR_HEX = {};

function hexToHsl(hex) {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(String(hex == null ? '' : hex).trim());
  if (!m) return null;
  const h6 = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  const r = parseInt(h6.slice(0,2),16)/255, g = parseInt(h6.slice(2,4),16)/255, b = parseInt(h6.slice(4,6),16)/255;
  const mx = Math.max(r,g,b), mn = Math.min(r,g,b), d = mx - mn;
  let h = 0;
  if (d) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (mx + mn) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

// The inline style for a person's initial badge. Null colour returns nothing at all,
// which leaves the stylesheet's grey in place - so a person with no colour picked looks
// exactly as they did before.
function personChipStyle(hex) {
  const t = derivedBadge(hex);
  return t ? { background: t.bg, borderColor: t.border, color: t.text } : undefined;
}

// One hex in, a readable badge out. Null for anything that is not a hex, so the caller
// can fall back rather than render something broken.
function derivedBadge(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  // A near-grey brand would give a badge with no colour at all, and a fluorescent one a
  // badge that shouts; both are clamped into a range that still reads as the brand.
  const s = Math.max(25, Math.min(85, hsl.s));
  return {
    bg: `hsl(${hsl.h}, ${Math.round(s * 0.6)}%, 93%)`,
    border: `hsl(${hsl.h}, ${Math.round(s * 0.75)}%, 78%)`,
    text: `hsl(${hsl.h}, ${s}%, 32%)`,
  };
}

function brandColor(brand) {
  return derivedBadge(BRAND_COLOUR_HEX[brand]) || BRAND_COLORS[brand] || BRAND_COLORS.R;
}

// Andrew, 20 Oct: the To-do count on the browser tab, for when the CRM is an ordinary tab
// rather than the installed app. Draws a red badge onto the tab icon; null puts the plain
// icon back. The icon is same-origin, so the canvas can be read back.
let faviconPlain = null;
function setTabBadge(text) {
  const link = document.querySelector('link[rel="icon"]');
  if (!link) return;
  if (!faviconPlain) faviconPlain = link.href;
  if (!text) { link.href = faviconPlain; return; }
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 32;
    const g = c.getContext('2d');
    g.drawImage(img, 0, 0, 32, 32);
    const r = text.length > 1 ? 11 : 9;
    g.fillStyle = '#b91c1c';
    g.beginPath(); g.arc(32 - r, r, r, 0, 2 * Math.PI); g.fill();
    g.fillStyle = '#fff';
    g.font = `bold ${text.length > 2 ? 10 : 14}px sans-serif`;
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(text, 32 - r, r + 1);
    link.href = c.toDataURL('image/png');
  };
  img.src = faviconPlain;
}
const STAGES = ['Lead','Visit','Quote','Invoicing','Jobs','Completed','Dead'];
// Completed and Dead grow without limit, so the board shows the most recent few and
// offers the rest in a list rather than a column nobody can scroll to the end of.
const CAPPED_STAGES = { Completed: 10, Dead: 10 };
const JOB_STATUS_LABELS = {
  agree_dates: 'Agree dates', order_materials: 'Order materials',
  assign_labour: 'Assign labour', job_report: 'Job report',
  complete: 'Complete', guarantee: 'Guarantee',
};
const PAYMENT_STATUS_LABELS = {
  awaiting: 'Awaiting invoicing', ready_to_send: 'Ready to send',
  outstanding: 'Outstanding', overdue: 'Overdue', paid: 'Paid', cancelled: 'Cancelled',
};
const ANDY_STAGES = ['Lead'];
const TIME_BANDS = [
  { label:'8–10',  start:'08:00', end:'10:00' },
  { label:'10–12', start:'10:00', end:'12:00' },
  { label:'12–2',  start:'12:00', end:'14:00' },
  { label:'2–4',   start:'14:00', end:'16:00' },
  { label:'4–6',   start:'16:00', end:'18:00' },
];
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function displayOwner(lead) {
  return lead.assigned_to || ownerOf(lead.stage);
}
function ownerOf(stage) { return ANDY_STAGES.includes(stage) ? 'Andy' : 'LD'; }
function daysSince(ts) { return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000); }
function fmtLongDate(ts) {
  if (!ts) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(ts)) ? new Date(ts + 'T00:00:00') : new Date(ts);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' });
}
// A quote is "out" when it has been sent and the customer has not yet said yes or no.
// That is what separates a lead waiting on us (to quote) from one waiting on them (quoted).
function quoteOut(l) {
  const q = l && l.last_quote;
  return !!(q && q.sent_at && !q.accepted_at && !q.declined_at
    && q.status !== 'accepted' && q.status !== 'declined' && q.status !== 'updated');
}
// White, because it is not an action for us - Andrew, 2 October.
const QUOTED_BOX = { bg: '#ffffff', text: '#3f3f46', border: '#d4d4d8' };
// Andrew, 3 Nov: the Quote stage shows as two columns on the board and list - To quote (waiting
// on us) and Quoted (a quote is with the customer). Still one stage underneath; the column is
// worked out from the quote, so sending one moves the card across by itself.
const BOARD_COLUMNS = ['Lead','Visit','To quote','Quoted','Invoicing','Jobs','Completed','Dead'];
const boardColumnOf = (l) => (l.stage === 'Quote' ? (quoteOut(l) ? 'Quoted' : 'To quote') : l.stage);
const stageOfColumn = (col) => (col === 'To quote' || col === 'Quoted' ? 'Quote' : col);
const columnColour = (col) => (col === 'To quote' ? STAGE_COLORS.Quote : col === 'Quoted' ? '#e4e4e7' : STAGE_COLORS[col]);
function fmt(v) { return v ? '£' + Number(v).toLocaleString() : '—'; }
function getWeekStart(date) {
  const d = new Date(date); const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff); d.setHours(0,0,0,0); return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate()+n); return d; }
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}
function extractPostcode(address) {
  if (!address) return '';
  const m = address.match(/[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}/i);
  return m ? m[0].toUpperCase() : '';
}
function extractHouseNum(address) {
  if (!address) return '';
  const m = address.match(/^(\d+[a-z]?)/i);
  return m ? m[1] : '';
}
function bandForTime(time) {
  for (const b of TIME_BANDS) { if (time >= b.start && time < b.end) return b.label; }
  return TIME_BANDS[0].label;
}

function displayName(title, first, last) {
  const parts = [title, first, last].filter(Boolean);
  if (parts.length === 0) return 'Unknown';
  return parts.join(' ');
}
function tileDisplayName(title, first, last) {
  if (title && last) return title + ' ' + last;
  if (first && last) return first + ' ' + last;
  if (first) return first;
  if (last) return last;
  return 'Unknown';
}

function crewInitials(n) {
  return String(n || '').trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase();
}

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 20; h++) {
    for (let m of [0,15,30,45]) {
      opts.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    }
  }
  return opts;
})();

function TimeSelect({ value, onChange }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)}>
      {TIME_OPTIONS.map(t=><option key={t} value={t}>{t}</option>)}
    </select>
  );
}

const STAGE_COLORS = {
  Lead: '#d9d2e9',
  Visit: '#00ffff',
  Quote: '#ffff00',
  Invoicing: '#a8d8a8',
  Jobs: '#a8c8f0',
  Completed: '#ff9900',
  Dead: '#d6534a',
};
const PILL_TONES = {
  red:   { bg: '#fdecec', color: '#b91c1c', border: '#f8d4d4' },
  amber: { bg: '#fff6e5', color: '#b45309', border: '#f6e0b5' },
  blue:  { bg: '#eef6ff', color: '#185fa5', border: '#cfe3fb' },
  green: { bg: '#e9f7ef', color: '#16a34a', border: '#c3ecd4' },
  grey:  { bg: '#f4f4f5', color: '#57534e', border: '#e7e5e4' },
};
function JobPill({ pill }) {
  const t = PILL_TONES[pill && pill.tone] || PILL_TONES.grey;
  return <span style={{ fontSize: 9.5, fontWeight: 700, background: t.bg, color: t.color,
    border: `1px solid ${t.border}`, borderRadius: 9, padding: '1px 7px', whiteSpace: 'nowrap' }}>
    {pill ? pill.text : ''}
  </span>;
}
// Andrew, 2 Nov: phone numbers read 07900 111 222. Anything that is not a UK number stays as typed.
function formatUkPhone(v) {
  const raw = String(v == null ? '' : v).trim();
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('0044')) d = d.slice(2);
  if (d.startsWith('44') && d.length === 12) d = '0' + d.slice(2);
  if (d.length === 10 && d.startsWith('7')) d = '0' + d;
  if (d.length !== 11 || d[0] !== '0') return raw;
  if (d.startsWith('02')) return `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`;
  if (d.startsWith('011') || d[3] === '1') return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  return `${d.slice(0, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
}
const STATUS_BOX_COLORS = {
  Lead: { bg: STAGE_COLORS.Lead+'66', text: '#3d2f5c', border: '#b8a8d4' },
  Quote: { bg: STAGE_COLORS.Quote+'66', text: '#7a6300', border: '#e6c200' },
};
// PM Question shows yellow (Andrew, 3 Nov); every other lead status keeps the Lead colours.
const leadStatusBox = (s) => (s === 'pm_question' ? STATUS_BOX_COLORS.Quote : STATUS_BOX_COLORS.Lead);
const LEAD_STATUS_LABELS = {
  // Set by the server when a visit is proposed and cleared when it is confirmed or
  // removed, so it is in the list to be READ rather than picked - though picking it by
  // hand does no harm.
  visit_proposed: 'Visit proposed',
  waiting_photos: 'Waiting on photos',
  waiting_survey: 'Waiting on survey',
  missed_call: 'Missed call',
  pm_question: 'PM Question',
};
// The status a card shows: lead statuses on Lead cards, quote statuses on Quote cards. The
// board and list filter by this (Andrew, 3 Nov), so a status left over from an earlier stage
// never pulls a card in that does not show it.
const QUOTE_STATUS_LABELS = { priority_quote: 'Priority quote' };
const shownStatus = (l) => (l.stage === 'Lead' ? l.lead_status || '' : l.stage === 'Quote' ? l.quote_status || '' : '');
const isStatusFilter = (v) => v === 'all' || v === 'none' || v in LEAD_STATUS_LABELS || v in QUOTE_STATUS_LABELS;
const leadStatusOptions = () =>
  Object.keys(LEAD_STATUS_LABELS).map(k => <option key={k} value={k}>{LEAD_STATUS_LABELS[k]}</option>);
// ---- Upload queue (Andrew, 18 Oct: groundwork for the field tablets, todo item 16) -------
// A site photo or voice note is written to this device (IndexedDB) the moment it is taken,
// then sent in the background, oldest first. With no signal it waits and tries again every
// 30 seconds and whenever the connection comes back; it survives the app being closed.
// Each item carries a key the server remembers, so a retry after a lost reply cannot file
// the same photo twice. Where IndexedDB is unavailable the old direct upload is used.
const UQ_DB = 'rn-uploads', UQ_STORE = 'queue';
let uqDbPromise = null, uqRunning = false, uqTimer = null;
function uqOpen() {
  if (!uqDbPromise) uqDbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no IndexedDB')); return; }
    const req = indexedDB.open(UQ_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(UQ_STORE, { keyPath: 'id', autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch(e => { uqDbPromise = null; throw e; });
  return uqDbPromise;
}
async function uqTx(mode, fn) {
  const db = await uqOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(UQ_STORE, mode);
    const out = fn(tx.objectStore(UQ_STORE));
    tx.oncomplete = () => resolve(out && 'result' in out ? out.result : undefined);
    tx.onerror = () => reject(tx.error);
  });
}
const uqAll = () => uqTx('readonly', st => st.getAll()).then(r => r || []);
const uqChanged = () => { try { window.dispatchEvent(new Event('rn-uq-change')); } catch (e) {} };
const uqKey = () => (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
async function uqEnqueue(file, leadId, type) {
  await uqTx('readwrite', st => st.add({ key: uqKey(), lead_id: leadId, type, name: file.name || '',
    blob: file, created: Date.now(), attempts: 0, error: '' }));
  uqChanged();
  uqProcess();
}
async function uqProcess() {
  if (uqRunning) return;
  uqRunning = true;
  clearTimeout(uqTimer);
  let retryLater = false;
  try {
    const items = (await uqAll()).sort((a, b) => a.id - b.id);
    for (const it of items) {
      if (it.error && it.attempts >= 5) continue;            // stuck: shown, left for a person
      const fd = new FormData();
      if (it.name) fd.append('file', it.blob, it.name); else fd.append('file', it.blob);
      fd.append('lead_id', it.lead_id);
      fd.append('type', it.type);
      fd.append('client_key', it.key);
      let r;
      try { r = await fetch('/api/attachments', { method: 'POST', credentials: 'include', body: fd }); }
      catch (e) { retryLater = true; break; }               // no signal: stop, keep the order
      if (r.ok) {
        await uqTx('readwrite', st => st.delete(it.id));
        try { window.dispatchEvent(new CustomEvent('rn-upload-done', { detail: { lead_id: it.lead_id } })); } catch (e) {}
        uqChanged();
        continue;
      }
      const msg = await r.text().catch(() => '');
      if (r.status === 401 || r.status >= 500) { retryLater = true; break; }   // signed out or server busy
      await uqTx('readwrite', st => st.put({ ...it, attempts: it.attempts + 1,
        error: `Upload refused (${r.status}) ${msg.slice(0, 120)}` }));
      uqChanged();
    }
  } catch (e) {
    retryLater = true;
  } finally {
    uqRunning = false;
    if (retryLater) uqTimer = setTimeout(uqProcess, 30000);
  }
}
async function uqDiscard(id) { await uqTx('readwrite', st => st.delete(id)); uqChanged(); }
// Re-renders whenever the queue changes. leadId narrows it to one lead.
function useUploadQueue(leadId) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let live = true;
    const read = () => uqAll().then(all => { if (live) setItems(leadId == null ? all : all.filter(i => String(i.lead_id) === String(leadId))); }).catch(() => {});
    read();
    window.addEventListener('rn-uq-change', read);
    return () => { live = false; window.removeEventListener('rn-uq-change', read); };
  }, [leadId]);
  return items;
}

// Uploaded photos come in three sizes (server: /uploads/:name?size=). 'thumb' 300px for
// lists and strips, 'display' 1600px for viewing; no size is the untouched original.
const uploadUrl = (filename, size) => `/uploads/${filename}${size ? `?size=${size}` : ''}`;

function jobNumber(id) {
  return String(id).padStart(4, '0');
}
function ordinal(n) {
  const s = ['th','st','nd','rd'], v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
}
function fmtVisitDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  return `${day} ${ordinal(d.getDate())} ${month}`;
}

async function api(path, opts = {}) {
  const r = await fetch(path, { credentials:'include', headers:{'Content-Type':'application/json'}, ...opts });
  if (!r.ok) throw new Error((await r.json()).error || r.statusText);
  return r.json();
}

// Each person's views come back as they left them (Andrew, 2 Oct: "each person when they
// log back into the CRM, everything is as was"). Saved on the server against the login,
// one set for phone and one for desktop, so a phone left on the diary does not move the
// desktop off the board. "Phone" is the same 760px breakpoint the CSS uses, read once.
// Only tabs, filters, sorts and toggles are kept - not search text, an open lead, drafts
// or dates: the diary and the map's day filter always open on this week, and Reports
// keeps the period ("This month"), not the dates it covered when it was picked.
const PREF_DEVICE = (typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(max-width: 760px)').matches) ? 'phone' : 'desktop';
const PrefsContext = createContext(null);
const isBrandPref = (v) => v === 'all' || BRAND_KEYS.includes(v);
const oneOf = (...vals) => (v) => vals.includes(v);

// useState that is remembered. A stored value is used only if `ok` accepts it, so a
// brand or tab that no longer exists falls back to the default instead of showing nothing.
function usePref(key, def, ok) {
  const store = useContext(PrefsContext);
  const [value, setValue] = useState(() => {
    const saved = store ? store.get(key) : undefined;
    return saved !== undefined && ok(saved) ? saved : def;
  });
  const set = useCallback((next) => {
    setValue(prev => {
      const v = typeof next === 'function' ? next(prev) : next;
      if (store) store.save({ [key]: v });
      return v;
    });
  }, [store, key]);
  return [value, set];
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
:root{
  --ink:#18181b; --ink-soft:#52525b; --ink-faint:#a1a1aa;
  --line:#e4e4e7; --line-soft:#f0f0f0;
  --surface:#ffffff; --canvas:#fafafa; --canvas-sunken:#f1f1f0;
  --accent:#0d8a5f; --accent-hover:#0a7350; --accent-soft:#e8f7ef; --accent-soft-line:#bfe8d3;
  --amber:#b45309; --amber-soft:#fef3e2; --amber-soft-line:#fcd9a8;
  --red:#b91c1c; --red-soft:#fef2f2; --red-soft-line:#fecaca;
  --blue:#1d4ed8; --blue-soft:#eff6ff; --blue-soft-line:#bfdbfe;
  --radius-sm:6px; --radius:9px; --radius-lg:13px;
  --shadow-sm:0 1px 2px rgba(24,24,27,0.04);
  --shadow:0 4px 14px rgba(24,24,27,0.07), 0 1px 3px rgba(24,24,27,0.06);
  --shadow-lg:0 24px 60px rgba(24,24,27,0.18), 0 4px 16px rgba(24,24,27,0.08);
}
body{font-family:'Inter',system-ui,-apple-system,sans-serif;background:var(--canvas);color:var(--ink);font-size:14px;-webkit-font-smoothing:antialiased;
  overscroll-behavior-y:contain}
html{overscroll-behavior-y:contain}
.app{display:flex;flex-direction:column;height:100vh;overflow:hidden}

/* Topbar */
.topbar{background:var(--surface);border-bottom:1px solid var(--line);padding:0 20px;display:flex;align-items:center;gap:14px;height:54px;flex-shrink:0}
.logo{font-weight:700;font-size:16px;letter-spacing:-0.02em;color:var(--ink)}
.logo span{color:var(--accent)}
.topbar-right{display:flex;gap:10px;margin-left:auto;align-items:center}

/* Form controls */
input:not([type]),input[type=text],input[type=email],input[type=tel],input[type=number],input[type=date],input[type=time],input[type=search]{
  font-family:inherit;font-size:13px;padding:7px 11px;border:1px solid var(--line);border-radius:var(--radius-sm);
  background:var(--surface);color:var(--ink);outline:none;transition:border-color .12s,box-shadow .12s;
  width:100%;min-width:0;height:34px;box-sizing:border-box;
}
select{
  font-family:inherit;font-size:12.5px;padding:5px 28px 5px 11px;border:1px solid var(--line);border-radius:var(--radius-sm);
  background:var(--surface);color:var(--ink);outline:none;transition:border-color .12s,box-shadow .12s;
  width:100%;min-width:0;height:34px;box-sizing:border-box;
  cursor:pointer;appearance:none;-webkit-appearance:none;
  background-image:url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2352525b' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat:no-repeat;background-position:right 10px center;background-size:14px;
}
select:focus,input:focus{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}
select:hover,input:hover{border-color:#d4d4d8}
select.select-compact{width:auto;height:32px;padding:5px 26px 5px 9px;font-size:12px;background-position:right 7px center;background-size:12px}
textarea{
  font-family:inherit;font-size:13px;padding:9px 10px;border:1px solid var(--line);border-radius:var(--radius-sm);
  background:var(--surface);color:var(--ink);outline:none;resize:vertical;line-height:1.5;transition:border-color .12s,box-shadow .12s;
  width:100%;box-sizing:border-box;
}
textarea:focus{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}

/* Buttons */
.btn{display:inline-flex;align-items:center;gap:6px;padding:7px 13px;border-radius:var(--radius-sm);border:1px solid var(--line);
  background:var(--surface);color:var(--ink);font-size:13px;font-family:inherit;cursor:pointer;font-weight:500;
  transition:background .12s,border-color .12s,transform .04s;}
.btn:hover{background:var(--canvas-sunken);border-color:#d4d4d8}
.btn:active{transform:scale(0.97)}
.btn-green{background:var(--accent);color:#fff;border-color:var(--accent);box-shadow:var(--shadow-sm)}
.btn-green:hover{background:var(--accent-hover);border-color:var(--accent-hover)}
.btn-red{border-color:var(--red-soft-line);color:var(--red);background:var(--surface)}
.btn-red:hover{background:var(--red-soft)}
.btn-sm{padding:5px 10px;font-size:12px;border-radius:6px}

/* Tabs */
.tabs{display:flex;gap:2px;background:var(--canvas-sunken);padding:3px;border-radius:9px;width:fit-content}
.tab{padding:6px 13px;border-radius:7px;border:none;background:transparent;color:var(--ink-soft);font-size:12.5px;
  cursor:pointer;font-family:inherit;font-weight:500;transition:background .12s,color .12s}
.tab:hover{color:var(--ink)}
.tab.active{background:var(--surface);color:var(--ink);box-shadow:var(--shadow-sm)}

/* Dashboard metrics */
.dash{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;padding:14px 20px;flex-shrink:0;background:var(--surface);border-bottom:1px solid var(--line)}
.metric{background:var(--canvas);border:1px solid var(--line-soft);border-radius:var(--radius);padding:11px 14px;transition:border-color .15s}
.metric:hover{border-color:var(--line)}
.metric-label{font-size:10.5px;color:var(--ink-faint);font-weight:600;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:4px}
.metric-val{font-size:21px;font-weight:700;letter-spacing:-0.01em}
.metric-val.green{color:var(--accent)}.metric-val.amber{color:var(--amber)}.metric-val.blue{color:var(--blue)}

.main{flex:1;overflow:hidden;display:flex;flex-direction:column}
.filter-bar{display:flex;gap:8px;align-items:center;padding:10px 20px;background:var(--surface);border-bottom:1px solid var(--line);flex-shrink:0}
.filter-label{font-size:12px;color:var(--ink-soft);font-weight:500}

/* Kanban */
.kanban{flex:1;overflow-x:auto;overflow-y:hidden;display:flex;gap:10px;padding:14px 20px}
.col{flex:0 0 212px;display:flex;flex-direction:column;max-height:100%}
.col-head{padding:8px 10px;font-size:11.5px;font-weight:600;display:flex;align-items:center;justify-content:space-between;
  border-radius:var(--radius) var(--radius) 0 0;background:var(--canvas-sunken);border:1px solid var(--line-soft);border-bottom:none;color:var(--ink-soft);letter-spacing:0.1px}
.dot{width:6px;height:6px;border-radius:50%;background:var(--accent);display:inline-block;margin-right:6px}
.dot.andy{background:var(--blue)}.dot.dead{background:var(--red)}.dot.done{background:var(--accent)}
.col-body{flex:1;overflow-y:auto;background:var(--canvas);border:1px solid var(--line-soft);border-top:none;
  border-radius:0 0 var(--radius) var(--radius);padding:7px;display:flex;flex-direction:column;gap:6px;transition:background .15s;
  scrollbar-width:none;-ms-overflow-style:none;}
.col-body.drag-over{background:var(--accent-soft);border-color:var(--accent-soft-line)}
.col-body::-webkit-scrollbar{display:none}

.card{background:var(--surface);border:1px solid var(--line-soft);border-radius:var(--radius-sm);padding:10px 11px;
  cursor:grab;transition:border-color .12s,box-shadow .12s,opacity .12s;user-select:none;display:flex;flex-direction:column;gap:6px}
.card:hover{border-color:#d4d4d8;box-shadow:var(--shadow-sm)}
.card.dragging{opacity:0.4;cursor:grabbing}
.card-top{display:flex;align-items:center;gap:6px}
.job-num{font-size:10px;font-weight:600;color:var(--ink-faint);font-family:'SF Mono',Consolas,monospace;letter-spacing:0.2px}
.card-name-row{font-size:12.5px;font-weight:600;line-height:1.3;letter-spacing:-0.01em;
  display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;min-height:16px}
.bbadge{font-size:9.5px;font-weight:700;padding:2px 6px;border-radius:5px;white-space:nowrap;letter-spacing:0.2px;flex-shrink:0}
.card-addr{font-size:11.5px;color:var(--ink-soft);line-height:1.4;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:32px;height:32px}
/* One fixed slot for whatever a stage adds - a status, job pills, a date - so every card
   is the same height whichever column it is in. Andrew, 2 October: "i want all rows to
   stay the same height". */
.card-extra{height:26px;display:flex;align-items:center;gap:4px;overflow:hidden;white-space:nowrap}
.card-date{font-size:10px;color:var(--ink-faint);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.quoted-box{display:flex;align-items:center;width:100%;height:26px;padding:0 8px;border-radius:6px;
  font-size:10.5px;font-weight:600;border:1px solid;box-sizing:border-box}

/* The lead list. Tight columns, one line per lead: the phone number never wraps, and the
   address and notes are cut off with the whole text on hover. */
.lead-list thead th{padding:7px 5px}
.lead-list tbody td{padding:4px 5px;white-space:nowrap}
.lead-list td.name{max-width:160px;font-weight:600}
.lead-list td.clip{overflow:hidden;text-overflow:ellipsis}
.lead-list td.addr{max-width:170px}
.lead-list td.notes{max-width:230px;color:var(--ink-soft)}
.lead-list td.email{max-width:160px}
.lead-list td select.select-compact{width:140px !important}
.lead-list td .quoted-box{width:140px !important}

/* Quoted - the quotes that are with the customer. One line each, oldest first. */
.qd-list{display:flex;flex-direction:column;gap:6px}
.qd-row{display:grid;grid-template-columns:44px 118px minmax(120px,1.3fr) 36px 78px 72px minmax(150px,2fr) auto;
  gap:10px;align-items:center;background:var(--surface);border:1px solid var(--line-soft);border-radius:var(--radius);
  padding:8px 12px;cursor:pointer;font-size:12.5px}
.qd-row:hover{border-color:#d4d4d8}
.qd-row > *{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.qd-date{color:var(--ink-soft);font-size:12px}
.qd-name{font-weight:600}
.qd-val{font-weight:700;color:var(--accent);text-align:right}
.qd-row > .bbadge{justify-self:start}
.qd-pill{font-size:9.5px;font-weight:700;border:1px solid;border-radius:9px;padding:1px 7px;justify-self:start}
.qd-chase{font-size:11.5px}
@media (max-width:760px){
  .qd-row{grid-template-columns:44px 1fr auto;row-gap:4px}
  .qd-date,.qd-chase{grid-column:1 / -1}
  .qd-row > button{display:none}
}

/* Jobs diary: an explicit place to tap on a touch screen, whatever the day already
   holds. Hidden with a mouse, where dragging does the job. */
.jg-add{display:none}
.card-foot{display:flex;align-items:center;justify-content:space-between;margin-top:auto}
.card-val{font-size:11.5px;font-weight:700;color:var(--accent)}
.age{font-size:10.5px;color:var(--ink-faint)}
.empty-col{padding:14px;text-align:center;color:var(--ink-faint);font-size:11.5px}

/* List view */
.list-wrap{flex:1;overflow-y:auto;padding:14px 20px}
table{width:100%;border-collapse:collapse;background:var(--surface);border-radius:var(--radius);overflow:hidden;border:1px solid var(--line-soft);box-shadow:var(--shadow-sm)}
thead th{padding:9px 12px;text-align:left;font-size:10.5px;font-weight:600;color:var(--ink-faint);border-bottom:1px solid var(--line-soft);
  white-space:nowrap;text-transform:uppercase;letter-spacing:0.5px}
tbody tr{border-bottom:1px solid var(--line-soft);cursor:pointer;transition:background .1s}
tbody tr:hover{background:var(--canvas)}
tbody tr:last-child{border-bottom:none}
tbody td{padding:6px 12px;font-size:12.5px;height:30px;max-height:30px;box-sizing:border-box;overflow:hidden}
.stage-chip{display:inline-block;font-size:10.5px;font-weight:600;padding:3px 9px;border-radius:11px;background:var(--canvas-sunken);color:var(--ink-soft)}
.owner-chip{display:inline-flex;align-items:center;font-size:10.5px;font-weight:600;padding:3px 9px;border-radius:11px}
.owner-chip.andy{background:var(--blue-soft);color:var(--blue)}
.owner-chip.ld{background:var(--accent-soft);color:var(--accent-hover)}

/* Email queue */
.eq-bar{background:var(--surface);border-top:1px solid var(--line);padding:9px 20px;display:flex;align-items:center;gap:9px;flex-shrink:0;flex-wrap:wrap}
.eq-label{font-size:11.5px;font-weight:700;color:var(--amber)}
.eq-item{background:var(--amber-soft);border:1px solid var(--amber-soft-line);border-radius:var(--radius-sm);padding:4px 10px;
  display:flex;align-items:center;gap:7px;font-size:11.5px;color:#78350f}
.eq-approve{background:var(--accent);color:#fff;border:none;border-radius:5px;padding:3px 9px;font-size:10.5px;cursor:pointer;font-family:inherit;font-weight:600}
.eq-approve:hover{background:var(--accent-hover)}
.eq-dismiss{background:none;border:none;color:var(--amber);cursor:pointer;font-size:17px;line-height:1;padding:0 2px;opacity:0.7}
.eq-dismiss:hover{opacity:1}

/* Modals */
.modal-overlay{position:fixed;inset:0;background:rgba(24,24,27,0.5);z-index:50;display:flex;align-items:center;justify-content:center;
  padding:20px;backdrop-filter:blur(2px)}
.modal{background:var(--surface);border-radius:var(--radius-lg);width:100%;max-width:600px;max-height:90vh;overflow-y:auto;
  display:flex;flex-direction:column;box-shadow:var(--shadow-lg)}
.modal-sm{max-width:420px}
.card-move{display:none}
.offline-bar{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;
  padding:8px 14px;background:#fef3c7;border-bottom:1px solid #fcd34d;color:#78350f;font-size:12.5px;font-weight:600}
.field-item{display:block;width:100%;text-align:left;cursor:pointer;font:inherit;color:inherit}
.fd-team{display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:15px;font-weight:600;color:var(--ink-soft);margin-bottom:12px}
.fd-strip{display:flex;align-items:stretch;gap:2px;background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-lg);margin-bottom:14px;overflow:hidden}
.fd-arrow{flex:none;width:40px;border:none;background:none;font-size:30px;line-height:1;color:var(--ink-soft);cursor:pointer}
.fd-days{flex:1;display:flex;overflow-x:auto;scrollbar-width:none;position:relative}
.fd-days::-webkit-scrollbar{display:none}
.fd-day{flex:none;width:72px;min-height:78px;display:flex;flex-direction:column;align-items:center;gap:2px;padding:10px 0 7px;border:none;border-bottom:4px solid transparent;background:none;font:inherit;color:var(--ink-soft);cursor:pointer}
.fd-dow{font-size:14px}
.fd-num{font-size:22px;font-weight:500}
.fd-day.is-today .fd-dow,.fd-day.is-today .fd-num{font-weight:800;color:var(--ink)}
.fd-day.on{border-bottom-color:var(--accent)}
.fd-dot{width:8px;height:8px;border-radius:50%;background:transparent}
.fd-dot.some{background:var(--blue)}
.fd-dot.all{background:var(--accent)}
.fd-dot.past{background:var(--ink-faint)}
.fd-hero{background:linear-gradient(135deg,#1d4ed8,#1e3a8a);color:#fff;border-radius:var(--radius-lg);padding:20px 22px;margin-bottom:14px;transition:background .4s}
.fd-hero.all{background:linear-gradient(135deg,#10a06e,#0a7350)}
.fd-hero-top{display:flex;align-items:center;justify-content:space-between;gap:12px}
.fd-hero-title{font-size:36px;font-weight:800;line-height:1.1}
.fd-hero-date{font-size:17px;opacity:.85;margin-top:2px}
.fd-score{font-size:44px;font-weight:800;line-height:1}
.fd-bar{height:14px;background:rgba(255,255,255,.22);border-radius:7px;margin-top:16px;overflow:hidden}
.fd-bar div{height:100%;background:#4ade80;border-radius:7px;transition:width .8s cubic-bezier(.2,.8,.2,1)}
.fd-hero.all .fd-bar div{background:#fff}
.fd-hero-line{font-size:19px;font-weight:600;margin-top:12px}
.fd-other{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:var(--canvas-sunken);border-radius:var(--radius-lg);padding:14px 18px;margin-bottom:14px}
.fd-other-date{font-size:20px;font-weight:700;color:var(--ink-soft)}
.fd-none{font-size:17px;color:var(--ink-soft);padding:14px 4px}
.fd-item{display:flex;align-items:center;gap:14px;background:var(--surface);border:1px solid var(--line);border-left:8px solid var(--blue);border-radius:var(--radius-lg);padding:16px 18px;margin-bottom:12px}
.fd-item.survey{border-left-color:#7c3aed}
.fd-item-tick{flex:none;width:44px;height:44px;border-radius:50%;border:3px solid var(--line);display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:800;color:#fff}
.fd-item-top{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.fd-kind{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.03em;color:#fff;background:var(--blue);border-radius:5px;padding:2px 8px}
.fd-item.survey .fd-kind{background:#7c3aed}
.fd-time{font-size:16px;font-weight:700}
.fd-state{margin-left:auto;font-size:15px;font-weight:700;color:var(--ink-soft)}
.fd-name{font-size:23px;font-weight:700;line-height:1.2;margin-top:6px;overflow-wrap:anywhere}
.fd-addr{font-size:17px;color:var(--ink-soft);margin-top:3px}
.fd-postcode{font-size:22px;font-weight:800;letter-spacing:.02em;margin-top:2px}
.fd-head-postcode{font-size:28px;font-weight:800;letter-spacing:.02em;margin-top:2px}
.fd-item.next{border-width:2px 2px 2px 8px;border-color:var(--blue);box-shadow:0 4px 14px rgba(29,78,216,.18)}
.fd-item.next .fd-item-tick{border-color:var(--blue)}
.fd-item.next .fd-state{color:var(--blue)}
.fd-item.survey.next{border-color:#7c3aed;box-shadow:0 4px 14px rgba(124,58,237,.18)}
.fd-item.survey.next .fd-item-tick{border-color:#7c3aed}
.fd-item.survey.next .fd-state{color:#7c3aed}
.fd-item.live,.fd-item.survey.live{border-color:#d97706;background:var(--amber-soft)}
.fd-item.live .fd-item-tick{border-color:#d97706;background:#d97706;font-size:16px}
.fd-item.live .fd-state{color:var(--amber)}
.fd-item.done,.fd-item.survey.done{border-color:var(--accent-soft-line);border-left-color:var(--accent);background:var(--accent-soft)}
.fd-item.done .fd-item-tick{border-color:var(--accent);background:var(--accent)}
.fd-item.done .fd-kind{background:var(--accent)}
.fd-item.done .fd-state{color:var(--accent-hover)}
.fd-item.done .fd-name{color:var(--ink-soft)}
.fd-item.quiet{border-left-color:var(--line);box-shadow:none}
.fd-item.quiet .fd-kind{background:var(--ink-faint)}
.fd-item.quiet .fd-item-tick{display:none}
.fd-back{display:inline-flex;align-items:center;min-height:48px;padding:0 18px;margin-bottom:12px;font:inherit;font-size:17px;font-weight:700;background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);color:var(--ink);cursor:pointer}
.fd-head{background:linear-gradient(135deg,#1d4ed8,#1e3a8a);color:#fff;border-radius:var(--radius-lg);padding:20px 22px;margin-bottom:12px}
.fd-detail.survey .fd-head{background:linear-gradient(135deg,#7c3aed,#4c1d95)}
.fd-head-meta{font-size:16px;font-weight:600;opacity:.85}
.fd-head-name{font-size:30px;font-weight:800;line-height:1.15;margin-top:4px;overflow-wrap:anywhere}
.fd-head-addr{font-size:18px;opacity:.9;margin-top:3px}
.fd-head-links{display:flex;gap:10px;flex-wrap:wrap;margin-top:14px}
.fd-head-links a{display:inline-flex;align-items:center;min-height:48px;padding:0 22px;border-radius:var(--radius);background:rgba(255,255,255,.18);color:#fff;font-size:17px;font-weight:700;text-decoration:none}
.fd-go{display:block;width:100%;min-height:68px;margin-bottom:12px;border:none;border-radius:var(--radius-lg);background:var(--accent);color:#fff;font:inherit;font-size:22px;font-weight:800;cursor:pointer}
.fd-go:disabled{opacity:.6}
.fd-done{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;min-height:68px;padding:10px 18px;margin-bottom:12px;background:var(--accent-soft);border:1px solid var(--accent-soft-line);border-radius:var(--radius-lg);color:var(--accent-hover);font-size:20px;font-weight:800}
.fd-tabs{display:flex;gap:6px;margin-bottom:12px;background:var(--canvas-sunken);border-radius:var(--radius-lg);padding:5px}
.fd-tabs button{flex:1;min-height:50px;border:none;border-radius:var(--radius);background:none;font:inherit;font-size:18px;font-weight:700;color:var(--ink-soft);cursor:pointer}
.fd-tabs button.on{background:var(--surface);color:var(--ink);box-shadow:var(--shadow-sm)}
.gal-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:10px}
.gal-item{min-width:0}
.gal-item img,.gal-item video{display:block;width:100%;aspect-ratio:1;object-fit:cover;border-radius:var(--radius);border:1px solid var(--line-soft);background:#000;cursor:pointer}
.gal-cap{font-size:13px;color:var(--ink-soft);margin-top:4px}
.gal-doc{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;aspect-ratio:1;padding:12px;border-radius:var(--radius);border:1px solid var(--line-soft);background:var(--surface);color:var(--ink);text-decoration:none;text-align:center}
.gal-doc:hover{border-color:var(--line)}
.gal-doc-ext{font-size:12px;font-weight:800;letter-spacing:.06em;color:#fff;background:var(--ink-soft);border-radius:4px;padding:3px 7px}
.gal-doc-ext.pdf{background:#c2410c}.gal-doc-ext.doc{background:#1d4ed8}.gal-doc-ext.xls{background:#15803d}
.gal-doc-name{font-size:12.5px;font-weight:600;line-height:1.3;word-break:break-word;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.fd-modal-back{position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;padding:16px}
.fd-modal{width:100%;max-width:520px;background:var(--surface);border-radius:var(--radius-lg);padding:22px}
.fd-modal-text{font-size:17px;color:var(--ink-soft);margin-bottom:14px}
.fd-modal textarea{width:100%;font:inherit;font-size:17px;padding:10px;border:1px solid var(--line);border-radius:var(--radius)}
.fd-modal-row{display:flex;justify-content:space-between;gap:10px;margin-top:12px}
.fd-modal-row .btn{font-size:16px;min-height:48px;padding:0 18px}
.fd-owners{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:12px}
.fd-owners .fd-tick{min-height:52px;padding:6px 18px;font-size:17px}
.fd-handle{flex:none;width:52px;min-height:64px;display:flex;align-items:center;justify-content:center;font-size:30px;color:var(--ink-soft);cursor:grab;touch-action:none;user-select:none;-webkit-user-select:none}
.fd-item.lifted{box-shadow:0 8px 24px rgba(0,0,0,.22);transform:scale(1.015)}
.fd-view{display:flex;align-items:center;justify-content:center;min-height:56px;margin-bottom:12px;background:var(--canvas-sunken);border-radius:var(--radius-lg);color:var(--ink-soft);font-size:18px;font-weight:700}
.fd-tick{display:flex;align-items:center;justify-content:center;min-height:68px;padding:10px;font:inherit;font-size:20px;font-weight:700;background:var(--surface);border:2px solid var(--line);border-radius:var(--radius-lg);color:var(--ink);cursor:pointer}
.fd-tick.on{border-color:var(--accent);background:var(--accent-soft);color:var(--accent-hover)}
.fd-tick-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px}
.fd-times{font-size:15px;color:var(--ink-soft);margin:-2px 4px 12px}
.fd-card{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius-lg);padding:16px 18px;margin-bottom:12px}
.fd-card-title{font-size:19px;font-weight:800;margin-bottom:10px}
.fd-work{font-size:17px;padding:8px 0;border-top:1px solid var(--line-soft)}
.fd-work:first-of-type{border-top:none;padding-top:0}
.fd-small{font-size:15px;color:var(--ink-soft);margin-top:8px}
.fd-detail textarea{font-size:17px;line-height:1.4}
.fd-detail .btn{font-size:16px;min-height:48px;padding-left:18px;padding-right:18px}
.fd-detail .offline-card,.fd-detail .sec{font-size:15px}
.td-admin{background:#1e1b4b!important;border-color:#1e1b4b!important;color:#fff}
.td-admin .td-note{color:#e0e7ff!important}
.td-admin .td-meta{color:#a5b4fc!important}
.fd-pop{display:inline-block;animation:fdPop .5s cubic-bezier(.2,1.6,.4,1) both}
@keyframes fdPop{from{transform:scale(0)}to{transform:scale(1)}}
@media (max-width:520px){.fd-day{width:60px}.fd-arrow{width:28px}.fd-hero-title{font-size:30px}.fd-score{font-size:36px}.fd-name{font-size:20px}}
.ad-source{font-size:12px;color:var(--ink-soft);background:var(--blue-soft);border:1px solid var(--blue-soft-line);border-radius:6px;padding:6px 10px;margin-top:8px}
.tl-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px}
.tl-name{display:flex;align-items:center;gap:8px;justify-content:center;min-height:52px;padding:10px;border:1px solid var(--line);
  border-radius:var(--radius);background:var(--surface);font-size:14px;font-weight:600;cursor:pointer;color:var(--ink)}
.tl-name.on{border-color:var(--accent);background:var(--accent-soft);color:var(--accent-hover)}
.uq-box{font-size:12px;color:#78350f;background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:7px 10px;margin-bottom:10px}
.uq-pill{position:fixed;left:12px;bottom:calc(68px + env(safe-area-inset-bottom,0px));z-index:65;background:#fef3c7;color:#78350f;
  border:1px solid #fcd34d;border-radius:999px;padding:5px 11px;font-size:12px;font-weight:600;box-shadow:var(--shadow-sm)}
.offline-card{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);padding:12px 14px;margin-bottom:10px}
/* A visit row in the lead panel. The buttons drop under the date on a narrow screen rather
   than the date and PROPOSED being squeezed over the Confirm button (Andrew, 14 Oct). */
.visit-row{display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;gap:6px 10px;
  padding:7px 0;border-top:1px solid var(--line-soft)}
.visit-row-text{flex:1 1 170px;min-width:0;font-size:12.5px}
.visit-row-btns{display:flex;gap:6px;flex-wrap:wrap;margin-left:auto}
.modal-tabbed{height:84vh;max-height:84vh;overflow:hidden}
.modal-tabbed .mbody{flex:1;overflow-y:auto}
.mhead{padding:20px 22px;border-bottom:1px solid var(--line-soft);display:flex;align-items:flex-start;justify-content:space-between;
  background:linear-gradient(135deg, var(--canvas) 0%, var(--surface) 60%);border-radius:var(--radius-lg) var(--radius-lg) 0 0}
.mtitle{font-size:19px;font-weight:800;letter-spacing:-0.02em;color:var(--ink)}
.mbody{padding:20px 22px;flex:1;display:flex;flex-direction:column;gap:11px}
.mfoot{padding:14px 22px;border-top:1px solid var(--line-soft);display:flex;gap:9px;justify-content:flex-end;background:var(--canvas)}
.fl{display:flex;flex-direction:column;gap:4px;min-width:0}
.fl label{font-size:10px;font-weight:600;color:var(--ink-faint);text-transform:uppercase;letter-spacing:0.6px}
.fl input,.fl select,.fl textarea{width:100%}
.row2{display:grid;grid-template-columns:1fr 1fr;gap:10px;min-width:0}
.row3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;min-width:0}

/* Quote editor line-item rows, and the quoted-vs-actual table. Classes rather than
   inline styles so the phone can be told something different - see the mobile block. */
.qe-line{background:var(--surface);border:1px solid var(--line);border-radius:var(--radius);padding:12px 14px;
  margin-bottom:12px;display:flex;flex-direction:column;gap:8px;box-shadow:var(--shadow-sm)}
.qe-line-num{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--accent);
  padding-bottom:6px;border-bottom:1px solid var(--line-soft)}
.qe-costs{display:grid;grid-template-columns:70px 1fr 1fr;gap:8px;min-width:0}
.qe-markup{display:grid;grid-template-columns:90px 1fr 100px auto;gap:8px;align-items:end;min-width:0}
.qva-row{display:grid;grid-template-columns:1fr 90px 90px 140px;gap:8px;align-items:center;min-width:0}
.sec{font-size:11px;font-weight:700;color:var(--ink-soft);text-transform:uppercase;letter-spacing:0.6px;
  padding-bottom:8px;border-bottom:1px solid var(--line-soft);margin-bottom:4px}
.log-item{display:flex;gap:9px;padding:6px 0;border-bottom:1px solid var(--line-soft)}
.log-time{font-size:10.5px;color:var(--ink-faint);white-space:nowrap;padding-top:1px;min-width:92px}
.log-text{font-size:12.5px;color:var(--ink-soft)}
.xcbtn{background:none;border:none;cursor:pointer;color:var(--ink-faint);font-size:24px;line-height:1;padding:0;transition:color .12s}
.xcbtn:hover{color:var(--ink)}

/* Dropdown menus (the Actions button on quotes and invoices) */
.menu-item{display:block;width:100%;text-align:left;background:none;border:none;cursor:pointer;
  font:inherit;font-size:12.5px;color:var(--ink);padding:7px 10px;border-radius:6px;
  white-space:nowrap;transition:background .12s}
.menu-item:hover{background:var(--canvas)}
.menu-item:disabled{color:var(--ink-faint);cursor:default;background:none}

/* Login */
.login-wrap{display:flex;align-items:center;justify-content:center;height:100vh;background:
  radial-gradient(circle at 30% 20%, #f0fdf6 0%, var(--canvas) 60%)}
.login-box{background:var(--surface);border:1px solid var(--line-soft);border-radius:var(--radius-lg);padding:36px;width:340px;box-shadow:var(--shadow)}
.login-title{font-size:22px;font-weight:700;margin-bottom:5px;letter-spacing:-0.02em}
.login-sub{font-size:13px;color:var(--ink-soft);margin-bottom:24px}
.login-field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.login-field label{font-size:12.5px;font-weight:500;color:var(--ink-soft)}
.login-field input{width:100%;padding:9px 11px;font-size:13.5px}
.err{font-size:12.5px;color:var(--red);margin-bottom:10px;background:var(--red-soft);padding:7px 10px;border-radius:6px}
.user-info{font-size:12.5px;color:var(--ink-soft);font-weight:500}

/* Diary */
.cal-wrap{flex:1;overflow:hidden;display:flex;flex-direction:column}
.cal-nav{display:flex;align-items:center;gap:13px;padding:11px 20px;background:var(--surface);border-bottom:1px solid var(--line);flex-shrink:0}
.cal-title{font-size:14.5px;font-weight:700;letter-spacing:-0.01em}

/* Spreadsheet */
.spreadsheet-wrap{flex:1;overflow:auto;padding:14px 20px}
.ss-table{font-size:11.5px;border-collapse:collapse;background:var(--surface);border:1px solid var(--line-soft);min-width:100%;border-radius:var(--radius);overflow:hidden}
.ss-table thead th{padding:7px 9px;text-align:left;font-size:10.5px;font-weight:600;color:var(--ink-faint);border-bottom:1px solid var(--line-soft);
  border-right:1px solid var(--line-soft);white-space:nowrap;background:var(--canvas-sunken);position:sticky;top:0;z-index:1;cursor:pointer}
.ss-table thead th:hover{background:#ececeb}
.ss-table tbody tr{border-bottom:1px solid var(--line-soft);cursor:pointer}
.ss-table tbody tr:hover{background:var(--canvas)}
.ss-table tbody td{padding:6px 9px;border-right:1px solid var(--line-soft);white-space:nowrap;max-width:200px;overflow:hidden;text-overflow:ellipsis}
.ss-table .won{background:#f3fbf7}.ss-table .dead{background:#fef8f8;color:var(--ink-faint)}

/* Diary grid */
.diary-grid{flex:1;overflow:auto;display:grid;grid-template-columns:56px repeat(7,1fr);grid-auto-rows:min-content;align-content:start;gap:1px;background:var(--line-soft);padding:1px}
.dg-corner{background:var(--canvas-sunken);position:sticky;top:0;left:0;z-index:3}
.dg-dayhead{background:var(--canvas-sunken);padding:0;height:26px;max-height:26px;overflow:hidden;
  text-align:center;font-size:12px;font-weight:700;color:var(--ink-soft);position:sticky;top:0;z-index:2;cursor:default;
  display:flex;align-items:center;justify-content:center;line-height:1;box-sizing:border-box;white-space:nowrap;border:none}
.dg-dayhead.today{color:var(--accent);background:var(--accent-soft);font-weight:700}
.dg-timelabel{background:var(--canvas-sunken);
  padding:3px 5px;font-size:10px;color:var(--ink-faint);text-align:right;display:flex;align-items:center;justify-content:flex-end;font-weight:500}
.dg-cell{padding:2px;min-height:172px;
  background:var(--surface);cursor:pointer;transition:background .12s;position:relative}
.dg-cell.today-col{background:#fbfdfb}
.dg-cell:hover{background:var(--canvas)}
.dg-cell.unavail{background:repeating-linear-gradient(135deg, #f4f4f3 0px, #f4f4f3 6px, #ececeb 6px, #ececeb 12px);cursor:pointer;
  display:flex;align-items:center;justify-content:center}
.dg-cell.unavail::after{display:none}
.unavail-label{font-size:9px;color:var(--ink-soft);font-weight:600;padding:4px 8px;text-align:center;
  background:var(--surface);border:1px solid var(--line-soft);border-radius:4px;margin:0}
.visit-pill{border-radius:6px;padding:4px 6px;margin-bottom:2px;font-size:10px;line-height:1.4;cursor:pointer;
  display:flex;justify-content:space-between;align-items:flex-start;gap:3px;box-shadow:var(--shadow-sm);transition:filter .1s}
.visit-pill:hover{filter:brightness(0.97)}
.visit-pill-del{background:none;border:none;cursor:pointer;font-size:13px;line-height:1;padding:0;flex-shrink:0;opacity:0.55}
.visit-pill-del:hover{opacity:1}
.visit-pill-edit{background:none;border:none;cursor:pointer;font-size:11px;line-height:1;padding:0;flex-shrink:0;opacity:0.55}
.visit-pill-edit:hover{opacity:1}

/* The two invoice tables. In CSS rather than inline so the breakpoint below can
   reach them, and so :hover handles its own border instead of two JS handlers
   that leave a touch device stuck in the hover state after a tap. */
.jobs-grid{grid-template-columns:repeat(7,1fr)}
.job-lab{margin-top:10px;border-top:1px solid var(--line-soft);padding-top:8px}
.rep-wrap{flex:1;overflow-y:auto;padding:14px 20px;max-width:1180px;margin:0 auto;width:100%}
.rep-filters{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:16px}
.rep-preset{font-size:11.5px;padding:4px 9px;border:1px solid var(--line-soft);border-radius:999px;
  background:var(--canvas);cursor:pointer;color:var(--ink-faint)}
.rep-preset.on{border-color:var(--accent);color:var(--accent);font-weight:600}
.rep-sec{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:var(--ink-faint);
  margin:20px 0 8px;font-weight:600}
.rep-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px}
.rep-tile{background:var(--canvas);border:1px solid var(--line-soft);border-radius:var(--radius);padding:11px 13px}
.rep-tile-l{font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;color:var(--ink-faint)}
.rep-tile-v{font-size:21px;font-weight:600;margin-top:3px;line-height:1.15}
.rep-tile-s{font-size:11px;color:var(--ink-faint);margin-top:2px}
.rep-tile.hero .rep-tile-v{font-size:27px;color:var(--accent)}
.rep-warn{background:var(--canvas);border:1px solid var(--line-soft);border-left:3px solid #b45309;
  border-radius:var(--radius);padding:10px 13px;font-size:12px;margin-top:10px}
.rep-warn b{color:#b45309}
.rep-table{width:100%;border-collapse:collapse;font-size:12.5px}
.rep-table th{text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;
  color:var(--ink-faint);font-weight:600;padding:5px 8px;border-bottom:1px solid var(--line-soft)}
.rep-table td{padding:6px 8px;border-bottom:1px solid var(--line-soft)}
.rep-table td.n,.rep-table th.n{text-align:right;font-variant-numeric:tabular-nums}
.rep-table tr:last-child td{border-bottom:none}
.rep-empty{color:var(--ink-faint);font-size:12.5px;padding:14px 0}
@media (max-width:760px){.rep-wrap{padding:12px 14px}.rep-tile-v{font-size:18px}}
@media (pointer: coarse){.rep-preset{min-height:34px;display:inline-flex;align-items:center}}
.job-lab-head{display:flex;align-items:center;gap:10px;font-size:12.5px;cursor:pointer;user-select:none}
.job-lab-head:hover{color:var(--accent)}
.job-lab-arrow{width:12px;color:var(--ink-faint);font-size:10px}
.job-lab-rev{margin-left:auto;color:var(--ink-faint);font-size:11.5px}
.job-lab-rev em{font-style:normal;color:#b45309}
.job-lab-body{margin-top:7px;padding-left:22px}
.job-lab-row{display:grid;grid-template-columns:1.4fr .7fr .7fr .8fr 1fr;gap:8px;font-size:12px;padding:3px 0;color:var(--ink-faint)}
.job-lab-row span:first-child{color:var(--ink)}
.job-lab-h{font-size:10.5px;text-transform:uppercase;letter-spacing:.04em}
.sup-row{display:flex;flex-wrap:wrap;gap:10px 14px;align-items:flex-start;padding:9px 0;border-top:1px solid var(--line-soft)}
.sup-row:first-child{border-top:none;padding-top:2px}
.sup-main{flex:1 1 260px;min-width:0;display:flex;flex-direction:column;gap:3px}
.sup-name{font-size:12.5px;font-weight:600;color:var(--ink)}
.sup-meta{font-size:11.5px;color:var(--ink-soft)}
.sup-desc{font-size:11.5px;color:var(--ink-faint);overflow-wrap:anywhere}
.sup-files{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px}
.sup-thumb{display:block;width:64px;height:64px;border:1px solid var(--line);border-radius:var(--radius-sm);
  overflow:hidden;background:var(--canvas-sunken)}
.sup-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.sup-doc{display:flex;align-items:center;justify-content:center;width:64px;height:64px;border:1px solid var(--line);
  border-radius:var(--radius-sm);background:var(--canvas-sunken);font-size:11px;font-weight:700;color:var(--ink-soft);text-decoration:none}
.sup-missing{font-size:11.5px;color:var(--ink-faint);font-style:italic}
.sup-verdict{font-size:11.5px;margin-top:3px;color:var(--ink-soft)}
.sup-actions{display:flex;flex-wrap:wrap;gap:6px;align-items:center}
.sup-undo{background:none;border:none;padding:0 2px;font:inherit;font-size:11.5px;color:var(--ink-faint);
  text-decoration:underline;cursor:pointer}
.cons-month{background:var(--surface);border:1px solid var(--line-soft);border-radius:var(--radius);
  padding:12px 14px;margin-bottom:10px}
.cons-month-head{display:flex;align-items:center;gap:10px;font-size:13px;flex-wrap:wrap}
.cons-month-body{margin-top:8px}
@media (max-width:760px){.sup-main{flex-basis:100%}}
@media (pointer: coarse){.job-lab-head{min-height:44px}}
.jg-cell{min-height:240px;padding:3px}

/* While a job is being placed: every day is a target, the days it is already on
   are marked. Base CSS rather than the touch block - picking days is now how
   multi-day scheduling works with a mouse too. */
.jg-cell.jg-target{box-shadow:inset 0 0 0 2px var(--accent-soft-line)}
.jg-cell.jg-on{box-shadow:inset 0 0 0 2px var(--accent)}

.jg-crew{display:flex;gap:3px;flex-wrap:wrap;margin-top:4px;cursor:pointer}
.jg-chip{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;
  padding:0 4px;border-radius:9px;background:rgba(255,255,255,0.78);border:1px solid currentColor;
  font-size:9px;font-weight:700;line-height:1}
.jg-chip-empty{opacity:0.65;font-weight:600}

.jg-modal-back{position:fixed;inset:0;z-index:80;background:rgba(24,24,27,0.4);
  display:flex;align-items:center;justify-content:center;padding:20px}
.jg-modal{background:var(--surface);border-radius:10px;padding:16px;width:min(340px,100%);
  max-height:80vh;overflow-y:auto;box-shadow:0 12px 40px rgba(0,0,0,0.25)}
.jg-person{display:flex;align-items:center;justify-content:space-between;width:100%;
  padding:10px 12px;margin-bottom:6px;border-radius:6px;border:1px solid var(--line);
  background:var(--canvas);font-size:13px;color:var(--ink);cursor:pointer;min-height:42px}
.jg-person.on{border-color:var(--accent);background:var(--accent-soft);font-weight:600}

/* Same height, same radius and the same sunken fill as the <audio> player that sits
   under the photos, so a recording in progress and a recording already made read as one
   family. Full width, on its own line - see the note on the markup. */
.rec-live{display:flex;align-items:center;gap:10px;height:30px;margin-top:8px;
  padding:0 12px;background:var(--canvas-sunken);border:1px solid var(--line-soft);border-radius:15px}
.rec-dot{width:8px;height:8px;border-radius:50%;background:var(--red);flex-shrink:0;
  animation:rec-pulse 1.2s ease-in-out infinite}
.rec-track{flex:1;min-width:0;height:6px;border-radius:3px;background:var(--line);overflow:hidden}
.rec-fill{display:block;height:100%;width:0;background:var(--red);border-radius:3px;transition:width .06s linear}
.rec-live-label{font-size:11px;color:var(--ink-faint);flex-shrink:0}
@keyframes rec-pulse{0%,100%{opacity:1}50%{opacity:0.35}}

.nt-wrap{position:relative}
.nt-bell{background:transparent;border:1px solid var(--line-soft);border-radius:6px;width:32px;height:32px;
  display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ink-faint);font-size:16px;
  position:relative;flex-shrink:0}
.nt-bell:hover{background:var(--canvas-sunken);color:var(--ink)}
.nt-bell.on{background:var(--canvas-sunken);color:var(--ink)}
.nt-count{position:absolute;top:-6px;right:-6px;min-width:16px;height:16px;padding:0 4px;border-radius:8px;
  background:var(--red);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;
  justify-content:center;line-height:1}
.nt-panel{position:absolute;top:38px;right:0;z-index:70;width:min(360px,calc(100vw - 24px));
  background:var(--surface);border:1px solid var(--line);border-radius:10px;
  box-shadow:0 12px 40px rgba(0,0,0,0.18);overflow:hidden}
.nt-head{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;
  border-bottom:1px solid var(--line-soft);font-size:12.5px;font-weight:600}
.nt-list{max-height:min(420px,60vh);overflow-y:auto}
.nt-item{display:block;width:100%;text-align:left;border:none;border-bottom:1px solid var(--line-soft);
  background:var(--surface);padding:10px 12px;cursor:pointer;font-size:12.5px;color:var(--ink)}
.nt-item:hover{background:var(--canvas)}
.nt-item.unread{background:var(--blue-soft)}
.nt-item.unread:hover{background:var(--blue-soft)}
.nt-item-title{font-weight:600;margin-bottom:2px}
.nt-item-body{color:var(--ink-soft);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nt-item-when{font-size:10.5px;color:var(--ink-faint);margin-top:3px}
.nt-empty{padding:18px 12px;font-size:12.5px;color:var(--ink-faint);text-align:center}

.jc-crew{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.jc-crew-day{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line-soft);
  border-radius:6px;padding:4px 8px;background:var(--canvas);cursor:pointer}
.jc-crew-date{font-size:11px;font-weight:600;color:var(--ink-faint)}
.jc-crew-chip{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;
  padding:0 5px;border-radius:10px;background:var(--accent-soft);border:1px solid var(--accent-soft-line);
  font-size:9.5px;font-weight:700;line-height:1;color:var(--ink)}
.jc-crew-none{font-size:11px;font-weight:600;color:var(--accent)}

.lab-wrap{padding:16px 20px 30px;max-width:1000px}
.lab-head{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:14px}
.lab-card{border:1px solid var(--line);border-radius:9px;background:var(--surface);margin-bottom:12px;overflow:hidden}
.lab-card-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:11px 14px;
  background:var(--canvas-sunken);border-bottom:1px solid var(--line-soft);font-size:13px}
.lab-card-body{padding:12px 14px}
.lab-warn{border:1px solid var(--amber-soft-line);background:var(--amber-soft);border-radius:9px;
  padding:12px 14px;margin-bottom:14px}
.lab-ex{display:grid;grid-template-columns:1fr auto;gap:10px;align-items:center;
  padding:9px 0;border-top:1px solid var(--amber-soft-line);font-size:13px}
.lab-ex:first-of-type{border-top:none}
.lab-times{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.lab-times input[type=time]{width:104px}
.lab-stint{display:grid;grid-template-columns:1fr auto auto;gap:10px;align-items:center;
  padding:8px 0;border-top:1px solid var(--line-soft);font-size:13px}
.lab-edited{font-size:11px;color:var(--amber);font-weight:600}
.lab-num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
.lab-week{display:grid;grid-template-columns:1fr 60px 76px 92px 82px 92px auto;gap:8px;align-items:center;
  padding:10px 0;border-bottom:1px solid var(--line-soft);font-size:13px}
.lab-week-head{font-size:10.5px;font-weight:600;color:var(--ink-faint);text-transform:uppercase;letter-spacing:0.5px}
.lab-day{display:grid;grid-template-columns:1fr 70px 110px auto;gap:8px;align-items:center;
  padding:7px 0 7px 14px;font-size:12.5px;border-top:1px solid var(--line-soft)}
@media (max-width: 760px){
  .lab-wrap{padding:12px 12px 30px}
  .lab-week{grid-template-columns:1fr 1fr;gap:6px}
  .lab-week-head{display:none}
  .lab-day{grid-template-columns:1fr 1fr;padding-left:0}
  .lab-ex{grid-template-columns:1fr}
  .lab-stint{grid-template-columns:1fr}
}

.team-row{display:grid;grid-template-columns:1fr 130px 150px 110px 110px;gap:10px;align-items:center;
  padding:10px 0;border-bottom:1px solid var(--line-soft);font-size:13px}
.team-row.off{opacity:0.55}
.team-head{font-size:10.5px;font-weight:600;color:var(--ink-faint);text-transform:uppercase;letter-spacing:0.5px}
@media (max-width: 760px){
  .team-row{grid-template-columns:1fr 1fr;gap:6px}
  .team-head{display:none}
}

/* Three lines. Drawn rather than an icon font so it inherits the active colour
   from .mnav-item without a second rule. */
.mnav-burger{display:block;width:20px;height:2px;border-radius:1px;background:currentColor;position:relative}
.mnav-burger::before,.mnav-burger::after{content:'';position:absolute;left:0;
  width:20px;height:2px;border-radius:1px;background:currentColor}
.mnav-burger::before{top:-6px}
.mnav-burger::after{top:6px}

/* The move button only exists where dragging cannot work. */
.jg-move{display:none}
.jg-placing{display:none}

/* Hidden until the phone breakpoint switches it on. */
.mnav{display:none}
.mnav-back{display:none}
.mnav-sheet{display:none}

@media (max-width: 760px){
  /* The bar is fixed, so the app is shortened by exactly its height rather than
     having content disappear underneath it. */
  /* viewport-fit=cover (index.html) is what makes env(safe-area-inset-bottom) non-zero on an
     iPhone; without it the bar sat on the home indicator. The bar grows by the inset, so the
     app and the More sheet above it move up by the same amount. */
  .app.app{height:calc(100dvh - 56px - env(safe-area-inset-bottom,0px))}
  .topbar-right > .tabs{display:none}

  .mnav{display:flex;position:fixed;left:0;right:0;bottom:0;height:56px;z-index:60;
    background:var(--surface);border-top:1px solid var(--line);
    padding-bottom:env(safe-area-inset-bottom,0px);box-sizing:content-box}
  .mnav-item{flex:1;border:none;background:none;cursor:pointer;position:relative;
    font-size:11.5px;font-weight:600;color:var(--ink-faint);
    display:flex;align-items:center;justify-content:center;min-height:56px}
  .mnav-item.active{color:var(--accent)}
  .mnav-item.active::before{content:'';position:absolute;top:0;left:22%;right:22%;height:2px;background:var(--accent)}
  .mnav-badge{position:absolute;top:9px;right:50%;transform:translateX(18px);
    min-width:15px;height:15px;padding:0 4px;border-radius:8px;background:var(--red);
    color:#fff;font-size:9.5px;font-weight:700;display:flex;align-items:center;justify-content:center;line-height:1}

  .mnav-back{display:block;position:fixed;inset:0;z-index:59;background:rgba(24,24,27,0.35)}
  .mnav-sheet{display:flex;flex-direction:column;position:fixed;left:0;right:0;
    bottom:calc(56px + env(safe-area-inset-bottom,0px));z-index:61;
    background:var(--surface);border-top:1px solid var(--line)}
  .mnav-sheet-item{border:none;background:none;text-align:left;cursor:pointer;
    padding:14px 18px;font-size:14px;color:var(--ink);border-bottom:1px solid var(--line-soft);min-height:48px}
  .mnav-sheet-item.active{color:var(--accent);font-weight:600}

  /* Diary. The 66px is the time gutter plus the grid's padding and gaps, so one
     day fills what is left of the screen exactly. */
  .cal-nav{flex-wrap:wrap;gap:8px;padding:10px 12px}
  .cal-hint{display:none !important}
  .cal-title{font-size:13px}
  .diary-grid{grid-template-columns:56px repeat(7,calc(100vw - 58px));
    scroll-snap-type:x mandatory;scroll-padding-left:58px}
  .dg-cell,.dg-dayhead,.jg-cell{scroll-snap-align:start;scroll-snap-stop:always}
  .dg-timelabel{position:sticky;left:0;z-index:2}
  .dg-cell{min-height:120px}

  /* Jobs mode, same one-day-per-swipe as Visits. No time gutter here, so a day
     is the full screen less the grid's own 1px padding either side. */
  .jobs-grid{grid-template-columns:repeat(7,100vw);
    scroll-snap-type:x mandatory}
  .jg-strip{padding:8px 12px !important}
  .jg-strip-head{flex-wrap:wrap}
  .jg-strip-hint{display:none}

  /* P3 item 21. The nav is fixed at the bottom and sits above every modal, so the
     overlay reserves its height and the modal fits the box it is given rather than a
     fixed share of the screen. */
  .modal-overlay{padding:12px 12px calc(12px + 56px + env(safe-area-inset-bottom,0px))}
  .modal{max-height:100%;max-width:calc(100vw - 24px)}
  .modal-tabbed{height:100%;max-height:100%}
  .mbody,.view-scroll{overscroll-behavior-y:contain}

  /* P3 item 20. Two columns and nothing collides. The Remove button takes a line of
     its own rather than being squeezed against the VAT box. */
  .qe-costs{grid-template-columns:1fr 1fr}
  .qe-markup{grid-template-columns:1fr 1fr;align-items:start}
  .qe-markup > :last-child{grid-column:1 / -1;justify-self:end;margin-top:2px}

  /* P3 item 24. The variance reads underneath rather than pushing the table off the
     side of the screen. */
  .qva-row{grid-template-columns:1fr 78px 78px;row-gap:2px}
  .qva-row > :nth-child(4){grid-column:1 / -1;text-align:right !important;font-size:11.5px}
}

/* A view that fills the remaining height and scrolls inside it. Without this a
   view is laid out inside .main's overflow:hidden and anything below the fold is
   unreachable rather than merely off-screen. */
.view-scroll{flex:1;min-height:0;overflow-y:auto;padding:0 0 30px}

.money-row{display:grid;grid-template-columns:1fr 150px 118px 130px 120px;gap:12px;align-items:center;
  padding:10px 14px;background:var(--surface);border:1px solid var(--line-soft);
  border-radius:var(--radius);margin-bottom:7px}
.invoice-row{display:grid;grid-template-columns:90px 1fr 120px 110px 100px;gap:12px;align-items:center;
  padding:11px 14px;background:var(--surface);border:1px solid var(--line-soft);
  border-radius:var(--radius);cursor:pointer;transition:border-color .12s}
.invoice-row:hover{border-color:#d4d4d8}

/* Stacked, because five columns of fixed width do not fit a phone however they
   are shared out. The !important is there to beat the inline text-align:right on
   the amount cells, which reads as a stray right margin once stacked. */
@media (max-width: 760px){
  .money-row,.invoice-row{grid-template-columns:1fr;gap:3px;padding:12px 14px}
  .money-row > *,.invoice-row > *{text-align:left !important}
}

/* 100vh is measured against the viewport with the address bar hidden, so on a
   phone or tablet browser the bottom of the app sits under the chrome. dvh
   tracks the visible area; the plain rule stays first as the fallback. */
.app{height:100vh;height:100dvh}
.login-wrap{height:100vh;height:100dvh}

/* Tablet and below. The dashboard's five metrics and the two-column form rows
   are the only grids that do not reflow on their own. */
@media (max-width: 1024px){
  .dash{grid-template-columns:repeat(2,1fr);gap:8px;padding:10px 14px}
  .topbar{height:auto;min-height:54px;flex-wrap:wrap;padding:8px 14px;gap:10px}
  .topbar-right{margin-left:0;width:100%;flex-wrap:wrap;gap:8px}
  .tabs{flex-wrap:wrap;width:100%}
  .filter-bar{flex-wrap:wrap;padding:10px 14px}
  .kanban{padding:12px 14px}
}

@media (max-width: 760px){
  .row2{grid-template-columns:1fr}

  /* Four counters that answer nothing anyone opens a phone to ask, taking a
     third of the screen to do it. They stay on the desktop where there is room. */
  .dash{display:none}

  /* One stage per screen. scroll-snap-stop:always is the important half - it
     stops a fling carrying you past a column into the gap between two. */
  .kanban{scroll-snap-type:x mandatory;scroll-padding-left:12px;padding:10px 12px;gap:12px}
  .col{flex:0 0 100%;scroll-snap-align:start;scroll-snap-stop:always}

  /* The filter bars put a label and its dropdown on separate lines once they
     wrapped. Two columns pairs each label with the control it belongs to; the
     trailing button (Colour: Off) has no label, so it spans both. */
  .filter-bar{display:grid;grid-template-columns:auto 1fr;gap:8px 10px;align-items:center}
  .filter-bar > .btn{grid-column:1 / -1;justify-self:start}
  .filter-label{margin-left:0 !important}
  .filter-bar select.select-compact{width:100%}

  /* Settings: a 180px rail against a 393px screen left nothing for the panel.
     Horizontal strip across the top instead. */
  .settings-nav{width:100% !important;border-right:none !important;
    border-bottom:1px solid var(--line-soft);display:flex;gap:6px;overflow-x:auto;padding:8px 10px !important}
  .settings-nav > div{display:none}
  .settings-nav > button{width:auto !important;white-space:nowrap;flex-shrink:0}
  .settings-wrap{flex-direction:column !important}
}

/* Keyed on touch rather than width, so a landscape tablet at 1920x1200 still
   gets targets a gloved hand can hit while the desktop keeps its compact
   toolbars. 44px is the floor both Apple and Google publish. */
@media (pointer: coarse){
  .btn{min-height:44px;padding:10px 15px}
  .btn-sm{min-height:38px}
  .tabs .tab{min-height:40px}
  input,select,textarea{min-height:44px}
  .fl input,.fl select{height:auto;min-height:44px}
  /* Andrew, 18 Oct: pop-ups "too wide on a mobile". iOS zooms the page in when a field
     under 16px is tapped and leaves it zoomed, so every pop-up after that is wider than
     the screen. 16px on touch screens stops the zoom happening at all. */
  input,select,textarea{font-size:16px !important}

  /* The board's equivalent: a "Move to" box on each card that can still move. */
  .card-move{display:block !important;width:100%;font-size:12px;margin-top:4px}
  /* Drag and drop is mouse-only - a touch screen never fires dragstart - so on
     any touch device a job is moved by tapping Move and then tapping a day. */
  .jg-move{display:block;margin-top:4px;width:100%;min-height:32px;
    border:1px solid currentColor;background:rgba(255,255,255,0.55);
    border-radius:4px;font-size:10.5px;font-weight:700;color:inherit;cursor:pointer}
  .jg-placing{display:flex;align-items:center;gap:10px;flex-wrap:wrap;
    padding:8px 12px;background:var(--accent-soft);
    border-bottom:1px solid var(--accent-soft-line);flex-shrink:0}
  .jg-placing-text{font-size:12px;font-weight:600;color:var(--accent);margin-right:auto}
  .jg-cell.jg-target{box-shadow:inset 0 0 0 2px var(--accent-soft-line)}
  .jg-add{display:block;width:100%;min-height:36px;margin-top:4px;border:1px dashed var(--line);
    background:var(--canvas);border-radius:6px;font-size:11.5px;font-weight:600;color:var(--ink-soft);cursor:pointer}
  .jg-add.on{border-style:solid;border-color:var(--accent);color:var(--accent);background:var(--accent-soft)}
}
`;

// Field app step 2 (docs/field-app-spec.md). On a registered company tablet: tap your name,
// enter your PIN. Anywhere else (and on a tablet via the link) it is username and password.
function Login({ onLogin }) {
  const [tablet,setTablet]=useState(undefined);   // undefined while checking, null = not a tablet
  const [usePassword,setUsePassword]=useState(false);
  useEffect(()=>{api('/api/tablet').then(d=>setTablet(d&&d.device?d:null)).catch(()=>setTablet(null));},[]);
  if(tablet===undefined)return<div className="login-wrap"/>;
  if(tablet&&!usePassword)return<TabletLogin tablet={tablet} onLogin={onLogin} onPassword={()=>setUsePassword(true)}/>;
  return<PasswordLogin onLogin={onLogin} onBack={tablet?()=>setUsePassword(false):null}/>;
}

function TabletLogin({ tablet, onLogin, onPassword }) {
  const [who,setWho]=useState(null);
  const [pin,setPin]=useState('');
  const [err,setErr]=useState('');
  const [busy,setBusy]=useState(false);
  const submit=async(e)=>{
    e&&e.preventDefault();
    if(busy||!who)return;
    setBusy(true);setErr('');
    try{const d=await api('/api/tablet/login',{method:'POST',body:JSON.stringify({user_id:who.id,pin})});onLogin(d.user,true);}
    catch(ex){setErr(ex.message);setPin('');}
    setBusy(false);
  };
  return(
    <div className="login-wrap"><div className="login-box" style={{maxWidth:520}}>
      <div className="login-title">RN <span style={{color:'#16a34a'}}>Pipeline</span></div>
      <div className="login-sub">{tabletLabel(tablet.device)}</div>
      {!who?(
        <>
          <div style={{fontSize:13,color:'var(--ink-soft)',margin:'4px 0 10px'}}>Tap your name</div>
          {(tablet.staff||[]).length===0&&<div style={{fontSize:12.5,color:'var(--ink-faint)'}}>Nobody is set up for tablets yet. In Settings, under People, tick "Can sign in on tablets" and give them a PIN.</div>}
          <div className="tl-grid">
            {(tablet.staff||[]).map(p=>(
              <button key={p.id} className="tl-name" onClick={()=>{setWho(p);setPin('');setErr('');}}>
                <span className="jc-crew-chip" style={personChipStyle(p.colour)}>{crewInitials(p.display_name)}</span>
                {p.display_name}
              </button>
            ))}
          </div>
        </>
      ):(
        <form onSubmit={submit}>
          <div style={{fontSize:15,fontWeight:600,margin:'6px 0 10px'}}>{who.display_name}</div>
          <div className="login-field"><label>PIN</label>
            <input type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="off" maxLength={6} autoFocus
              value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,''))} style={{fontSize:22,letterSpacing:'0.3em',textAlign:'center'}}/></div>
          {err&&<div className="err">{err}</div>}
          <button type="submit" className="btn btn-green" disabled={busy||pin.length<4} style={{width:'100%',justifyContent:'center',padding:'10px'}}>{busy?'Signing in…':'Sign in'}</button>
          <button type="button" className="btn btn-sm" style={{marginTop:10}} onClick={()=>{setWho(null);setErr('');}}>Not {who.display_name.split(' ')[0]}?</button>
        </form>
      )}
      <button type="button" onClick={onPassword} style={{marginTop:16,background:'none',border:'none',color:'var(--ink-faint)',fontSize:12,cursor:'pointer',textDecoration:'underline'}}>
        Sign in with username and password
      </button>
    </div></div>
  );
}

function PasswordLogin({ onLogin, onBack }) {
  const [u,setU]=useState('');const [p,setP]=useState('');const [err,setErr]=useState('');
  const submit=async(e)=>{e.preventDefault();setErr('');try{const d=await api('/api/login',{method:'POST',body:JSON.stringify({username:u,password:p})});onLogin(d.user);}catch(e){setErr('Invalid username or password');}};
  return(<div className="login-wrap"><div className="login-box"><div className="login-title">RN <span style={{color:'#16a34a'}}>Pipeline</span></div><div className="login-sub">Roofing Network Ltd</div><form onSubmit={submit}><div className="login-field"><label>Username</label><input type="text" value={u} onChange={e=>setU(e.target.value)} autoFocus/></div><div className="login-field"><label>Password</label><input type="password" value={p} onChange={e=>setP(e.target.value)}/></div>{err&&<div className="err">{err}</div>}<button type="submit" className="btn btn-green" style={{width:'100%',justifyContent:'center',padding:'8px'}}>Sign in</button></form>{onBack&&<button type="button" onClick={onBack} style={{marginTop:14,background:'none',border:'none',color:'var(--ink-faint)',fontSize:12,cursor:'pointer',textDecoration:'underline'}}>Back to tablet sign-in</button>}</div></div>);
}

function BrandBadge({ brand }) {
  const c = brandColor(brand);
  return <span className="bbadge" style={{background:c.bg,color:c.text,border:`1px solid ${c.border}`}}>{brand}</span>;
}

// Words that carry nothing in a heading. Kept deliberately narrow: anything that
// could name a part of a roof or a place on it stays in.
const TITLE_STOPWORDS = new Set(['the','a','an','to','of','on','in','at','for','with','from','and','or','but',
  'is','are','was','were','be','been','being','it','its','we','our','us','i','my','me','you','your',
  'there','this','that','these','those','has','have','had','will','would','can','could','should',
  'need','needs','needed','needing','want','wants','get','gets','got','go','goes','going','do','does',
  'did','done','also','just','really','very','quite','some','any','all','back','up','down','out','over',
  'off','right','left','new','bit','lot','whole','then','so','as','by','into','onto',
  'nice','good','bad','lovely','great','fine','okay']);

function suggestLineTitle(text) {
  if (!text) return '';
  let s = String(text).replace(/\s+/g, ' ').trim();
  const first = s.match(/^[^.!?]+/);
  if (first) s = first[0].trim();
  // "So, right, we're going to..." - the words before the actual point.
  const filler = /^(so|right|ok|okay|and|but|then|now|yeah|basically|obviously|erm|um|uh|well|look|i think|i reckon|we are going to|we're going to|we will|we'll|we need to|i am going to|i'm going to|i will|i'll|it is|it's|there is|there's|this is|that's|we've got|we have got)\b[,\s]*/i;
  let prev;
  do { prev = s; s = s.replace(filler, '').trim(); } while (s !== prev && s);
  // One thing per line: a second clause describes a second item, not more title.
  s = s.split(/\s*,\s*|\s+(?:and|because|that|which|so that|then|but)\b/i)[0].trim();

  const words = s.split(/\s+/).filter(Boolean);
  const kept = words.filter(w => !TITLE_STOPWORDS.has(w.toLowerCase().replace(/[^a-z'-]/gi, '')));
  // Falling back to the raw words matters for something like "Flat roof" where
  // every word would otherwise survive stripping anyway.
  let out = (kept.length ? kept : words).slice(0, 4).join(' ');
  out = out.replace(/[,;:\-\s]+$/, '');
  if (!out) return '';
  return out.charAt(0).toUpperCase() + out.slice(1);
}

function PhotoLightbox({ photos, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const prev = (e) => { e.stopPropagation(); setIndex(i => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e.stopPropagation(); setIndex(i => (i + 1) % photos.length); };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + photos.length) % photos.length);
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % photos.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose]);
  const photo = photos[index];
  return (
    <div className="modal-overlay" onClick={onClose} style={{zIndex:60,background:'rgba(10,10,10,0.88)'}}>
      <div onClick={e=>e.stopPropagation()} style={{position:'relative',maxWidth:'90vw',maxHeight:'88vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <img src={uploadUrl(photo.filename, 'display')} alt={photo.original_name}
          style={{maxWidth:'90vw',maxHeight:'88vh',objectFit:'contain',borderRadius:8,boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}/>
        {photos.length > 1 && (
          <>
            <button onClick={prev} style={{position:'absolute',left:-50,top:'50%',transform:'translateY(-50%)',
              background:'rgba(255,255,255,0.15)',border:'none',color:'#fff',width:38,height:38,borderRadius:'50%',
              fontSize:18,cursor:'pointer'}}>‹</button>
            <button onClick={next} style={{position:'absolute',right:-50,top:'50%',transform:'translateY(-50%)',
              background:'rgba(255,255,255,0.15)',border:'none',color:'#fff',width:38,height:38,borderRadius:'50%',
              fontSize:18,cursor:'pointer'}}>›</button>
            <div style={{position:'absolute',bottom:-32,left:'50%',transform:'translateX(-50%)',color:'#fff',fontSize:12,opacity:0.8}}>
              {index+1} / {photos.length}
            </div>
          </>
        )}
        <a href={uploadUrl(photo.filename)} target="_blank" rel="noreferrer"
          style={{position:'absolute',top:-30,left:0,color:'#fff',fontSize:12,opacity:0.85}}>Full size original</a>
        <button onClick={onClose} style={{position:'absolute',top:-36,right:0,background:'none',border:'none',color:'#fff',fontSize:26,cursor:'pointer',lineHeight:1}}>×</button>
      </div>
    </div>
  );
}

function VisitInformationSection({ leadId, visitNotes, onVisitNotesChange, onReadyToQuote, currentStage, quotes, latestQuote, onOpenQuote, onNewQuote, fieldMode }) {
  // Saved as it is typed. Everything beside this box - photos, recordings - saves
  // itself the moment it happens, so notes that quietly needed a button were the
  // odd one out, and the buttons underneath made losing them the default.
  const notesTimer = useRef(null);
  const [notesSaved, setNotesSaved] = useState(false);
  const saveNotes = useCallback(async (text) => {
    try {
      await api(`/api/leads/${leadId}/visit-notes`, { method: 'PUT', body: JSON.stringify({ visit_notes: text }) });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 1800);
    } catch (e) {
      // On the tablet with no signal the note is kept and sent later (field app step 5).
      if (fieldMode && isNetErr(e)) fqAdd({ method: 'PUT', url: `/api/leads/${leadId}/visit-notes`, body: { visit_notes: text }, replace: true });
      /* otherwise the text is still on screen and Save changes still works */
    }
  }, [leadId, fieldMode]);
  const handleNotes = (v) => {
    onVisitNotesChange(v);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => saveNotes(v), 700);
  };
  // Leaving the box saves at once, which is what covers clicking straight into a quote.
  const flushNotes = (v) => {
    if (notesTimer.current) { clearTimeout(notesTimer.current); notesTimer.current = null; }
    saveNotes(v);
  };
  useEffect(() => () => { if (notesTimer.current) clearTimeout(notesTimer.current); }, []);

  const { showAlert } = useDialog();
  const [attachments, setAttachments] = useState([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const audioCtxRef = useRef(null);
  const meterRafRef = useRef(null);
  const [level, setLevel] = useState(0);

  const load = useCallback(()=>{
    api(`/api/attachments?lead_id=${leadId}`).then(setAttachments).catch(()=>{});
  }, [leadId]);
  useEffect(()=>{ load(); }, [load]);

  const queued = useUploadQueue(leadId);
  useEffect(() => {
    const done = (e) => { if (String(e.detail && e.detail.lead_id) === String(leadId)) load(); };
    window.addEventListener('rn-upload-done', done);
    return () => window.removeEventListener('rn-upload-done', done);
  }, [leadId, load]);

  const uploadFile = async (file, type) => {
    // Saved on this device first, then sent in the background - see uqEnqueue.
    try { await uqEnqueue(file, leadId, type); return; } catch (e) { /* no IndexedDB: upload directly */ }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('lead_id', leadId);
      form.append('type', type);
      const r = await fetch('/api/attachments', { method:'POST', credentials:'include', body:form });
      if (!r.ok) {
        const raw = await r.text().catch(() => '');
        let msg = '';
        try { msg = (JSON.parse(raw) || {}).error || ''; } catch (_) { msg = raw.slice(0, 200); }
        throw new Error(`Upload failed (${r.status})${msg ? ': ' + msg : ''}`);
      }
      load();
    } catch (e) { await showAlert(e.message); }
    setUploading(false);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(f => uploadFile(f, 'photo'));
    e.target.value = '';
  };

  // 0 to 1, read from the live stream. Only ever used to draw the bar.
  const startMeter = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      audioCtxRef.current = ctx;
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        // Root mean square around the 128 midpoint: loudness, not a single sample.
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 3.2));
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { /* a meter is not worth failing a recording over */ }
  };

  const stopMeter = () => {
    if (meterRafRef.current) { cancelAnimationFrame(meterRafRef.current); meterRafRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} audioCtxRef.current = null; }
    setLevel(0);
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('This browser will not give a web page access to the microphone.');
      }
      if (typeof MediaRecorder === 'undefined') {
        throw new Error('This browser cannot record audio. On an iPhone this usually means iOS needs updating.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Ask what the browser can actually produce rather than assuming webm, which no
      // version of Safari has ever supported.
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus'];
      const supported = MediaRecorder.isTypeSupported
        ? candidates.find(t => MediaRecorder.isTypeSupported(t))
        : null;
      const mr = supported ? new MediaRecorder(stream, { mimeType: supported }) : new MediaRecorder(stream);
      const closeMic = () => { stopMeter(); try { stream.getTracks().forEach(t => t.stop()); } catch (_) {} };
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onerror = async (e) => {
        closeMic();
        setRecording(false);
        const why = (e && e.error && (e.error.message || e.error.name)) || 'unknown error';
        await showAlert('The recording stopped unexpectedly: ' + why);
      };
      mr.onstop = async () => {
        try {
          if (!chunksRef.current.length) throw new Error('Nothing was recorded.');
          // Label the file with what the recorder actually made, not what we asked for.
          const type = String(mr.mimeType || supported || 'audio/webm').split(';')[0];
          const ext = type.includes('mp4') ? 'm4a'
                    : type.includes('aac') ? 'aac'
                    : type.includes('ogg') ? 'ogg'
                    : type.includes('wav') ? 'wav'
                    : 'webm';
          const blob = new Blob(chunksRef.current, { type });
          const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type });
          await uploadFile(file, 'voice');
        } catch (err) {
          await showAlert('Could not save the recording: ' + err.message);
        } finally {
          closeMic();
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      startMeter(stream);
    } catch (e) {
      await showAlert('Could not start recording: ' + (e && e.message ? e.message : e));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const [confirmDelId, setConfirmDelId] = useState(null);
  const del = async (id) => {
    await api(`/api/attachments/${id}`, { method: 'DELETE' });
    load();
    setConfirmDelId(null);
  };

  const photos = attachments.filter(a => a.type === 'photo');
  const voiceNotes = attachments.filter(a => a.type === 'voice');

  return (
    <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
      <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>Visit information</div>

      <div className="fl" style={{marginBottom:10}}>
        <label>Notes from visit</label>
        <textarea rows={3} value={visitNotes||''} onChange={e=>handleNotes(e.target.value)}
          onBlur={e=>flushNotes(e.target.value)} placeholder="What did you find on site?"/>
        <div style={{fontSize:10.5,color:'var(--ink-faint)',marginTop:2,height:13}}>
          {notesSaved ? 'Saved' : 'Saves as you type'}
        </div>
      </div>

      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:'var(--ink-faint)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6}}>
          Photos &amp; voice notes
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
          <button className="btn btn-sm" onClick={()=>fileInputRef.current.click()} disabled={uploading}>
            {uploading?'Uploading…':'Take photo'}
          </button>
          <button className="btn btn-sm" onClick={()=>libraryInputRef.current.click()} disabled={uploading}>
            From library
          </button>
          {!recording ? (
            <button className="btn btn-sm" style={{background:'var(--blue-soft)',color:'var(--blue)',borderColor:'var(--blue-soft-line)'}} onClick={startRecording}>
              ● Record voice note
            </button>
          ) : (
            <button className="btn btn-sm" style={{background:'var(--red-soft)',color:'var(--red)',borderColor:'var(--red-soft-line)'}} onClick={stopRecording}>■ Stop &amp; save</button>
          )}
        </div>
        {recording && (
          <div className="rec-live" title="The microphone is live">
            <span className="rec-dot"/>
            <span className="rec-track"><span className="rec-fill" style={{width:`${Math.round(Math.min(1, level) * 100)}%`}}/></span>
            <span className="rec-live-label">Recording</span>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple capture="environment" style={{display:'none'}} onChange={handlePhotoSelect}/>
        <input ref={libraryInputRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={handlePhotoSelect}/>
      </div>

      {photos.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,marginBottom:voiceNotes.length?10:0}}>
          {photos.map((p, i) => (
            <div key={p.id} style={{position:'relative',borderRadius:6,overflow:'hidden',border:'1px solid var(--line-soft)'}}>
              <img src={uploadUrl(p.filename, 'thumb')} alt={p.original_name} onClick={()=>setLightboxIndex(i)}
                style={{width:'100%',height:70,objectFit:'cover',display:'block',cursor:'pointer'}}/>
              <button onClick={()=>setConfirmDelId(p.id)} style={{position:'absolute',top:2,right:2,background:'rgba(0,0,0,0.6)',color:'#fff',
                border:'none',borderRadius:4,width:18,height:18,fontSize:12,cursor:'pointer',lineHeight:1}}>×</button>
            </div>
          ))}
        </div>
      )}
      {lightboxIndex !== null && (
        <PhotoLightbox photos={photos} startIndex={lightboxIndex} onClose={()=>setLightboxIndex(null)}/>
      )}

      {voiceNotes.map(v => (
        <div key={v.id} style={{padding:'6px 0',borderTop:'1px solid var(--line-soft)'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <audio controls src={`/uploads/${v.filename}`} style={{height:30,flex:1}}/>
            <span style={{fontSize:10.5,color:'var(--ink-faint)'}}>{new Date(v.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>
            <button className="btn btn-sm btn-red" onClick={()=>setConfirmDelId(v.id)}>Remove</button>
          </div>
          <VoiceTranscript note={v} onSaved={load}/>
        </div>
      ))}

      {queued.length > 0 && (
        <div className="uq-box">
          {queued.filter(q => !q.error).length > 0 && (
            <div>{queued.filter(q => !q.error).length} {queued.filter(q => !q.error).length === 1 ? 'item' : 'items'} saved on this device, uploading
              {typeof navigator !== 'undefined' && navigator.onLine === false ? ' when there is signal' : '…'}</div>
          )}
          {queued.filter(q => q.error).map(q => (
            <div key={q.id} style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
              <span style={{flex:1}}>{q.type === 'voice' ? 'Voice note' : 'Photo'} not accepted: {q.error}</span>
              <button className="btn btn-sm" onClick={()=>uqDiscard(q.id)}>Discard</button>
            </div>
          ))}
        </div>
      )}
      {photos.length===0 && voiceNotes.length===0 && (
        <div style={{fontSize:12,color:'var(--ink-faint)',marginBottom:10}}>No photos or voice notes yet.</div>
      )}

      {confirmDelId!==null&&(
        <ConfirmModal
          title="Remove attachment"
          message="This will permanently delete this file. This can't be undone."
          confirmLabel="Remove"
          danger
          onConfirm={()=>del(confirmDelId)}
          onCancel={()=>setConfirmDelId(null)}
        />
      )}

      {(() => {
        const sent = (quotes || []).filter(q => q.sent_at);
        const quoteBtn = {
          flex:1, justifyContent:'center', display:'flex', alignItems:'center', gap:6,
          padding:'9px 13px', borderRadius:'var(--radius-sm)', border:'1px solid var(--line)',
          background:'var(--surface)', color:'var(--ink)', fontWeight:600, fontSize:13, cursor:'pointer',
        };
        // In the field there is no quoting: just Visit complete, or a note that it is done.
        if (fieldMode) {
          return ['Lead','Visit'].includes(currentStage)
            ? <button className="btn btn-green" style={{width:'100%',justifyContent:'center',marginTop:10,padding:12}} onClick={onReadyToQuote}>Visit complete</button>
            : <div style={{marginTop:10,fontSize:13,fontWeight:600,color:'var(--accent)'}}>✓ Visit complete - passed to the office to quote</div>;
        }
        if (sent.length) {
          return (
            <div style={{display:'flex',gap:8,marginTop:10}}>
              <button style={quoteBtn} onClick={()=>onOpenQuote(latestQuote.id)}>Edit quote</button>
              <button style={quoteBtn} onClick={onNewQuote}>+ New quote</button>
            </div>
          );
        }
        if (currentStage!=='Quote'&&currentStage!=='Dead'&&currentStage!=='Completed') {
          return (
            <button style={{...quoteBtn,width:'100%',marginTop:10}} onClick={onReadyToQuote}>
              Visit complete →
            </button>
          );
        }
        // At the Quote stage with nothing sent yet: the draft is in the list below,
        // so the only thing missing here is a way to start another one.
        if (currentStage==='Quote') {
          return (
            <button style={{...quoteBtn,width:'100%',marginTop:10}} onClick={onNewQuote}>
              + New quote
            </button>
          );
        }
        return null;
      })()}
    </div>
  );
}

// The transcript sits under its recording and can be corrected in place. A fixed
// measurement is worth more than the machine's guess, so an edited transcript is
// marked and never overwritten by a re-run.
function VoiceTranscript({ note, onSaved }) {
  const { showAlert } = useDialog();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.transcript || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(note.transcript || ''); }, [note.transcript]);

  const status = note.transcript_status;
  const save = async () => {
    setSaving(true);
    try {
      await api(`/api/attachments/${note.id}/transcript`, { method: 'PUT', body: JSON.stringify({ transcript: text }) });
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { showAlert(e.message); }
    setSaving(false);
  };

  if (status === 'queued' || !status) {
    return <div style={{fontSize:11.5,color:'var(--ink-faint)',paddingLeft:2,marginTop:2}}>Transcribing…</div>;
  }
  if (status === 'failed') {
    return (
      <div style={{fontSize:11.5,color:'var(--red)',paddingLeft:2,marginTop:2}}>
        Could not transcribe this recording{note.transcript_error ? ` - ${note.transcript_error}` : ''}. The audio is still here.
      </div>
    );
  }
  if (editing) {
    return (
      <div style={{marginTop:4}}>
        <textarea rows={3} value={text} onChange={e => setText(e.target.value)} style={{width:'100%',fontSize:12.5}}/>
        <div style={{display:'flex',gap:6,marginTop:4}}>
          <button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save transcript'}</button>
          <button className="btn btn-sm" onClick={() => { setText(note.transcript || ''); setEditing(false); }}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{fontSize:12.5,color:'var(--ink-soft)',paddingLeft:2,marginTop:2,lineHeight:1.5}}>
      {note.transcript ? note.transcript : <span style={{color:'var(--ink-faint)'}}>Nothing was said on this recording.</span>}
      <button onClick={() => setEditing(true)}
        style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'0 0 0 6px'}}>
        {status === 'edited' ? 'Edit' : 'Fix'}
      </button>
      {status === 'edited' && <span style={{fontSize:10.5,color:'var(--ink-faint)',marginLeft:6}}>corrected</span>}
    </div>
  );
}

function VisitSection({ leadId, onBookVisit, onVisitChanged }) {
  const [visits, setVisits] = useState([]);
  const [editV, setEditV] = useState(null);
  const [confirmDelVisitId, setConfirmDelVisitId] = useState(null);
  const [offerConfirmId, setOfferConfirmId] = useState(null);
  const load = useCallback(()=>{ api(`/api/visits?lead_id=${leadId}`).then(v=>setVisits(v)).catch(()=>{}); }, [leadId]);
  useEffect(()=>{ load(); }, [load]);
  const del = async(id)=>{
    await api(`/api/visits/${id}`,{method:'DELETE'});
    setVisits(v=>v.filter(x=>x.id!==id));
    setConfirmDelVisitId(null);
    onVisitChanged&&onVisitChanged();
  };
  const confirmVisit = async(id)=>{
    await api(`/api/visits/${id}`,{method:'PUT',body:JSON.stringify({...visits.find(v=>v.id===id),status:'confirmed'})});
    load();
    onVisitChanged&&onVisitChanged();
    setOfferConfirmId(id);
  };
  return(
    <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:visits.length?10:0}}>
        <div className="sec" style={{border:'none',marginBottom:0,paddingBottom:0}}>Visits booked</div>
        <button className="btn btn-sm" style={{background:'var(--blue-soft)',color:'var(--blue)',borderColor:'var(--blue-soft-line)'}} onClick={onBookVisit}>+ Book visit</button>
      </div>
      {visits.map(v=>(
        <div key={v.id} className="visit-row">
          <div className="visit-row-text">
            <div>
              <strong>{fmtVisitDate(v.visit_date)}</strong> at <strong>{v.visit_time}</strong>
              <span style={{marginLeft:7,fontSize:10.5,fontWeight:700,textTransform:'uppercase',color:v.status==='proposed'?'var(--ink-faint)':'var(--accent)'}}>
                {v.status==='proposed'?'Proposed':'Confirmed'}
              </span>
            </div>
            {v.notes&&<div style={{color:'var(--ink-soft)',marginTop:3}}>{v.notes}</div>}
          </div>
          <div className="visit-row-btns">
            {v.status==='proposed'&&<button className="btn btn-sm btn-green" onClick={()=>confirmVisit(v.id)}>Confirm</button>}
            <button className="btn btn-sm" onClick={()=>setEditV(v)}>Edit</button>
            <button className="btn btn-sm btn-red" onClick={()=>setConfirmDelVisitId(v.id)}>Remove</button>
          </div>
        </div>
      ))}
      {editV&&<EditVisitModal visit={editV} onClose={()=>setEditV(null)} onSaved={(status)=>{
        if(editV.status==='proposed'&&status==='confirmed')setOfferConfirmId(editV.id);
        setEditV(null);load();onVisitChanged&&onVisitChanged();}}/>}
      {offerConfirmId!==null&&<VisitConfirmEmailModal leadId={leadId} visitId={offerConfirmId} onClose={()=>setOfferConfirmId(null)}/>}
      {confirmDelVisitId!==null&&(
        <ConfirmModal
          title="Remove visit"
          message="This will permanently remove this booked visit. This can't be undone."
          confirmLabel="Remove"
          danger
          onConfirm={()=>del(confirmDelVisitId)}
          onCancel={()=>setConfirmDelVisitId(null)}
        />
      )}
    </div>
  );
}

function AddressLookup({ house, street, town, postcode, onChange }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [err, setErr] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text.trim() || text.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true); setErr('');
      try {
        const data = await api(`/api/address-lookup/${encodeURIComponent(text.trim())}`);
        setSuggestions(data.suggestions || []);
        setShowDropdown(true);
      } catch (e) { setErr(e.message); }
      setSearching(false);
    }, 300);
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    runSearch(val);
  };

  const pick = async (s) => {
    setResolving(true); setErr(''); setShowDropdown(false);
    try {
      const addr = await api(`/api/address-get/${encodeURIComponent(s.id)}`);
      onChange({ house: addr.house, street: addr.street, town: addr.town, postcode: addr.postcode });
      setQuery(s.address);
      setSuggestions([]);
    } catch (e) { setErr(e.message); }
    setResolving(false);
  };

  return (
    <div className="fl">
      <label>Address lookup</label>
      <div style={{position:'relative'}}>
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={()=>{ if (suggestions.length) setShowDropdown(true); }}
          onBlur={()=>setTimeout(()=>setShowDropdown(false), 150)}
          placeholder="Start typing a house number, street, or postcode…"
          style={{background:'var(--accent-soft)',border:'1px solid var(--accent-soft-line)'}}
        />
        {showDropdown && suggestions.length > 0 && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:10,background:'var(--surface)',
            border:'1px solid var(--line-soft)',borderRadius:6,maxHeight:220,overflowY:'auto',marginTop:2,boxShadow:'var(--shadow)'}}>
            {suggestions.map((s,i) => (
              <div key={s.id||i} onClick={()=>pick(s)} style={{padding:'7px 10px',fontSize:12.5,cursor:'pointer',borderBottom:'1px solid var(--line-soft)'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--canvas)'} onMouseLeave={e=>e.currentTarget.style.background='var(--surface)'}>
                {s.address}
              </div>
            ))}
          </div>
        )}
      </div>
      {searching && <div style={{fontSize:11,color:'var(--ink-faint)',marginTop:4}}>Searching…</div>}
      {err && <div style={{fontSize:11,color:'#dc2626',marginTop:4}}>{err}</div>}
      {resolving && <div style={{fontSize:11,color:'var(--ink-faint)',marginTop:4}}>Loading address…</div>}
      <div style={{marginTop:12,paddingTop:10,borderTop:'1px solid var(--line-soft)'}}>
        <label style={{display:'block',marginBottom:5}}>Address details</label>
        <div className="row3">
          <input type="text" placeholder="House no./name" value={house||''} onChange={e=>onChange({house:e.target.value,street,town,postcode})}/>
          <input type="text" placeholder="Street" value={street||''} onChange={e=>onChange({house,street:e.target.value,town,postcode})}/>
          <input type="text" placeholder="Town" value={town||''} onChange={e=>onChange({house,street,town:e.target.value,postcode})}/>
        </div>
        <input type="text" placeholder="Postcode" value={postcode||''} onChange={e=>onChange({house,street,town,postcode:e.target.value})} style={{marginTop:6,maxWidth:140}}/>
      </div>
    </div>
  );
}

function EditAddressModal({ house, street, town, postcode, onChange, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose} style={{zIndex:65}}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div className="mtitle">Edit address</div>
          <button className="xcbtn" onClick={onClose}>×</button>
        </div>
        <div className="mbody">
          <AddressLookup house={house} street={street} town={town} postcode={postcode} onChange={onChange}/>
          <MapPreview address={[house,street,town,postcode].filter(Boolean).join(', ')}/>
        </div>
        <div className="mfoot">
          <button className="btn btn-green btn-sm" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function MapPreview({ address }) {
  if (!address || !address.trim()) return null;
  const src = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  return (
    <div className="fl">
      <label>Map</label>
      <iframe tabIndex={-1}
        title="Address map"
        src={src}
        width="100%"
        height="180"
        style={{border:0,borderRadius:8}}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

function MessagesSection({ leadId, leadEmail, defaultAssignee, onSentReply, composePurpose }) {
  const { showAlert } = useDialog();
  const [messages, setMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [composing, setComposing] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [attachedPhoto, setAttachedPhoto] = useState(null); // {filename, originalName}
  const [recipients, setRecipients] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const seededLeadRef = useRef(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);

  const [logging, setLogging] = useState(false);
  const [logFrom, setLogFrom] = useState('');
  const [logSubject, setLogSubject] = useState('');
  const [logBody, setLogBody] = useState('');
  const [logPhotos, setLogPhotos] = useState([]); // [{filename, originalName}]
  const [uploadingLogPhoto, setUploadingLogPhoto] = useState(false);
  const logFileInputRef = useRef(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState([]);

  const allKnownEmails = (leadEmail ? [{ email: leadEmail, is_primary: true }] : []).concat(emails.filter(e => e.email !== leadEmail));
  const messagePhotos = messages.filter(m => m.attachment_filename && !isDocFile(m.attachment_filename)).map(m => ({ filename: m.attachment_filename, original_name: m.subject || 'Attachment' }));

  const dismissTodo = async (todoId) => {
    try {
      await api(`/api/todo-items/${todoId}/dismiss`, { method: 'POST' });
      setMessages(prev => prev.map(m => m.todo_id === todoId ? { ...m, todo_dismissed_at: new Date().toISOString() } : m));
    } catch (e) { showAlert(e.message); }
  };

  const load = useCallback(() => {
    api(`/api/leads/${leadId}/messages`).then(setMessages).catch(() => {});
    api(`/api/leads/${leadId}/emails`).then(setEmails).catch(() => {});
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!messages.length || seededLeadRef.current === leadId) return;
    seededLeadRef.current = leadId;
    setExpandedIds(new Set([messages[messages.length - 1].id]));
  }, [messages, leadId]);
  useEffect(() => {
    api(`/api/attachments?lead_id=${leadId}`).then(all => {
      setExistingPhotos(all.filter(a => a.type === 'photo' || a.type === 'message_photo'));
    }).catch(() => {});
  }, [leadId]);
  useEffect(() => { api('/api/email-templates').then(setTemplates).catch(() => {}); }, []);
  useEffect(() => { if (leadEmail) setRecipients([leadEmail]); }, [leadEmail]);
  useEffect(() => { if (leadEmail && !logFrom) setLogFrom(leadEmail); }, [leadEmail]);

  const [flagging, setFlagging] = useState(false);
  const [flagNote, setFlagNote] = useState('');
  const [flagAssignee, setFlagAssignee] = useState(defaultAssignee || 'Andy');

  const saveFlag = async () => {
    if (!flagNote.trim()) { await showAlert('Add a note about what needs doing.'); return; }
    try {
      await api('/api/todo-items', { method: 'POST', body: JSON.stringify({ lead_id: leadId, note: flagNote.trim(), assigned_to: flagAssignee }) });
      setFlagging(false); setFlagNote('');
      load();
    } catch (e) { showAlert(e.message); }
  };

  const applyTemplate = async (id) => {
    setSelectedTemplate(id);
    const t = templates.find(x => String(x.id) === String(id));
    if (!t) return;
    try {
      const filled = await api(`/api/leads/${leadId}/fill-template`, { method: 'POST', body: JSON.stringify({ subject: '', body: t.body || '' }) });
      setBody(filled.body);
    } catch (e) {
      setBody(t.body || '');
    }
  };

  // Opened with a purpose - today that means a visit was just booked. Waits for the
  // templates to load, opens the composer once, and writes the template in. The ref makes
  // it once per opening: re-running it would overwrite whatever had been typed since.
  const seededComposeRef = useRef(false);
  useEffect(() => {
    if (!composePurpose || seededComposeRef.current || !templates.length) return;
    const t = templates.find(x => x.purpose === composePurpose);
    if (!t) return;
    seededComposeRef.current = true;
    const threadSubj = messages.find(m => m.subject)?.subject;
    if (threadSubj) setSubject(threadSubj);
    setComposing(true);
    // applyTemplate is not in the dependency list on purpose: the ref above makes this
    // a one-shot seed, so re-running it could only overwrite something already typed.
    applyTemplate(t.id);
  }, [composePurpose, templates, messages]);

  const toggleRecipient = (email) => {
    setRecipients(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('lead_id', leadId);
      fd.append('type', 'message_photo');
      const r = await fetch('/api/attachments', { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) throw new Error((await r.json()).error || 'Upload failed');
      const a = await r.json();
      setAttachedPhoto({ filename: a.filename, originalName: a.original_name });
    } catch (err) { showAlert(err.message); }
    setUploadingPhoto(false);
    e.target.value = '';
  };

  const send = async () => {
    if (!body.trim()) { await showAlert('Write a message first.'); return; }
    if (recipients.length === 0) { await showAlert('Choose at least one recipient.'); return; }
    setSending(true);
    try {
      await api(`/api/leads/${leadId}/send-email`, { method: 'POST', body: JSON.stringify({ subject, body, to: recipients, attachment_filename: attachedPhoto ? attachedPhoto.filename : null }) });
      await api(`/api/leads/${leadId}/dismiss-reply-todos`, { method: 'POST' }).catch(() => {});
      setComposing(false); setSubject(''); setBody(''); setSelectedTemplate(''); setAttachedPhoto(null);
      load();
      if (onSentReply) onSentReply();
    } catch (e) { showAlert(e.message); }
    setSending(false);
  };

  const uploadLogPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingLogPhoto(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('lead_id', leadId);
        fd.append('type', 'message_photo');
        const r = await fetch('/api/attachments', { method: 'POST', credentials: 'include', body: fd });
        if (!r.ok) throw new Error((await r.json()).error || 'Upload failed');
        const a = await r.json();
        setLogPhotos(prev => [...prev, { filename: a.filename, originalName: a.original_name }]);
      }
    } catch (err) { showAlert(err.message); }
    setUploadingLogPhoto(false);
    e.target.value = '';
  };

  const removeLogPhoto = (filename) => setLogPhotos(prev => prev.filter(p => p.filename !== filename));

  const saveLoggedMessage = async () => {
    if (!logBody.trim() && logPhotos.length === 0) { await showAlert('Add the message text or at least one photo.'); return; }
    try {
      const first = logPhotos[0];
      await api(`/api/leads/${leadId}/messages`, { method: 'POST', body: JSON.stringify({
        direction: 'in', from_email: logFrom || null, to_email: null, subject: logSubject, body: logBody,
        attachment_filename: first ? first.filename : null, sent_at: new Date()
      }) });
      for (const extra of logPhotos.slice(1)) {
        await api(`/api/leads/${leadId}/messages`, { method: 'POST', body: JSON.stringify({
          direction: 'in', from_email: logFrom || null, to_email: null, subject: '', body: '',
          attachment_filename: extra.filename, sent_at: new Date()
        }) });
      }
      setLogging(false); setLogSubject(''); setLogBody(''); setLogPhotos([]);
      load();
    } catch (e) { showAlert(e.message); }
  };

  const firstLine = (text) => {
    if (!text) return '';
    const line = text.split('\n').find(l => l.trim().length > 0) || '';
    return line.length > 100 ? line.slice(0, 100) + '…' : line;
  };

  // Display only. Never used for anything that is sent or stored.
  const stripQuoted = (text) => {
    if (!text) return '';
    const lines = String(text).split('\n');
    const kept = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^\s*>/.test(line)) continue;
      // The attribution that introduces a quote block - "On 3 Oct 2026, Toby Turner
      // wrote:" - which reads as a dangling fragment once the quote under it has gone.
      // Dropped only when the next thing with any content in it is actually quoted.
      if (/\bwrote:\s*$/i.test(line.trim())) {
        const next = lines.slice(i + 1).find(l => l.trim().length);
        if (next && /^\s*>/.test(next)) continue;
      }
      kept.push(line);
    }
    const out = kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    // A message that is nothing but a quote still has to show something.
    return out || String(text).trim();
  };

  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 0, paddingBottom: 0 }}>Messages</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setFlagging(true)}>+ Flag for follow-up</button>
          <button className="btn btn-sm btn-green" onClick={() => { const threadSubj = messages.find(m => m.subject)?.subject; if (threadSubj) setSubject(threadSubj); setComposing(true); }}>+ Reply / message</button>
        </div>
      </div>

      {flagging && (
        <div style={{ border: '1px solid var(--line-soft)', borderRadius: 6, padding: 10, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea rows={2} value={flagNote} onChange={e => setFlagNote(e.target.value)} placeholder="What needs doing? e.g. Lawrance to confirm which material was quoted" />
          <select value={flagAssignee} onChange={e => setFlagAssignee(e.target.value)}>
            <option value="Andy">Andy</option>
            <option value="LD">Lawrance</option>
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-green btn-sm" onClick={saveFlag}>Add to to-do list</button>
            <button className="btn btn-sm" onClick={() => setFlagging(false)}>Cancel</button>
          </div>
        </div>
      )}

      {emails.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 10 }}>
          Known addresses: {emails.map(e => e.email).join(', ')}
        </div>
      )}

      {composing && (
        <div style={{ border: '1px solid var(--line-soft)', borderRadius: 6, padding: 10, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.length > 0 && (
            <select value={selectedTemplate} onChange={e => applyTemplate(e.target.value)}>
              <option value="">Custom message…</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {allKnownEmails.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12.5 }}>
              <span style={{ color: 'var(--ink-faint)' }}>Send to:</span>
              {allKnownEmails.map(e => (
                <label key={e.email} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={recipients.includes(e.email)} onChange={() => toggleRecipient(e.email)} />
                  {e.email}
                </label>
              ))}
            </div>
          )}
          {!messages.find(m => m.subject) && (
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" />
          )}
          <textarea rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="Message" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadPhoto} />
            <button type="button" className="btn btn-sm" onClick={() => fileInputRef.current.click()} disabled={uploadingPhoto}>
              {uploadingPhoto ? 'Uploading…' : '+ Upload photo'}
            </button>
            {existingPhotos.length > 0 && (
              <button type="button" className="btn btn-sm" onClick={() => setShowPhotoPicker(v => !v)}>
                {showPhotoPicker ? 'Hide existing photos' : `Choose from ${existingPhotos.length} existing photo${existingPhotos.length !== 1 ? 's' : ''}`}
              </button>
            )}
            {attachedPhoto && (
              <span style={{ fontSize: 11.5, color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={uploadUrl(attachedPhoto.filename, 'thumb')} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--line-soft)' }} />
                {attachedPhoto.originalName}
                <button type="button" onClick={() => setAttachedPhoto(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: 0 }}>×</button>
              </span>
            )}
          </div>
          {showPhotoPicker && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 56px)', gap: 8, marginTop: -4, marginBottom: 4 }}>
              {existingPhotos.map(p => (
                <img key={p.id} src={uploadUrl(p.filename, 'thumb')} alt="" onClick={() => { setAttachedPhoto({ filename: p.filename, originalName: p.original_name }); setShowPhotoPicker(false); }}
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line-soft)', cursor: 'pointer' }} />
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-green btn-sm" onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
            <button className="btn btn-sm" onClick={() => { setComposing(false); setAttachedPhoto(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {messages.length === 0 && !composing && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>No messages yet.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, idx) => {
          const isExpanded = expandedIds.has(m.id);
          const isTodo = m.direction === 'todo';
          const todoOpen = isTodo && !m.todo_dismissed_at;
          // A to-do is written here, so there is nothing quoted in it to strip.
          const shown = isTodo ? (m.body || '') : stripQuoted(m.body);
          const lines = shown.split('\n').filter(l => l.trim().length > 0);
          const hasMore = lines.length > 1 || (lines[0] || '').length > 100 || !!m.attachment_filename;
          const showSubject = m.subject && (idx === 0 || isTodo);
          return (
            <div key={m.id} onClick={() => hasMore && toggleExpanded(m.id)}
              style={{ background: todoOpen ? 'var(--blue-soft)' : 'var(--surface)', border: `1px solid ${todoOpen ? 'var(--blue-soft-line)' : 'var(--line-soft)'}`,
                borderRadius: 8, padding: '10px 12px', cursor: hasMore ? 'pointer' : 'default' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 2 }}>
                <div style={{ fontSize: 11, color: todoOpen ? 'var(--blue)' : 'var(--ink-faint)', fontWeight: todoOpen ? 700 : 400 }}>
                  {isTodo ? (todoOpen ? '📌 To-do' : 'To-do · done') : (m.direction === 'out' ? 'Sent' : 'Received')} · {m.sent_at ? new Date(m.sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {todoOpen && m.todo_id && (
                    <button className="btn btn-sm" style={{ padding: '2px 9px', fontSize: 10.5 }}
                      onClick={(e) => { e.stopPropagation(); dismissTodo(m.todo_id); }}>Done</button>
                  )}
                  {hasMore && <span style={{ fontSize: 10, color: 'var(--ink-faint)' }}>{isExpanded ? '▲ collapse' : '▼ expand'}</span>}
                </div>
              </div>
              {showSubject && <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 2, color: todoOpen ? 'var(--blue)' : 'inherit' }}>{m.subject}</div>}
              {(isExpanded || !hasMore) ? (
                <>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', whiteSpace: 'pre-wrap' }}>{shown}</div>
                  {m.attachment_filename && isDocFile(m.attachment_filename) && (
                    <a href={uploadUrl(m.attachment_filename)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      style={{ display: 'inline-block', marginTop: 6, padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', textDecoration: 'none', background: 'var(--surface)' }}>
                      📄 {m.attachment_name || 'Document'}
                    </a>
                  )}
                  {m.attachment_filename && !isDocFile(m.attachment_filename) && (
                    <img src={uploadUrl(m.attachment_filename, 'thumb')} alt="Attachment" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, marginTop: 6, display: 'block', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); setLightboxIndex(messagePhotos.findIndex(p => p.filename === m.attachment_filename)); }} />
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {firstLine(shown) || (m.attachment_filename ? (isDocFile(m.attachment_filename) ? '📄 Document attached' : '📎 Photo attached') : '')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12 }}>
        {logging ? (
          <div style={{ border: '1px solid var(--line-soft)', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>Record a message that came in outside the CRM (e.g. a new email thread), including any photos that were attached.</div>
            {allKnownEmails.length > 1 ? (
              <select value={logFrom} onChange={e => setLogFrom(e.target.value)}>
                {allKnownEmails.map(e => <option key={e.email} value={e.email}>{e.email}</option>)}
                <option value="">Other / unknown address</option>
              </select>
            ) : (
              <input type="email" value={logFrom} onChange={e => setLogFrom(e.target.value)} placeholder="Sender's email address" />
            )}
            <input type="text" value={logSubject} onChange={e => setLogSubject(e.target.value)} placeholder="Subject (optional)" />
            <textarea rows={5} value={logBody} onChange={e => setLogBody(e.target.value)} placeholder="Paste or type the message" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input ref={logFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={uploadLogPhoto} />
              <button type="button" className="btn btn-sm" onClick={() => logFileInputRef.current.click()} disabled={uploadingLogPhoto}>
                {uploadingLogPhoto ? 'Uploading…' : '+ Add photo(s)'}
              </button>
              {logPhotos.map(p => (
                <span key={p.filename} style={{ fontSize: 11.5, color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 4, padding: '4px 8px 4px 4px' }}>
                  <img src={uploadUrl(p.filename, 'thumb')} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3 }} />
                  {p.originalName}
                  <button type="button" onClick={() => removeLogPhoto(p.filename)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-green btn-sm" onClick={saveLoggedMessage}>Save to this lead's conversation</button>
              <button className="btn btn-sm" onClick={() => { setLogging(false); setLogPhotos([]); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className="btn btn-sm" onClick={() => setLogging(true)}>+ Log received message</button>
        )}
      </div>
      {lightboxIndex !== null && (
        <PhotoLightbox photos={messagePhotos} startIndex={lightboxIndex} onClose={()=>setLightboxIndex(null)}/>
      )}
    </div>
  );
}

// Andrew, 3 Nov: small jobs priced by email or phone with no visit - one line, one price,
// paid on completion. The email is the Quick quote template (Settings > Templates), filled
// with the job and price and editable before it goes. "Quoted by phone" records it as given.
const QUICK_QUOTE_FALLBACK = {
  subject: 'Your quote from {{brand_name}}',
  body: 'Hi {{first_name}},\n\nThanks for getting in touch. From what you have told us, the cost for {{job}} is {{price}} + VAT ({{total}} in total).\n\n'
    + 'You can view and accept the quote here:\n\n{{quote_button}}\n\nOnce you have accepted, we will be in touch to arrange a date. '
    + 'Payment is due once the work is done.\n\nIf you have any questions, just reply to this email or call us on {{brand_phone}}.\n\nThanks,\n{{brand_name}}',
};
const quickMoney = (n) => '£' + Number(n || 0).toLocaleString('en-GB', Number.isInteger(Number(n)) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function QuickQuoteModal({ lead, onClose, onDone }) {
  const { showAlert } = useDialog();
  const [title, setTitle] = useState('Roof repair');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('400');
  const [to, setTo] = useState(lead.email || '');
  // Every template that uses {{price}} is offered (Andrew, 3 Nov: a separate one for the minimum
  // charge); the one holding the quick_quote purpose comes first and is picked to start with.
  const [tpls, setTpls] = useState(null);
  const [tplId, setTplId] = useState(null);
  const tpl = tpls ? (tpls.find(t => t.id === tplId) || tpls[0]) : null;
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(null);
  const net = Math.round((Number(price) || 0) * 100) / 100;
  const total = Math.round(net * 120) / 100;
  useEffect(() => {
    api('/api/email-templates').then(list => {
      const usable = (list || []).filter(t => t.purpose === 'quick_quote' || /\{\{\s*price\s*\}\}/.test(t.body || ''))
        .sort((a, b) => (b.purpose === 'quick_quote') - (a.purpose === 'quick_quote'));
      setTpls(usable.length ? usable : [{ id: 0, name: 'Quick quote', ...QUICK_QUOTE_FALLBACK }]);
    }).catch(() => setTpls([{ id: 0, name: 'Quick quote', ...QUICK_QUOTE_FALLBACK }]));
  }, []);
  // The email follows the job and price until it is typed in by hand.
  useEffect(() => {
    if (!tpl || edited) return;
    const job = title.trim() ? title.trim().charAt(0).toLowerCase() + title.trim().slice(1) : 'the work';
    const t = setTimeout(() => {
      api(`/api/leads/${lead.id}/fill-template`, { method: 'POST', body: JSON.stringify({ subject: tpl.subject, body: tpl.body,
        extra: { job, price: quickMoney(net), total: quickMoney(total) } }) })
        .then(r => { setSubject(r.subject); setMessage(r.body); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [tpl, edited, title, net, total, lead.id]);
  const go = async (mode) => {
    if (!title.trim()) { await showAlert('Say what the job is.'); return; }
    if (!(net > 0)) { await showAlert('Give a price above £0.'); return; }
    if (mode === 'email' && !to.trim()) { await showAlert('There is no email address to send it to.'); return; }
    setBusy(mode);
    try {
      const r = await api(`/api/leads/${lead.id}/quick-quote`, { method: 'POST', body: JSON.stringify({
        title: title.trim(), description, price: net, vat_percent: 20, mode, to_email: to.trim(), subject, message }) });
      await showAlert(mode === 'email' ? `Quote ${r.reference} sent to ${to.trim()}.` : `Quote ${r.reference} recorded as given by phone.`, { title: 'Quick quote' });
      onDone();
    } catch (e) { await showAlert(e.message); onDone(true); }
    setBusy(null);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="mhead"><div><div className="mtitle">Quick quote</div>
          <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>One price, paid on completion. No visit needed.</div></div>
          <button className="xcbtn" onClick={onClose}>×</button></div>
        <div className="mbody">
          <div className="row2">
            <div className="fl"><label>Job</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} autoFocus/></div>
            <div className="fl"><label>Price ex VAT (£)</label><input type="number" min="0" step="1" value={price} onChange={e => setPrice(e.target.value)}/>
              <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginTop: 4 }}>{quickMoney(net)} + VAT = <strong>{quickMoney(total)}</strong>
                {net !== 400 && <button type="button" tabIndex={-1} onClick={() => setPrice('400')} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Minimum charge £400</button>}</div></div>
          </div>
          <div className="fl"><label>On the quote (optional)</label>
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Replace up to 5 slipped or broken tiles on the front roof slope"/></div>
          <div className="row2">
            <div className="fl"><label>Email to</label><input type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="No email on this lead"/></div>
            <div className="fl"><label>Email wording</label>
              <select value={tpl ? tpl.id : ''} onChange={e => { setTplId(Number(e.target.value)); setEdited(false); }}>
                {(tpls || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
          </div>
          <div className="fl"><label>Subject</label><input type="text" value={subject} onChange={e => { setSubject(e.target.value); setEdited(true); }}/></div>
          <div className="fl"><label>Message</label>
            <textarea rows={11} value={message} onChange={e => { setMessage(e.target.value); setEdited(true); }}/>
            <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>{'{{quote_button}}'} on its own line puts the green "View and accept your quote" button there; without it there is no button. The quote PDF is attached either way.
              {edited && <> <button type="button" onClick={() => setEdited(false)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Reset the wording</button></>}</div></div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-sm" disabled={!!busy} onClick={() => go('phone')}>{busy === 'phone' ? 'Saving…' : 'Quoted by phone'}</button>
          <button className="btn btn-green btn-sm" disabled={!!busy || !to.trim()} onClick={() => go('email')}>{busy === 'email' ? 'Sending…' : 'Send by email'}</button>
        </div>
      </div>
    </div>
  );
}

function QuoteSummarySection({ quotes, onOpenQuote, onNewQuote, onQuickQuote, onDeleted }) {
  const { showAlert } = useDialog();
  const list = quotes || [];
  const [delId, setDelId] = useState(null);
  // Only an unsent draft offers Delete; the server refuses anything else regardless.
  const isDraft = (q) => q.status === 'draft' && !q.sent_at && !q.accepted_at;
  const del = async () => {
    const id = delId; setDelId(null);
    try { await api(`/api/quotes/${id}`, { method: 'DELETE' }); onDeleted && onDeleted(); }
    catch (e) { showAlert(e.message); }
  };

  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 0, paddingBottom: 0 }}>Quotes</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onQuickQuote && <button className="btn btn-sm" onClick={onQuickQuote}>Quick quote</button>}
          <button className="btn btn-sm" onClick={onNewQuote}>+ New quote</button>
        </div>
      </div>
      {list.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>No quotes yet.</div>}
      {list.map(q => (
        <div key={q.id} style={{ borderTop: '1px solid var(--line-soft)', padding: '7px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12.5 }}>
              <span onClick={() => onOpenQuote(q.id)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
                {q.reference ? `Quote #${q.reference}` : 'Draft quote'}
              </span>
              <span style={{ color: 'var(--ink-faint)', marginLeft: 8 }}>{q.status}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12 }}>
              <span style={{ color: 'var(--ink-soft)' }}>{q.sent_at ? `Sent ${new Date(q.sent_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}` : 'Not sent'}</span>
              <span style={{ fontWeight: 700 }}>£{(parseFloat(q.total_value)||0).toFixed(2)}</span>
              {isDraft(q) && <button className="btn btn-sm btn-red" onClick={() => setDelId(q.id)}>Delete</button>}
            </div>
          </div>
          {(() => {
            const chase = quoteChaseStatus(q);
            if (!chase) return null;
            return (
              <div style={{ fontSize: 11.5, marginTop: 4, color: CHASE_COLOURS[chase.tone], fontWeight: (chase.tone === 'chase' || chase.tone === 'bounce') ? 600 : 400 }}>
                {(chase.tone === 'chase' || chase.tone === 'bounce') ? '⚠ ' : ''}{chase.text}
              </div>
            );
          })()}
          {q.status === 'declined' && (
            <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 4 }}>
              Declined{q.declined_at ? ` ${new Date(q.declined_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}` : ''}
              {q.declined_reason ? ` - ${q.declined_reason}` : ''}
              {q.declined_message_body && (
                <div style={{ marginTop: 3, color: 'var(--ink-soft)', fontStyle: 'italic' }}>
                  "{String(q.declined_message_body).replace(/\s+/g, ' ').trim().slice(0, 300)}"
                </div>
              )}
            </div>
          )}
          {q.accepted_at && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 4 }}>
              Accepted by {q.accepted_name || 'customer'} ({q.accepted_email || 'no email given'}) on {new Date(q.accepted_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
              {q.signature_filename && <> · <a href={`/uploads/${q.signature_filename}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>View signature</a></>}
              {q.accepted_message && <div style={{ marginTop: 4, fontStyle: 'italic' }}>"{q.accepted_message}"</div>}
            </div>
          )}
        </div>
      ))}
      {delId !== null && (
        <ConfirmModal title="Delete draft quote" danger confirmLabel="Delete"
          message="This draft has never been sent. Deleting it removes it and its line items for good."
          onConfirm={del} onCancel={() => setDelId(null)}/>
      )}
    </div>
  );
}

function InvoiceSummarySection({ leadId, onOpenInvoice, refreshKey }) {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api(`/api/leads/${leadId}/invoices`).then(setInvoices).catch(() => {}); }, [leadId, refreshKey]);

  if (invoices.length === 0) return null;

  const statusLabel = { unsent: 'Not sent', sent: 'Sent', 'part-paid': 'Part paid', paid: 'Paid', cancelled: 'Cancelled' };

  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Invoices</div>
      {invoices.map(i => (
        <div key={i.id} style={{ borderTop: '1px solid var(--line-soft)', padding: '7px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12.5 }}>
            <span onClick={() => onOpenInvoice(i.id)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
              {i.reference}
            </span>
            <span style={{ color: 'var(--ink-faint)', marginLeft: 8 }}>{i.type === 'deposit' ? `${i.deposit_percent || 50}% deposit` : (i.type === 'balance' ? 'Balance' : 'Invoice')} · {statusLabel[i.status] || i.status}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12 }}>
            <span style={{ color: 'var(--ink-soft)' }}>{i.sent_at ? `Sent ${new Date(i.sent_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}` : ''}</span>
            <span style={{ fontWeight: 700 }}>£{(parseFloat(i.total_value)||0).toFixed(2)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function JobCostsSection({ leadId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api(`/api/leads/${leadId}/costs`).then(setData).catch(() => {}); }, [leadId]);

  // Nothing quoted and nothing spent: no point taking up room on the lead.
  if (!data || (!data.has_costs && !data.quoted.total)) return null;

  const m = (n) => {
    const v = Number(n) || 0;
    return `${v < 0 ? '-' : ''}£${Math.abs(v).toFixed(2)}`;
  };
  const Variance = ({ value }) => {
    const v = Number(value) || 0;
    if (Math.abs(v) < 0.005) return <span style={{ color: 'var(--ink-faint)' }}>on budget</span>;
    const over = v > 0;
    return <span style={{ color: over ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
      {over ? '+' : '-'}{m(Math.abs(v))} {over ? 'over' : 'under'}
    </span>;
  };
  const Row = ({ label, quoted, actual, variance, strong }) => (
    <div className="qva-row" style={{ fontSize: 12.5, padding: '5px 0',
      borderTop: '1px solid var(--line-soft)', fontWeight: strong ? 700 : 400 }}>
      <span>{label}</span>
      <span style={{ textAlign: 'right', color: 'var(--ink-soft)' }}>{quoted == null ? '—' : m(quoted)}</span>
      <span style={{ textAlign: 'right' }}>{m(actual)}</span>
      <span style={{ textAlign: 'right' }}>{variance == null ? '' : <Variance value={variance}/>}</span>
    </div>
  );

  // Against invoiced, not received: whether the job made money does not depend on
  // whether the customer has got round to paying yet.
  const margin = data.invoiced - data.actual.total;
  const outstanding = data.invoiced - data.received;

  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Quoted vs actual</div>

      <div className="qva-row" style={{ fontSize: 10.5, color: 'var(--ink-faint)',
        textTransform: 'uppercase', letterSpacing: '.04em', paddingBottom: 3 }}>
        <span /><span style={{ textAlign: 'right' }}>Quoted</span><span style={{ textAlign: 'right' }}>Actual</span><span />
      </div>

      <Row label="Materials" quoted={data.quoted.materials} actual={data.actual.materials} variance={data.variance.materials}/>
      <Row label="Labour" quoted={data.quoted.labour} actual={data.actual.labour} variance={data.variance.labour}/>
      {data.actual.other !== 0 && <Row label="Other costs" quoted={null} actual={data.actual.other} variance={null}/>}
      <Row label="Total cost" quoted={data.quoted.total} actual={data.actual.total} variance={data.variance.total} strong/>

      <div style={{ borderTop: '2px solid var(--line-soft)', marginTop: 6, paddingTop: 7 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ color: 'var(--ink-soft)' }}>Invoiced {m(data.invoiced)}, less actual cost</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: margin >= 0 ? '#16a34a' : '#dc2626' }}>{m(margin)}</span>
        </div>
        {Math.abs(data.invoiced - data.received) > 0.005 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 3 }}>
            <span>{m(data.received)} of that has been received{outstanding > 0.005 ? `, ${m(outstanding)} still to come` : ''}</span>
          </div>
        )}
      </div>

      {data.unassigned > 0 && (
        <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 8 }}>
          {data.unassigned} cost {data.unassigned === 1 ? 'line is' : 'lines are'} assigned to the customer rather than
          a specific job, so {data.unassigned === 1 ? 'it counts' : 'they count'} here but cannot be tied to one quote.
          Assign {data.unassigned === 1 ? 'it' : 'them'} to the job in QuickBooks to fix that.
        </div>
      )}
      {data.unmapped_accounts.length > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 6 }}>
          Counted as other costs because {data.unmapped_accounts.length === 1 ? 'this account is' : 'these accounts are'} not
          classified yet: {data.unmapped_accounts.join(', ')}.
        </div>
      )}

      {data.has_costs && (
        <>
          <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => setOpen(o => !o)}>
            {open ? 'Hide' : `Show ${data.lines.length} cost ${data.lines.length === 1 ? 'line' : 'lines'}`}
          </button>
          {open && (
            <div style={{ marginTop: 8 }}>
              {data.lines.map(l => (
                <div key={l.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12,
                  padding: '4px 0', borderTop: '1px solid var(--line-soft)' }}>
                  <span style={{ minWidth: 70, color: 'var(--ink-soft)' }}>
                    {l.txn_date ? new Date(l.txn_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{l.supplier || 'Supplier not named'}</span>
                    {l.description ? <span style={{ color: 'var(--ink-soft)' }}> · {l.description}</span> : null}
                    <span style={{ color: 'var(--ink-faint)' }}> · {l.account_name || 'no account'}</span>
                    {l.attribution === 'customer' ? <span style={{ color: '#b45309' }}> · customer, not job</span> : null}
                  </span>
                  <span style={{ fontWeight: 700, minWidth: 72, textAlign: 'right' }}>{m(l.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Nothing at all when QuickBooks is happy, which is almost always. When it is not,
// it says which thing failed and what QuickBooks actually said - the message is the
// useful part, because it is usually something only a human can fix, like a name
// QuickBooks will not accept.
function QboIssuesBanner({ refreshKey }) {
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api('/api/quickbooks/issues').then(rows => setIssues(rows || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  // Slow poll: these change on the five-minute retry, not second by second.
  useEffect(() => {
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  if (!issues.length) return null;

  const retry = async () => {
    setBusy(true);
    try { await api('/api/quickbooks/issues/retry', { method: 'POST' }); } catch (e) {}
    load();
    setBusy(false);
  };
  const dismiss = async (id) => {
    try { await api(`/api/quickbooks/issues/${id}/dismiss`, { method: 'POST' }); } catch (e) {}
    load();
  };
  const who = (i) => [i.first_name, i.last_name].filter(Boolean).join(' ') || i.name || '';

  return (
    <div style={{ background: '#fff8ec', border: '1px solid #f6e0b5', borderRadius: 'var(--radius)',
      padding: '10px 14px', margin: '0 0 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>
          {issues.length === 1 ? '1 thing has' : `${issues.length} things have`} not reached QuickBooks
        </span>
        <button className="btn btn-sm" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>
        <button className="btn btn-sm" onClick={retry} disabled={busy}>{busy ? 'Retrying…' : 'Retry now'}</button>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
          The work itself went through; this is the bookkeeping copy only.
        </span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {issues.map(i => (
            <div key={i.id} style={{ borderTop: '1px solid #f6e0b5', padding: '7px 0', display: 'flex',
              alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, fontSize: 12.5 }}>
                <div style={{ fontWeight: 600 }}>
                  {i.what || i.operation}{i.reference ? ` — ${i.reference}` : ''}
                  {who(i) ? <span style={{ color: 'var(--ink-faint)', fontWeight: 400 }}> · {who(i)}</span> : null}
                </div>
                <div style={{ color: '#b91c1c', marginTop: 2 }}>{i.message}</div>
                <div style={{ color: 'var(--ink-faint)', fontSize: 11, marginTop: 2 }}>
                  {i.attempts} {i.attempts === 1 ? 'attempt' : 'attempts'}, last
                  {' '}{new Date(i.last_failed_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <button className="btn btn-sm" onClick={() => dismiss(i.id)}>Dismiss</button>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 8 }}>
            These retry on their own every five minutes. Dismissing only clears the message -
            if the cause is still there it will come back.
          </div>
        </div>
      )}
    </div>
  );
}

// The only place a job's status changes. Everything here is deliberate: a status
// list, a date, the materials tick, and one button that ends the job.
// Andrew, 18 Oct: "on the card when i get to the jobs section i want to be able to action
// it from the card ... if i enter details on the card or the section they should sync."
// The job actions and their pop-ups, shared by the Jobs page and the lead card so both edit
// the same job record the same way. reload() refreshes whichever list is showing.
function useJobActions(reload, onChanged) {
  const { showAlert, showConfirm } = useDialog();
  const [busy, setBusy] = useState(null);
  const [people, setPeople] = useState([]);
  const [crewFor, setCrewFor] = useState(null);   // {dayId, dateStr, name, selected:[user ids]}
  const [crewBusy, setCrewBusy] = useState(false);
  const [readyToSend, setReadyToSend] = useState(null);
  useEffect(() => {
    api('/api/users').then(rows => setPeople((rows || []).filter(x => x.active))).catch(() => {});
  }, []);
  const changed = () => { reload(); if (onChanged) onChanged(); };


  const sendRaised = async (raised) => {
    const refs = raised.map(i => i.reference).join(', ');
    const failed = [];
    for (const inv of raised) {
      try { await api(`/api/invoices/${inv.id}/send`, { method: 'POST', body: JSON.stringify({}) }); }
      catch (e) { failed.push(`${inv.reference} - ${e.message}`); }
    }
    changed();
    // Named rather than counted: which one failed is the thing somebody has to act on.
    if (failed.length) await showAlert(`Could not send ${failed.join('; ')}`, { title: 'Not sent' });
    else await showAlert(`${refs} sent to the customer.`, { title: 'Sent' });
  };

  const save = async (job, body) => {
    setBusy(job.id);
    try { await api(`/api/jobs/${job.id}`, { method: 'PUT', body: JSON.stringify(body) }); changed(); }
    catch (e) { showAlert(e.message); }
    setBusy(null);
  };
  const complete = async (job) => {
    if (!(await showConfirm('Mark this job complete? Anything owed on completion will be prepared as an invoice.',
      { title: 'Job complete', confirmLabel: 'Mark complete' }))) return;
    setBusy(job.id);

    let raised = [];
    try {
      const out = await api(`/api/jobs/${job.id}/complete`, { method: 'POST' });
      raised = out.raised || [];
      changed();
    } catch (e) { showAlert(e.message); setBusy(null); return; }

    if (raised.length) setReadyToSend(raised);
    setBusy(null);
  };
  const issueGuarantee = async (job, templateId, send) => {
    const what = send ? 'Issue the guarantee and email it to the customer?' : 'Record the guarantee as issued without emailing it?';
    const extra = templateId ? '' : ' There is no wording selected, so no document is produced - only a record that it was issued.';
    if (!(await showConfirm(what + extra, { title: 'Guarantee', confirmLabel: send ? 'Issue and send' : 'Issue' }))) return;
    setBusy(job.id);
    try {
      const out = await api(`/api/jobs/${job.id}/guarantee-issued`, {
        method: 'POST', body: JSON.stringify({ template_id: templateId || null, send: !!send }) });
      changed();
      if (out && out.send_error) await showAlert(out.send_error, { title: 'Guarantee issued' });
    } catch (e) { showAlert(e.message); }
    setBusy(null);
  };

  const saveCrew = async (ids) => {
    if (!crewFor || crewBusy) return;
    setCrewBusy(true);
    try {
      await api(`/api/job-days/${crewFor.dayId}/crew`, { method: 'PUT', body: JSON.stringify({ user_ids: ids }) });
      reload();
      setCrewFor(c => c ? { ...c, selected: ids } : c);
    } catch (e) { showAlert(e.message); }
    setCrewBusy(false);
  };

  const modals = (
    <>
      {readyToSend && (
        <div className="modal-overlay" onClick={() => setReadyToSend(null)} style={{ zIndex: 70 }}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <div className="mtitle">Job complete</div>
              <button className="xcbtn" onClick={() => setReadyToSend(null)}>×</button>
            </div>
            <div className="mbody">
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
                {readyToSend.length > 1 ? 'These are' : 'This is'} prepared and ready to send. Have a look before it goes.
              </div>
              {readyToSend.map(inv => (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{inv.reference}</span>
                  <a className="btn btn-sm" href={`/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                    style={{ marginLeft: 'auto', textDecoration: 'none' }}>Preview PDF</a>
                </div>
              ))}
            </div>
            <div className="mfoot">
              <button className="btn btn-sm" onClick={() => setReadyToSend(null)}>Not yet</button>
              <button className="btn btn-green btn-sm"
                onClick={() => { const r = readyToSend; setReadyToSend(null); sendRaised(r); }}>
                {readyToSend.length > 1 ? 'Send them' : 'Send it'}
              </button>
            </div>
          </div>
        </div>
      )}
      {crewFor && (
        <div className="jg-modal-back" onClick={() => setCrewFor(null)}>
          <div className="jg-modal" onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Who is on this job</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginBottom: 12 }}>
              {crewFor.name} · {crewFor.dateStr}
            </div>
            {people.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', marginBottom: 10 }}>
                No logins to assign yet. Add people in Settings, under Team.
              </div>
            )}
            {people.map(p => {
              const on = crewFor.selected.includes(p.id);
              return (
                <button key={p.id} className={`jg-person${on ? ' on' : ''}`} disabled={crewBusy}
                  onClick={() => saveCrew(on ? crewFor.selected.filter(x => x !== p.id) : [...crewFor.selected, p.id])}>
                  <span>{p.display_name}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{ROLE_LABELS[p.role] || p.role}</span>
                </button>
              );
            })}
            <button className="btn btn-sm" style={{ marginTop: 6 }} onClick={() => setCrewFor(null)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
  return { busy, save, complete, issueGuarantee, setCrewFor, modals };
}

const jobDayLabel = (ds) => {
  const d = new Date(String(ds) + 'T00:00:00');
  return isNaN(d) ? String(ds) : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

// One job, with everything that can be done to it. onSelectLead is given on the Jobs page,
// where the name opens the lead; on the lead card it is left out because that is where
// you already are.
function JobActionCard({ j, a, onSelectLead }) {
  const { busy, save, complete, issueGuarantee, setCrewFor } = a;
  const who = (x) => tileDisplayName(x.title, x.first_name, x.last_name) || x.name || '—';
  const dayLabel = jobDayLabel;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)',
      padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        {onSelectLead ? (
          <>
            <span onClick={() => onSelectLead(j.lead_id)} style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--accent)', cursor: 'pointer' }}>
              {who(j)}
            </span>
            <BrandBadge brand={j.brand}/>
            <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>{j.address} · Quote {j.quote_reference}</span>
          </>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Quote {j.quote_reference}</span>
        )}
        <span style={{ marginLeft: 'auto' }}><JobPill pill={{ text: JOB_STATUS_LABELS[j.status] || j.status, tone: j.status === 'complete' ? 'green' : 'grey' }}/></span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', fontSize: 12.5 }}>
        {j.status !== 'complete' && j.status !== 'guarantee' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'var(--ink-faint)' }}>Stage</span>
            <select className="select-compact" value={j.status} disabled={busy === j.id}
              onChange={e => save(j, { status: e.target.value })}>
              {['agree_dates','order_materials','assign_labour','job_report'].map(s =>
                <option key={s} value={s}>{JOB_STATUS_LABELS[s]}</option>)}
            </select>
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--ink-faint)' }}>Scheduled</span>
          <input type="date" className="select-compact" value={j.scheduled_date ? String(j.scheduled_date).slice(0, 10) : ''}
            disabled={busy === j.id} onChange={e => save(j, { scheduled_date: e.target.value || null })}/>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={!!j.guarantee_needed} disabled={busy === j.id}
            onChange={e => save(j, { guarantee_needed: e.target.checked })}/>
          Guarantee needed
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={!!j.review_requested} disabled={busy === j.id}
            onChange={e => save(j, { review_requested: e.target.checked })}/>
          Send review request
        </label>
      </div>

      <textarea placeholder="Notes on materials" defaultValue={j.materials_notes || ''} rows={2}
        onBlur={e => { if (e.target.value !== (j.materials_notes || '')) save(j, { materials_notes: e.target.value }); }}
        style={{ width: '100%', marginTop: 8, fontSize: 12.5 }}/>

      {(j.days || []).length > 0 ? (
        <div className="jc-crew" style={{ marginTop: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>Team</span>
          {j.days.map(d => (
            <span key={d.id} className="jc-crew-day" title="Who is on this day"
              onClick={() => setCrewFor({ dayId: d.id, dateStr: dayLabel(d.work_date), name: who(j),
                selected: (d.crew || []).map(u => u.id) })}>
              <span className="jc-crew-date">{dayLabel(d.work_date)}</span>
              {(d.crew || []).length
                ? d.crew.map(u => <span key={u.id} className="jc-crew-chip" title={u.display_name}
                    style={personChipStyle(u.colour)}>{crewInitials(u.display_name)}</span>)
                : <span className="jc-crew-none">+ team</span>}
            </span>
          ))}
        </div>
      ) : (
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-faint)' }}>
          No dates on this job yet, so there is nobody to put on it. Set the dates above or in the diary.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        {j.status === 'guarantee' && (
          <GuaranteeIssue job={j} busy={busy === j.id} onIssue={issueGuarantee}/>
        )}
        {j.status !== 'complete' && j.status !== 'guarantee' && (
          <button className="btn btn-green btn-sm" disabled={busy === j.id} onClick={() => complete(j)}>Job complete</button>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>
          {j.payment_stages.map(s => `${s.percent}% ${PAYMENT_STATUS_LABELS[s.status] || s.status}`).join(' · ')}
        </span>
      </div>
      {(j.days || []).some(d => (d.report_note || '').trim() || d.no_photo_reason) && (
        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginBottom: 4 }}>From site</div>
          {j.days.filter(d => (d.report_note || '').trim() || d.no_photo_reason).map(d => (
            <div key={d.id} style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}><b>{dayLabel(d.work_date)}:</b> {d.report_note}
              {d.no_photo_reason && <div style={{ color: 'var(--amber)' }}>No photo taken: {d.no_photo_reason}</div>}
            </div>
          ))}
        </div>
      )}
      <JobPhotos leadId={j.lead_id}/>
      <JobLabour jobId={j.id}/>
      <JobSupplierInvoices jobId={j.id}/>
    </div>
  );
}

function JobsView({ onSelectLead, refreshKey, onChanged }) {
  const [jobs, setJobs] = useState([]);
  const [filterBrand, setFilterBrand] = usePref('jobs.brand', 'all', isBrandPref);
  const [tab, setTab] = usePref('jobs.tab', 'live', oneOf('live', 'done', 'consumables'));

  const [consumables, setConsumables] = useState(null);
  const loadConsumables = useCallback(() => api('/api/consumables')
    .then(d => setConsumables(d || null))
    .catch(() => setConsumables(null)), []);
  const load = useCallback(() => {
    api('/api/jobs').then(rows => setJobs(rows || [])).catch(() => {});
    loadConsumables();
  }, [loadConsumables]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const actions = useJobActions(load, onChanged);
  const live = jobs.filter(j => (filterBrand === 'all' || j.brand === filterBrand) && j.status !== 'complete');
  const done = jobs.filter(j => (filterBrand === 'all' || j.brand === filterBrand) && j.status === 'complete')
    .slice().sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));


  return (
    <div className="view-scroll">
      {actions.modals}
      <div className="filter-bar">
        <span className="filter-label">Brand:</span>
        <select className="select-compact" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k => <option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4, marginLeft: 12 }}>
          <button className={`tab${tab === 'live' ? ' active' : ''}`} onClick={() => setTab('live')}>Live ({live.length})</button>
          <button className={`tab${tab === 'done' ? ' active' : ''}`} onClick={() => setTab('done')}>Completed ({done.length})</button>
          {consumables && (
            <button className={`tab${tab === 'consumables' ? ' active' : ''}`} onClick={() => setTab('consumables')}>
              Consumables{consumables.to_check ? ` (${consumables.to_check} to check)` : ''}
            </button>
          )}
        </div>
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '22px 16px 0' }}>
        {tab === 'consumables' ? <ConsumablesPanel data={consumables} reload={loadConsumables}/>
          : tab === 'live'
          ? (live.length === 0
              ? <div style={{ color: 'var(--ink-faint)', fontSize: 13, paddingBottom: 20 }}>No live jobs.</div>
              : live.map(j => <JobActionCard key={j.id} j={j} a={actions} onSelectLead={onSelectLead}/>))
          : (done.length === 0
              ? <div style={{ color: 'var(--ink-faint)', fontSize: 13, paddingBottom: 20 }}>Nothing completed yet.</div>
              : done.map(j => <JobActionCard key={j.id} j={j} a={actions} onSelectLead={onSelectLead}/>))}
      </div>
    </div>
  );
}

// Reports. Stat tiles and sorted tables rather than charts, deliberately: these are
// exact figures across eighteen brands that get compared and acted on, and a table
// gives the actual pounds and percentages where a bar gives a shape. Trend charts
// earn their place once there is history to plot.
function ReportsView() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [brand, setBrand] = usePref('reports.brand', 'all', isBrandPref);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = usePref('reports.preset', 'this_month',
    oneOf('all', 'this_month', 'last_month', 'quarter', 'ytd', 'custom'));
  const [split, setSplit] = usePref('reports.split', false, v => typeof v === 'boolean');
  const prefs = useContext(PrefsContext);
  const startPreset = useRef(preset);

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const applyPreset = useCallback((key) => {
    const now = new Date(); let a, b;
    if (key === 'this_month') { a = new Date(now.getFullYear(), now.getMonth(), 1); b = now; }
    else if (key === 'last_month') { a = new Date(now.getFullYear(), now.getMonth() - 1, 1); b = new Date(now.getFullYear(), now.getMonth(), 0); }
    else if (key === 'quarter') { a = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); b = now; }
    else if (key === 'ytd') { a = new Date(now.getFullYear(), 0, 1); b = now; }
    else if (key === 'all') { setPreset('all'); setFrom('all'); setTo(iso(now)); return; }
    else return;
    setPreset(key); setFrom(iso(a)); setTo(iso(b));
  }, [setPreset]);
  // Opens on the period last used. A custom range comes back as the dates entered.
  useEffect(() => {
    const day = /^\d{4}-\d{2}-\d{2}$/;
    const r = prefs ? prefs.get('reports.range') : null;
    if (startPreset.current === 'custom' && r && day.test(r.from || '') && day.test(r.to || '') && r.from <= r.to) {
      setFrom(r.from); setTo(r.to);
    } else {
      applyPreset(startPreset.current === 'custom' ? 'this_month' : startPreset.current);
    }
  }, [applyPreset, prefs]);
  useEffect(() => {
    if (prefs && preset === 'custom' && from && to && from !== 'all') prefs.save({ 'reports.range': { from, to } });
  }, [prefs, preset, from, to]);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true); setErr('');
    const qs = `from=${from}&to=${to}&brand=${encodeURIComponent(brand)}${split ? '&split=month' : ''}`;
    api(`/api/reports?${qs}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setData(null); setLoading(false); });
  }, [from, to, brand, split]);

  const gbp = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const gbp0 = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
  const pct = (n) => (n == null ? '—' : n + '%');
  const num = (n) => Number(n || 0).toLocaleString('en-GB');
  const Tile = ({ label, value, sub, hero }) => (
    <div className={'rep-tile' + (hero ? ' hero' : '')}>
      <div className="rep-tile-l">{label}</div>
      <div className="rep-tile-v">{value}</div>
      {sub && <div className="rep-tile-s">{sub}</div>}
    </div>
  );

  return (
    <div className="rep-wrap">
      <div className="rep-filters">
        {[['all','All time'],['this_month','This month'],['last_month','Last month'],['quarter','This quarter'],['ytd','Year to date']].map(([k,l]) => (
          <button key={k} className={'rep-preset' + (preset === k ? ' on' : '')} onClick={() => applyPreset(k)}>{l}</button>
        ))}
        <input type="date" className="select-compact" value={from === 'all' ? ((data && data.from) || '') : from}
          onChange={e => { setPreset('custom'); setFrom(e.target.value); }}/>
        <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>to</span>
        <input type="date" className="select-compact" value={to}
          onChange={e => { setPreset('custom'); setTo(e.target.value); }}/>
        <select className="select-compact" value={brand} onChange={e => setBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k => <option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <button className={'rep-preset' + (split ? ' on' : '')} onClick={() => setSplit(s => !s)}>
          {split ? 'Split by month: on' : 'Split by month'}
        </button>
        {loading && <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>Working…</span>}
      </div>

      {err && <div className="rep-warn"><b>Could not load:</b> {err}</div>}
      {!data && !err && <div className="rep-empty">Pick a range.</div>}

      {data && split && Array.isArray(data.months) && (
        <>
          <div className="rep-sec">By month{brand !== 'all' ? ` - ${BRANDS[brand] || brand}` : ''}</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rep-table">
              <thead><tr>
                <th>Month</th><th className="n">Leads</th><th className="n">Quotes sent</th><th className="n">Quoted ex VAT</th>
                <th className="n">Won</th><th className="n">Won ex VAT</th><th className="n">Conversion</th>
                <th className="n">Invoiced ex VAT</th><th className="n">Received</th><th className="n">Jobs done</th>
              </tr></thead>
              <tbody>
                {data.months.map(m => (
                  <tr key={m.month}>
                    <td>{new Date(m.month + '-01T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</td>
                    <td className="n">{num(m.leads_in)}</td>
                    <td className="n">{num(m.quotes_sent)}</td>
                    <td className="n">{gbp0(m.quoted_ex_vat)}</td>
                    <td className="n">{num(m.won)}</td>
                    <td className="n">{gbp0(m.accepted_ex_vat)}</td>
                    <td className="n">{pct(m.conversion_pct)}</td>
                    <td className="n">{gbp0(m.invoiced_ex_vat)}</td>
                    <td className="n">{gbp0(m.received)}</td>
                    <td className="n">{num(m.jobs_completed)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>Whole range</td>
                  <td className="n">{num(data.pipeline.leads_in)}</td>
                  <td className="n">{num(data.pipeline.quotes_sent)}</td>
                  <td className="n">{gbp0(data.money.quoted_ex_vat)}</td>
                  <td className="n">{num(data.pipeline.quotes_accepted_in_range)}</td>
                  <td className="n">{gbp0(data.money.accepted_ex_vat)}</td>
                  <td className="n">{pct(data.pipeline.conversion_pct)}</td>
                  <td className="n">{gbp0(data.money.invoiced_ex_vat)}</td>
                  <td className="n">{gbp0(data.money.received)}</td>
                  <td className="n">{num(data.delivery.jobs_completed)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 6 }}>
            Each month is worked out exactly as the tiles below are, for that month alone. Conversion is on the quotes sent that month.
          </div>
        </>
      )}

      {data && (
        <>
          <div className="rep-sec">Money — invoiced is the headline</div>
          <div className="rep-tiles">
            <Tile hero label="Invoiced ex VAT" value={gbp0(data.money.invoiced_ex_vat)}
              sub={`${num(data.money.invoices_sent)} invoices · ${gbp0(data.money.invoiced_gross)} inc VAT`}/>
            <Tile label="Received" value={gbp0(data.money.received)} sub="Payments banked in range"/>
            <Tile label="Quoted ex VAT" value={gbp0(data.money.quoted_ex_vat)} sub="Quotes sent in range"/>
            <Tile label="Accepted ex VAT" value={gbp0(data.money.accepted_ex_vat)} sub="Won in range"/>
          </div>

          <div className="rep-sec">Pipeline</div>
          <div className="rep-tiles">
            <Tile label="Leads in" value={num(data.pipeline.leads_in)}/>
            <Tile label="Quotes sent" value={num(data.pipeline.quotes_sent)}/>
            <Tile label="Conversion" value={pct(data.pipeline.conversion_pct)}
              sub={`${num(data.pipeline.quotes_sent_then_accepted)} of those quotes won`}/>
            <Tile label="Average quote" value={gbp0(data.pipeline.avg_quote_value)}/>
            <Tile label="Lead to quote" value={data.pipeline.median_days_lead_to_quote == null ? '—' : data.pipeline.median_days_lead_to_quote + ' d'} sub="Median"/>
            <Tile label="Quote to win" value={data.pipeline.median_days_quote_to_accept == null ? '—' : data.pipeline.median_days_quote_to_accept + ' d'} sub="Median"/>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 6 }}>
            Conversion is measured on the quotes sent in this window - of what went out, how much came back.
            Dividing wins-in-window by sends-in-window mixes two different cohorts and flatters a slow month.
          </div>

          <div className="rep-sec">Delivery and labour</div>
          <div className="rep-tiles">
            <Tile label="Jobs completed" value={num(data.delivery.jobs_completed)}/>
            <Tile label="Days scheduled" value={num(data.delivery.scheduled_days)}/>
            <Tile label="Person-days worked" value={num(data.delivery.worked_person_days)}/>
            <Tile label="Hours logged" value={num(data.labour.hours)}/>
            <Tile label="Labour cost" value={gbp0(data.labour.cost)} sub="From tablet time"/>
          </div>

          <div className="rep-sec">Margin</div>
          <div className="rep-tiles">
            <Tile label="Revenue ex VAT" value={gbp0(data.margin.revenue_ex_vat)}/>
            <Tile label="Costs counted" value={gbp0(data.margin.cost_total)}
              sub={`Materials ${gbp0(data.costs.materials_actual)} · Labour ${gbp0(data.costs.labour_crm)}`}/>
            <Tile label="Margin" value={gbp0(data.margin.margin)}/>
            <Tile label="Margin %" value={pct(data.margin.margin_pct)}/>
          </div>
          {!data.margin.reliable && (
            <div className="rep-warn">
              <b>Margin is not reliable for this range.</b> {data.costs.incomplete_reasons.join(' ')}
              {' '}Revenue minus incomplete costs reads high. Treat the revenue and cost lines as real and the margin as indicative only.
            </div>
          )}
          {data.costs.labour_qbo_excluded > 0 && (
            <div className="rep-warn">
              <b>{gbp0(data.costs.labour_qbo_excluded)} of subcontractor labour came in from QuickBooks and is excluded above.</b>
              {' '}The CRM allocation ({gbp0(data.costs.labour_crm)}) is what counts, because it can be split across jobs.
              These two should broadly agree - a wide gap means logged time and invoiced time have drifted apart.
            </div>
          )}
          {(data.costs.estimated_materials > 0 || data.costs.estimated_labour > 0) && (
            <div className="rep-tiles" style={{ marginTop: 10 }}>
              <Tile label="Quoted materials" value={gbp0(data.costs.estimated_materials)} sub="What the quote assumed"/>
              <Tile label="Quoted labour" value={gbp0(data.costs.estimated_labour)} sub="What the quote assumed"/>
              <Tile label="Actual materials" value={gbp0(data.costs.materials_actual)}/>
              <Tile label="Actual labour" value={gbp0(data.costs.labour_crm)}/>
            </div>
          )}

          <div className="rep-sec">By brand</div>
          {data.by_brand.length === 0 ? <div className="rep-empty">Nothing in this range.</div> : (
            <table className="rep-table">
              <thead><tr>
                <th>Brand</th><th className="n">Leads</th><th className="n">Quotes</th>
                <th className="n">Won</th><th className="n">Conversion</th><th className="n">Invoiced ex VAT</th>
              </tr></thead>
              <tbody>
                {data.by_brand.map(b => (
                  <tr key={b.brand}>
                    <td>{BRANDS[b.brand] || b.brand}</td>
                    <td className="n">{num(b.leads)}</td>
                    <td className="n">{num(b.quotes_sent)}</td>
                    <td className="n">{num(b.won)}</td>
                    <td className="n">{pct(b.conversion_pct)}</td>
                    <td className="n">{gbp(b.invoiced_ex_vat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="rep-sec">People</div>
          {data.labour.people.length === 0 ? <div className="rep-empty">No time logged in this range.</div> : (
            <table className="rep-table">
              <thead><tr>
                <th>Who</th><th className="n">Hours</th><th className="n">Days</th>
                <th className="n">Cost</th><th className="n">Cost per day</th>
              </tr></thead>
              <tbody>
                {data.labour.people.map(p => (
                  <tr key={p.user_id}>
                    <td>{p.display_name}</td>
                    <td className="n">{p.hours}</td>
                    <td className="n">{p.days}</td>
                    <td className="n">{gbp(p.cost)}</td>
                    <td className="n">{gbp(p.cost_per_day)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="rep-sec">Why quotes were lost</div>
          {data.pipeline.loss_reasons.length === 0 ? <div className="rep-empty">Nothing declined in this range.</div> : (
            <table className="rep-table">
              <thead><tr><th>Reason, in the customer's words</th><th className="n">Count</th></tr></thead>
              <tbody>
                {data.pipeline.loss_reasons.map((r, i) => (
                  <tr key={i}><td>{r.reason}</td><td className="n">{num(r.count)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', margin: '6px 0 30px' }}>
            "Too expensive" is a pricing signal. "Went with someone else" is usually a speed signal.
            They need opposite responses, which is why the wording is kept rather than a tick box.
          </div>
        </>
      )}
    </div>
  );
}

// Pick the work type, look at the document, then send it. Preview renders the same
// builder the PDF uses, in the page, so what is checked is what the customer receives.
function GuaranteeIssue({ job, busy, onIssue }) {
  const [templates, setTemplates] = useState([]);
  const [chosen, setChosen] = useState('');
  const [preview, setPreview] = useState(false);
  useEffect(() => {
    api('/api/guarantee-templates').then(t => {
      setTemplates(t || []);
      if ((t || []).length === 1) setChosen(String(t[0].id));
    }).catch(() => setTemplates([]));
  }, []);

  if (!templates.length) {
    return (
      <>
        <button className="btn btn-sm" disabled={busy} onClick={() => onIssue(job, null, false)}>Guarantee issued</button>
        <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
          No guarantee wording set up yet - add one under Settings &gt; Guarantees to send a document.
        </span>
      </>
    );
  }

  return (
    <>
      <select className="select-compact" value={chosen} disabled={busy}
        onChange={e => setChosen(e.target.value)} style={{ maxWidth: 220 }}>
        <option value="">Choose the work type…</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.years ? ` · ${t.years} yr` : ''}</option>)}
      </select>
      {chosen && (
        <button className="btn btn-sm" onClick={() => setPreview(true)}>Preview</button>
      )}
      {preview && chosen && (
        <DocPreview title="Guarantee preview"
          htmlUrl={`/api/jobs/${job.id}/guarantee-preview-html?template_id=${chosen}`}
          pdfUrl={`/api/jobs/${job.id}/guarantee.pdf?template_id=${chosen}`}
          onClose={() => setPreview(false)} />
      )}
      <button className="btn btn-green btn-sm" disabled={busy || !chosen}
        onClick={() => onIssue(job, chosen, true)}>Issue and send</button>
      <button className="btn btn-sm" disabled={busy}
        onClick={() => onIssue(job, chosen || null, false)}>Issue without sending</button>
    </>
  );
}

const SUPPLIER_TXN_KIND = { Bill: 'Bill', Purchase: 'Expense', VendorCredit: 'Credit note' };
function supplierMoney(n) {
  return (Number(n) < 0 ? '-£' : '£')
    + Math.abs(Number(n || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Confirm, query and undo, for one invoice. jobId is passed when the review happens on a
// job card, so a query lands on that job's lead; a consumable has no job.
function useSupplierReview(reload, jobId) {
  const { showAlert, showPrompt } = useDialog();
  const [busy, setBusy] = useState(null);
  const review = async (inv, status, note) => {
    setBusy(`${inv.txn_type}:${inv.txn_id}`);
    try {
      await api(`/api/supplier-invoices/${inv.txn_type}/${encodeURIComponent(inv.txn_id)}/review`,
        { method: 'POST', body: JSON.stringify({ status, note: note || '', job_id: jobId || null }) });
      await reload();
    } catch (e) { showAlert(e.message); }
    setBusy(null);
  };
  const query = async (inv) => {
    const note = await showPrompt(
      `What is wrong with the ${inv.supplier || 'supplier'} invoice? This goes on the to-do list.`,
      { title: 'Query invoice', placeholder: 'e.g. charged £14 a batten, agreed £11' });
    if (note === null) return;
    if (!String(note).trim()) {
      showAlert('Say what is wrong with it, so whoever picks up the to-do knows what to chase.');
      return;
    }
    review(inv, 'queried', note);
  };
  return { busy, review, query };
}

function SupplierInvoiceRow({ inv, busy, onReview, onQuery, context }) {
  const day = (s) => s
    ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'no date';
  const isImage = (f) => /^image\//.test(f.content_type || '');
  const ext = (f) => ((String(f.file_name || '').match(/\.([a-z0-9]{1,5})$/i) || [])[1] || 'file').toUpperCase();
  return (
    <div className="sup-row">
      <div className="sup-main">
        <span className="sup-name">{inv.supplier || 'Unknown supplier'}</span>
        <span className="sup-meta">
          {SUPPLIER_TXN_KIND[inv.txn_type] || inv.txn_type} · {day(inv.txn_date)} · {supplierMoney(inv.amount)} {context}
        </span>
        {inv.description && <span className="sup-desc">{inv.description}</span>}
        <div className="sup-files">
          {inv.files.length === 0 && <span className="sup-missing">No copy attached in QuickBooks</span>}
          {inv.files.map(f => f.available ? (
            <a key={f.id} href={`/api/supplier-invoice-files/${f.id}`} target="_blank" rel="noreferrer"
              title={`Open ${f.file_name}`} className={isImage(f) ? 'sup-thumb' : 'sup-doc'}>
              {isImage(f) ? <img src={`/api/supplier-invoice-files/${f.id}`} alt={f.file_name}/> : ext(f)}
            </a>
          ) : (
            <span key={f.id} className="sup-missing" title={f.fetch_error || ''}>
              {f.file_name} - not downloaded yet
            </span>
          ))}
        </div>
        {inv.status === 'queried' && (
          <div className="sup-verdict" style={{ color: 'var(--amber)' }}>
            Queried by {inv.reviewed_by}: {inv.note}
          </div>
        )}
        {inv.status === 'confirmed' && (
          <div className="sup-verdict" style={{ color: 'var(--accent)' }}>
            Checked by {inv.reviewed_by}, {new Date(inv.reviewed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            {inv.note ? ` - after a query: ${inv.note}` : ''}
          </div>
        )}
      </div>
      <div className="sup-actions">
        {inv.status !== 'confirmed' && (
          <button className="btn btn-green btn-sm" disabled={busy} onClick={() => onReview(inv, 'confirmed')}>
            {inv.status === 'queried' ? 'Resolved' : 'Confirm'}
          </button>
        )}
        {!inv.status && (
          <button className="btn btn-sm" disabled={busy} onClick={() => onQuery(inv)}>Query</button>
        )}
        {inv.status && (
          <button className="sup-undo" disabled={busy} onClick={() => onReview(inv, 'clear')}>Undo</button>
        )}
      </div>
    </div>
  );
}

function JobSupplierInvoices({ jobId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api(`/api/jobs/${jobId}/supplier-invoices`)
    .then(d => setData(d && d.invoices && d.invoices.length ? d : null))
    .catch(() => setData(null)), [jobId]);
  useEffect(() => { load(); }, [load]);
  const { busy, review, query } = useSupplierReview(load, jobId);

  if (!data) return null;
  return (
    <div className="job-lab">
      <div className="job-lab-head" onClick={() => setOpen(o => !o)}>
        <span className="job-lab-arrow">{open ? '▼' : '▶'}</span>
        <span style={{ color: 'var(--ink-faint)' }}>Supplier invoices</span>
        <strong>{data.invoices.length}</strong>
        <strong>{supplierMoney(data.total)}</strong>
        <span style={{ marginLeft: 'auto' }}>
          <JobPill pill={data.to_check
            ? { text: `${data.to_check} to check`, tone: 'amber' }
            : { text: 'All checked', tone: 'green' }}/>
        </span>
      </div>
      {open && (
        <div className="job-lab-body">
          {data.invoices.map(inv => (
            <SupplierInvoiceRow key={`${inv.txn_type}:${inv.txn_id}`} inv={inv} context="on this job, ex VAT"
              busy={busy === `${inv.txn_type}:${inv.txn_id}`} onReview={review} onQuery={query}/>
          ))}
        </div>
      )}
    </div>
  );
}

// Consumables: bought for general use, never charged to a job, reviewed the same way and
// totalled a month at a time. Months with something still to check open by default.
function ConsumablesPanel({ data, reload }) {
  const { busy, review, query } = useSupplierReview(reload, null);
  const [openMonths, setOpenMonths] = useState(null);
  const months = data ? data.months : [];
  const isOpen = (m) => (openMonths ? openMonths.includes(m.month) : m.to_check > 0);
  const toggle = (m) => setOpenMonths(prev => {
    const cur = prev || months.filter(x => x.to_check > 0).map(x => x.month);
    return cur.includes(m.month) ? cur.filter(x => x !== m.month) : [...cur, m.month];
  });
  const monthLabel = (ym) => new Date(ym + '-01T00:00:00').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  if (!data) return <div style={{ color: 'var(--ink-faint)', fontSize: 13 }}>Loading…</div>;
  if (!months.length) {
    return (
      <div style={{ color: 'var(--ink-faint)', fontSize: 13, lineHeight: 1.5, paddingBottom: 20 }}>
        No consumables in the last twelve months. A cost lands here when Dext posts it to a QuickBooks
        account with "consumables" in its name and no customer on it.
      </div>
    );
  }
  return (
    <div>
      {months.map(m => (
        <div key={m.month} className="cons-month">
          <div className="cons-month-head job-lab-head" onClick={() => toggle(m)}>
            <span className="job-lab-arrow">{isOpen(m) ? '▼' : '▶'}</span>
            <strong>{monthLabel(m.month)}</strong>
            <span style={{ color: 'var(--ink-faint)' }}>{m.invoices.length} invoice{m.invoices.length === 1 ? '' : 's'}</span>
            <strong>{supplierMoney(m.total)}</strong>
            <span style={{ marginLeft: 'auto' }}>
              <JobPill pill={m.to_check
                ? { text: `${m.to_check} to check`, tone: 'amber' }
                : { text: 'All checked', tone: 'green' }}/>
            </span>
          </div>
          {isOpen(m) && (
            <div className="cons-month-body">
              {m.invoices.map(inv => (
                <SupplierInvoiceRow key={`${inv.txn_type}:${inv.txn_id}`} inv={inv} context="ex VAT"
                  busy={busy === `${inv.txn_type}:${inv.txn_id}`} onReview={review} onQuery={query}/>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function JobLabour({ jobId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    api(`/api/labour/job/${jobId}`)
      .then(d => { if (live) setData(d && d.minutes ? d : null); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [jobId]);
  if (!data) return null;
  const money = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="job-lab">
      <div className="job-lab-head" onClick={() => setOpen(o => !o)}>
        <span className="job-lab-arrow">{open ? '▼' : '▶'}</span>
        <span style={{ color: 'var(--ink-faint)' }}>Labour</span>
        <strong>{data.hours} hrs</strong>
        <strong>{money(data.cost)}</strong>
        {data.revenue_ex_vat > 0 && (
          <span className="job-lab-rev">
            {money(data.revenue_ex_vat)} revenue ex VAT
            {!data.job_complete && <em> · provisional</em>}
          </span>
        )}
      </div>
      {open && (
        <div className="job-lab-body">
          <div className="job-lab-row job-lab-h">
            <span>Who</span><span>Hours</span><span>Days</span><span>Cost</span><span>Revenue share</span>
          </div>
          {data.people.map(p => (
            <div className="job-lab-row" key={p.user_id}>
              <span>{p.display_name}</span>
              <span>{p.hours}</span>
              <span>{p.days}</span>
              <span>{money(p.cost)}</span>
              <span>{money(p.revenue_share)}</span>
            </div>
          ))}
          {!data.job_complete && (
            <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 6 }}>
              More minutes can still be logged, so the revenue split is provisional until the job is complete.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Every payment still owed, grouped by how urgent it is. Overdue first, because
// that is the only group where something is going wrong.
function InvoicingView({ onSelectLead, onOpenInvoice, refreshKey, onChanged }) {
  const { showAlert } = useDialog();
  const [rows, setRows] = useState([]);
  const [filterBrand, setFilterBrand] = usePref('invoicing.brand', 'all', isBrandPref);

  const load = useCallback(() => {
    api('/api/invoicing').then(r => setRows(r || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const who = (j) => tileDisplayName(j.title, j.first_name, j.last_name) || j.name || '—';
  const filtered = rows.filter(j => filterBrand === 'all' || j.brand === filterBrand);

  // Amounts are gross. Anything not yet raised is a projection from the quote,
  // which is why the row greys it - it is not a figure anyone has been sent.
  const money = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sumOf = (list) => list.reduce((t, e) => t + Number(e.stage.amount || 0) - Number(e.stage.paid || 0), 0);

  // One row per payment, not per job: a 30/30/40 job has three things to chase.
  const entries = [];
  for (const j of filtered) {
    for (const s of j.payment_stages) {
      if (s.status === 'paid' || s.status === 'cancelled') continue;
      entries.push({ job: j, stage: s });
    }
  }
  const group = (name) => entries.filter(e => e.stage.status === name);
  const buckets = [
    { key: 'overdue', title: 'Overdue', tint: '#b91c1c' },
    { key: 'ready_to_send', title: 'Ready to send', tint: '#b45309' },
    { key: 'outstanding', title: 'Outstanding', tint: '#185fa5' },
    { key: 'awaiting', title: 'Awaiting invoicing', tint: '#57534e' },
  ];

  const Row = ({ e }) => (
    <div className="money-row" style={{ cursor: 'pointer' }}
      title={e.stage.invoice_id ? `Open ${e.stage.reference}` : 'Open the lead - no invoice raised yet'}
      onClick={() => (e.stage.invoice_id ? onOpenInvoice(e.stage.invoice_id) : onSelectLead(e.job.lead_id))}>
      <div>
        <span onClick={(ev) => { ev.stopPropagation(); onSelectLead(e.job.lead_id); }}
          style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}>
          {who(e.job)}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 8 }}>{e.job.address} · {e.job.brand}</span>
      </div>
      <div style={{ fontSize: 12.5 }}>
        {e.stage.percent}% {e.stage.trigger === 'acceptance' ? 'deposit' : (e.stage.trigger === 'start' ? 'on starting' : 'on completion')}
      </div>
      <div style={{ fontSize: 12.5, textAlign: 'right', fontWeight: 600,
        color: e.stage.projected ? 'var(--ink-faint)' : 'var(--ink)' }}
        title={e.stage.projected ? 'Projected from the quote - no invoice raised yet' : undefined}>
        {money(e.stage.amount)}
        {e.stage.paid > 0 && e.stage.paid < e.stage.amount &&
          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--ink-faint)' }}>{money(e.stage.paid)} paid</div>}
      </div>
      <div style={{ fontSize: 12.5 }}>
        {e.stage.reference
          ? <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{e.stage.reference}</span>
          : <span style={{ color: 'var(--ink-faint)' }}>not raised</span>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <JobPill pill={{
          text: e.stage.status === 'overdue' ? `Overdue ${e.stage.days_overdue}d` : (PAYMENT_STATUS_LABELS[e.stage.status] || e.stage.status),
          tone: e.stage.status === 'overdue' ? 'red' : (e.stage.status === 'ready_to_send' ? 'amber' : (e.stage.status === 'outstanding' ? 'blue' : 'grey')),
        }}/>
      </div>
    </div>
  );

  return (
    <div className="view-scroll">
      <div className="filter-bar">
        <span className="filter-label">Brand:</span>
        <select className="select-compact" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k => <option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-faint)' }}>
          {entries.length} outstanding · {money(sumOf(entries))}
        </span>
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '22px 16px 0' }}>
        {entries.length === 0 && <div style={{ color: 'var(--ink-faint)', fontSize: 13, paddingBottom: 20 }}>Nothing outstanding.</div>}
        {buckets.map(b => {
          const list = group(b.key);
          if (!list.length) return null;
          return (
            <div key={b.key} style={{ marginBottom: 22 }}>
              <div className="sec" style={{ color: b.tint }}>
                {b.title} <span style={{ color: 'var(--ink-faint)' }}>{list.length}</span>
                <span style={{ color: 'var(--ink-faint)', fontWeight: 400, marginLeft: 8 }}>{money(sumOf(list))}</span>
              </div>
              {list.map((e, i) => <Row key={`${e.job.id}-${e.stage.seq}-${i}`} e={e}/>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// The VAPID public key arrives base64url and the browser wants raw bytes.
function urlBase64ToUint8Array(b64) {
  const padded = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function NotificationSettingsPanel() {
  const { showAlert } = useDialog();
  const [prefs, setPrefs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [push, setPush] = useState({ enabled: false, public_key: '', devices: 0 });
  const [subscribed, setSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const canPush = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const denied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
  // iOS delivers web push only to a site that has been added to the Home Screen.
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone = typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

  useEffect(() => { api('/api/notification-prefs').then(r => setPrefs(r || [])).catch(() => {}); }, []);
  useEffect(() => { api('/api/push/config').then(r => setPush(r || { enabled: false })).catch(() => {}); }, []);
  useEffect(() => {
    if (!canPush) return;
    navigator.serviceWorker.getRegistration('/')
      .then(reg => reg ? reg.pushManager.getSubscription() : null)
      .then(sub => setSubscribed(!!sub))
      .catch(() => {});
  }, [canPush]);

  const save = async (kind, body) => {
    setBusy(kind);
    try {
      const saved = await api(`/api/notification-prefs/${kind}`, { method: 'PUT', body: JSON.stringify(body) });
      setPrefs(prev => prev.map(x => x.kind === kind ? { ...x, ...saved } : x));
    } catch (e) { showAlert(e.message); }
    setBusy(null);
  };
  // Turning a kind off in the app turns it off altogether - the in-app record is the
  // notification, and push is a second way of hearing about it.
  const toggleInApp = (p) => save(p.kind, { in_app: !p.in_app, push: !p.in_app ? p.push : false });
  const togglePush = (p) => save(p.kind, { in_app: p.in_app, push: !p.push });

  const enableOnThisDevice = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        await showAlert(perm === 'denied'
          ? 'This browser is blocking notifications for the site. Allow them in the browser settings, then try again.'
          : 'Notifications were not allowed, so nothing has changed.');
        setPushBusy(false);
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(push.public_key),
      });
      const j = sub.toJSON();
      await api('/api/push/subscribe', { method: 'POST',
        body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys, user_agent: navigator.userAgent }) });
      setSubscribed(true);
      api('/api/push/config').then(r => setPush(r || push)).catch(() => {});
    } catch (e) { showAlert(e.message || String(e)); }
    setPushBusy(false);
  };

  const disableOnThisDevice = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint: sub.endpoint }) });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      api('/api/push/config').then(r => setPush(r || push)).catch(() => {});
    } catch (e) { showAlert(e.message || String(e)); }
    setPushBusy(false);
  };

  const sendTest = async () => {
    setPushBusy(true);
    try {
      const r = await api('/api/push/test', { method: 'POST' });
      if (!r.sent) await showAlert('Nothing was sent - no device is registered for your account yet.');
    } catch (e) { showAlert(e.message); }
    setPushBusy(false);
  };

  return (
    <div style={{ padding: '16px 20px 30px', maxWidth: 620 }}>
      <div className="sec">Notifications</div>
      <div style={{ fontSize: 12, color: 'var(--ink-faint)', marginBottom: 14 }}>
        Your own settings - they change nothing for anyone else. A notification only ever goes
        to the person the lead is allocated to. Turning something off in the app turns it off
        altogether; push is a second way of hearing about it.
      </div>

      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)',
        borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Push on this device</div>
        {!push.enabled ? (
          <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            Push is not switched on for the server yet, so nothing is sent to this device.
          </div>
        ) : !canPush ? (
          <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            This browser cannot do push notifications.
          </div>
        ) : (isIOS && !standalone) ? (
          <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            On an iPhone or iPad, push only works once the CRM has been added to the Home
            Screen. Tap Share, then "Add to Home Screen", open it from there, and come back
            to this page.
          </div>
        ) : denied && !subscribed ? (
          <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
            This browser is blocking notifications for the site. Allow them in its settings, then
            reload this page.
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {subscribed ? (
              <>
                <button className="btn btn-sm" disabled={pushBusy} onClick={disableOnThisDevice}>
                  Turn off on this device
                </button>
                <button className="btn btn-sm" disabled={pushBusy} onClick={sendTest}>Send a test</button>
                <span style={{ fontSize: 11.5, color: '#16a34a', fontWeight: 600 }}>On</span>
              </>
            ) : (
              <button className="btn btn-green btn-sm" disabled={pushBusy} onClick={enableOnThisDevice}>
                {pushBusy ? 'Working…' : 'Turn on for this device'}
              </button>
            )}
            <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>
              {push.devices} device{push.devices === 1 ? '' : 's'} registered
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 58px 58px', gap: 8, fontSize: 10.5,
        color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '.04em', paddingBottom: 4 }}>
        <span/><span style={{ textAlign: 'center' }}>In app</span><span style={{ textAlign: 'center' }}>Push</span>
      </div>
      {prefs.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>Loading…</div>}
      {prefs.map(p => (
        <div key={p.kind} style={{ display: 'grid', gridTemplateColumns: '1fr 58px 58px', gap: 8,
          alignItems: 'center', padding: '9px 0', borderTop: '1px solid var(--line-soft)', fontSize: 13 }}>
          <span>{p.label}</span>
          <span style={{ textAlign: 'center' }}>
            <input type="checkbox" checked={!!p.in_app} disabled={busy === p.kind}
              onChange={() => toggleInApp(p)}/>
          </span>
          <span style={{ textAlign: 'center' }}>
            <input type="checkbox" checked={!!p.push && !!p.in_app} disabled={busy === p.kind || !p.in_app}
              onChange={() => togglePush(p)}/>
          </span>
        </div>
      ))}
    </div>
  );
}

function LeadJobsSection({ leadId, onChanged }) {
  const [jobs, setJobs] = useState(null);
  const load = useCallback(() => {
    api(`/api/jobs?lead_id=${leadId}`).then(r => setJobs(r || [])).catch(() => setJobs([]));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  const actions = useJobActions(load, onChanged);
  if (!jobs || !jobs.length) return null;
  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Jobs</div>
      {actions.modals}
      {jobs.map(j => <JobActionCard key={j.id} j={j} a={actions}/>)}
    </div>
  );
}

function LeadModal({ leadId, user, onClose, onSaved, onBookVisit, onOpenQuote, onOpenInvoice, invoiceRefreshKey, initialTab, composePurpose }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [lead,setLead]=useState(null);const [form,setForm]=useState({});const [saving,setSaving]=useState(false);
  const [editingAddress,setEditingAddress]=useState(false);
  const [modalTab,setModalTab]=useState(initialTab||'details');
  const [leadEmails,setLeadEmails]=useState([]);
  const [leadPhones,setLeadPhones]=useState([]);
  const [leadNames,setLeadNames]=useState([]);
  const [showAddField,setShowAddField]=useState(null); // 'name' | 'phone' | 'email' | null
  const [newName,setNewName]=useState({first:'',last:''});
  const [newPhone,setNewPhone]=useState('');
  const [newEmail,setNewEmail]=useState('');
  const [quotes,setQuotes]=useState([]);
  const loadQuotes=useCallback(()=>{api(`/api/quotes?lead_id=${leadId}`).then(q=>setQuotes(q||[])).catch(()=>{});},[leadId]);
  const [quickQuote,setQuickQuote]=useState(false);
  useEffect(()=>{loadQuotes();},[loadQuotes]);

  // Raising a quote opens it straight away - nobody creates one to look at later.
  const newQuote=async()=>{
    try{
      const q=await api('/api/quotes',{method:'POST',body:JSON.stringify({lead_id:leadId})});
      loadQuotes();
      onOpenQuote(q.id);
    }catch(e){showAlert(e.message);}
  };
  // The most recent quote, which is the one "Edit quote" means.
  const latestQuote=quotes.length?quotes.reduce((a,b)=>(new Date(b.created_at)>=new Date(a.created_at)?b:a)):null;

  useEffect(()=>{api(`/api/leads/${leadId}`).then(l=>{setLead(l);setForm({name:l.name||'',title:l.title||'',first_name:l.first_name||'',last_name:l.last_name||'',phone:l.phone||'',email:l.email||'',address:l.address||'',house:l.house||'',street:l.street||'',town:l.town||'',postcode:l.postcode||'',brand:l.brand,stage:l.stage,assigned_to:l.assigned_to||'Andy',value:l.value||'',deposit:l.deposit||'',visit_date:l.visit_date||'',job_dates:l.job_dates||'',notes:l.notes||'',visit_notes:l.visit_notes||'',lead_status:l.lead_status||'',quote_status:l.quote_status||'',customer_type:l.customer_type||'',dead_reason:l.dead_reason||''});});},[leadId]);
  useEffect(()=>{api(`/api/leads/${leadId}/emails`).then(setLeadEmails).catch(()=>{});},[leadId]);
  useEffect(()=>{api(`/api/leads/${leadId}/phones`).then(setLeadPhones).catch(()=>{});},[leadId]);
  useEffect(()=>{api(`/api/leads/${leadId}/names`).then(setLeadNames).catch(()=>{});},[leadId]);
  const save=async(overrides={})=>{setSaving(true);try{await api(`/api/leads/${leadId}`,{method:'PUT',body:JSON.stringify({...form,...overrides})});onSaved();onClose();}catch(e){showAlert(e.message);}setSaving(false);};
  const markDead=async()=>{const reason=await showPrompt('Why was this lost? If they said why - price, went elsewhere, timing - put it here.',{title:'Mark as dead',placeholder:'e.g. too expensive, went with someone else'});if(reason===null)return;setSaving(true);try{await api(`/api/leads/${leadId}`,{method:'PUT',body:JSON.stringify({...form,stage:'Dead',dead_reason:reason})});onSaved();onClose();}catch(e){showAlert(e.message);}setSaving(false);};
  const deleteLead=async()=>{
    if(!(await showConfirm('Delete this lead and everything on it - quotes, visits, messages, photos and voice notes? This cannot be undone.',{title:'Delete lead',confirmLabel:'Delete'})))return;
    setSaving(true);
    try{
      const r=await api(`/api/leads/${leadId}`,{method:'DELETE'});
      if(r&&r.quickbooks_customer_id) await showAlert(`Lead deleted. QuickBooks still holds this customer as ${r.quickbooks_customer_id} - remove it there if you want it gone.`);
      onSaved();onClose();
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };
  const readyToQuote=()=>{ save({stage:'Quote'}); };
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const addName=async()=>{
    if(!newName.first.trim()&&!newName.last.trim())return;
    try{
      const n=await api(`/api/leads/${leadId}/names`,{method:'POST',body:JSON.stringify({first_name:newName.first.trim(),last_name:newName.last.trim()})});
      setLeadNames(prev=>[...prev,n]);
      setNewName({first:'',last:''}); setShowAddField(null);
    }catch(e){showAlert(e.message);}
  };
  const addPhone=async()=>{
    if(!newPhone.trim())return;
    try{
      const p=await api(`/api/leads/${leadId}/phones`,{method:'POST',body:JSON.stringify({phone:newPhone.trim(),is_primary:false})});
      setLeadPhones(prev=>[...prev,p]);
      setNewPhone(''); setShowAddField(null);
    }catch(e){showAlert(e.message);}
  };
  const addEmail=async()=>{
    if(!newEmail.trim())return;
    try{
      const e=await api(`/api/leads/${leadId}/emails`,{method:'POST',body:JSON.stringify({email:newEmail.trim(),is_primary:false})});
      setLeadEmails(prev=>[...prev,e]);
      setNewEmail(''); setShowAddField(null);
    }catch(e){showAlert(e.message);}
  };

  if(!lead)return(<div className="modal-overlay"><div className="modal"><div className="mbody" style={{alignItems:'center'}}><span style={{color:'var(--ink-soft)'}}>Loading…</span></div></div></div>);
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-tabbed" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span className="job-num">{jobNumber(lead.id)}</span>
            <span className="mtitle" style={{fontSize:15}}>{displayName(lead.title,lead.first_name,lead.last_name)||lead.name}</span>
            <BrandBadge brand={lead.brand}/>
            <span className={`owner-chip ${form.assigned_to==='Andy'?'andy':'ld'}`}>{form.assigned_to==='LD'?'Lawrance':'Andy'}</span>
            {lead.stage==='Completed'&&<JobPill pill={{text:'Complete',tone:'green'}}/>}
            {lead.stage==='Dead'&&<JobPill pill={{text:'Dead',tone:'red'}}/>}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            {modalTab==='details'&&<button className="btn btn-green btn-sm" onClick={()=>save()} disabled={saving}>{saving?'Saving…':'Save changes'}</button>}
            <button className="xcbtn" onClick={onClose}>×</button>
          </div>
        </div>
        <div style={{display:'flex',gap:4,padding:'0 20px',borderBottom:'1px solid var(--line-soft)',background:'var(--surface)'}}>
          <button className={`tab${modalTab==='details'?' active':''}`} onClick={()=>setModalTab('details')}>Details</button>
          <button className={`tab${modalTab==='messages'?' active':''}`} onClick={()=>setModalTab('messages')}>Messages</button>
          <button className={`tab${modalTab==='gallery'?' active':''}`} onClick={()=>setModalTab('gallery')}>Gallery</button>
        </div>
        {modalTab==='gallery' ? <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}><LeadGallery leadId={leadId} withMessages/></div> : modalTab==='messages' ? (
          <div className="mbody">
            <MessagesSection leadId={leadId} leadEmail={form.email} defaultAssignee={form.assigned_to} composePurpose={composePurpose} onSentReply={initialTab==='messages'?onClose:undefined}/>
          </div>
        ) : (
        <div className="mbody">
          <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
            <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>Lead details</div>
            <div className="row3">
              <div className="fl"><label>Brand</label><select value={form.brand} onChange={e=>set('brand',e.target.value)}>{BRAND_KEYS.map(k=><option key={k} value={k}>{k} — {BRANDS[k]}</option>)}</select></div>
              <div className="fl"><label>Stage</label><select value={form.stage} onChange={e=>set('stage',e.target.value)}>{STAGES.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
              <div className="fl"><label>Allocated to</label><select value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)}><option value="Andy">Andy</option><option value="LD">Lawrance</option></select></div>
            </div>
            {lead&&!(lead.gclid||lead.gbraid||lead.wbraid||lead.utm_source)&&form.source==='phone'&&
              <AdTapMatch leadId={leadId} onUsed={()=>api(`/api/leads/${leadId}`).then(setLead).catch(()=>{})}/>}
            {lead&&(lead.gclid||lead.gbraid||lead.wbraid||lead.utm_source)&&(
              <div className="ad-source">
                <b>{lead.gclid||lead.gbraid||lead.wbraid?'From a Google Ads click':`From ${lead.utm_source}${lead.utm_medium?' / '+lead.utm_medium:''}`}</b>
                {lead.utm_campaign&&<> · campaign <b>{lead.utm_campaign}</b></>}
                {lead.utm_term&&<> · keyword <b>{lead.utm_term}</b></>}
                {lead.landing_page&&<> · landed on {lead.landing_page}</>}
                {lead.click_at&&<> · {new Date(lead.click_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</>}
              </div>
            )}
            <div className="row3" style={{marginTop:10}}>
              <div className="fl"><label>Customer</label><select value={form.customer_type||''} onChange={e=>set('customer_type',e.target.value)}><option value="">—</option><option value="domestic">Domestic</option><option value="commercial">Commercial</option></select></div>
              <div/><div/>
            </div>
            {form.stage==='Lead'&&(
              <div className="fl" style={{marginTop:10}}>
                <label>Lead status</label>
                <select value={form.lead_status||''} onChange={e=>set('lead_status',e.target.value)}
                  style={form.lead_status?{background:leadStatusBox(form.lead_status).bg,color:leadStatusBox(form.lead_status).text,borderColor:leadStatusBox(form.lead_status).border}:{}}>
                  <option value=""></option>
                  {leadStatusOptions()}
                </select>
              </div>
            )}
            {form.stage==='Quote'&&(
              <div className="fl" style={{marginTop:10}}>
                <label>Quote status</label>
                <select value={form.quote_status||''} onChange={e=>set('quote_status',e.target.value)}
                  style={form.quote_status?{background:STATUS_BOX_COLORS.Quote.bg,color:STATUS_BOX_COLORS.Quote.text,borderColor:STATUS_BOX_COLORS.Quote.border}:{}}>
                  <option value=""></option>
                  <option value="priority_quote">Priority quote</option>
                </select>
              </div>
            )}
          </div>
          <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
            <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>Contact details</div>
            <div className="row3">
              <div className="fl"><label>Title</label><select value={form.title||''} onChange={e=>set('title',e.target.value)}><option value="">—</option><option>Mr</option><option>Mrs</option><option>Miss</option><option>Ms</option><option>Dr</option></select></div>
              <div className="fl"><label>First name</label><input type="text" value={form.first_name||''} onChange={e=>set('first_name',e.target.value)}/></div>
              <div className="fl"><label>Last name</label><input type="text" value={form.last_name||''} onChange={e=>set('last_name',e.target.value)}/></div>
            </div>

            {leadNames.map(n=>(
              <div key={n.id} className="row2" style={{marginTop:8}}>
                <input type="text" defaultValue={n.first_name}
                  onBlur={async(ev)=>{
                    const val=ev.target.value.trim();
                    if(val!==n.first_name){
                      try{const updated=await api(`/api/lead-names/${n.id}`,{method:'PUT',body:JSON.stringify({first_name:val,last_name:n.last_name})});setLeadNames(prev=>prev.map(x=>x.id===n.id?updated:x));}
                      catch(err){showAlert(err.message);}
                    }
                  }} placeholder="Additional first name"/>
                <input type="text" defaultValue={n.last_name}
                  onBlur={async(ev)=>{
                    const val=ev.target.value.trim();
                    if(val!==n.last_name){
                      try{const updated=await api(`/api/lead-names/${n.id}`,{method:'PUT',body:JSON.stringify({first_name:n.first_name,last_name:val})});setLeadNames(prev=>prev.map(x=>x.id===n.id?updated:x));}
                      catch(err){showAlert(err.message);}
                    }
                  }} placeholder="Additional last name"/>
                <button type="button" onClick={async()=>{
                  try{ await api(`/api/lead-names/${n.id}`,{method:'DELETE'}); setLeadNames(prev=>prev.filter(x=>x.id!==n.id)); }
                  catch(err){showAlert(err.message);}
                }} style={{gridColumn:'1/-1',background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'4px 0',textAlign:'left'}}>Remove</button>
              </div>
            ))}

            {showAddField!=='name' ? (
              <button type="button" onClick={()=>setShowAddField('name')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block'}}>+ Add additional name</button>
            ) : (
              <div className="row2" style={{marginTop:6}}>
                <input type="text" value={newName.first} onChange={e=>setNewName(n=>({...n,first:e.target.value}))} placeholder="Additional first name"/>
                <input type="text" value={newName.last} onChange={e=>setNewName(n=>({...n,last:e.target.value}))} placeholder="Additional last name"/>
                <div style={{display:'flex',gap:8,marginTop:6,gridColumn:'1/-1'}}>
                  <button className="btn btn-sm btn-green" onClick={addName}>Add</button>
                  <button className="btn btn-sm" onClick={()=>setShowAddField(null)}>Cancel</button>
                </div>
              </div>
            )}

            <div className="row2" style={{marginTop:10}}>
              <div className="fl">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={e=>set('phone',e.target.value)} onBlur={e=>set('phone',formatUkPhone(e.target.value))}/>

                {leadPhones.map(p=>(
                  <div key={p.id} style={{marginTop:6}}>
                    <input type="tel" defaultValue={p.phone}
                      onBlur={async(ev)=>{
                        const val=formatUkPhone(ev.target.value);
                        ev.target.value=val;
                        if(val&&val!==p.phone){
                          try{const updated=await api(`/api/lead-phones/${p.id}`,{method:'PUT',body:JSON.stringify({phone:val})});setLeadPhones(prev=>prev.map(x=>x.id===p.id?updated:x));}
                          catch(err){showAlert(err.message);}
                        }
                      }}
                      style={{width:'100%'}}/>
                    <button type="button" onClick={async()=>{
                      try{ await api(`/api/lead-phones/${p.id}`,{method:'DELETE'}); setLeadPhones(prev=>prev.filter(x=>x.id!==p.id)); }
                      catch(err){showAlert(err.message);}
                    }} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'4px 0',display:'block',width:'100%',textAlign:'left'}}>Remove</button>
                  </div>
                ))}

                {showAddField!=='phone' ? (
                  <button type="button" onClick={()=>setShowAddField('phone')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block',width:'100%',textAlign:'left'}}>+ Add additional phone</button>
                ) : (
                  <div style={{marginTop:6,width:'100%'}}>
                    <input type="tel" value={newPhone} onChange={e=>setNewPhone(e.target.value)} onBlur={e=>setNewPhone(formatUkPhone(e.target.value))} placeholder="Additional phone" style={{width:'100%'}}/>
                    <div style={{display:'flex',gap:8,marginTop:6}}>
                      <button className="btn btn-sm btn-green" onClick={addPhone}>Add</button>
                      <button className="btn btn-sm" onClick={()=>setShowAddField(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="fl">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e=>set('email',e.target.value)}/>

                {leadEmails.filter(e=>!e.is_primary).map(e=>(
                  <div key={e.id} style={{marginTop:6}}>
                    <input type="email" defaultValue={e.email}
                      onBlur={async(ev)=>{
                        const val=ev.target.value.trim();
                        if(val&&val!==e.email){
                          try{
                            const updated=await api(`/api/lead-emails/${e.id}`,{method:'PUT',body:JSON.stringify({email:val})});
                            setLeadEmails(prev=>prev.map(x=>x.id===e.id?updated:x));
                          }catch(err){showAlert(err.message);}
                        }
                      }}
                      style={{width:'100%'}}/>
                    <button type="button" onClick={async()=>{
                      try{ await api(`/api/lead-emails/${e.id}`,{method:'DELETE'}); setLeadEmails(prev=>prev.filter(x=>x.id!==e.id)); }
                      catch(err){showAlert(err.message);}
                    }} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'4px 0',display:'block',width:'100%',textAlign:'left'}}>Remove</button>
                  </div>
                ))}

                {showAddField!=='email' ? (
                  <button type="button" onClick={()=>setShowAddField('email')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block',width:'100%',textAlign:'left'}}>+ Add additional email</button>
                ) : (
                  <div style={{marginTop:6,width:'100%'}}>
                    <input type="email" value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="Additional email" style={{width:'100%'}}/>
                    <div style={{display:'flex',gap:8,marginTop:6}}>
                      <button className="btn btn-sm btn-green" onClick={addEmail}>Add</button>
                      <button className="btn btn-sm" onClick={()=>setShowAddField(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="row3" style={{marginTop:10}}>
              <div className="fl"><label>House no./name</label><input type="text" value={form.house||''} readOnly onClick={()=>setEditingAddress(true)} style={{cursor:'pointer'}}/></div>
              <div className="fl"><label>Street</label><input type="text" value={form.street||''} readOnly onClick={()=>setEditingAddress(true)} style={{cursor:'pointer'}}/></div>
              <div className="fl"><label>Town</label><input type="text" value={form.town||''} readOnly onClick={()=>setEditingAddress(true)} style={{cursor:'pointer'}}/></div>
            </div>
            <div className="fl" style={{marginTop:10}}>
              <label>Postcode</label>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <input type="text" value={form.postcode||''} readOnly onClick={()=>setEditingAddress(true)} style={{maxWidth:140,cursor:'pointer'}}/>
                <button type="button" onClick={()=>setEditingAddress(true)} style={{background:'none',border:'none',color:'var(--accent)',fontSize:12.5,fontWeight:600,cursor:'pointer',padding:0}}>Edit address</button>
              </div>
            </div>
            <div style={{marginTop:10}}>
              <MapPreview address={[form.house,form.street,form.town,form.postcode].filter(Boolean).join(', ') || form.address}/>
            </div>
          </div>
          <div className="fl"><label>Notes</label><textarea rows={3} value={form.notes} onChange={e=>set('notes',e.target.value)}/></div>
          {form.stage==='Dead'&&<div className="fl"><label>Dead reason</label><input type="text" value={form.dead_reason} onChange={e=>set('dead_reason',e.target.value)}/></div>}
          <VisitSection leadId={leadId} onBookVisit={()=>onBookVisit(lead)} onVisitChanged={()=>{onSaved();api(`/api/leads/${leadId}`).then(l=>{setLead(l);set('stage',l.stage);set('assigned_to',l.assigned_to||'Andy');});}}/>
          <VisitInformationSection leadId={leadId} visitNotes={form.visit_notes} onVisitNotesChange={(v)=>set('visit_notes',v)} onReadyToQuote={readyToQuote} currentStage={form.stage}
            quotes={quotes} latestQuote={latestQuote} onOpenQuote={onOpenQuote} onNewQuote={newQuote}/>
          <QuoteSummarySection quotes={quotes} onOpenQuote={onOpenQuote} onNewQuote={newQuote} onQuickQuote={()=>setQuickQuote(true)} onDeleted={loadQuotes}/>
          {quickQuote&&lead&&<QuickQuoteModal lead={{...lead,email:form.email||lead.email}} onClose={()=>setQuickQuote(false)} onDone={(keepOpen)=>{
            if(!keepOpen)setQuickQuote(false);loadQuotes();onSaved();api(`/api/leads/${leadId}`).then(l=>{setLead(l);set('stage',l.stage);}).catch(()=>{});}}/>}
          {onOpenInvoice&&<InvoiceSummarySection leadId={leadId} onOpenInvoice={onOpenInvoice} refreshKey={invoiceRefreshKey}/>}
          <LeadJobsSection leadId={leadId}/>
          <JobCostsSection leadId={leadId}/>
        </div>
        )}
        {modalTab==='details'&&(
        <div className="mfoot">
          {(user.role==='admin'||user.role==='ld')&&(
            <button className="btn btn-sm" onClick={deleteLead} disabled={saving} style={{color:'var(--red)'}}>Delete lead</button>
          )}
          <button className="btn btn-red btn-sm" onClick={markDead}>Mark dead</button>
          <button className="btn btn-green btn-sm" onClick={()=>save()} disabled={saving}>{saving?'Saving…':'Save changes'}</button>
        </div>
        )}
      </div>
      {editingAddress&&(
        <EditAddressModal
          house={form.house} street={form.street} town={form.town} postcode={form.postcode}
          onChange={(a)=>setForm(f=>({...f,...a}))}
          onClose={()=>setEditingAddress(false)}
        />
      )}
    </div>
  );
}

function NewLeadModal({ onClose, onSaved }) {
  const { showAlert } = useDialog();
  const [form,setForm]=useState({name:'',title:'',first_name:'',last_name:'',phone:'',email:'',address:'',house:'',street:'',town:'',postcode:'',brand:'R',assigned_to:'Andy',notes:'',source:'phone',customer_type:'domestic'});
  const [saving,setSaving]=useState(false);
  const [extraPhone,setExtraPhone]=useState('');
  const [extraEmail,setExtraEmail]=useState('');
  const [extraName,setExtraName]=useState({first:'',last:''});
  const [showAddField,setShowAddField]=useState(null); // 'name' | 'phone' | 'email' | null
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    if(!(form.first_name||'').trim()&&!(form.last_name||'').trim()){await showAlert('First or last name is required');return;}
    setSaving(true);
    try{
      const newLead=await api('/api/leads',{method:'POST',body:JSON.stringify(form)});
      if(extraEmail.trim()) await api(`/api/leads/${newLead.id}/emails`,{method:'POST',body:JSON.stringify({email:extraEmail.trim(),is_primary:false})}).catch(()=>{});
      if(extraPhone.trim()) await api(`/api/leads/${newLead.id}/phones`,{method:'POST',body:JSON.stringify({phone:extraPhone.trim(),is_primary:false})}).catch(()=>{});
      if(extraName.first.trim()||extraName.last.trim()) await api(`/api/leads/${newLead.id}/names`,{method:'POST',body:JSON.stringify({first_name:extraName.first.trim(),last_name:extraName.last.trim()})}).catch(()=>{});

      // The server pulls in what this address already sent (Andrew, 3 Nov: doing it here as
      // well filed every email twice).

      onSaved(newLead);onClose();
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };

  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="mhead"><div className="mtitle">New lead</div><button className="xcbtn" onClick={onClose}>×</button></div>
        <div className="mbody">
          <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
            <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>Lead details</div>
            <div className="row3">
              <div className="fl"><label>Brand</label><select value={form.brand} onChange={e=>set('brand',e.target.value)}>{BRAND_KEYS.map(k=><option key={k} value={k}>{k} — {BRANDS[k]}</option>)}</select></div>
              <div className="fl"><label>Source</label><select value={form.source} onChange={e=>set('source',e.target.value)}><option value="phone">Phone</option><option value="email">Email</option><option value="form">Web form</option><option value="referral">Referral</option><option value="google_form">Google Ads lead form</option></select></div>
              <div className="fl"><label>Allocated to</label><select value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)}><option value="Andy">Andy</option><option value="LD">Lawrance</option></select></div>
            </div>
            <div className="row3" style={{marginTop:10}}>
              <div className="fl"><label>Customer</label><select value={form.customer_type||''} onChange={e=>set('customer_type',e.target.value)}><option value="">—</option><option value="domestic">Domestic</option><option value="commercial">Commercial</option></select></div>
              <div/><div/>
            </div>
          </div>
          <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
            <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>What do they need?</div>
            <textarea autoFocus rows={3} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="What is the problem, what have they tried, anything they have said about access or timing" style={{width:'100%'}}/>
          </div>
          <div style={{background:'var(--canvas)',border:'1px solid var(--line-soft)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
            <div className="sec" style={{border:'none',marginBottom:10,paddingBottom:0}}>Contact details</div>
            <div className="row3">
              <div className="fl"><label>Title</label><select value={form.title||''} onChange={e=>set('title',e.target.value)}><option value="">—</option><option>Mr</option><option>Mrs</option><option>Miss</option><option>Ms</option><option>Dr</option></select></div>
              <div className="fl"><label>First name</label><input type="text" value={form.first_name||''} onChange={e=>set('first_name',e.target.value)}/></div>
              <div className="fl"><label>Last name</label><input type="text" value={form.last_name||''} onChange={e=>set('last_name',e.target.value)}/></div>
            </div>
            {showAddField!=='name' ? (
              <button type="button" tabIndex={-1} onClick={()=>setShowAddField('name')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block'}}>+ Add additional name</button>
            ) : (
              <div className="row2" style={{marginTop:6}}>
                <input type="text" value={extraName.first} onChange={e=>setExtraName(n=>({...n,first:e.target.value}))} placeholder="Additional first name"/>
                <input type="text" value={extraName.last} onChange={e=>setExtraName(n=>({...n,last:e.target.value}))} placeholder="Additional last name"/>
                <button type="button" tabIndex={-1} onClick={()=>setShowAddField(null)} style={{gridColumn:'1/-1',background:'none',border:'none',color:'var(--ink-faint)',fontSize:11,cursor:'pointer',padding:'4px 0',textAlign:'left'}}>Cancel</button>
              </div>
            )}

            <div className="row2" style={{marginTop:10}}>
              <div className="fl">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={e=>set('phone',e.target.value)} onBlur={e=>set('phone',formatUkPhone(e.target.value))}/>
                {showAddField!=='phone' ? (
                  <button type="button" tabIndex={-1} onClick={()=>setShowAddField('phone')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block',width:'100%',textAlign:'left'}}>+ Add additional phone</button>
                ) : (
                  <div style={{marginTop:6,width:'100%'}}>
                    <input type="tel" value={extraPhone} onChange={e=>setExtraPhone(e.target.value)} onBlur={e=>setExtraPhone(formatUkPhone(e.target.value))} placeholder="Additional phone" style={{width:'100%'}}/>
                    <button type="button" tabIndex={-1} onClick={()=>setShowAddField(null)} style={{background:'none',border:'none',color:'var(--ink-faint)',fontSize:11,cursor:'pointer',padding:'4px 0',display:'block',width:'100%',textAlign:'left'}}>Done</button>
                  </div>
                )}
              </div>
              <div className="fl">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e=>set('email',e.target.value)}/>
                {showAddField!=='email' ? (
                  <button type="button" tabIndex={-1} onClick={()=>setShowAddField('email')} style={{background:'none',border:'none',color:'var(--accent)',fontSize:11.5,fontWeight:600,cursor:'pointer',padding:'5px 0',display:'block',width:'100%',textAlign:'left'}}>+ Add additional email</button>
                ) : (
                  <div style={{marginTop:6,width:'100%'}}>
                    <input type="email" value={extraEmail} onChange={e=>setExtraEmail(e.target.value)} placeholder="Additional email" style={{width:'100%'}}/>
                    <button type="button" tabIndex={-1} onClick={()=>setShowAddField(null)} style={{background:'none',border:'none',color:'var(--ink-faint)',fontSize:11,cursor:'pointer',padding:'4px 0',display:'block',width:'100%',textAlign:'left'}}>Done</button>
                  </div>
                )}
              </div>
            </div>

            <div style={{marginTop:10}}>
              <AddressLookup house={form.house} street={form.street} town={form.town} postcode={form.postcode}
                onChange={(a)=>setForm(f=>({...f,...a}))}/>
            </div>
            <div style={{marginTop:10}}>
              <MapPreview address={[form.house,form.street,form.town,form.postcode].filter(Boolean).join(', ')}/>
            </div>
          </div>
        </div>
        <div className="mfoot"><button className="btn btn-sm" onClick={onClose}>Cancel</button><button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving?'Saving…':'Add lead'}</button></div>
      </div>
    </div>
  );
}

function EditVisitModal({ visit, onClose, onSaved }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [date,setDate]=useState(visit.visit_date||'');
  const [time,setTime]=useState(visit.visit_time||'09:00');
  const [notes,setNotes]=useState(visit.notes||'');
  const [status,setStatus]=useState(visit.status||'confirmed');
  const [saving,setSaving]=useState(false);
  // Who goes (field app step 3): a person, or a team - whoever is on it that day sees it.
  const [who,setWho]=useState(visit.assigned_to_team_id?`t:${visit.assigned_to_team_id}`:(visit.assigned_to_user_id?`u:${visit.assigned_to_user_id}`:''));
  const [goers,setGoers]=useState({people:[],teams:[]});
  useEffect(()=>{Promise.all([api('/api/users'),api('/api/teams')]).then(([u,t])=>setGoers({
    people:(u||[]).filter(x=>x.active&&x.role!=='office'),teams:(t||[]).filter(x=>x.active)})).catch(()=>{});},[]);
  const whoBody=()=>({assigned_to_user_id:who.startsWith('u:')?Number(who.slice(2)):null,assigned_to_team_id:who.startsWith('t:')?Number(who.slice(2)):null});
  const save=async()=>{if(!date){await showAlert('Pick a date');return;}setSaving(true);try{await api(`/api/visits/${visit.id}`,{method:'PUT',body:JSON.stringify({visit_date:date,visit_time:time,notes,status,...whoBody()})});onSaved(status);onClose();}catch(e){showAlert(e.message);}setSaving(false);};
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead"><div><div className="mtitle">Edit visit</div><div style={{fontSize:12,color:'var(--ink-soft)',marginTop:2}}>{visit.lead_name}</div></div><button className="xcbtn" onClick={onClose}>×</button></div>
        <div className="mbody">
          <div className="row2">
            <div className="fl"><label>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} autoFocus/></div>
            <div className="fl"><label>Time</label><TimeSelect value={time} onChange={setTime}/></div>
          </div>
          <div className="fl">
            <label>Visit status</label>
            <div className="tabs">
              <button className={`tab${status==='proposed'?' active':''}`} onClick={()=>setStatus('proposed')} type="button">Proposed</button>
              <button className={`tab${status==='confirmed'?' active':''}`} onClick={()=>setStatus('confirmed')} type="button">Confirmed</button>
            </div>
          </div>
          <div className="fl"><label>Who's going</label>
            <select value={who} onChange={e=>setWho(e.target.value)}>
              <option value="">Not assigned</option>
              {goers.people.length>0&&<optgroup label="Person">{goers.people.map(p=><option key={p.id} value={`u:${p.id}`}>{p.display_name}</option>)}</optgroup>}
              {goers.teams.length>0&&<optgroup label="Team">{goers.teams.map(t=><option key={t.id} value={`t:${t.id}`}>{t.name}</option>)}</optgroup>}
            </select></div>
          <div className="fl"><label>Notes</label><textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)}/></div>
        </div>
        <div className="mfoot"><button className="btn btn-sm" onClick={onClose}>Cancel</button><button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving?'Saving…':'Save changes'}</button></div>
      </div>
    </div>
  );
}

function BookingBandPicker({ band, onPick, onCancel }) {
  const quarterOptions = [];
  let [sh, sm] = band.start.split(':').map(Number);
  const [eh] = band.end.split(':').map(Number);
  while (sh < eh || (sh === eh && sm === 0 && eh !== sh)) {
    quarterOptions.push(`${String(sh).padStart(2,'0')}:${String(sm).padStart(2,'0')}`);
    sm += 15;
    if (sm >= 60) { sm = 0; sh += 1; }
    if (sh >= eh) break;
  }
  return (
    <div style={{position:'absolute',inset:0,background:'rgba(255,255,255,0.97)',zIndex:5,display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',gap:4,padding:4,borderRadius:6,border:'1px solid var(--accent-soft-line)'}}>
      <div style={{fontSize:9,fontWeight:600,color:'var(--ink-soft)',marginBottom:2}}>Pick exact time</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:3,justifyContent:'center'}}>
        {quarterOptions.map(t=>(
          <button key={t} onClick={(e)=>{e.stopPropagation();onPick(t);}}
            style={{fontSize:10,padding:'3px 6px',border:'1px solid var(--line)',borderRadius:5,background:'var(--surface)',cursor:'pointer'}}
            onMouseEnter={e=>e.currentTarget.style.background='var(--accent-soft)'}
            onMouseLeave={e=>e.currentTarget.style.background='var(--surface)'}>
            {t}
          </button>
        ))}
      </div>
      <button onClick={(e)=>{e.stopPropagation();onCancel();}} style={{fontSize:9,color:'var(--ink-faint)',background:'none',border:'none',cursor:'pointer',marginTop:2}}>Cancel</button>
    </div>
  );
}

const DialogContext = createContext(null);
function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
}

function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null); // {type:'alert'|'confirm'|'prompt', message, title, danger, confirmLabel, defaultValue, resolve}
  const [promptValue, setPromptValue] = useState('');

  const showAlert = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setDialog({ type: 'alert', message, title: opts.title || 'Notice', resolve });
    });
  }, []);

  const showConfirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setDialog({ type: 'confirm', message, title: opts.title || 'Please confirm', danger: opts.danger, confirmLabel: opts.confirmLabel, cancelLabel: opts.cancelLabel, resolve });
    });
  }, []);

  const showPrompt = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setPromptValue(opts.defaultValue || '');
      setDialog({ type: 'prompt', message, title: opts.title || 'Please provide more detail', placeholder: opts.placeholder, resolve });
    });
  }, []);

  const close = (result) => {
    if (dialog) dialog.resolve(result);
    setDialog(null);
  };

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}
      {dialog && (
        <div className="modal-overlay" onClick={() => close(dialog.type === 'alert' ? undefined : (dialog.type === 'prompt' ? null : false))} style={{ zIndex: 80 }}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <div className="mtitle">{dialog.title}</div>
              <button className="xcbtn" onClick={() => close(dialog.type === 'alert' ? undefined : (dialog.type === 'prompt' ? null : false))}>×</button>
            </div>
            <div className="mbody">
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{dialog.message}</div>
              {dialog.type === 'prompt' && (
                <textarea autoFocus rows={3} style={{ marginTop: 10 }} value={promptValue} onChange={e => setPromptValue(e.target.value)}
                  placeholder={dialog.placeholder || ''}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); close(promptValue); } }} />
              )}
            </div>
            <div className="mfoot">
              {dialog.type === 'alert' ? (
                <button className="btn btn-green btn-sm" onClick={() => close(undefined)}>OK</button>
              ) : dialog.type === 'confirm' ? (
                <>
                  <button className="btn btn-sm" onClick={() => close(false)}>{dialog.cancelLabel || 'Cancel'}</button>
                  <button className={`btn btn-sm ${dialog.danger ? 'btn-red' : 'btn-green'}`} onClick={() => close(true)}>{dialog.confirmLabel || 'Confirm'}</button>
                </>
              ) : (
                <>
                  <button className="btn btn-sm" onClick={() => close(null)}>Cancel</button>
                  <button className="btn btn-green btn-sm" onClick={() => close(promptValue)}>OK</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

function ConfirmModal({ title, message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel} style={{zIndex:70}}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div className="mtitle">{title}</div>
          <button className="xcbtn" onClick={onCancel}>×</button>
        </div>
        <div className="mbody">
          <div style={{fontSize:13,color:'var(--ink-soft)',lineHeight:1.5}}>{message}</div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
          <button className={`btn btn-sm ${danger?'btn-red':'btn-green'}`} onClick={onConfirm}>{confirmLabel||'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

function CellActionPicker({ onBook, onBlock, onCancel }) {
  return (
    <div style={{position:'absolute',inset:0,background:'var(--surface)',zIndex:5,display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',gap:5,padding:4,borderRadius:6,border:'1px solid var(--line)',boxShadow:'var(--shadow)'}}>
      <button onClick={(e)=>{e.stopPropagation();onBook();}}
        style={{fontSize:10.5,fontWeight:600,padding:'5px 9px',border:'1px solid var(--accent-soft-line)',borderRadius:6,
          background:'var(--accent-soft)',color:'var(--accent-hover)',cursor:'pointer',width:'90%'}}>
        + Book visit
      </button>
      <button onClick={(e)=>{e.stopPropagation();onBlock();}}
        style={{fontSize:10.5,fontWeight:600,padding:'5px 9px',border:'1px solid var(--line)',borderRadius:6,
          background:'var(--canvas)',color:'var(--ink-soft)',cursor:'pointer',width:'90%'}}>
        Block this slot
      </button>
      <button onClick={(e)=>{e.stopPropagation();onCancel();}} style={{fontSize:9.5,color:'var(--ink-faint)',background:'none',border:'none',cursor:'pointer',marginTop:1}}>Cancel</button>
    </div>
  );
}

function BlockModal({ cell, onClose, onSaved }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [reason,setReason]=useState('');
  const [saving,setSaving]=useState(false);
  const save=async()=>{
    setSaving(true);
    try{
      await api('/api/blocked-slots',{method:'POST',body:JSON.stringify({block_date:cell.dateStr,band_label:cell.band,reason})});
      onSaved();
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div className="mtitle">Block off time</div>
          <button className="xcbtn" onClick={onClose}>×</button>
        </div>
        <div className="mbody">
          <div className="fl">
            <label>When</label>
            <div style={{fontSize:13,fontWeight:600,padding:'8px 0'}}>{fmtVisitDate(cell.dateStr)} · {cell.band}</div>
          </div>
          <div className="fl">
            <label>Reason</label>
            <input type="text" value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. picking up kids" autoFocus/>
          </div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving?'Saving…':'Block this slot'}</button>
        </div>
      </div>
    </div>
  );
}

function DirectBookModal({ cell, onClose, onSaved }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [search,setSearch]=useState('');
  const [results,setResults]=useState([]);
  const [selectedLead,setSelectedLead]=useState(null);
  const [time,setTime]=useState(cell.band ? TIME_BANDS.find(b=>b.label===cell.band).start : '09:00');
  const [visitStatus,setVisitStatus]=useState('proposed');
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    if(!search.trim()){ setResults([]); return; }
    const t=setTimeout(()=>{
      api(`/api/leads?search=${encodeURIComponent(search.trim())}`).then(setResults).catch(()=>{});
    },250);
    return ()=>clearTimeout(t);
  },[search]);

  const band=TIME_BANDS.find(b=>b.label===cell.band);
  const quarterOptions=[];
  if(band){
    let [sh,sm]=band.start.split(':').map(Number);
    const [eh]=band.end.split(':').map(Number);
    while(sh<eh){
      quarterOptions.push(`${String(sh).padStart(2,'0')}:${String(sm).padStart(2,'0')}`);
      sm+=15; if(sm>=60){sm=0;sh+=1;}
      if(sh>=eh)break;
    }
  }

  const save=async()=>{
    if(!selectedLead){await showAlert('Pick a lead first');return;}
    setSaving(true);
    try{
      await api('/api/visits',{method:'POST',body:JSON.stringify({lead_id:selectedLead.id,visit_date:cell.dateStr,visit_time:time,notes:'',status:visitStatus})});
      onSaved();
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };

  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div><div className="mtitle">Book a visit</div><div style={{fontSize:12,color:'var(--ink-soft)',marginTop:2}}>{fmtVisitDate(cell.dateStr)} · {cell.band}</div></div>
          <button className="xcbtn" onClick={onClose}>×</button>
        </div>
        <div className="mbody">
          {!selectedLead ? (
            <div className="fl">
              <label>Find lead</label>
              <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, phone, address…" autoFocus/>
              {results.length>0&&(
                <div style={{border:'1px solid var(--line-soft)',borderRadius:6,maxHeight:200,overflowY:'auto',marginTop:6}}>
                  {results.map(l=>(
                    <div key={l.id} onClick={()=>setSelectedLead(l)} style={{padding:'8px 10px',fontSize:12.5,cursor:'pointer',borderBottom:'1px solid var(--line-soft)'}}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--canvas)'} onMouseLeave={e=>e.currentTarget.style.background='var(--surface)'}>
                      <strong>{tileDisplayName(l.title,l.first_name,l.last_name)||l.name}</strong> <span style={{color:'var(--ink-faint)'}}>{jobNumber(l.id)}</span>
                      <div style={{color:'var(--ink-soft)',fontSize:11.5}}>{l.address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="fl">
                <label>Lead</label>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 10px',background:'var(--canvas)',borderRadius:6,border:'1px solid var(--line-soft)'}}>
                  <div>
                    <strong style={{fontSize:13}}>{tileDisplayName(selectedLead.title,selectedLead.first_name,selectedLead.last_name)||selectedLead.name}</strong>
                    <div style={{fontSize:11.5,color:'var(--ink-soft)'}}>{selectedLead.address}</div>
                  </div>
                  <button className="btn btn-sm" onClick={()=>setSelectedLead(null)}>Change</button>
                </div>
              </div>
              <div className="fl">
                <label>Exact time</label>
                <select value={time} onChange={e=>setTime(e.target.value)}>
                  {quarterOptions.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="fl">
                <label>Visit status</label>
                <div className="tabs">
                  <button className={`tab${visitStatus==='proposed'?' active':''}`} onClick={()=>setVisitStatus('proposed')} type="button">Proposed</button>
                  <button className={`tab${visitStatus==='confirmed'?' active':''}`} onClick={()=>setVisitStatus('confirmed')} type="button">Confirmed</button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-green btn-sm" onClick={save} disabled={saving||!selectedLead}>{saving?'Saving…':'Book visit'}</button>
        </div>
      </div>
    </div>
  );
}

function DiaryView({ onSelectLead, bookingLead, onBookingComplete, onCancelBooking, onVisitChanged, refreshKey }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [diaryMode,setDiaryMode]=usePref('diary.mode','visits',oneOf('visits','jobs')); // visits and jobs are unrelated - a toggle, not a merged view
  useEffect(()=>{if(bookingLead)setDiaryMode('visits');},[bookingLead,setDiaryMode]);
  const [anchorOffset,setAnchorOffset]=useState(0); // 0 = current rolling window, +7/-7 to page
  const [todayTick,setTodayTick]=useState(0); // bumped by the Today button so it fires even when the offset is already 0
  const [visits,setVisits]=useState([]);
  const [blocks,setBlocks]=useState([]);
  const [editVisit,setEditVisit]=useState(null);
  const [offerConfirm,setOfferConfirm]=useState(null);
  const [pickingCell,setPickingCell]=useState(null); // {dateStr, band}
  const [blockModalCell,setBlockModalCell]=useState(null); // {dateStr, band}
  const [directBookCell,setDirectBookCell]=useState(null); // {dateStr, band} - shows small action picker
  const [directBookModal,setDirectBookModal]=useState(null); // {dateStr, band} - full booking modal
  const [bookingVisitStatus,setBookingVisitStatus]=useState('proposed');
  const [confirmDeleteVisit,setConfirmDeleteVisit]=useState(null);
  const [confirmUnblock,setConfirmUnblock]=useState(null);
  const [saving,setSaving]=useState(false);

  // Rolling window anchored to today: yesterday is day 0, today is day 1, then +5 future
  const windowStart = addDays(new Date(), -1 + anchorOffset);
  windowStart.setHours(0,0,0,0);

  const load=useCallback(async()=>{
    try{
      const [v,b]=await Promise.all([
        api(`/api/visits?week_start=${fmtDate(windowStart)}`),
        api(`/api/blocked-slots?week_start=${fmtDate(windowStart)}`)
      ]);
      setVisits(v);setBlocks(b);
    }catch(e){}
  },[fmtDate(windowStart), refreshKey]);
  useEffect(()=>{load();},[load]);

  const today=fmtDate(new Date());
  const days=Array.from({length:7},(_,i)=>addDays(windowStart,i));

  // Put today's column under the reader. Off the current week there is no
  // .today header, so this correctly does nothing; on a desktop there is no
  // horizontal overflow and scrollLeft clamps to 0. The timeout is for React to
  // commit the new week before the column is measured.
  useEffect(()=>{
    if(anchorOffset!==0)return;
    const t=setTimeout(()=>{
      document.querySelectorAll('.diary-grid,.jobs-grid').forEach(g=>{
        const h=g.querySelector('.dg-dayhead.today');
        if(!h)return;
        const sp=parseFloat(getComputedStyle(g).scrollPaddingLeft)||0;
        g.scrollLeft=h.offsetLeft-sp;
      });
    },60);
    return ()=>clearTimeout(t);
  },[anchorOffset,todayTick,diaryMode]);
  const weekLabel=`${days[0].toLocaleDateString('en-GB',{day:'numeric',month:'short'})} – ${days[6].toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}`;

  const blockFor=(dateStr,band)=>blocks.find(b=>b.block_date===dateStr&&b.band_label===band);
  const visitsInBand=(dateStr,band)=>visits.filter(v=>{const vd=v.visit_date.split('T')[0];return vd===dateStr&&bandForTime(v.visit_time)===band;});
  const deleteVisit=(id,e)=>{e.stopPropagation();setConfirmDeleteVisit(id);};
  const doDeleteVisit=async()=>{
    if(confirmDeleteVisit==null)return;
    await api(`/api/visits/${confirmDeleteVisit}`,{method:'DELETE'});
    setConfirmDeleteVisit(null);
    load();
  };

  const unblock=(existing,dateStr,band)=>{
    setConfirmUnblock({existing,dateStr,band});
  };
  const doUnblock=async()=>{
    if(!confirmUnblock)return;
    await api(`/api/blocked-slots/${confirmUnblock.existing.id}`,{method:'DELETE'});
    setConfirmUnblock(null);
    load();
  };

  const handleCellClick=(dateStr,band)=>{
    const existing=blockFor(dateStr,band);
    if(bookingLead){
      if(existing){
        showAlert('This slot is blocked off. Pick another.');
        return;
      }
      setPickingCell({dateStr,band:band});
      return;
    }
    if(existing){ unblock(existing,dateStr,band); return; }
    setDirectBookCell({dateStr,band});
  };

  const confirmBooking=async(time)=>{
    if(!bookingLead||!pickingCell)return;
    setSaving(true);
    try{
      await api('/api/visits',{method:'POST',body:JSON.stringify({lead_id:bookingLead.id,visit_date:pickingCell.dateStr,visit_time:time,notes:'',status:bookingVisitStatus})});
      const booked=bookingLead;
      setPickingCell(null);
      load();
      onBookingComplete(booked);
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };

  return(
    <>
    <div className="cal-wrap">
      {bookingLead&&(
        <div style={{background:'var(--blue-soft)',borderBottom:'1px solid var(--blue-soft-line)',padding:'10px 20px',
          display:'flex',alignItems:'center',gap:10,flexShrink:0,flexWrap:'wrap'}}>
          <span style={{fontSize:'12.5px',fontWeight:600,color:'var(--blue)'}}>
            Booking visit for {tileDisplayName(bookingLead.title,bookingLead.first_name,bookingLead.last_name)||bookingLead.name}
          </span>
          <span style={{fontSize:'11.5px',color:'var(--ink-soft)'}}>{bookingLead.address}</span>
          <div className="tabs" style={{marginLeft:8}}>
            <button className={`tab${bookingVisitStatus==='proposed'?' active':''}`} onClick={()=>setBookingVisitStatus('proposed')}>Proposed</button>
            <button className={`tab${bookingVisitStatus==='confirmed'?' active':''}`} onClick={()=>setBookingVisitStatus('confirmed')}>Confirmed</button>
          </div>
          <span style={{fontSize:'11px',color:'var(--ink-faint)',marginLeft:'auto'}}>Click a free band below, then choose the exact time</span>
          <button className="btn btn-sm" onClick={onCancelBooking}>Cancel</button>
        </div>
      )}
      <div className="cal-nav">
        <button className="btn btn-sm" onClick={()=>setAnchorOffset(o=>o-7)}>← Prev</button>
        <span className="cal-title">{weekLabel}</span>
        <button className="btn btn-sm" onClick={()=>setAnchorOffset(o=>o+7)}>Next →</button>
        <button className="btn btn-sm" onClick={()=>{setAnchorOffset(0);setTodayTick(t=>t+1);}}>Today</button>
        {!bookingLead&&<div className="tabs" style={{marginLeft:6}}>
          <button className={`tab${diaryMode==='visits'?' active':''}`} onClick={()=>setDiaryMode('visits')}>Visits</button>
          <button className={`tab${diaryMode==='jobs'?' active':''}`} onClick={()=>setDiaryMode('jobs')}>Jobs</button>
        </div>}
        {!bookingLead&&diaryMode==='visits'&&<span className="cal-hint" style={{fontSize:11,color:'var(--ink-faint)',marginLeft:4}}>Click an empty slot to book a visit · click a blocked slot to unblock it</span>}
        {!bookingLead&&diaryMode==='jobs'&&<span className="cal-hint" style={{fontSize:11,color:'var(--ink-faint)',marginLeft:4}}>Drag a job onto a day to schedule it · drag it back to the top to unschedule</span>}
      </div>
      {diaryMode==='jobs'&&!bookingLead&&<JobsDiary days={days} today={today} windowStart={windowStart}
        onSelectLead={onSelectLead} refreshKey={refreshKey}/>}
      <div className="diary-grid" style={diaryMode==='jobs'&&!bookingLead?{display:'none'}:undefined}>
        <div className="dg-corner"/>
        {days.map((d,i)=>{const ds=fmtDate(d);return(<div key={i} className={`dg-dayhead${ds===today?' today':''}`}>{d.toLocaleDateString('en-GB',{weekday:'short'})} {d.getDate()} {d.toLocaleDateString('en-GB',{month:'short'})}</div>);})}
        {TIME_BANDS.map(band=>(
          <React.Fragment key={band.label}>
            <div className="dg-timelabel">{band.label}</div>
            {days.map((d,di)=>{
              const ds=fmtDate(d);
              const blocked=blockFor(ds,band.label);
              const bandVisits=visitsInBand(ds,band.label);
              const isPicking=pickingCell&&pickingCell.dateStr===ds&&pickingCell.band===band.label;
              const bookable=bookingLead&&!blocked;
              return(
                <div key={di}
                  className={`dg-cell${blocked?' unavail':''}${ds===today?' today-col':''}`}
                  style={bookable?{boxShadow:'inset 0 0 0 2px var(--accent-soft-line)',cursor:'pointer'}:{}}
                  onClick={()=>handleCellClick(ds,band.label)}>
                  {blocked&&<div className="unavail-label">{blocked.reason==='__default__'?'Unavailable':(blocked.reason||'Unavailable')}</div>}
                  {!blocked&&bandVisits.map(v=>{
                    const c=brandColor(v.brand);
                    const pc=extractPostcode(v.address);
                    const hn=extractHouseNum(v.address);
                    const isProposed=v.status==='proposed';
                    // Andrew, 18 Oct: a visit whose lead has had Visit complete pressed (so it
                    // is at Quote or beyond) stays in the diary, greyed out.
                    const visitDone=!['Lead','Visit'].includes(v.lead_stage||'Visit');
                    return(
                      <div key={v.id} className="visit-pill" style={visitDone
                          ?{background:'#f4f4f5',border:'1px solid #d4d4d8',color:'#71717a',opacity:0.8}
                          :{background:c.bg,border:`1px ${isProposed?'dashed':'solid'} ${c.border}`,color:c.text}}
                        onClick={e=>{e.stopPropagation();onSelectLead(v.lead_id);}}>
                        <div style={{lineHeight:1.4}}>
                          <div style={{fontWeight:700,fontSize:10}}>{v.visit_time} · {v.brand} · {visitDone?'Visit done':(isProposed?'Proposed':'Confirmed')}</div>
                          <div>{hn}{pc?' · '+pc:''}</div>
                          <div style={{fontWeight:500}}>{v.lead_name}</div>
                          {(v.assigned_team_name||v.assigned_user_name)&&<div style={{opacity:0.8}}>→ {v.assigned_team_name||v.assigned_user_name}</div>}
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:2}}>
                          <button className="visit-pill-edit" style={{color:visitDone?'#71717a':c.text}} onClick={e=>{e.stopPropagation();setEditVisit(v);}} title="Edit">✎</button>
                          <button className="visit-pill-del" style={{color:'#dc2626'}} onClick={(e)=>deleteVisit(v.id,e)} title="Remove">×</button>
                        </div>
                      </div>
                    );
                  })}
                  {isPicking&&<BookingBandPicker band={band} onPick={confirmBooking} onCancel={()=>setPickingCell(null)}/>}
                  {directBookCell&&directBookCell.dateStr===ds&&directBookCell.band===band.label&&
                    <CellActionPicker
                      onBook={()=>{setDirectBookModal({dateStr:ds,band:band.label});setDirectBookCell(null);}}
                      onBlock={()=>{setBlockModalCell({dateStr:ds,band:band.label});setDirectBookCell(null);}}
                      onCancel={()=>setDirectBookCell(null)}
                    />}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
    {editVisit&&<EditVisitModal visit={editVisit} onClose={()=>setEditVisit(null)} onSaved={(status)=>{
      if(editVisit.status==='proposed'&&status==='confirmed')setOfferConfirm({leadId:editVisit.lead_id,visitId:editVisit.id});
      setEditVisit(null);load();onVisitChanged&&onVisitChanged();}}/>}
    {offerConfirm&&<VisitConfirmEmailModal leadId={offerConfirm.leadId} visitId={offerConfirm.visitId} onClose={()=>setOfferConfirm(null)}/>}
    {blockModalCell&&<BlockModal cell={blockModalCell} onClose={()=>setBlockModalCell(null)} onSaved={()=>{setBlockModalCell(null);load();}}/>}
    {directBookModal&&<DirectBookModal cell={directBookModal} onClose={()=>setDirectBookModal(null)} onSaved={()=>{setDirectBookModal(null);load();onVisitChanged&&onVisitChanged();}}/>}
    {confirmDeleteVisit!==null&&(
      <ConfirmModal
        title="Remove visit"
        message="This will permanently remove this booked visit. This can't be undone."
        confirmLabel="Remove"
        danger
        onConfirm={doDeleteVisit}
        onCancel={()=>setConfirmDeleteVisit(null)}
      />
    )}
    {confirmUnblock&&(
      <ConfirmModal
        title="Unblock this slot"
        message={`This will make ${confirmUnblock.band} on ${fmtVisitDate(confirmUnblock.dateStr)} available again.`}
        confirmLabel="Unblock"
        onConfirm={doUnblock}
        onCancel={()=>setConfirmUnblock(null)}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The diary in jobs mode: a week of days with no time bands, and every
// unscheduled job along the top.
//
// A drop writes scheduled_date through the same route the Jobs tab uses, so a
// start-of-work payment becomes due the moment a date lands on a job, wherever
// the date came from.
// ---------------------------------------------------------------------------
function JobsDiary({ days, today, windowStart, onSelectLead, refreshKey }) {
  const { showAlert } = useDialog();
  const [scheduled,setScheduled]=useState([]);
  const [unscheduled,setUnscheduled]=useState([]);
  const [dragId,setDragId]=useState(null);
  const [overKey,setOverKey]=useState(null);
  // Pick a job, then tap days to add or remove them. This is the touch route -
  // dragging never fires from a finger - and it is also how multi-day works
  // with a mouse, because a span cannot be dragged.
  const [pickedId,setPickedId]=useState(null);
  const [pickedDates,setPickedDates]=useState([]);
  const [crewFor,setCrewFor]=useState(null); // {dayId, dateStr, name, selected:[user ids]}
  const [addFor,setAddFor]=useState(null);   // a date string, while choosing a job for it
  const [people,setPeople]=useState([]);
  const [brand,setBrand]=usePref('diary.jobsBrand','all',isBrandPref);
  const [sortMode,setSortMode]=usePref('diary.jobsSort','brand',oneOf('brand','name','age'));
  const [busy,setBusy]=useState(false);

  const load=useCallback(async()=>{
    try{
      const [s,u,p]=await Promise.all([
        api(`/api/diary/jobs?week_start=${fmtDate(windowStart)}`),
        api('/api/diary/unscheduled'),
        api('/api/users'),
      ]);
      setScheduled(s||[]);setUnscheduled(u||[]);
      setPeople((p||[]).filter(x=>x.active));
    }catch(e){}
  },[fmtDate(windowStart),refreshKey]);
  useEffect(()=>{load();},[load]);

  const daysOf=(j)=>(j.days||[]).map(d=>d.work_date);
  const initials=crewInitials;

  // Dragging moves the whole block: the server shifts every day by the same
  // delta and carries the crew across with it.
  const setDate=async(jobId,dateStr)=>{
    if(busy)return;
    setBusy(true);
    try{
      await api(`/api/jobs/${jobId}`,{method:'PUT',body:JSON.stringify({scheduled_date:dateStr})});
      await load();
    }catch(e){showAlert(e.message);}
    setBusy(false);
    setDragId(null);setOverKey(null);
  };

  const writeDays=async(jobId,dates)=>{
    if(busy)return false;
    setBusy(true);
    let ok=false;
    try{
      await api(`/api/jobs/${jobId}/days`,{method:'PUT',body:JSON.stringify({dates})});
      await load();ok=true;
    }catch(e){showAlert(e.message);}
    setBusy(false);
    return ok;
  };

  const startMove=(j)=>{
    if(pickedId===j.id){setPickedId(null);setPickedDates([]);return;}
    setPickedId(j.id);setPickedDates(daysOf(j));
  };

  const toggleDay=async(ds)=>{
    if(!pickedId)return;
    const next=pickedDates.includes(ds)
      ? pickedDates.filter(x=>x!==ds)
      : [...pickedDates,ds].sort();
    if(await writeDays(pickedId,next))setPickedDates(next);
  };

  const clearDays=async()=>{
    if(!pickedId)return;
    if(await writeDays(pickedId,[]))setPickedDates([]);
  };

  const saveCrew=async(ids)=>{
    if(!crewFor||busy)return;
    setBusy(true);
    try{
      await api(`/api/job-days/${crewFor.dayId}/crew`,{method:'PUT',body:JSON.stringify({user_ids:ids})});
      await load();
      setCrewFor(c=>c?{...c,selected:ids}:c);
    }catch(e){showAlert(e.message);}
    setBusy(false);
  };

  const visible=unscheduled.filter(j=>brand==='all'||j.brand===brand);
  const sorted=[...visible].sort((a,b)=>{
    if(sortMode==='brand')return (a.brand||'').localeCompare(b.brand||'')||a.id-b.id;
    if(sortMode==='name')return (nameOf(a)||'').localeCompare(nameOf(b)||'');
    return a.id-b.id;  // oldest first
  });

  function nameOf(j){return tileDisplayName(j.title,j.first_name,j.last_name)||j.lead_name||'—';}

  const jobCard=(j,inStrip,day,key)=>{
    const c=brandColor(j.brand);
    const pc=extractPostcode(j.address);
    const hn=extractHouseNum(j.address);
    const all=daysOf(j);
    const idx=day?all.indexOf(day.work_date):-1;
    const picked=pickedId===j.id;
    // Andrew, 18 Oct: a completed job stays in the diary as a record, greyed out.
    const done=!!j.completed_at||j.status==='complete'||j.status==='guarantee';
    // While a job is being placed the card must not open the lead, and its click
    // has to reach the cell underneath so that the day toggles.
    return (
      <div key={key} draggable onDragStart={()=>setDragId(j.id)} onDragEnd={()=>{setDragId(null);setOverKey(null);}}
        onClick={()=>{if(pickedId)return;onSelectLead(j.lead_id);}}
        title={j.address||''}
        style={{background:done?'#f4f4f5':c.bg,border:`${picked?2:1}px solid ${picked?'var(--accent)':(done?'#d4d4d8':c.border)}`,
          color:done?'#71717a':c.text,borderRadius:6,
          padding:'5px 7px',fontSize:10.5,lineHeight:1.45,cursor:'grab',marginBottom:3,
          opacity:dragId===j.id?0.45:(done?0.8:1),
          minWidth:inStrip?168:undefined,flexShrink:inStrip?0:undefined}}>
        <div style={{fontWeight:700,fontSize:10}}>
          {j.brand} · {JOB_STATUS_LABELS[j.status]||j.status}
          {all.length>1&&idx>=0&&<span style={{marginLeft:5,opacity:0.75}}>day {idx+1}/{all.length}</span>}
        </div>
        <div style={{fontWeight:600}}>{nameOf(j)}</div>
        <div>{hn}{pc?' · '+pc:''}</div>
        {day&&(
          <div className="jg-crew" title="Who is on this day"
            onClick={e=>{e.stopPropagation();
              setCrewFor({dayId:day.id,dateStr:day.work_date,name:nameOf(j),
                selected:(day.crew||[]).map(u=>u.id)});}}>
            {(day.crew||[]).length
              ? day.crew.map(u=><span key={u.id} className="jg-chip" title={u.display_name} style={personChipStyle(u.colour)}>{initials(u.display_name)}</span>)
              : <span className="jg-chip jg-chip-empty">+ team</span>}
          </div>
        )}
        <button className="jg-move" onClick={e=>{e.stopPropagation();startMove(j);}}>
          {picked?'Done':'Move'}
        </button>
      </div>
    );
  };

  const dayJobs=(ds)=>scheduled
    .filter(j=>(j.days||[]).some(d=>d.work_date===ds))
    .map(j=>({j, day:(j.days||[]).find(d=>d.work_date===ds)}));
  const pickedJob=[...unscheduled,...scheduled].find(j=>j.id===pickedId);

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
      {pickedId&&(
        <div className="jg-placing">
          <span className="jg-placing-text">
            {pickedJob?nameOf(pickedJob):'Job'} - tap days to add or remove
            {pickedDates.length>0&&<b style={{marginLeft:6}}>{pickedDates.length} day{pickedDates.length>1?'s':''}</b>}
          </span>
          <button className="btn btn-sm" disabled={busy} onClick={clearDays}>Remove all dates</button>
          <button className="btn btn-sm" onClick={()=>{setPickedId(null);setPickedDates([]);}}>Done</button>
        </div>
      )}

      {addFor&&(
        <div className="jg-modal-back" onClick={()=>setAddFor(null)}>
          <div className="jg-modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:13.5,fontWeight:600}}>Add a job</div>
            <div style={{fontSize:11.5,color:'var(--ink-soft)',marginBottom:12}}>
              {new Date(addFor+'T00:00:00').toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})}
            </div>
            {sorted.length===0&&(
              <div style={{fontSize:12.5,color:'var(--ink-faint)',marginBottom:10}}>
                Nothing waiting to be scheduled. To put a job that already has dates on another day, tap Move on it.
              </div>
            )}
            {sorted.map(j=>(
              <button key={j.id} className="jg-person" disabled={busy}
                onClick={async()=>{if(await writeDays(j.id,[...daysOf(j),addFor].sort()))setAddFor(null);}}>
                <span>{nameOf(j)}</span>
                <span style={{fontSize:11,color:'var(--ink-faint)'}}>{j.brand}</span>
              </button>
            ))}
            <button className="btn btn-sm" style={{marginTop:6}} onClick={()=>setAddFor(null)}>Close</button>
          </div>
        </div>
      )}
      {crewFor&&(
        <div className="jg-modal-back" onClick={()=>setCrewFor(null)}>
          <div className="jg-modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:13.5,fontWeight:600}}>Who is on this job</div>
            <div style={{fontSize:11.5,color:'var(--ink-soft)',marginBottom:12}}>
              {crewFor.name} · {crewFor.dateStr}
            </div>
            {people.length===0&&(
              <div style={{fontSize:12.5,color:'var(--ink-faint)',marginBottom:10}}>
                No logins to assign yet. Add people in Settings, under Team.
              </div>
            )}
            {people.map(p=>{
              const on=crewFor.selected.includes(p.id);
              return (
                <button key={p.id} className={`jg-person${on?' on':''}`} disabled={busy}
                  onClick={()=>saveCrew(on?crewFor.selected.filter(x=>x!==p.id):[...crewFor.selected,p.id])}>
                  <span>{p.display_name}</span>
                  <span style={{fontSize:11,color:'var(--ink-faint)'}}>{ROLE_LABELS[p.role]||p.role}</span>
                </button>
              );
            })}
            <button className="btn btn-sm" style={{marginTop:6}} onClick={()=>setCrewFor(null)}>Close</button>
          </div>
        </div>
      )}
      <div className="jg-strip"
        onDragOver={e=>{e.preventDefault();setOverKey('strip');}}
        onDragLeave={()=>setOverKey(k=>k==='strip'?null:k)}
        onDrop={e=>{e.preventDefault();if(dragId)setDate(dragId,null);}}
        style={{flexShrink:0,borderBottom:'1px solid var(--line)',background:overKey==='strip'?'var(--accent-soft)':'var(--surface)',
          padding:'8px 20px'}}>
        <div className="jg-strip-head" style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
          <span style={{fontSize:11.5,fontWeight:600,color:'var(--ink-soft)'}}>Unscheduled ({visible.length})</span>
          <select className="select-compact" value={brand} onChange={e=>setBrand(e.target.value)}>
            <option value="all">All brands</option>
            {BRAND_KEYS.map(k=><option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
          </select>
          <select className="select-compact" value={sortMode} onChange={e=>setSortMode(e.target.value)}>
            <option value="brand">Sort: brand</option>
            <option value="name">Sort: customer</option>
            <option value="age">Sort: accepted first</option>
          </select>
          <span className="jg-strip-hint" style={{fontSize:11,color:'var(--ink-faint)',marginLeft:'auto'}}>Drop here to take a date off a job</span>
        </div>
        <div style={{display:'flex',gap:6,overflowX:'auto',paddingBottom:2,minHeight:56}}>
          {sorted.length===0&&<span style={{fontSize:11.5,color:'var(--ink-faint)'}}>Nothing waiting to be scheduled.</span>}
          {sorted.map(j=>jobCard(j,true,null,j.id))}
        </div>
      </div>

      <div className="jobs-grid" style={{flex:1,overflow:'auto',display:'grid',
        gap:1,background:'var(--line-soft)',padding:1,alignContent:'start'}}>
        {days.map((d,i)=>{
          const ds=fmtDate(d);
          return <div key={`h${i}`} className={`dg-dayhead${ds===today?' today':''}`}>
            {d.toLocaleDateString('en-GB',{weekday:'short'})} {d.getDate()} {d.toLocaleDateString('en-GB',{month:'short'})}
          </div>;
        })}
        {days.map((d,i)=>{
          const ds=fmtDate(d);
          const list=dayJobs(ds);
          const on=pickedDates.includes(ds);
          return (
            <div key={`c${i}`}
              className={`jg-cell${pickedId?(on?' jg-on':' jg-target'):''}`}
              onDragOver={e=>{e.preventDefault();setOverKey(ds);}}
              onDragLeave={()=>setOverKey(k=>k===ds?null:k)}
              onDrop={e=>{e.preventDefault();if(dragId)setDate(dragId,ds);}}
              onClick={()=>{if(pickedId)toggleDay(ds);}}
              style={{background:overKey===ds?'var(--accent-soft)':(ds===today?'#fbfdfb':'var(--surface)')}}>
              {list.map(({j,day})=>jobCard(j,false,day,`${j.id}-${ds}`))}
              {pickedId ? (
                <button className={`jg-add${on?' on':''}`} onClick={e=>{e.stopPropagation();toggleDay(ds);}}>
                  {on?'On this day - tap to remove':'+ Add to this day'}
                </button>
              ) : (
                <button className="jg-add" onClick={e=>{e.stopPropagation();setAddFor(ds);}}>+ Add a job</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Today's saved copy (Andrew, 18 Oct: "service can be patchy") --------------------------
// Fetched from /api/offline/today every 10 minutes while there is signal and kept in this
// phone's storage. Read-only: nothing can be changed from it, so there is nothing to sync
// back and nothing to conflict. Cleared on sign out.
const OFFLINE_KEY = 'rn.offline.today';
function readOfflineCopy() {
  try { const v = JSON.parse(localStorage.getItem(OFFLINE_KEY) || 'null'); return v && v.saved_at ? v : null; }
  catch (e) { return null; }
}
function saveOfflineCopy() {
  api('/api/offline/today').then(d => {
    try { localStorage.setItem(OFFLINE_KEY, JSON.stringify(d)); } catch (e) {}
  }).catch(() => {});
}
function clearOfflineCopy() { try { localStorage.removeItem(OFFLINE_KEY); } catch (e) {} }

function OfflineToday({ data, onBack, onRetry }) {
  if (!data) {
    return (
      <div style={{ padding: 24, maxWidth: 520, margin: '0 auto' }}>
        <div className="sec">No signal</div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          There is no saved copy on this phone yet. It is saved automatically while the CRM is open with signal.
        </div>
        <button className="btn btn-sm" style={{ marginTop: 12 }} onClick={onRetry}>Try again</button>
      </div>
    );
  }
  const saved = new Date(data.saved_at);
  const savedLabel = saved.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const dayKeys = [...new Set([...(data.visits || []).map(v => v.visit_date), ...(data.job_days || []).map(d => d.work_date)])].sort();
  const who = (x) => [x.title, x.first_name, x.last_name].filter(Boolean).join(' ') || x.name || x.company_name || '—';
  const telOf = (p) => 'tel:' + String(p || '').replace(/[^\d+]/g, '');
  const mapOf = (x) => `https://maps.apple.com/?q=${encodeURIComponent([x.address, x.postcode].filter(Boolean).join(', '))}`;
  const contact = (x) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
      {x.phone && <a className="btn btn-sm" href={telOf(x.phone)} style={{ textDecoration: 'none' }}>Call {x.phone}</a>}
      {x.address && <a className="btn btn-sm" href={mapOf(x)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>Directions</a>}
    </div>
  );
  const note = (label, text) => text && String(text).trim()
    ? <div style={{ fontSize: 12.5, marginTop: 6, whiteSpace: 'pre-wrap' }}><span style={{ color: 'var(--ink-faint)' }}>{label}: </span>{text}</div> : null;
  return (
    <div style={{ height: '100dvh', overflowY: 'auto', background: 'var(--canvas)' }}>
      <div className="offline-bar" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
        <span>Saved copy from {savedLabel} - read only</span>
        <span style={{ display: 'flex', gap: 6 }}>
          {onBack && <button className="btn btn-sm" onClick={onBack}>Back</button>}
          <button className="btn btn-sm" onClick={onRetry}>Try again</button>
        </span>
      </div>
      <div style={{ padding: '12px 14px 40px', maxWidth: 640, margin: '0 auto' }}>
        {dayKeys.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Nothing booked today or tomorrow in the saved copy.</div>}
        {dayKeys.map(day => (
          <div key={day} style={{ marginBottom: 18 }}>
            <div className="sec" style={{ marginBottom: 8 }}>{fmtVisitDate(day)}</div>
            {(data.visits || []).filter(v => v.visit_date === day).map(v => (
              <div key={`v${v.id}`} className="offline-card">
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase' }}>
                  Visit · {v.visit_time} · {v.brand} · {v.status === 'proposed' ? 'Proposed' : 'Confirmed'}{v.assigned_user ? ` · ${v.assigned_user}` : ''}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, marginTop: 3 }}>{who(v)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{v.address}</div>
                {contact(v)}
                {note('Visit notes', v.notes)}
                {note('Lead notes', v.lead_notes)}
                {note('From earlier visits', v.visit_notes)}
              </div>
            ))}
            {(data.job_days || []).filter(d => d.work_date === day).map(d => (
              <div key={`j${d.day_id}`} className="offline-card">
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase' }}>
                  Job · {d.brand} · {JOB_STATUS_LABELS[d.status] || d.status}{d.quote_reference ? ` · Quote ${d.quote_reference}` : ''}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, marginTop: 3 }}>{who(d)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{d.address}</div>
                <div style={{ fontSize: 12.5, marginTop: 4 }}>
                  <span style={{ color: 'var(--ink-faint)' }}>Team: </span>{(d.crew || []).length ? d.crew.join(', ') : 'nobody set'}
                </div>
                {contact(d)}
                {(d.items || []).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {d.items.map((it, i) => (
                      <div key={i} style={{ fontSize: 12.5, padding: '5px 0', borderTop: '1px solid var(--line-soft)' }}>
                        <span style={{ fontWeight: 600 }}>{it.title || `Item ${i + 1}`}</span>
                        {it.optional && <span style={{ color: 'var(--ink-faint)' }}> (optional - check it was taken)</span>}
                        {it.description && <div style={{ whiteSpace: 'pre-wrap', color: 'var(--ink-soft)' }}>{it.description}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {note('Materials', d.materials_notes)}
                {note('Lead notes', d.lead_notes)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Mirrors FULL_ACCESS_ROLES on the server: everyone else is a field login.
const FULL_ACCESS_ROLES_CLIENT = ['admin', 'ld', 'office'];

// "Who's with you today?" - pick the team, tick who turned up. Writes today's team list,
// which also puts them on that team's jobs for today (server: setTeamDay).
function TeamToday({ data, me, onDone }) {
  const { showAlert } = useDialog();
  const mine = data.teams.filter(t => data.my_teams.includes(t.id));
  const [teamId, setTeamId] = useState(data.my_team_today || (mine.length === 1 ? mine[0].id : null));
  const team = data.teams.find(t => t.id === teamId);
  const [ticked, setTicked] = useState(() => new Set(team ? (team.today.length ? team.today : [me.id]) : [me.id]));
  const [busy, setBusy] = useState(false);
  const pick = (t) => { setTeamId(t.id); setTicked(new Set(t.today.length ? t.today : [me.id])); };
  const toggle = (id) => setTicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    setBusy(true);
    try { await api('/api/field/team-today', { method: 'PUT', body: JSON.stringify({ team_id: teamId, user_ids: [...ticked] }) }); onDone(); }
    catch (e) { showAlert(e.message); }
    setBusy(false);
  };
  const changing = !!data.my_team_today;
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--canvas)' }}>
      <div className="topbar" style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{me.display_name}</span>
      </div>
      <div className="fd-detail" style={{ maxWidth: 760, margin: '0 auto', padding: '14px 16px 32px' }}>
        {changing && <button className="fd-back" onClick={onDone}>← Back</button>}
        <div className="fd-head">
          <div className="fd-head-name">{changing ? 'Change team' : "Who's with you today?"}</div>
          <div className="fd-head-addr">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>
        <div className="fd-card">
          <div className="fd-card-title">Team</div>
          <div className="fd-tick-grid">
            {(mine.length ? mine : data.teams).map(t => (
              <button key={t.id} className={`fd-tick${teamId === t.id ? ' on' : ''}`} onClick={() => pick(t)}>{t.name}</button>
            ))}
          </div>
        </div>
        {team && (
          <div className="fd-card">
            <div className="fd-card-title">Who's on {team.name}</div>
            <div className="fd-tick-grid">
              {team.members.map(p => (
                <button key={p.id} className={`fd-tick${ticked.has(p.id) ? ' on' : ''}`} onClick={() => toggle(p.id)}>
                  {ticked.has(p.id) ? '✓ ' : ''}{p.display_name}{p.id === me.id ? ' (you)' : ''}
                </button>
              ))}
            </div>
            {changing && <div className="fd-small">Anyone moving teams stops on their old job and joins this team's job from now. Their time so far stays where it was.</div>}
          </div>
        )}
        <button className="fd-go" disabled={busy || !team || !ticked.size} onClick={save}>{busy ? 'Saving…' : (changing ? 'Save' : 'Start the day')}</button>
        {!changing && <button className="btn" onClick={onDone}>Not with a team today</button>}
      </div>
    </div>
  );
}

// My day - surveys and jobs (field app steps 3-4). Today first, other days on the date strip.
// Jobs for teams join this list in step 4.
// Work can only be changed on its own day: earlier and later days are there to look at.
const fieldLocked = (ymd) => ymd !== new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
// Andrew, 31 Oct: every photo and video on a lead in one place. withMessages adds pictures that
// came by email, which only office logins can open. Andrew, 3 Nov: and the documents that came
// by email (PDF, Word, spreadsheets), as tiles that open the file.
const isVideoFile = (name) => /\.(mp4|webm|mov|m4v)$/i.test(name || '');
const isImageFile = (name) => /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(name || '');
// Documents kept from customers' emails (server: KEPT_DOC_EXT) - shown as a file button, not a picture.
const isDocFile = (name) => /\.(pdf|docx?|xlsx?|odt|ods|rtf|csv|txt)$/i.test(name || '');
function LeadGallery({ leadId, withMessages }) {
  const [media, setMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const queued = useUploadQueue(leadId).filter(q => q.type !== 'voice');
  const load = useCallback(async () => {
    const [att, msgs] = await Promise.all([
      api(`/api/attachments?lead_id=${leadId}`).catch(() => []),
      withMessages ? api(`/api/leads/${leadId}/messages`).catch(() => []) : Promise.resolve([]),
    ]);
    const label = { photo: 'Survey', job_photo: 'Job', video: 'Video' };
    const list = (att || []).filter(a => a.type !== 'voice' && (isImageFile(a.filename) || isVideoFile(a.filename)))
      .map(a => ({ key: `a${a.id}`, filename: a.filename, original_name: a.original_name, from: label[a.type] || 'Upload', at: a.created_at }));
    const seen = new Set(list.map(x => x.filename));
    for (const m of (msgs || [])) {
      const f = m.attachment_filename;
      if (f && !seen.has(f) && (isImageFile(f) || isVideoFile(f) || isDocFile(f))) {
        seen.add(f);
        list.push({ key: `m${m.id}`, filename: f, original_name: isDocFile(f) ? (m.attachment_name || 'Document') : (m.subject || 'Attachment'), from: 'Email', at: m.sent_at || m.created_at });
      }
    }
    setMedia(list.sort((x, y) => new Date(y.at) - new Date(x.at)));
  }, [leadId, withMessages]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const done = (e) => { if (String(e.detail && e.detail.lead_id) === String(leadId)) load(); };
    window.addEventListener('rn-upload-done', done);
    return () => window.removeEventListener('rn-upload-done', done);
  }, [leadId, load]);
  if (!media) return <div className="fd-none">Loading…</div>;
  const photos = media.filter(m => !isVideoFile(m.filename) && !isDocFile(m.filename));
  const docExt = (f) => ((f.match(/\.(\w+)$/) || [])[1] || '').toLowerCase();
  const docKind = (ext) => ext === 'pdf' ? 'pdf' : /^(docx?|odt|rtf)$/.test(ext) ? 'doc' : /^(xlsx?|ods|csv)$/.test(ext) ? 'xls' : '';
  return (
    <>
      {queued.length > 0 && <div className="uq-box">{queued.length} saved on this device, uploading…</div>}
      {media.length === 0 && !queued.length && <div className="fd-none">No photos or videos yet.</div>}
      <div className="gal-grid">
        {media.map(m => (
          <div key={m.key} className="gal-item">
            {isDocFile(m.filename)
              ? <a className="gal-doc" href={uploadUrl(m.filename)} target="_blank" rel="noopener noreferrer">
                  <span className={`gal-doc-ext ${docKind(docExt(m.filename))}`}>{docExt(m.filename).toUpperCase()}</span>
                  <span className="gal-doc-name">{m.original_name}</span>
                </a>
              : isVideoFile(m.filename)
              ? <video src={uploadUrl(m.filename)} controls preload="metadata" playsInline/>
              : <img src={uploadUrl(m.filename, 'thumb')} alt={m.original_name || 'Photo'} onClick={() => setLightboxIndex(photos.indexOf(m))}/>}
            <div className="gal-cap"><b>{m.from}</b> {new Date(m.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
          </div>
        ))}
      </div>
      {lightboxIndex !== null && <PhotoLightbox photos={photos} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)}/>}
    </>
  );
}
// Details and Gallery along the top of a job or survey. Details stays mounted so nothing typed
// or uploading is lost by looking at the gallery.
function FieldTabs({ leadId, children }) {
  const [tab, setTab] = useState('details');
  return (
    <>
      <div className="fd-tabs">
        <button className={tab === 'details' ? 'on' : ''} onClick={() => setTab('details')}>Details</button>
        <button className={tab === 'gallery' ? 'on' : ''} onClick={() => setTab('gallery')}>Gallery</button>
      </div>
      <div style={{ display: tab === 'details' ? 'block' : 'none' }}>{children}</div>
      {tab === 'gallery' && leadId && <div className="fd-card"><LeadGallery leadId={leadId}/></div>}
    </>
  );
}
// Andrew, 31 Oct: a photo before Done, or a note saying why not. A photo taken today counts,
// including one still waiting on this tablet to upload. Asked on the screen, because a photo
// that has not uploaded yet is invisible to the server.
function useFieldPhotoGate(leadId, type) {
  const { showAlert } = useDialog();
  const queued = useUploadQueue(leadId == null ? -1 : leadId);
  const [ask, setAsk] = useState(null);
  const [reason, setReason] = useState('');
  const input = useRef(null);
  const check = async (explained) => {
    if (explained) return { ok: true };
    if (queued.some(q => q.type !== 'voice')) return { ok: true };
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    const rows = await api(`/api/attachments?lead_id=${leadId}`).catch(() => []);
    const taken = (rows || []).some(a => a.type !== 'voice'
      && new Date(a.created_at).toLocaleDateString('en-CA', { timeZone: 'Europe/London' }) === today);
    if (taken) return { ok: true };
    setReason('');
    return new Promise(resolve => setAsk(() => resolve));
  };
  const finish = (r) => { const go = ask; setAsk(null); if (go) go(r); };
  const took = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    try { for (const f of files) await uqEnqueue(f, leadId, type); finish({ ok: true }); }
    catch (err) { showAlert('That photo could not be saved. Try again.'); }
  };
  const dialog = ask && (
    <div className="fd-modal-back">
      <div className="fd-modal">
        <div className="fd-card-title">Add a photo of the work</div>
        <input ref={input} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={took}/>
        <button className="fd-go" onClick={() => input.current && input.current.click()}>Take photo</button>
        <div className="fd-modal-text">Can't get one? Just add a note instead.</div>
        <textarea rows={3} value={reason} placeholder="Add a note" onChange={e => setReason(e.target.value)}/>
        <div className="fd-modal-row">
          <button className="btn" onClick={() => finish({ ok: false })}>Back</button>
          <button className="btn btn-green" disabled={!reason.trim()} onClick={() => finish({ ok: true, reason: reason.trim() })}>Save note</button>
        </div>
      </div>
    </div>
  );
  return { check, dialog };
}
// Where the tablet is, for Start and Finish. When it cannot say, it says why, and the server
// raises an admin to-do.
const fieldFix = () => new Promise(resolve => {
  if (!navigator.geolocation) return resolve({ fix_error: 'unsupported' });
  navigator.geolocation.getCurrentPosition(
    p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    e => resolve({ fix_error: e.code === 1 ? 'denied' : e.code === 3 ? 'timeout' : 'unavailable' }),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
});
// The order work is done in: as arranged under Day order, then jobs, then surveys by booked time.
const fieldItemOrder = (a, b) => {
  const oa = a.order == null ? Infinity : a.order, ob = b.order == null ? Infinity : b.order;
  if (oa !== ob) return oa - ob;
  if (a.kind !== b.kind) return a.kind === 'job' ? -1 : 1;
  return String(a.time || '').localeCompare(String(b.time || '')) || (a.num - b.num);
};
// Andrew, 30 Oct: today is the screen, other days are a tap away on a date strip (BBC Sport
// fixtures style), and every finished job should feel like progress.
const fieldDateParts = (ymd) => {
  const d = new Date(ymd + 'T12:00:00Z');
  return { dow: d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }), num: d.getUTCDate(),
           long: d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }) };
};
function FieldDateStrip({ dates, today, selected, status, onPick }) {
  const row = useRef(null);
  useEffect(() => {
    const el = row.current && row.current.querySelector('.fd-day.is-today');
    if (el) row.current.scrollLeft = el.offsetLeft;
  }, []);
  const nudge = (dir) => { if (row.current) row.current.scrollBy({ left: dir * row.current.clientWidth * 0.7, behavior: 'smooth' }); };
  return (
    <div className="fd-strip">
      <button className="fd-arrow" aria-label="Earlier days" onClick={() => nudge(-1)}>‹</button>
      <div className="fd-days" ref={row}>
        {dates.map(d => {
          const { dow, num } = fieldDateParts(d);
          return (
            <button key={d} className={`fd-day${d === today ? ' is-today' : ''}${d === selected ? ' on' : ''}`} onClick={() => onPick(d)}>
              <span className="fd-dow">{d === today ? 'Today' : dow}</span>
              <span className="fd-num">{num}</span>
              <span className={`fd-dot ${status[d] || ''}`}/>
            </button>
          );
        })}
      </div>
      <button className="fd-arrow" aria-label="Later days" onClick={() => nudge(1)}>›</button>
    </div>
  );
}
function FieldDayHero({ date, done, total }) {
  const pct = total ? Math.round(done / total * 100) : 0;
  const [shown, setShown] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShown(pct), 80); return () => clearTimeout(t); }, [pct]);
  const all = total > 0 && done === total;
  return (
    <div className={`fd-hero${all ? ' all' : ''}`}>
      <div className="fd-hero-top">
        <div>
          <div className="fd-hero-title">Today</div>
          <div className="fd-hero-date">{fieldDateParts(date).long}</div>
        </div>
        {total > 0 && <div className="fd-score">{all ? <span className="fd-pop">✓</span> : `${done}/${total}`}</div>}
      </div>
      {total > 0 && <div className="fd-bar"><div style={{ width: `${shown}%` }}/></div>}
      {total === 0 && <div className="fd-hero-line">Nothing booked today.</div>}
    </div>
  );
}
function FieldDayItem({ kind, time, name, address, postcode, state, next, quiet, onClick }) {
  return (
    <button className={`fd-item field-item ${kind}${state.done ? ' done' : ''}${state.live ? ' live' : ''}${next ? ' next' : ''}${quiet ? ' quiet' : ''}`} onClick={onClick}>
      <div className="fd-item-tick">{state.done ? <span className="fd-pop">✓</span> : state.live ? '●' : ''}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fd-item-top">
          <span className="fd-kind">{kind === 'job' ? 'Job' : 'Survey'}</span>
          {time && <span className="fd-time">{time}</span>}
          <span className="fd-state">{next && !state.live ? 'Up next' : state.label}</span>
        </div>
        <div className="fd-name">{name}</div>
        <div className="fd-addr">{address}</div>
        {postcode && <div className="fd-postcode">{postcode}</div>}
      </div>
    </button>
  );
}
// Day order: drag a card by its handle to set the order that team sees. Booking is untouched.
// Pointer events, because HTML5 drag never fires from a finger.
function DayOrder({ onBack }) {
  const { showAlert } = useDialog();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  const [date, setDate] = useState(today);
  const [items, setItems] = useState(null);
  const [owner, setOwner] = useState(null);
  const [dragging, setDragging] = useState(null);
  const list = useRef(null);
  const who = (x) => [x.title, x.first_name, x.last_name].filter(Boolean).join(' ') || x.name || x.company_name || '—';
  const load = useCallback(() => api(`/api/field/board?date=${date}`).then(d => setItems(d.items.map(x => ({ ...x, num: x.id, order: x.field_order })).sort(fieldItemOrder)))
    .catch(e => { setItems([]); showAlert(e.message); }), [date, showAlert]);
  useEffect(() => { setItems(null); load(); }, [load]);
  const owners = items ? [...new Set(items.map(i => i.owner))] : [];
  const shownOwner = owners.includes(owner) ? owner : owners[0];
  const rows = (items || []).filter(i => i.owner === shownOwner);
  const dates = [];
  for (let d = today, n = 0; n <= 14; n++, d = nextYmd(d)) dates.push(d);
  const keyOf = (i) => `${i.kind}${i.id}`;
  const move = (e) => {
    if (!dragging || !list.current) return;
    const cards = [...list.current.querySelectorAll('.fd-order-row')];
    let to = cards.findIndex(c => { const r = c.getBoundingClientRect(); return e.clientY < r.top + r.height / 2; });
    if (to < 0) to = cards.length - 1;
    setItems(prev => {
      const mine = prev.filter(i => i.owner === shownOwner), rest = prev.filter(i => i.owner !== shownOwner);
      const from = mine.findIndex(i => keyOf(i) === dragging);
      if (from < 0 || from === to) return prev;
      const [it] = mine.splice(from, 1); mine.splice(to, 0, it);
      return [...mine, ...rest];
    });
  };
  const drop = async () => {
    if (!dragging) return;
    setDragging(null);
    try { await api('/api/field/order', { method: 'PUT', body: JSON.stringify({ items: rows.map(i => ({ kind: i.kind, id: i.id })) }) }); }
    catch (e) { showAlert(e.message); load(); }
  };
  return (
    <div className="fd-detail" style={{ maxWidth: 760, margin: '0 auto', padding: '14px 16px 32px', width: '100%', overflowY: 'auto' }}>
      {onBack && <button className="fd-back" onClick={onBack}>← Back</button>}
      <div className="fd-head">
        <div className="fd-head-name">Day order</div>
        <div className="fd-head-addr">Drag to set the order. Booked times do not change.</div>
      </div>
      <FieldDateStrip dates={dates} today={today} selected={date} status={{}} onPick={setDate}/>
      {owners.length > 1 && <div className="fd-owners">{owners.map(o => (
        <button key={o} className={`fd-tick${o === shownOwner ? ' on' : ''}`} onClick={() => setOwner(o)}>{o}</button>
      ))}</div>}
      {items && !rows.length && <div className="fd-none">Nothing booked.</div>}
      {!items && <div className="fd-none">Loading…</div>}
      <div ref={list} onPointerMove={move} onPointerUp={drop} onPointerCancel={drop}>
        {rows.map(i => (
          <div key={keyOf(i)} className={`fd-item fd-order-row ${i.kind}${dragging === keyOf(i) ? ' lifted' : ''}`}>
            <div className="fd-handle" aria-label="Drag to move" onPointerDown={e => { e.preventDefault(); try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {} setDragging(keyOf(i)); }}>⠿</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="fd-item-top">
                <span className="fd-kind">{i.kind === 'job' ? 'Job' : 'Survey'}</span>
                {i.time && <span className="fd-time">{i.time}</span>}
              </div>
              <div className="fd-name">{who(i)}</div>
              <div className="fd-addr">{i.address}</div>
              {i.postcode && <div className="fd-postcode">{i.postcode}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function MyDaySurveys({ uid }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [picked, setPicked] = useState(null);
  const load = useCallback(() => api('/api/field/day').then(setData).catch(e => {
    const c = isNetErr(e) ? readFieldCopy(uid) : null;
    setData(c ? { ...c.day, offline: c.saved_at } : { surveys: [], jobs: [] });
  }), [uid]);
  useEffect(() => { load(); }, [load]);
  // A tablet left on overnight must not still call yesterday "Today".
  useEffect(() => {
    const back = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [load]);
  if (openId) return <FieldVisit id={openId} uid={uid} onBack={() => { setOpenId(null); load(); }}/>;
  if (openJob) return <FieldJob dayId={openJob} uid={uid} onBack={() => { setOpenJob(null); load(); }}/>;
  if (!data) return <div className="offline-card" style={{ color: 'var(--ink-faint)' }}>Loading your day…</div>;
  const today = data.date || new Date().toISOString().slice(0, 10);
  const who = (x) => [x.title, x.first_name, x.last_name].filter(Boolean).join(' ') || x.name || x.company_name || '—';
  const visitState = (v) => (v.done || v.finished_at) ? { label: 'Done', done: true }
    : v.started_at ? { label: 'On site', live: true } : { label: v.status === 'proposed' ? 'Proposed' : 'Booked' };
  const jobState = (j) => (j.completed_at || ['complete', 'guarantee', 'job_report'].includes(j.status)) ? { label: 'Finished', done: true }
    : j.open_sessions ? { label: 'Working', live: true }
    : j.sessions ? { label: 'Done', done: true } : { label: 'Booked' };
  const items = {};
  const add = (d, it) => { (items[d] = items[d] || []).push(it); };
  for (const j of (data.jobs || [])) add(j.work_date, { key: `j${j.day_id}`, kind: 'job', num: j.day_id, order: j.field_order, name: who(j), address: j.address, postcode: j.postcode, state: jobState(j), open: () => setOpenJob(j.day_id) });
  for (const v of data.surveys) add(v.visit_date, { key: `v${v.id}`, kind: 'survey', num: v.id, order: v.field_order, time: v.visit_time, name: who(v), address: v.address, postcode: v.postcode, state: visitState(v), open: () => setOpenId(v.id) });
  // Back a week to look at, on to two weeks ahead. Today is what opens; see fieldLocked.
  const dates = [];
  const first = new Date(today + 'T12:00:00Z'); first.setUTCDate(first.getUTCDate() - 7);
  for (let d = first.toISOString().slice(0, 10), n = 0; n <= 21; n++, d = nextYmd(d)) dates.push(d);
  const status = {};
  for (const d of dates) {
    const l = items[d] || [];
    status[d] = !l.length ? '' : l.every(i => i.state.done) ? 'all' : d < today ? 'past' : 'some';
  }
  const selected = picked && dates.includes(picked) ? picked : today;
  const shownItems = [...(items[selected] || [])].sort(fieldItemOrder);
  const isToday = selected === today;
  const nextKey = isToday ? ((shownItems.find(i => !i.state.done) || {}).key) : null;
  return (
    <>
      {data.offline && <div className="uq-box">No signal - this is the copy saved {new Date(data.offline).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}. Today and tomorrow open in full; anything you do is kept on the tablet and sent when there is signal.</div>}
      <FieldDateStrip dates={dates} today={today} selected={selected} status={status} onPick={setPicked}/>
      {isToday
        ? <FieldDayHero date={today} done={shownItems.filter(i => i.state.done).length} total={shownItems.length}/>
        : <div className="fd-other">
            <div className="fd-other-date">{fieldDateParts(selected).long}</div>
            <button className="btn" onClick={() => setPicked(null)}>Back to today</button>
          </div>}
      {!isToday && shownItems.length === 0 && <div className="fd-none">Nothing booked.</div>}
      {shownItems.map(i => (
        <FieldDayItem key={i.key} kind={i.kind} time={i.time} name={i.name} address={i.address} postcode={i.postcode} state={i.state}
          next={i.key === nextKey} quiet={!isToday} onClick={i.open}/>
      ))}
    </>
  );
}

// ---- Field app step 5: today's work kept on the tablet, and taps kept until there is signal ----
// The copy: today's and tomorrow's surveys and jobs with everything their screens show,
// refreshed every 10 minutes and on reconnect, kept per person, cleared on sign out.
// The queue: Start / Stop / notes / reports / Visit complete / Work finished made with no
// signal are kept in order, each with the time it happened (the server uses that time, within
// 48 hours), and sent as soon as there is a connection. Photos use the upload queue.
const LAST_USER_KEY = 'rn.lastUser', FQ_KEY = 'rn.fieldq';
const fieldCopyKey = (uid) => `rn.offline.field.${uid}`;
const isNetErr = (e) => e instanceof TypeError;
function rememberUser(u) {
  try { if (u) localStorage.setItem(LAST_USER_KEY, JSON.stringify({ id: u.id, display_name: u.display_name, role: u.role }));
        else localStorage.removeItem(LAST_USER_KEY); } catch (e) {}
}
function lastUser() { try { return JSON.parse(localStorage.getItem(LAST_USER_KEY) || 'null'); } catch (e) { return null; } }
function readFieldCopy(uid) {
  try { const c = JSON.parse(localStorage.getItem(fieldCopyKey(uid)) || 'null'); return c && c.saved_at ? c : null; } catch (e) { return null; }
}
function writeFieldCopy(uid, c) { try { localStorage.setItem(fieldCopyKey(uid), JSON.stringify(c)); } catch (e) {} }
function patchFieldCopy(uid, fn) { const c = readFieldCopy(uid); if (c) { fn(c); writeFieldCopy(uid, c); } }
const nextYmd = (ymd) => { const d = new Date(ymd + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };
async function saveFieldCopy(uid) {
  try {
    const [day, teams] = await Promise.all([api('/api/field/day'), api('/api/field/teams').catch(() => null)]);
    const keep = [day.date, nextYmd(day.date)];
    const visits = {}, jobs = {};
    await Promise.all([
      ...day.surveys.filter(v => keep.includes(v.visit_date)).map(v =>
        api(`/api/field/visits/${v.id}`).then(d => { visits[v.id] = d; }).catch(() => {})),
      ...(day.jobs || []).filter(j => keep.includes(j.work_date)).map(j =>
        api(`/api/field/jobs/${j.day_id}`).then(d => { jobs[j.day_id] = d; }).catch(() => {})),
    ]);
    writeFieldCopy(uid, { saved_at: new Date().toISOString(), day, teams, visits, jobs });
  } catch (e) { /* no signal: keep the copy we have */ }
}
const fqRead = () => { try { return JSON.parse(localStorage.getItem(FQ_KEY) || '[]'); } catch (e) { return []; } };
const fqWrite = (l) => { try { localStorage.setItem(FQ_KEY, JSON.stringify(l)); } catch (e) {} try { window.dispatchEvent(new Event('rn-fq-change')); } catch (e) {} };
let fqRunning = false, fqTimer = null;
function fqAdd({ method, url, body, replace }) {
  let l = fqRead();
  if (replace) l = l.filter(x => !(x.method === method && x.url === url));   // a note: only the latest text matters
  l.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, method, url, body: { ...(body || {}), at: new Date().toISOString() } });
  fqWrite(l);
  fqProcess();
}
async function fqProcess() {
  if (fqRunning) return;
  fqRunning = true; clearTimeout(fqTimer);
  let later = false;
  try {
    for (const it of fqRead()) {
      let r;
      try { r = await fetch(it.url, { method: it.method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(it.body) }); }
      catch (e) { later = true; break; }
      if (r.status === 401 || r.status >= 500) { later = true; break; }   // signed out or server busy: try again later
      fqWrite(fqRead().filter(x => x.id !== it.id));                     // sent - or refused for good (shown in the log)
      if (!r.ok) console.warn('Field queue: refused', it.method, it.url, r.status);
    }
  } finally {
    fqRunning = false;
    if (later) fqTimer = setTimeout(fqProcess, 30000);
  }
}
function useFieldQueueCount() {
  const [n, setN] = useState(() => fqRead().length);
  useEffect(() => {
    const read = () => setN(fqRead().length);
    window.addEventListener('rn-fq-change', read);
    return () => window.removeEventListener('rn-fq-change', read);
  }, []);
  return n;
}

// Site photos for a job (attachments of type 'job_photo' on the lead), through the same
// upload queue as survey photos. Used on the tablet and, read-only, on the office job card.
function JobPhotos({ leadId, canAdd }) {
  const [rows, setRows] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const inputRef = useRef(null);
  const queued = useUploadQueue(leadId).filter(q => q.type === 'job_photo');
  const load = useCallback(() => api(`/api/attachments?lead_id=${leadId}`)
    .then(r => setRows((r || []).filter(a => a.type === 'job_photo'))).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const done = (e) => { if (String(e.detail && e.detail.lead_id) === String(leadId)) load(); };
    window.addEventListener('rn-upload-done', done);
    return () => window.removeEventListener('rn-upload-done', done);
  }, [leadId, load]);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) { try { await uqEnqueue(f, leadId, 'job_photo'); } catch (err) {} }
    e.target.value = '';
  };
  if (!canAdd && !rows.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginBottom: 6 }}>Site photos</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {rows.map((p, i) => (
          <img key={p.id} src={uploadUrl(p.filename, 'thumb')} alt={p.original_name || 'Site photo'} onClick={() => setLightboxIndex(i)}
            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line-soft)', cursor: 'pointer' }}/>
        ))}
        {canAdd && (
          <>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={add}/>
            <button className="btn btn-sm" onClick={() => inputRef.current && inputRef.current.click()}>Take photo</button>
          </>
        )}
      </div>
      {queued.length > 0 && <div className="uq-box" style={{ marginTop: 6 }}>{queued.length} saved on this device, uploading…</div>}
      {lightboxIndex !== null && <PhotoLightbox photos={rows} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)}/>}
    </div>
  );
}

// One job on one day, on the tablet (field app step 4): who and where, the crew, Start work /
// Stop (the labour clock), the work from the quote without prices, photos, the day's report,
// and Work finished which hands the job to the office as a job report.
function FieldJob({ dayId, uid, onBack }) {
  const { showAlert, showConfirm } = useDialog();
  const [d, setD] = useState(null);
  const photoGate = useFieldPhotoGate(d && d.lead_id, 'job_photo');
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const load = useCallback(() => api(`/api/field/jobs/${dayId}`).then(x => { setD(x); setNote(x.report_note || ''); })
    .catch(e => {
      const c = isNetErr(e) ? readFieldCopy(uid) : null;
      const x = c && c.jobs[dayId];
      if (x) { setD(x); setNote(x.report_note || ''); } else showAlert(isNetErr(e) ? 'No signal, and this job is not in the saved copy (only today and tomorrow are kept).' : e.message);
    }), [dayId, uid, showAlert]);
  // What a tap did, applied to the screen and the saved copy, when it has to wait for signal.
  const offlineApply = (fn) => { setD(prev => { const n = JSON.parse(JSON.stringify(prev)); fn(n); return n; });
    patchFieldCopy(uid, c => { const x = c.jobs[dayId]; if (!x) return; fn(x);
      const row = (c.day.jobs || []).find(r => String(r.day_id) === String(dayId));
      if (row) Object.assign(row, { status: x.status, day_done_at: x.day_done_at, sessions: x.sessions.length, open_sessions: x.sessions.filter(s => !s.finished_at).length }); }); };
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="offline-card" style={{ color: 'var(--ink-faint)' }}>Loading…</div>;
  const who = [d.title, d.first_name, d.last_name].filter(Boolean).join(' ') || d.name || d.company_name || '—';
  const where = [d.address, d.postcode].filter(Boolean).join(', ');
  const open = d.sessions.filter(x => !x.finished_at);
  const done = ['job_report', 'complete', 'guarantee'].includes(d.status);
  const clock = async (what, extra) => {
    setBusy(true);
    const body = { ...(await fieldFix()), ...extra };
    try { await api(`/api/field/jobs/${dayId}/${what}`, { method: 'POST', body: JSON.stringify(body) }); await load(); }
    catch (e) {
      if (!isNetErr(e)) showAlert(e.message);
      else {
        fqAdd({ method: 'POST', url: `/api/field/jobs/${dayId}/${what}`, body });
        const now = new Date().toISOString();
        offlineApply(n => {
          if (what === 'start') {
            const people = n.crew.length ? n.crew : [{ id: uid, display_name: 'You' }];
            for (const p of people) n.sessions.push({ id: `q${p.id}${now}`, user_id: p.id, display_name: p.display_name, started_at: now, finished_at: null });
            n.day_done_at = null;
          } else {
            n.sessions.forEach(x => { if (!x.finished_at) x.finished_at = now; });
            if (what === 'day-done') { n.day_done_at = now; if (extra && extra.no_photo_reason) n.no_photo_reason = extra.no_photo_reason; }
          }
        });
      }
    }
    setBusy(false);
  };
  const typeNote = (v) => {
    setNote(v); setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => api(`/api/field/jobs/${dayId}/report`, { method: 'PUT', body: JSON.stringify({ report_note: v }) })
      .then(() => setSaved(true)).catch(e => {
        if (isNetErr(e)) { fqAdd({ method: 'PUT', url: `/api/field/jobs/${dayId}/report`, body: { report_note: v }, replace: true });
          patchFieldCopy(uid, c => { if (c.jobs[dayId]) c.jobs[dayId].report_note = v; }); setSaved(true); }
      }), 800);
  };
  const finishJob = async () => {
    try { await api(`/api/field/jobs/${dayId}/finished`, { method: 'POST' }); await load(); }
    catch (e) {
      if (!isNetErr(e)) { showAlert(e.message); return; }
      fqAdd({ method: 'POST', url: `/api/field/jobs/${dayId}/finished`, body: {} });
      const now = new Date().toISOString();
      offlineApply(n => { n.status = 'job_report'; n.day_done_at = n.day_done_at || now; n.sessions.forEach(x => { if (!x.finished_at) x.finished_at = now; }); });
    }
  };
  // One button for the end of the day: it asks whether that was the whole job.
  const tapDone = async () => {
    const photo = await photoGate.check(d.no_photo_reason);
    if (!photo.ok) return;
    const whole = await showConfirm('Is the whole job finished?', { title: 'Done', confirmLabel: 'Job finished', cancelLabel: 'Not yet' });
    await clock('day-done', { ...(whole ? {} : { not_finished: true }), ...(photo.reason ? { no_photo_reason: photo.reason } : {}) });
    if (whole) await finishJob();
  };
  const t = (x) => x ? new Date(x).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const locked = fieldLocked(d.work_date);
  return (
    <div className="fd-detail job">
      <button className="fd-back" onClick={onBack}>← Back</button>
      {photoGate.dialog}
      <FieldTabs leadId={d.lead_id}>
      <div className="fd-head">
        <div className="fd-head-meta">{fmtVisitDate(d.work_date)}{d.day_count > 1 ? ` · Day ${d.day_no} of ${d.day_count}` : ''}</div>
        <div className="fd-head-name">{who}</div>
        <div className="fd-head-addr">{d.address}</div>
        {d.postcode && <div className="fd-head-postcode">{d.postcode}</div>}
        <div className="fd-head-links">
          {d.phone && <a href={'tel:' + String(d.phone).replace(/[^\d+]/g, '')}>Call</a>}
          {where && <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(where)}`} target="_blank" rel="noreferrer">Directions</a>}
        </div>
      </div>
      {done
        ? <div className="fd-done">✓ Job finished</div>
        : locked
          ? <div className="fd-view">View only</div>
          : open.length > 0
            ? <button className="fd-go" disabled={busy} onClick={tapDone}>Done</button>
            : d.sessions.length > 0
              ? <div className="fd-done">✓ Done <button className="btn" disabled={busy} onClick={() => clock('start')}>Back on site</button></div>
              : <button className="fd-go" disabled={busy} onClick={() => clock('start')}>Start work</button>}
      {d.sessions.length > 0 && (
        <div className="fd-times">
          {d.sessions.map(x => <div key={x.id}>{x.display_name}: {t(x.started_at)} - {x.finished_at ? t(x.finished_at) : 'on site'}</div>)}
        </div>
      )}
      <div className="fd-card">
        <div className="fd-card-title">The work</div>
        {d.items.filter(i => !i.optional).map((i, n) => (
          <div key={n} className="fd-work">
            <div style={{ fontWeight: 700 }}>{i.title || `Item ${n + 1}`}</div>
            {i.description && <div style={{ whiteSpace: 'pre-wrap', color: 'var(--ink-soft)' }}>{i.description}</div>}
          </div>
        ))}
        {d.items.some(i => i.optional) && <div className="fd-small">
          Optional extras on the quote: {d.items.filter(i => i.optional).map(i => i.title || 'item').join(', ')} - check with the office whether they were taken.</div>}
        {d.materials_notes && <div className="fd-small"><b>Materials: </b>{d.materials_notes}</div>}
        {d.lead_notes && <div className="fd-small" style={{ whiteSpace: 'pre-wrap' }}><b>About the job: </b>{d.lead_notes}</div>}
        <div className="fd-small"><b>Crew: </b>{d.crew.length ? d.crew.map(c => c.display_name).join(', ') : 'nobody set - Start work puts you on it'}</div>
      </div>
      <div className="fd-card">
        <div className="fd-card-title">Job feedback</div>
        <textarea rows={4} value={note} placeholder={locked ? '' : 'Add a note and photos'} readOnly={locked}
          onChange={e => typeNote(e.target.value)}/>
        <div style={{ fontSize: 13, color: 'var(--ink-faint)', minHeight: 18 }}>{saved ? 'Saved' : ''}</div>
        <JobPhotos leadId={d.lead_id} canAdd={!locked}/>
      </div>
      </FieldTabs>
    </div>
  );
}

// One survey on site: who and where, Start / Finish, then the same visit section the office
// uses - notes saved as typed, photos and voice notes through the upload queue - and
// Visit complete. Location is asked for only at Start and Finish (docs/field-app-spec.md).
function FieldVisit({ id, uid, onBack }) {
  const { showAlert, showConfirm } = useDialog();
  const [v, setV] = useState(null);
  const photoGate = useFieldPhotoGate(v && v.lead_id, 'photo');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api(`/api/field/visits/${id}`).then(d => { setV(d); setNotes(d.visit_notes || ''); })
    .catch(e => {
      const c = isNetErr(e) ? readFieldCopy(uid) : null;
      const d = c && c.visits[id];
      if (d) { setV(d); setNotes(d.visit_notes || ''); } else showAlert(isNetErr(e) ? 'No signal, and this survey is not in the saved copy (only today and tomorrow are kept).' : e.message);
    }), [id, uid, showAlert]);
  const offlineApply = (fn) => { setV(prev => { const n = { ...prev }; fn(n); return n; });
    patchFieldCopy(uid, c => { if (c.visits[id]) fn(c.visits[id]); }); };
  useEffect(() => { load(); }, [load]);
  if (!v) return <div className="offline-card" style={{ color: 'var(--ink-faint)' }}>Loading…</div>;
  const who = [v.title, v.first_name, v.last_name].filter(Boolean).join(' ') || v.name || v.company_name || '—';
  const where = [v.address, v.postcode].filter(Boolean).join(', ');
  const stamp = async (what, extra) => {
    setBusy(true);
    const body = { ...(await fieldFix()), ...extra };
    try { await api(`/api/field/visits/${id}/${what}`, { method: 'POST', body: JSON.stringify(body) }); await load(); }
    catch (e) {
      if (!isNetErr(e)) showAlert(e.message);
      else {
        fqAdd({ method: 'POST', url: `/api/field/visits/${id}/${what}`, body });
        const now = new Date().toISOString();
        offlineApply(n => { if (what === 'start') n.started_at = n.started_at || now; else { n.finished_at = now; n.started_at = n.started_at || now; if (extra && extra.no_photo_reason) n.no_photo_reason = extra.no_photo_reason; } });
      }
    }
    setBusy(false);
  };
  const finishSurvey = async () => {
    const photo = await photoGate.check(v.no_photo_reason);
    if (photo.ok) await stamp('finish', photo.reason ? { no_photo_reason: photo.reason } : {});
  };
  const complete = async () => {
    const photo = await photoGate.check(v.no_photo_reason);
    if (!photo.ok) return;
    const why = photo.reason ? { no_photo_reason: photo.reason } : {};
    if (!(await showConfirm('Mark this visit complete? It goes to the office to be quoted.', { title: 'Visit complete', confirmLabel: 'Visit complete' }))) return;
    try { await api(`/api/field/visits/${id}/complete`, { method: 'POST', body: JSON.stringify(why) }); await load(); }
    catch (e) {
      if (!isNetErr(e)) { showAlert(e.message); return; }
      fqAdd({ method: 'POST', url: `/api/field/visits/${id}/complete`, body: why });
      offlineApply(n => { n.no_photo_reason = n.no_photo_reason || why.no_photo_reason; n.lead_stage = 'Quote'; n.finished_at = n.finished_at || new Date().toISOString(); });
    }
  };
  const time = (t) => t ? new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const locked = fieldLocked(v.visit_date);
  return (
    <div className="fd-detail survey">
      <button className="fd-back" onClick={onBack}>← Back</button>
      {photoGate.dialog}
      <FieldTabs leadId={v.lead_id}>
      <div className="fd-head">
        <div className="fd-head-meta">{fmtVisitDate(v.visit_date)} · {v.visit_time}</div>
        <div className="fd-head-name">{who}</div>
        <div className="fd-head-addr">{v.address}</div>
        {v.postcode && <div className="fd-head-postcode">{v.postcode}</div>}
        <div className="fd-head-links">
          {v.phone && <a href={'tel:' + String(v.phone).replace(/[^\d+]/g, '')}>Call</a>}
          {where && <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(where)}`} target="_blank" rel="noreferrer">Directions</a>}
        </div>
      </div>
      {v.finished_at ? <div className="fd-done">✓ Survey finished</div>
        : locked ? <div className="fd-view">View only</div>
        : !v.started_at ? <button className="fd-go" disabled={busy} onClick={() => stamp('start')}>Start survey</button>
        : <button className="fd-go" disabled={busy} onClick={finishSurvey}>Finish survey</button>}
      {v.started_at && <div className="fd-times">Started {time(v.started_at)}{v.finished_at ? ` · finished ${time(v.finished_at)}` : ''}</div>}
      {(v.notes || v.lead_notes) && (
        <div className="fd-card">
          {v.notes && <div className="fd-small"><b>Booking notes: </b>{v.notes}</div>}
          {v.lead_notes && <div className="fd-small" style={{ whiteSpace: 'pre-wrap' }}><b>About the job: </b>{v.lead_notes}</div>}
        </div>
      )}
      <fieldset disabled={locked} style={{ border: 'none', minWidth: 0 }}>
        <VisitInformationSection leadId={v.lead_id} visitNotes={notes} onVisitNotesChange={setNotes}
          onReadyToQuote={complete} currentStage={v.lead_stage} quotes={[]} fieldMode/>
      </fieldset>
      </FieldTabs>
    </div>
  );
}

// Field logins' home: today's team, then My day.
function FieldHome({ user, onChangeTeam, onLogout, offline }) {
  const { showConfirm } = useDialog();
  const [teams, setTeams] = useState(null);
  const waiting = useFieldQueueCount() + useUploadQueue(null).length;
  useEffect(() => {
    api('/api/field/teams').then(setTeams).catch(() => { const c = readFieldCopy(user.id); if (c && c.teams) setTeams(c.teams); });
  }, [user.id]);
  // Keep today's copy fresh and send anything waiting: now, every 10 minutes, on reconnect.
  useEffect(() => {
    const go = () => { fqProcess(); uqProcess(); saveFieldCopy(user.id); };
    go();
    const t = setInterval(go, 10 * 60 * 1000);
    window.addEventListener('online', go);
    return () => { clearInterval(t); window.removeEventListener('online', go); };
  }, [user.id]);
  const [ordering, setOrdering] = useState(false);
  const today = teams && teams.my_team_today ? teams.teams.find(t => t.id === teams.my_team_today) : null;
  const people = today ? today.members.filter(m => today.today.includes(m.id)).map(m => m.display_name) : [];
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--canvas)' }}>
      <div className="topbar" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ marginRight: 'auto', fontSize: 15, fontWeight: 600 }}>{user.display_name}</span>
        {offline ? <button className="btn btn-sm" onClick={() => window.location.reload()}>Try again</button>
          : <button className="btn btn-sm" onClick={async () => {
              const n = fqRead().length;
              if (n && !(await showConfirm(`${n} thing${n === 1 ? '' : 's'} you did without signal ${n === 1 ? 'has' : 'have'} not been sent yet. If you sign out, ${n === 1 ? 'it goes' : 'they go'} under the next person to sign in on this tablet. Sign out anyway?`,
                { title: 'Not sent yet', confirmLabel: 'Sign out anyway' }))) return;
              onLogout();
            }}>Sign out</button>}
      </div>
      {waiting > 0 && <div className="uq-pill">{waiting} waiting to send</div>}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '14px 16px 32px' }}>
        <div className="fd-team">
          <span>{today ? `${today.name}: ${people.join(', ')}` : 'Not on a team today'}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            {!offline && user.role === 'pm' && <button className="btn btn-sm" onClick={() => setOrdering(true)}>Day order</button>}
            {!offline && <button className="btn btn-sm" onClick={onChangeTeam}>{today ? 'Change team' : 'Join a team'}</button>}
          </span>
        </div>
        {ordering ? <DayOrder onBack={() => setOrdering(false)}/> : <MyDaySurveys uid={user.id}/>}
      </div>
    </div>
  );
}

// A job is booked once it has a day in the diary.
const mapJobBooked = (j) => (j.days || []).length > 0 || !!j.scheduled_date;

function MapOverviewView({ leads }) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [visits, setVisits] = useState([]);
  const [filterEmployee, setFilterEmployee] = usePref('map.person', 'all', oneOf('all', 'Andy', 'LD'));
  const [filterBrand, setFilterBrand] = usePref('map.brand', 'all', isBrandPref);
  const [filterDay, setFilterDay] = useState('all');
  // Andrew, 18 Oct: a Jobs toggle like the diary's, showing where the work is and what is
  // booked (has a day in the diary) against what is not.
  const [mode, setMode] = usePref('map.mode', 'leads', oneOf('leads', 'jobs'));
  const [booking, setBooking] = usePref('map.booking', 'all', oneOf('all', 'booked', 'unbooked'));
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    const weekStart = fmtDate(getWeekStart(new Date()));
    api(`/api/visits?week_start=${weekStart}`).then(setVisits).catch(()=>{});
  }, []);
  useEffect(() => {
    if (mode !== 'jobs') return;
    api('/api/jobs').then(r => setJobs(r || [])).catch(() => setJobs([]));
  }, [mode]);

  useEffect(() => {
    if (window.L) { setLoaded(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  }, []);

  const dayOptions = (() => {
    const ws = getWeekStart(new Date());
    return Array.from({length:7},(_,i)=>{ const d=addDays(ws,i); return { value: fmtDate(d), label: fmtVisitDate(fmtDate(d)) }; });
  })();

  const filteredLeads = React.useMemo(() => leads.filter(l => {
    if (['Completed','Dead'].includes(l.stage)) return false;
    if (!l.address) return false;
    if (filterEmployee !== 'all' && l.assigned_to !== filterEmployee) return false;
    if (filterBrand !== 'all' && l.brand !== filterBrand) return false;
    if (filterDay !== 'all') {
      const hasVisitOnDay = visits.some(v => v.lead_id === l.id && v.visit_date.split('T')[0] === filterDay);
      if (!hasVisitOnDay) return false;
    }
    return true;
  }), [leads, visits, filterEmployee, filterBrand, filterDay]);

  const filteredJobs = React.useMemo(() => jobs.filter(j => {
    if (j.completed_at || j.status === 'complete' || j.status === 'guarantee') return false;
    if (!j.address) return false;
    if (filterEmployee !== 'all' && (j.assigned_to || 'Andy') !== filterEmployee) return false;
    if (filterBrand !== 'all' && j.brand !== filterBrand) return false;
    if (booking === 'booked' && !mapJobBooked(j)) return false;
    if (booking === 'unbooked' && mapJobBooked(j)) return false;
    if (filterDay !== 'all' && !(j.days || []).some(d => String(d.work_date).split('T')[0] === filterDay)) return false;
    return true;
  }), [jobs, filterEmployee, filterBrand, filterDay, booking]);

  // One list of pins whichever mode is on: where it is, how it looks, what the popup says.
  const pins = React.useMemo(() => mode === 'jobs'
    ? filteredJobs.map(j => {
        const c = brandColor(j.brand);
        const booked = mapJobBooked(j);
        const first = (j.days || []).map(d => String(d.work_date).split('T')[0]).sort()[0] || j.scheduled_date;
        return {
          key: `j${j.id}`, address: j.address,
          // Booked: a solid pin. Not booked: a white pin with a dashed ring, so the gaps read at a glance.
          html: booked
            ? `<div style="background:${c.bg};border:2px solid ${c.text};border-radius:50%;width:16px;height:16px;"></div>`
            : `<div style="background:#fff;border:3px dashed ${c.text};border-radius:50%;width:16px;height:16px;"></div>`,
          popup: `<strong>${j.brand}</strong> ${tileDisplayName(j.title,j.first_name,j.last_name)||j.name}<br/>${j.address}<br/>`
            + `<span style="color:#78716c;font-size:11px">${JOB_STATUS_LABELS[j.status]||j.status} · `
            + `${booked ? `booked${first ? ' from ' + fmtVisitDate(first) : ''}` : 'not booked yet'}</span>`,
        };
      })
    : filteredLeads.map(lead => {
        const c = brandColor(lead.brand);
        return {
          key: `l${lead.id}`, address: lead.address,
          html: `<div style="background:${c.bg};border:2px solid ${c.text};border-radius:50%;width:16px;height:16px;"></div>`,
          popup: `<strong>${lead.brand}</strong> ${tileDisplayName(lead.title,lead.first_name,lead.last_name)||lead.name}<br/>${lead.address}<br/><span style="color:#78716c;font-size:11px">${lead.stage} · ${lead.assigned_to||'Andy'}</span>`,
        };
      }), [mode, filteredJobs, filteredLeads]);

  useEffect(() => {
    if (!loaded || !mapRef.current) return;
    if (!leafletMapRef.current) {
      leafletMapRef.current = window.L.map(mapRef.current).setView([52.0, -0.1], 9);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 18
      }).addTo(leafletMapRef.current);
    }
    const map = leafletMapRef.current;
    map.eachLayer(l => { if (l instanceof window.L.Marker) map.removeLayer(l); });

    let bounds = [];
    let cancelled = false;

    (async () => {
      for (const p of pins) {
        if (cancelled) return;
        try {
          const geo = await api(`/api/geocode?address=${encodeURIComponent(p.address)}`);
          if (cancelled) return;
          if (geo.lat && geo.lon) {
            const icon = window.L.divIcon({ className: '', html: p.html, iconSize: [16,16] });
            const marker = window.L.marker([geo.lat, geo.lon], { icon }).addTo(map);
            marker.bindPopup(p.popup);
            bounds.push([geo.lat, geo.lon]);
          }
        } catch (e) {}
      }
      if (bounds.length > 0) map.fitBounds(bounds, { padding: [30,30] });
    })();

    return () => { cancelled = true; };
  }, [loaded, pins]);

  return (
    <div style={{flex:1,minHeight:0,overflow:'hidden',padding:'12px 20px',display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
        <div className="tabs">
          <button className={`tab${mode==='leads'?' active':''}`} onClick={()=>setMode('leads')} type="button">Leads</button>
          <button className={`tab${mode==='jobs'?' active':''}`} onClick={()=>setMode('jobs')} type="button">Jobs</button>
        </div>
        {mode==='jobs'&&(
          <select className="select-compact" value={booking} onChange={e=>setBooking(e.target.value)}>
            <option value="all">Booked and not booked</option>
            <option value="booked">Booked only</option>
            <option value="unbooked">Not booked only</option>
          </select>
        )}
        <select className="select-compact" value={filterEmployee} onChange={e=>setFilterEmployee(e.target.value)}>
          <option value="all">All employees</option>
          <option value="Andy">Andy</option>
          <option value="LD">Lawrance</option>
        </select>
        <select className="select-compact" value={filterBrand} onChange={e=>setFilterBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k=><option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <select className="select-compact" value={filterDay} onChange={e=>setFilterDay(e.target.value)}>
          <option value="all">{mode==='jobs'?'Any day':'Any day (this week)'}</option>
          {dayOptions.map(d=><option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        {mode==='jobs'&&(
          <span style={{fontSize:11.5,color:'var(--ink-soft)',display:'flex',alignItems:'center',gap:10}}>
            <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:11,height:11,borderRadius:'50%',background:'#a1a1aa',border:'2px solid #52525b'}}/>Booked</span>
            <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:11,height:11,borderRadius:'50%',background:'#fff',border:'2px dashed #52525b'}}/>Not booked</span>
          </span>
        )}
        <span style={{fontSize:11.5,color:'var(--ink-faint)',marginLeft:'auto'}}>{pins.length} shown</span>
      </div>
      <div ref={mapRef} style={{flex:1,borderRadius:'var(--radius)',border:'1px solid var(--line-soft)',minHeight:240,
        position:'relative',zIndex:0,isolation:'isolate'}}/>
    </div>
  );
}

// The visit, shown where the price is decided. Read-only on purpose: corrections
// belong on the lead where the transcript lives, so there is one copy of what was
// said rather than a divergent one inside every quote.
// A newline is a line item. Nothing else is a boundary - not a full stop, not a
// comma - so text with no Enters in it stays as one item however long it runs.
function splitIntoSegments(text) {
  const t = String(text || '').trim();
  if (!t) return [];
  const lines = t.split(/\r?\n+/).map(s => s.trim()).filter(Boolean);
  return lines.length ? lines : [t];
}

function VisitBlock({ label, text, keyName, copied, onCopy, onAddSegments }) {
  const [sel, setSel] = useState('');
  const bodyRef = useRef(null);
  // One source of truth: whatever the browser says is selected right now. Nothing
  // in here ever clears it, so a phrase can be added, added again, or narrowed and
  // added again, without going back to the note to re-find it.
  useEffect(() => {
    const read = () => {
      const s = window.getSelection();
      const inBlock = s && !s.isCollapsed && bodyRef.current
        && bodyRef.current.contains(s.anchorNode) && bodyRef.current.contains(s.focusNode);
      setSel(inBlock ? s.toString().trim() : '');
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, []);
  // Keeps the highlight alive through the click. Without it the browser collapses
  // the selection on mousedown, exactly as it would for a click anywhere else.
  const keepSelection = (e) => e.preventDefault();
  // A finger is different: the tap itself can collapse the highlight before the click
  // arrives (todo item 14). What was highlighted is caught at pointerdown, the first
  // thing a tap fires, and the click uses that.
  const grabbed = useRef('');
  const grab = () => { grabbed.current = sel; };
  const take = () => { const g = grabbed.current; grabbed.current = ''; return g || sel; };
  const active = splitIntoSegments(sel || text);
  return (
    <div style={{ borderTop: '1px solid var(--line-soft)', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onMouseDown={keepSelection} onPointerDown={grab} onClick={() => onCopy(take() || text, keyName)}>
            {copied === keyName ? 'Copied' : 'Copy'}
          </button>
          <button className="btn btn-sm" onMouseDown={keepSelection} onPointerDown={grab} onClick={() => onAddSegments(splitIntoSegments(take() || text))}>
            {active.length > 1 ? `+ Add as ${active.length} items` : '+ Add as item'}
          </button>
        </span>
      </div>
      <div ref={bodyRef} draggable={false} onDragStart={e => e.preventDefault()}
        onMouseDown={e => {
          if (e.shiftKey) return;
          const s = window.getSelection();
          if (s && !s.isCollapsed) { try { s.removeAllRanges(); } catch (err) {} }
        }}
        style={{ fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.55, whiteSpace: 'pre-wrap', WebkitUserDrag: 'none' }}>{text}</div>
      <div style={{ fontSize: 10.5, color: 'var(--ink-faint)', marginTop: 3, minHeight: 13 }}>
        {sel ? 'Using the highlighted text. It stays highlighted, so you can add it again or narrow it down.'
             : 'Highlight part of this to use just that bit.'}
      </div>
    </div>
  );
}

function VisitContextPanel({ visit, open, onToggle, onAddSegments, onOpenPhoto }) {
  const [copied, setCopied] = useState(null);
  if (!visit) return null;

  const spoken = (visit.voice || []).filter(v => v.transcript && v.transcript.trim());
  const pending = (visit.voice || []).filter(v => v.transcript_status === 'queued' || !v.transcript_status);
  const hasAnything = (visit.notes && visit.notes.trim()) || spoken.length || pending.length || (visit.photos || []).length;
  if (!hasAnything) return null;

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); }
    catch (e) { /* clipboard refused - the text is on screen to select by hand */ }
  };

  const blockProps = { copied, onCopy: copy, onAddSegments };

  return (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="sec" style={{ border: 'none', marginBottom: 0, paddingBottom: 0 }}>From the visit</div>
        <button className="btn btn-sm" onClick={onToggle}>{open ? 'Hide' : 'Show'}</button>
      </div>

      {open && (
        <div style={{ marginTop: 8 }}>
          {visit.notes && visit.notes.trim() &&
            <VisitBlock {...blockProps} label="Notes on site" text={visit.notes.trim()} keyName="notes"/>}

          {spoken.map(v => (
            <VisitBlock key={v.id} {...blockProps}
              label={`Voice note - ${new Date(v.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}${v.transcript_seconds ? ` - ${Math.round(v.transcript_seconds)}s` : ''}`}
              text={v.transcript.trim()} keyName={`v${v.id}`}/>
          ))}

          {pending.length > 0 && (
            <div style={{ borderTop: '1px solid var(--line-soft)', padding: '8px 0', fontSize: 12, color: 'var(--ink-faint)' }}>
              {pending.length} recording{pending.length === 1 ? '' : 's'} still being transcribed - reopen this quote in a moment.
            </div>
          )}

          {(visit.photos || []).length > 0 && (
            <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 8, marginTop: 2 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                Photos from site
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {visit.photos.map((p, i) => (
                  <img key={p.id} src={uploadUrl(p.filename, 'thumb')} alt={p.original_name || 'Site photo'}
                    onClick={() => onOpenPhoto(i)}
                    style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 5, border: '1px solid var(--line-soft)', cursor: 'pointer' }}/>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const DOC_WIDTH = 700;

function DocPreview({ title, htmlUrl, pdfUrl, onClose }) {
  const [html, setHtml] = useState('');
  const [err, setErr] = useState('');
  const [scale, setScale] = useState(1);
  const [docHeight, setDocHeight] = useState(900);
  const boxRef = useRef(null);

  useEffect(() => {
    let live = true;
    setHtml(''); setErr('');
    api(htmlUrl).then(d => { if (live) setHtml((d && d.html) || ''); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [htmlUrl]);

  useEffect(() => {
    const fit = () => {
      const el = boxRef.current;
      if (el && el.clientWidth) setScale(Math.min(1, el.clientWidth / DOC_WIDTH));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [html]);

  // srcDoc is same-origin, so the frame can be asked how tall it ended up.
  const onFrameLoad = (e) => {
    try {
      const d = e.target.contentDocument;
      if (d && d.documentElement) setDocHeight(Math.max(400, d.documentElement.scrollHeight));
    } catch (_) { /* leave the default height rather than fail the preview */ }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 70 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <div className="mhead">
          <div className="mtitle">{title}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <a className="btn btn-green btn-sm" href={pdfUrl} target="_blank" rel="noreferrer">Download PDF</a>
            <button className="xcbtn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="mbody" style={{ padding: 10 }}>
          {err && <div className="err">{err}</div>}
          {!err && !html && <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Loading…</div>}
          {!err && html && (
            <div ref={boxRef} style={{ width: '100%', overflow: 'hidden', height: Math.round(docHeight * scale) }}>
              <iframe title={title} srcDoc={html} onLoad={onFrameLoad} scrolling="no"
                style={{ width: DOC_WIDTH, height: docHeight, border: 'none', display: 'block',
                  background: '#fff', transform: `scale(${scale})`, transformOrigin: 'top left' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuoteBuilderModal({ leadId, quoteId, onClose, onSaved, onSentAndClose }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [quote, setQuote] = useState(null);
  const [items, setItems] = useState([]);
  const [defaultItems, setDefaultItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemPickerFilter, setItemPickerFilter] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ intro_text: '', terms_text: '', valid_days: 28, reference: '', status: 'draft' });
  const itemSaveRef = useRef({});
  const itemTimerRef = useRef({});

  const [visit, setVisit] = useState(null);
  const [showVisit, setShowVisit] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  useEffect(() => { api(`/api/leads/${leadId}/visit-record`).then(setVisit).catch(() => {}); }, [leadId]);

  const load = useCallback(() => {
    api(`/api/quotes/${quoteId}`).then(q => {
      setQuote(q);
      setItems(q.items || []);
      setForm({ intro_text: q.intro_text || '', terms_text: q.terms_text || '', valid_days: q.valid_days || 28, reference: q.reference || '', status: q.status || 'draft' });
    });
  }, [quoteId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/api/default-line-items').then(setDefaultItems).catch(() => {}); }, []);
  const visiblePickerItems = React.useMemo(() => {
    const q = itemPickerFilter.trim().toLowerCase();
    if (!q) return defaultItems;
    // Every word has to appear somewhere, so "felt batten" narrows rather than widens.
    const terms = q.split(/\s+/);
    return defaultItems.filter(d => {
      const hay = ((d.title || '') + ' ' + (d.description || '')).toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [defaultItems, itemPickerFilter]);

  const preVat = (it) => {
    const qty = parseFloat(it.quantity || 1);
    const base = (parseFloat(it.materials_cost || 0) + parseFloat(it.labour_cost || 0)) * qty;
    const markupType = it.markup_type || 'percent';
    const markupValue = parseFloat(it.markup_value || 0);
    const markupAmount = markupType === 'flat' ? markupValue : base * (markupValue / 100);
    return base + markupAmount;
  };
  const itemVat = (it) => preVat(it) * (parseFloat(it.vat_percent==null?20:it.vat_percent) / 100);
  const itemTotal = (it) => preVat(it) + itemVat(it);

  const requiredItems = items.filter(it => !it.is_optional);
  const optionalItems = items.filter(it => it.is_optional);
  const subTotal = requiredItems.reduce((s, it) => s + preVat(it), 0);
  const vatTotal = requiredItems.reduce((s, it) => s + itemVat(it), 0);
  const grandTotal = subTotal + vatTotal;

  const ROUND_TO = 10;   // a line, including VAT
  const TOTAL_TO = 30;   // the grand total, including VAT - see the note on why 30

  // What it would take to land THIS line's inc-VAT total on `to`, and where the
  // difference goes. With no target it rounds to the next £10, which is what the line's
  // own button does. Returns null when the line is already there, which is also what
  // hides the button.
  const lineRoundUp = (it, to) => {
    // Work from the figure the customer sees. The raw total carries float residue, and
    // a line showing £150.00 must not be treated as £150.000001 and pushed to £160.
    const inc = Math.round(itemTotal(it) * 100) / 100;
    if (!(inc > 0)) return null;
    // No epsilon here. The old total-level button added one so it always did something;
    // on a line that is wrong twice over - it bumped a line already sitting on a £10 up
    // to the next one, and pressing the button again kept adding for ever.
    const target = to == null ? Math.ceil(inc / ROUND_TO) * ROUND_TO : to;
    const shortfall = target - inc;
    if (shortfall < 0.005) return null;
    const rate = parseFloat(it.vat_percent == null ? 20 : it.vat_percent);
    // Backwards from the inc-VAT target to what the line has to gain pre-VAT.
    const addPreVat = shortfall / (1 + rate / 100);
    const qty = parseFloat(it.quantity || 1);
    const costBase = (parseFloat(it.materials_cost || 0) + parseFloat(it.labour_cost || 0)) * qty;
    const type = it.markup_type || 'percent';

    let patch;
    if (type === 'percent' && costBase > 0) {
      // Six places, because the column now holds six. Two would leave this line up to
      // about 20p away from its target on a cost base this size, which is the whole bug.
      const pct = parseFloat(it.markup_value || 0) + (addPreVat / costBase) * 100;
      patch = { markup_value: Math.round(pct * 1000000) / 1000000 };
    } else {
      // No cost base, so a percentage has nothing to be a percentage of. Six places here
      // too: a flat markup held to the penny is multiplied by 1.2 on its way to the line
      // total, so two places left the line up to half a penny off its target - enough to
      // show a £500.01 where the button had promised £500.00.
      const flatNow = type === 'flat' ? parseFloat(it.markup_value || 0) : 0;
      patch = { markup_type: 'flat', markup_value: Math.round((flatNow + addPreVat) * 1000000) / 1000000 };
    }
    return { target, shortfall, addPreVat, patch };
  };

  // One line. No confirmation: the line total sitting next to the button is the report,
  // and a dialog per click would be noise on a six-line quote.
  const roundLine = (it) => {
    const r = lineRoundUp(it);
    if (r) updateItem(it.id, r.patch);
  };

  // Every line on its next £10, then whichever required line is largest carries the
  // extra needed to put the grand total on a £30. The largest line is chosen because the
  // same few pounds distort it least.
  const roundAllPlan = () => {
    const plan = new Map();
    let runningTotal = 0;
    for (const it of items) {
      const inc = Math.round(itemTotal(it) * 100) / 100;
      const target = inc > 0 ? Math.ceil(inc / ROUND_TO) * ROUND_TO : inc;
      plan.set(it.id, target);
      if (!it.is_optional) runningTotal += target;
    }
    // Optional lines are rounded for consistency but never move the total, which counts
    // required lines only - the same rule the totals panel already follows.
    let topUp = 0;
    if (runningTotal > 0) {
      const wanted = Math.ceil(Math.round(runningTotal * 100) / 100 / TOTAL_TO) * TOTAL_TO;
      topUp = wanted - runningTotal;
    }
    if (topUp > 0.005) {
      const biggest = requiredItems.reduce((a, b) => (plan.get(b.id) > plan.get(a.id) ? b : a), requiredItems[0]);
      if (biggest) plan.set(biggest.id, plan.get(biggest.id) + topUp);
    }
    const moves = [];
    for (const it of items) {
      const target = plan.get(it.id);
      const r = lineRoundUp(it, target);
      if (r) moves.push({ it, target, patch: r.patch, from: Math.round(itemTotal(it) * 100) / 100 });
    }
    const finalTotal = requiredItems.reduce((s, it) => s + plan.get(it.id), 0);
    return { moves, finalTotal };
  };

  const roundableCount = items.filter(it => lineRoundUp(it)).length;
  // What the total becomes once Round all lines has run. Shown on the button so the
  // figure is known before it is pressed rather than after.
  const roundedGrandTotal = roundAllPlan().finalTotal;

  const roundAllLines = async () => {
    const { moves, finalTotal } = roundAllPlan();
    const changed = [];
    for (const m of moves) {
      updateItem(m.it.id, m.patch);
      changed.push(`${m.it.title || 'Untitled line'}: £${m.from.toFixed(2)} → £${m.target.toFixed(2)}`);
    }
    if (!changed.length) return;
    changed.push(`\nTotal: £${finalTotal.toFixed(2)}`);
    await showAlert(
      `${changed.length} line${changed.length === 1 ? '' : 's'} rounded up. The difference went into each line's own markup.\n\n${changed.join('\n')}`,
      { title: 'Rounded up' });
  };

  // Given in person or read down the phone. Records it as sent without emailing, and
  // without a snapshot - nothing went out from here, and quote_snapshots is meant to
  // hold what a customer was actually shown.
  const [markingSent, setMarkingSent] = useState(false);
  const markQuoteSent = async () => {
    setMarkingSent(true);
    try {
      await flushItemSaves();
      const updated = await api(`/api/quotes/${quoteId}/mark-sent`, { method: 'POST' });
      setQuote(q => ({ ...q, ...updated }));
      if (onSaved) onSaved();
    } catch (e) { await showAlert(e.message); }
    setMarkingSent(false);
  };

  const addItem = async (title, description) => {
    try {
      const it = await api(`/api/quotes/${quoteId}/items`, { method: 'POST', body: JSON.stringify({ title: title||'', description: description||'', quantity: 1, is_optional: false, materials_cost: 0, labour_cost: 0, markup_type: 'percent', markup_value: 0, vat_percent: 20 }) });
      setItems(prev => [...prev, it]);
      setShowItemPicker(false);
    } catch (e) { showAlert(e.message); }
  };

  const addItems = async (texts) => {
    const made = [];
    try {
      for (const t of texts) {
        const body = { title: suggestLineTitle(t), description: t, quantity: 1, is_optional: false, materials_cost: 0, labour_cost: 0, markup_type: 'percent', markup_value: 0, vat_percent: 20 };
        made.push(await api(`/api/quotes/${quoteId}/items`, { method: 'POST', body: JSON.stringify(body) }));
      }
    } catch (e) { showAlert(e.message); }
    if (made.length) setItems(prev => [...prev, ...made]);
  };

  const updateItem = (id, patch) => {
    const current = items.find(it => it.id === id);
    if (!current) return;
    const merged = { ...current, ...patch };
    setItems(prev => prev.map(it => (it.id === id ? merged : it)));
    itemSaveRef.current[id] = merged;
    if (itemTimerRef.current[id]) clearTimeout(itemTimerRef.current[id]);
    itemTimerRef.current[id] = setTimeout(() => {
      const payload = itemSaveRef.current[id];
      if (!payload) return;
      api(`/api/quote-items/${id}`, { method: 'PUT', body: JSON.stringify(payload) }).catch(() => {});
    }, 500);
  };

  const flushItemSaves = async () => {
    const ids = Object.keys(itemTimerRef.current);
    for (const id of ids) {
      if (itemTimerRef.current[id]) { clearTimeout(itemTimerRef.current[id]); delete itemTimerRef.current[id]; }
      const payload = itemSaveRef.current[id];
      if (payload) {
        try { await api(`/api/quote-items/${id}`, { method: 'PUT', body: JSON.stringify(payload) }); } catch (e) {}
        delete itemSaveRef.current[id];
      }
    }
  };

  const removeItem = async (id) => {
    if (!(await showConfirm('Remove this line item?', {danger:true, confirmLabel:'Remove'}))) return;
    if (itemTimerRef.current[id]) { clearTimeout(itemTimerRef.current[id]); delete itemTimerRef.current[id]; }
    delete itemSaveRef.current[id];
    setItems(prev => prev.filter(it => it.id !== id));
    try { await api(`/api/quote-items/${id}`, { method: 'DELETE' }); } catch (e) {}
  };

  // Returns false when the save was rejected (e.g. a duplicate reference) so that
  // callers abort instead of carrying on against the stale values still in the DB.
  const saveQuoteMeta = async () => {
    await flushItemSaves();
    try { await api(`/api/quotes/${quoteId}`, { method: 'PUT', body: JSON.stringify(form) }); onSaved(); return true; } catch (e) { showAlert(e.message); return false; }
  };

  const saveAndClose = async () => {
    if (!(await saveQuoteMeta())) return;
    onClose();
  };

  const openPreview = async () => {
    setLoadingPreview(true); setPdfError('');
    const saved = await saveQuoteMeta();
    setLoadingPreview(false);
    if (saved) setShowPreview(true);
  };

  const sendQuote = async () => {
    if (items.length === 0) { await showAlert('Add at least one line item before sending.'); return; }
    if (!(await saveQuoteMeta())) return;
    if (!(await showConfirm(`Send this quote to ${quote.email || 'the customer'}?`, {title:'Send quote'}))) return;
    setSending(true);
    try {
      const result = await api(`/api/quotes/${quoteId}/send`, { method: 'POST', body: JSON.stringify({}) });
      await showAlert(`Quote sent — reference ${result.reference}`, {title:'Sent'});
      onSaved();
      if (onSentAndClose) onSentAndClose(); else onClose();
    } catch (e) { showAlert(e.message); }
    setSending(false);
  };

  const declineQuote = async () => {
    // Asked rather than confirmed: the reason is the part worth having, and a
    // yes/no box throws it away.
    const reason = await showPrompt('Why was this one lost? If they said why - price, went elsewhere, timing - put it here.', {
      title: 'Mark declined', placeholder: 'e.g. too expensive, went with someone else',
    });
    if (reason === null) return;
    try {
      await api(`/api/quotes/${quoteId}/decline`, { method: 'POST', body: JSON.stringify({ reason }) });
      await showAlert('Quote marked as declined.', {title:'Done'});
      load(); onSaved();
    } catch (e) { showAlert(e.message); }
  };

  const revertToDraft = async () => {
    if (!(await showConfirm('This will keep the accepted quote as a historical record and create a new draft (v2) for you to edit. The lead stage will not change automatically — use this only if you genuinely need to revise an agreed quote.', {title:'Revert to draft', danger:true, confirmLabel:'Create new draft'}))) return;
    try {
      const newQuote = await api(`/api/quotes/${quoteId}/revert-to-draft`, { method: 'POST' });
      await showAlert(`New draft created (v${newQuote.version}). The original quote is kept as a record.`, {title:'Done'});
      onSaved();
      onClose();
    } catch (e) { showAlert(e.message); }
  };

  if (!quote) return (
    <div className="modal-overlay"><div className="modal"><div className="mbody" style={{ alignItems: 'center' }}><span style={{ color: 'var(--ink-soft)' }}>Loading…</span></div></div></div>
  );

  if (showPreview) {
    return (
      <DocPreview
        title={`Quote preview${form.reference ? ` · ${form.reference}` : ''}`}
        htmlUrl={`/api/quotes/${quoteId}/preview-html`}
        pdfUrl={`/api/quotes/${quoteId}/pdf`}
        onClose={() => setShowPreview(false)} />
    );
  }

  // Andrew, 18 Oct: "hard to see where the fields for line 1 ends and line 2 starts" -
  // each line is its own white card with a number on it.
  const renderLineItemEditor = (it, i) => (
    <div key={it.id} className="qe-line">
      <div className="qe-line-num">{it.is_optional ? 'Optional item' : 'Line'} {i + 1}</div>
      <div className="row2">
        <div className="fl">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Title
            {(it.description || '').trim() && (
              <button type="button" onClick={() => updateItem(it.id, { title: suggestLineTitle(it.description) })}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0, textTransform: 'none', letterSpacing: 0 }}>
                Suggest from description
              </button>
            )}
          </label>
          <input type="text" value={it.title || ''} onChange={e => updateItem(it.id, { title: e.target.value })} placeholder="e.g. Scaffolding Safety & Access" />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', paddingBottom: 7 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!it.is_optional} onChange={e => updateItem(it.id, { is_optional: e.target.checked })} />
            Optional item
          </label>
        </div>
      </div>
      <textarea rows={4} value={it.description} onChange={e => updateItem(it.id, { description: e.target.value })} placeholder="Description" style={{ minHeight: 90 }} />
      <div className="qe-costs">
        <div className="fl"><label>Qty</label><input type="number" value={it.quantity===1?1:(parseFloat(it.quantity)===0?'':it.quantity)} onChange={e => updateItem(it.id, { quantity: e.target.value })} /></div>
        <div className="fl"><label>Materials £</label><input type="number" value={parseFloat(it.materials_cost)===0 ? '' : it.materials_cost} placeholder="0" onChange={e => updateItem(it.id, { materials_cost: e.target.value })} /></div>
        <div className="fl"><label>Labour £</label><input type="number" value={parseFloat(it.labour_cost)===0 ? '' : it.labour_cost} placeholder="0" onChange={e => updateItem(it.id, { labour_cost: e.target.value })} /></div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', textAlign: 'right' }}>Materials + Labour: £{((parseFloat(it.materials_cost||0)+parseFloat(it.labour_cost||0))*parseFloat(it.quantity||1)).toFixed(2)}</div>
      <div className="qe-markup">
        <div className="fl"><label>Markup</label>
          <select value={it.markup_type || 'percent'} onChange={e => updateItem(it.id, { markup_type: e.target.value })}>
            <option value="percent">%</option>
            <option value="flat">£</option>
          </select>
        </div>
        <div className="fl"><label>Markup value</label><input type="number" value={parseFloat(it.markup_value)===0 ? '' : it.markup_value} placeholder="0" onChange={e => updateItem(it.id, { markup_value: e.target.value })} /></div>
        <div className="fl"><label>VAT %</label><input type="number" value={it.vat_percent==null?'':it.vat_percent} placeholder="20" onChange={e => updateItem(it.id, { vat_percent: e.target.value })} /></div>
        <button className="btn btn-sm btn-red" onClick={() => removeItem(it.id)}>Remove</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginTop: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 20, fontSize: 12.5 }}>
          <span style={{ color: 'var(--ink-soft)' }}>VAT: £{itemVat(it).toFixed(2)}</span>
          <span style={{ fontWeight: 700, color: 'var(--accent)' }}>Line total: £{itemTotal(it).toFixed(2)}</span>
        </div>
        {lineRoundUp(it) && (
          <button className="btn btn-sm" onClick={() => roundLine(it)}
            title="Adds the difference to this line's markup">
            Round up to £{lineRoundUp(it).target.toFixed(2)}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={async () => { await flushItemSaves(); onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="mhead">
          <div>
            <div className="mtitle">{quote.reference ? `Quote #${quote.reference}` : 'New quote (draft)'}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>
              {tileDisplayName(quote.title, quote.first_name, quote.last_name) || quote.name} ·
              <BrandBadge brand={quote.brand} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn-sm" onClick={saveQuoteMeta}>Save</button>
            <button className="btn btn-sm" onClick={openPreview} disabled={loadingPreview}>{loadingPreview ? 'Loading…' : 'Preview & PDF'}</button>
            {quote.status === 'sent' && <button className="btn btn-sm btn-red" onClick={declineQuote}>Mark declined</button>}
            {quote.status === 'accepted' && <button className="btn btn-sm btn-red" onClick={revertToDraft}>Revert to draft</button>}
            {quote.status !== 'accepted' && <button className="btn btn-green btn-sm" onClick={sendQuote} disabled={sending}>{sending ? 'Sending…' : (quote.sent_at ? 'Re-send' : 'Send to customer')}</button>}
            <button className="xcbtn" onClick={async () => { await flushItemSaves(); onClose(); }}>×</button>
          </div>
        </div>
        <div className="mbody">
          {pdfError && <div style={{ fontSize: 12, color: '#dc2626' }}>{pdfError}</div>}
          <div className="row2">
            <div className="fl"><label>Reference</label><input type="text" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} /></div>
            <div className="fl"><label>Valid for (days)</label><input type="number" value={form.valid_days} onChange={e => setForm(f => ({ ...f, valid_days: e.target.value }))} /></div>
          </div>
          <div className="fl"><label>Intro text</label><textarea rows={3} value={form.intro_text} onChange={e => setForm(f => ({ ...f, intro_text: e.target.value }))} /></div>

          <VisitContextPanel visit={visit} open={showVisit} onAddSegments={addItems} onToggle={() => setShowVisit(v => !v)}
            onOpenPhoto={(i) => setLightboxIndex(i)}/>
          {lightboxIndex !== null && visit && (
            <PhotoLightbox photos={visit.photos} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)}/>
          )}

          <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Line items</div>
            {requiredItems.length === 0 && optionalItems.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-faint)', marginBottom: 10 }}>No line items yet.</div>}
            {requiredItems.map(renderLineItemEditor)}
            {optionalItems.length > 0 && (
              <div style={{ marginTop: requiredItems.length ? 16 : 0, fontSize: 11.5, fontWeight: 700, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Optional items</div>
            )}
            {optionalItems.map(renderLineItemEditor)}

            <div style={{ marginTop: 10, display: 'flex', gap: 8, position: 'relative' }}>
              <button className="btn btn-sm" onClick={() => { setItemPickerFilter(''); setShowItemPicker(v => !v); }}>+ Add from default items</button>
              <button className="btn btn-sm btn-green" onClick={() => addItem('', '')}>+ Add line item</button>
              {showItemPicker && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 10, background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 6, maxHeight: 220, overflowY: 'auto', minWidth: 280, boxShadow: 'var(--shadow)' }}>
                  {defaultItems.length === 0 && <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-faint)' }}>No default items yet — add some under Settings.</div>}
                  {defaultItems.length > 6 && (
                    <div style={{ position: 'sticky', top: 0, background: 'var(--surface)', padding: 6, borderBottom: '1px solid var(--line-soft)' }}>
                      <input type="text" autoFocus value={itemPickerFilter} onChange={e => setItemPickerFilter(e.target.value)}
                        placeholder="Filter items" style={{ width: '100%', fontSize: 12, padding: '4px 6px', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {visiblePickerItems.length === 0 && defaultItems.length > 0 && (
                    <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-faint)' }}>Nothing matches "{itemPickerFilter}".</div>
                  )}
                  {visiblePickerItems.map(d => (
                    <div key={d.id} onClick={() => addItem(d.title, d.description)} style={{ padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid var(--line-soft)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--canvas)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {d.title && <div style={{ fontWeight: 600 }}>{d.title}</div>}
                      <div style={{ color: 'var(--ink-soft)', fontSize: 11.5, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                        {(d.description || '').split('\n').slice(0, 3).join('\n')}
                      </div>
                      {(d.description || '').split('\n').length > 3 && (
                        <div style={{ color: 'var(--ink-faint)', fontSize: 11 }}>
                          +{(d.description || '').split('\n').length - 3} more lines
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {requiredItems.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                <div style={{ fontSize: 12.5, display: 'flex', gap: 30 }}><span>Sub Total</span><span style={{ minWidth: 80, textAlign: 'right' }}>£{subTotal.toFixed(2)}</span></div>
                <div style={{ fontSize: 12.5, display: 'flex', gap: 30 }}><span>VAT</span><span style={{ minWidth: 80, textAlign: 'right' }}>£{vatTotal.toFixed(2)}</span></div>
                <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', gap: 30, borderTop: '1px solid var(--ink)', paddingTop: 6 }}><span>Total</span><span style={{ minWidth: 80, textAlign: 'right' }}>£{grandTotal.toFixed(2)}</span></div>
                {grandTotal > 0 && roundableCount > 0 && (
                  <button className="btn btn-sm" style={{ marginTop: 6 }} onClick={roundAllLines}
                    title="Rounds every line to the next £5, each into its own markup">
                    Round all lines → £{roundedGrandTotal.toFixed(2)}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="fl"><label>Terms</label><textarea rows={6} value={form.terms_text} onChange={e => setForm(f => ({ ...f, terms_text: e.target.value }))} />
            {!quote.sent_at && (
              <button type="button" onClick={markQuoteSent} disabled={markingSent}
                style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>
                {markingSent ? 'Marking…' : 'Mark quote as sent'}
              </button>
            )}
          </div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={saveAndClose}>Save quote</button>
          <button className="btn btn-sm" onClick={openPreview} disabled={loadingPreview}>{loadingPreview ? 'Loading…' : 'Preview & PDF'}</button>
          {quote.status === 'sent' && <button className="btn btn-sm btn-red" onClick={declineQuote}>Mark declined</button>}
          {quote.status === 'accepted' && <button className="btn btn-sm btn-red" onClick={revertToDraft}>Revert to draft</button>}
          {quote.status !== 'accepted' && <button className="btn btn-green btn-sm" onClick={sendQuote} disabled={sending}>{sending ? 'Sending…' : (quote.sent_at ? 'Re-send' : 'Send to customer')}</button>}
        </div>
      </div>
    </div>
  );
}

function BookVisitForLeadModal({ lead, onClose, onSaved }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [date,setDate]=useState('');
  const [time,setTime]=useState('09:00');
  const [visitStatus,setVisitStatus]=useState('proposed');
  const [notes,setNotes]=useState('');
  const [saving,setSaving]=useState(false);

  const save=async()=>{
    if(!date){await showAlert('Pick a date');return;}
    setSaving(true);
    try{
      await api('/api/visits',{method:'POST',body:JSON.stringify({lead_id:lead.id,visit_date:date,visit_time:time,notes,status:visitStatus})});
      onSaved();
    }catch(e){showAlert(e.message);}
    setSaving(false);
  };

  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="mhead">
          <div><div className="mtitle">Book a visit</div><div style={{fontSize:12,color:'var(--ink-soft)',marginTop:2}}>{tileDisplayName(lead.title,lead.first_name,lead.last_name)||lead.name} — {lead.address}</div></div>
          <button className="xcbtn" onClick={onClose}>×</button>
        </div>
        <div className="mbody">
          <div className="row2">
            <div className="fl"><label>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} autoFocus/></div>
            <div className="fl"><label>Time</label><TimeSelect value={time} onChange={setTime}/></div>
          </div>
          <div className="fl">
            <label>Visit status</label>
            <div className="tabs">
              <button className={`tab${visitStatus==='proposed'?' active':''}`} onClick={()=>setVisitStatus('proposed')} type="button">Proposed</button>
              <button className={`tab${visitStatus==='confirmed'?' active':''}`} onClick={()=>setVisitStatus('confirmed')} type="button">Confirmed</button>
            </div>
          </div>
          <div className="fl"><label>Notes</label><textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Access notes etc."/></div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving?'Saving…':'Book visit'}</button>
        </div>
      </div>
    </div>
  );
}

function ToDoView({ onSelectLead, refreshKey, onOpenReview }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [items, setItems] = useState([]);
  const [filterPerson, setFilterPerson] = usePref('todo.person', 'all', oneOf('all', 'Andy', 'LD'));

  const load = useCallback(() => {
    const qs = filterPerson !== 'all' ? `?assigned_to=${encodeURIComponent(filterPerson)}` : '';
    api(`/api/todo-items${qs}`).then(setItems).catch(() => {});
  }, [filterPerson]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const dismiss = async (id, e) => {
    e.stopPropagation();
    try { await api(`/api/todo-items/${id}/dismiss`, { method: 'POST' }); setItems(prev => prev.filter(i => i.id !== id)); }
    catch (err) { showAlert(err.message); }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="filter-bar">
        <span className="filter-label">Assigned to:</span>
        <select className="select-compact" value={filterPerson} onChange={e => setFilterPerson(e.target.value)}>
          <option value="all">Everyone</option>
          <option value="Andy">Andy</option>
          <option value="LD">Lawrance</option>
        </select>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
        {items.length === 0 && <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, textAlign: 'center', padding: 40 }}>Nothing waiting on anyone right now.</div>}
        {items.map(it => (
          <div key={it.id} className={it.type === 'admin' ? 'td-admin' : ''} onClick={() => { if (it.type === 'review' && onOpenReview) onOpenReview(it.gbp_review_id); else if (it.lead_id) onSelectLead(it.lead_id); }} style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)',
            borderRadius: 'var(--radius)', padding: '12px 14px', cursor: (it.lead_id || it.type === 'review') ? 'pointer' : 'default', marginBottom: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#d4d4d8'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--line-soft)'}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                  color: it.type === 'admin' ? '#1e1b4b' : it.type === 'reply' ? 'var(--blue)' : (it.type === 'payment' ? '#16a34a' : (it.type === 'review' ? '#7c3aed' : 'var(--amber)')),
                  background: it.type === 'admin' ? '#fbbf24' : it.type === 'reply' ? 'var(--blue-soft)' : (it.type === 'payment' ? '#e9f7ef' : (it.type === 'review' ? '#f3effe' : 'var(--amber-soft)')),
                  borderRadius: 4, padding: '2px 6px' }}>
                  {it.type === 'admin' ? 'Admin' : it.type === 'reply' ? 'New reply' : (it.type === 'payment' ? 'Paid'
                    : (it.type === 'supplier' ? 'Supplier query' : (it.type === 'review' ? 'Google review' : 'Flagged')))}
                </span>
                {it.lead_id ? (
                  <>
                    <BrandBadge brand={it.brand} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{tileDisplayName(it.title, it.first_name, it.last_name) || it.name}</span>
                  </>
                ) : (
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{it.type === 'admin' ? 'System check' : it.type === 'review' ? 'Reply needed' : 'Consumables'}</span>
                )}
              </div>
              <div className="td-note" style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{it.note}</div>
              <div className="td-meta" style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
                {it.assigned_to ? `Assigned to ${it.assigned_to === 'LD' ? 'Lawrance' : it.assigned_to}` : 'Unassigned'} · {new Date(it.created_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
              </div>
            </div>
            <button className="btn btn-sm" onClick={e => dismiss(it.id, e)}>Dismiss</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Google reviews (docs/review-manager-spec.md). Reads the reviews on our own Business
// Profiles, replies from here, and says which job a review is about - the review-rate
// measurement. Admin and Lawrance only, like supplier review: a reply speaks for the brand.
function starText(n) { const k = Math.max(0, Math.min(5, n || 0)); return '★★★★★'.slice(0, k) + '☆☆☆☆☆'.slice(0, 5 - k); }

function ReviewCard({ r, focused, onChanged, onSelectLead }) {
  const { showAlert, showConfirm } = useDialog();
  const [reply, setReply] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sugg, setSugg] = useState(null);

  const post = async () => {
    setSaving(true);
    try {
      await api(`/api/reviews/${r.id}/reply`, { method: 'POST', body: JSON.stringify({ comment: reply }) });
      setEditing(false); setReply(''); onChanged();
    } catch (e) { showAlert(e.message); }
    setSaving(false);
  };
  const remove = async () => {
    if (!(await showConfirm('Delete this reply from Google? The review stays; only our reply is removed.', { title: 'Delete reply', confirmLabel: 'Delete' }))) return;
    try { await api(`/api/reviews/${r.id}/reply`, { method: 'DELETE' }); onChanged(); } catch (e) { showAlert(e.message); }
  };
  const findJobs = async () => {
    try { setSugg(await api(`/api/reviews/${r.id}/suggestions`)); } catch (e) { showAlert(e.message); }
  };
  // "No reply needed" takes it out of Needs a reply and clears its to-do; Undo puts it back.
  const setNoReply = async (undo) => {
    try { await api(`/api/reviews/${r.id}/dismiss`, { method: 'POST', body: JSON.stringify({ undo }) }); onChanged(); }
    catch (e) { showAlert(e.message); }
  };
  const match = async (jobId) => {
    try { await api(`/api/reviews/${r.id}/match`, { method: 'POST', body: JSON.stringify({ job_id: jobId }) }); setSugg(null); onChanged(); }
    catch (e) { showAlert(e.message); }
  };

  const who = r.reviewer_anonymous ? 'A Google user' : (r.reviewer_name || 'A Google user');
  const low = (r.stars || 0) <= 3;
  const date = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const dismissed = !r.reply_comment && !!r.reply_dismissed_at;
  const showBox = (!r.reply_comment && !dismissed) || editing;

  return (
    <div id={`review-${r.id}`} style={{ background: 'var(--surface)', border: `1px solid ${focused ? 'var(--blue)' : 'var(--line-soft)'}`,
      borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <BrandBadge brand={r.brand} />
        <span style={{ color: low ? 'var(--red)' : '#d97706', letterSpacing: 1, fontSize: 14 }} aria-label={`${r.stars} stars`}>{starText(r.stars)}</span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{who}</span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>{date(r.create_time)}</span>
      </div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: r.comment ? 'var(--ink)' : 'var(--ink-faint)' }}>
        {r.comment || 'No comment - a star rating only.'}
      </div>

      {r.reply_comment && !editing && (
        <div style={{ marginTop: 10, background: 'var(--bg-soft, #f7f7f8)', borderRadius: 6, padding: '8px 10px' }}>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 3 }}>
            Our reply · {date(r.reply_time)}{r.replied_by ? ` · ${r.replied_by}` : ''}
          </div>
          <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{r.reply_comment}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button className="btn btn-sm" onClick={() => { setReply(r.reply_comment); setEditing(true); }}>Edit</button>
            <button className="btn btn-sm" onClick={remove}>Delete</button>
          </div>
        </div>
      )}
      {showBox && (
        <div style={{ marginTop: 10 }}>
          {low && !r.reply_comment && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginBottom: 4 }}>
              The next customer reads this reply. Calm and specific, and offer to put it right.
            </div>
          )}
          <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)} maxLength={4000}
            placeholder={`Reply as ${r.location_title || 'the brand'}`} style={{ width: '100%' }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button className="btn btn-green btn-sm" onClick={post} disabled={saving || !reply.trim()}>{saving ? 'Posting…' : (editing ? 'Update reply' : 'Post reply')}</button>
            {editing && <button className="btn btn-sm" onClick={() => { setEditing(false); setReply(''); }}>Cancel</button>}
            {!editing && <button className="btn btn-sm" onClick={() => setNoReply(false)}>No reply needed</button>}
          </div>
        </div>
      )}
      {dismissed && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-faint)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>No reply needed{r.reply_dismissed_by ? ` - ${r.reply_dismissed_by}` : ''}, {date(r.reply_dismissed_at)}</span>
          <button className="btn btn-sm" onClick={() => setNoReply(true)}>Undo</button>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {r.lead_id ? (
          <>
            <span>About:</span>
            <button onClick={() => onSelectLead(r.lead_id)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--blue)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>
              {tileDisplayName(null, r.lead_first_name, r.lead_last_name) || r.lead_name}
            </button>
            {r.days_from_request != null && <span>· {r.days_from_request} days after the review request</span>}
            <button className="btn btn-sm" onClick={findJobs}>Change</button>
          </>
        ) : (
          <>
            <span>Which job is this about?</span>
            <button className="btn btn-sm" onClick={findJobs}>Find the job</button>
          </>
        )}
      </div>
      {sugg && (
        <div style={{ marginTop: 6, borderTop: '1px solid var(--line-soft)', paddingTop: 6 }}>
          {sugg.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>No finished {r.brand} jobs in the year before this review.</div>}
          {sugg.map(j => (
            <div key={j.job_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{tileDisplayName(null, j.first_name, j.last_name) || j.name}</b>
                {j.address ? ` · ${j.address}` : ''} · finished {date(j.completed_at)}
              </span>
              <button className="btn btn-sm" onClick={() => match(j.job_id)}>This one</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {r.lead_id && <button className="btn btn-sm" onClick={() => match(null)}>Not matched</button>}
            <button className="btn btn-sm" onClick={() => setSugg(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsView({ onSelectLead, focusId }) {
  const { showAlert, showConfirm } = useDialog();
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [brand, setBrand] = useState('');
  const [show, setShow] = usePref('reviews.show', 'needs_reply', oneOf('needs_reply', 'all', 'unmatched'));
  const [syncing, setSyncing] = useState(false);
  const [showProfiles, setShowProfiles] = useState(false);

  const loadStatus = useCallback(() => { api('/api/reviews/status').then(setStatus).catch(e => setStatus({ error: e.message })); }, []);
  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (brand) q.set('brand', brand);
    if (show !== 'all') q.set('show', show);
    api(`/api/reviews?${q}`).then(setRows).catch(() => {});
  }, [brand, show]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status && status.connected) load(); }, [status, load]);
  // Opened from a to-do: make sure that review is in the list, then bring it into view.
  useEffect(() => { if (focusId) { setBrand(''); setShow('all'); } }, [focusId, setShow]);
  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`review-${focusId}`);
    if (el) el.scrollIntoView({ block: 'center' });
  }, [focusId, rows]);

  const changed = () => { load(); loadStatus(); };
  const syncNow = async () => {
    setSyncing(true);
    try { await api('/api/reviews/sync', { method: 'POST' }); changed(); } catch (e) { showAlert(e.message); }
    setSyncing(false);
  };
  const setLocationBrand = async (name, b) => {
    try { await api('/api/reviews/locations', { method: 'PUT', body: JSON.stringify({ name, brand: b || null }) }); loadStatus(); load(); }
    catch (e) { showAlert(e.message); }
  };
  const disconnect = async () => {
    if (!(await showConfirm('Disconnect the CRM from Google? Reviews stop updating until someone connects again.', { title: 'Disconnect Google', confirmLabel: 'Disconnect' }))) return;
    try { await api('/api/reviews/disconnect', { method: 'POST' }); loadStatus(); } catch (e) { showAlert(e.message); }
  };

  const card = (title, body, action) => (
    <div style={{ maxWidth: 620, margin: '40px auto', background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{body}</div>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );

  if (!status) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-faint)' }}>Loading…</div>;
  if (status.error) return card('Reviews could not load', status.error);
  if (!status.configured) return card('Google reviews are not switched on yet',
    <>Google is reviewing the Business Profile API application (case {status.application_case}, sent 20 October 2026; Google says 7-10 business days).
      Once it is approved, the Google sign-in details go on the server and a Connect button appears here.
      Review requests to customers carry on as normal in the meantime.</>);
  if (!status.connected) return card('Connect Google',
    <>Sign in with the Google account that manages the Business Profiles. The CRM can then read every brand's reviews,
      reply to them, and bring new ones into To-do.{status.last_error ? <div style={{ color: 'var(--red)', marginTop: 8 }}>{status.last_error}</div> : null}</>,
    <a className="btn btn-green btn-sm" href="/api/reviews/connect">Connect Google</a>);

  const mapped = status.locations.filter(l => l.brand);
  const brandOptions = [...new Set(mapped.map(l => l.brand))];
  const synced = status.last_sync_at ? new Date(status.last_sync_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="filter-bar">
        <span className="filter-label">Brand:</span>
        <select className="select-compact" value={brand} onChange={e => setBrand(e.target.value)}>
          <option value="">All brands</option>
          {brandOptions.map(b => <option key={b} value={b}>{BRANDS[b] || b}</option>)}
        </select>
        <span className="filter-label">Show:</span>
        <select className="select-compact" value={show} onChange={e => setShow(e.target.value)}>
          <option value="needs_reply">Needs a reply</option>
          <option value="unmatched">Not matched to a job</option>
          <option value="all">All reviews</option>
        </select>
        <button className="btn btn-sm" onClick={() => setShowProfiles(v => !v)}>Profiles ({mapped.length})</button>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>Updated {synced}</span>
        <button className="btn btn-sm" onClick={syncNow} disabled={syncing}>{syncing ? 'Checking…' : 'Check now'}</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
        {status.last_error && <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 10 }}>{status.last_error}</div>}
        {showProfiles && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginBottom: 8 }}>
              Your Roofing Network profiles, matched to brands by name. A profile's first read files its existing reviews without to-dos.
            </div>
            {status.locations.map(l => (
              <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '5px 0', borderTop: '1px solid var(--line-soft)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{l.title}</b>{l.address ? <span style={{ color: 'var(--ink-faint)' }}> · {l.address}</span> : null}
                  {l.last_error ? <div style={{ color: 'var(--red)', fontSize: 11.5 }}>{l.last_error}</div> : null}
                </div>
                {l.brand && <span style={{ fontSize: 11.5, color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>
                  {l.reviews} reviews{l.average ? ` · ${l.average}★` : ''}{l.unreplied ? ` · ${l.unreplied} unanswered` : ''}
                </span>}
                <select className="select-compact" value={l.brand || ''} onChange={e => setLocationBrand(l.name, e.target.value)}>
                  {BRAND_KEYS.filter(k => k !== 'RN').map(k => <option key={k} value={k}>{BRANDS[k]}</option>)}
                </select>
              </div>
            ))}
            <div style={{ marginTop: 8 }}><button className="btn btn-sm" onClick={disconnect}>Disconnect Google</button></div>
          </div>
        )}
        {rows.length === 0 && (
          <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, textAlign: 'center', padding: 40 }}>
            {show === 'needs_reply' ? 'Every review has a reply.' : 'No reviews to show.'}
          </div>
        )}
        {rows.map(r => <ReviewCard key={r.id} r={r} focused={String(r.id) === String(focusId)} onChanged={changed} onSelectLead={onSelectLead} />)}
      </div>
    </div>
  );
}

function money(n) { return `£${(parseFloat(n) || 0).toFixed(2)}`; }

function InvoiceStatusPill({ invoice }) {
  const map = {
    unsent: { label: 'Not sent', bg: '#f4f4f5', color: '#57534e', border: '#e7e5e4' },
    sent: { label: 'Sent', bg: '#eef6ff', color: '#185fa5', border: '#cfe3fb' },
    'part-paid': { label: 'Part paid', bg: '#fff6e5', color: '#b45309', border: '#f6e0b5' },
    paid: { label: 'Paid', bg: '#e9f7ef', color: '#16a34a', border: '#c3ecd4' },
    cancelled: { label: 'Cancelled', bg: '#fdecec', color: '#dc2626', border: '#f8d4d4' },
  };
  const s = map[invoice.status] || map.unsent;
  return <span style={{ fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 10, padding: '2px 9px' }}>{s.label}</span>;
}

// Money owed, and the paperwork behind it. Two answers to two different
// questions - "what should I be chasing" and "what have we raised" - which is
// why they stay separate views rather than merging into one list.
function InvoicingHub({ leads, onSelectLead, onOpenInvoice, refreshKey, onChanged }) {
  const [pane, setPane] = usePref('invoicing.pane', 'due', oneOf('due', 'all'));
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 20px 0' }}>
        <div className="tabs">
          <button className={`tab${pane === 'due' ? ' active' : ''}`} onClick={() => setPane('due')}>Money due</button>
          <button className={`tab${pane === 'all' ? ' active' : ''}`} onClick={() => setPane('all')}>All invoices</button>
        </div>
      </div>
      {pane === 'due'
        ? <InvoicingView onSelectLead={onSelectLead} onOpenInvoice={onOpenInvoice} refreshKey={refreshKey} onChanged={onChanged}/>
        : <InvoicesView leads={leads} onOpenInvoice={onOpenInvoice} refreshKey={refreshKey}/>}
    </div>
  );
}

function InvoicesView({ leads, onOpenInvoice, refreshKey }) {
  const [invoices, setInvoices] = useState([]);
  const [filterBrand, setFilterBrand] = usePref('invoices.brand', 'all', isBrandPref);
  const [settledOpen, setSettledOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api('/api/invoices').then(rows => { setInvoices(rows); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const filtered = invoices.filter(i => filterBrand === 'all' || i.brand === filterBrand);
  const unsent = filtered.filter(i => i.status === 'unsent');
  const outstanding = filtered.filter(i => i.status === 'sent' || i.status === 'part-paid');
  const settled = filtered.filter(i => i.status === 'paid' || i.status === 'cancelled');

  const custName = (i) => tileDisplayName(i.title, i.first_name, i.last_name) || i.name || '—';

  const Row = ({ i }) => (
    <div className="invoice-row" onClick={() => onOpenInvoice(i.id)}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>{i.reference}</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{custName(i)}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{i.type === 'deposit' ? `${i.deposit_percent || 50}% deposit` : (i.type === 'balance' ? 'Balance' : 'Invoice')} · {i.brand}</div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
        {i.status === 'sent' && i.sent_at && <>Sent {new Date(i.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}<br /><span style={{ color: i.opened_at ? '#16a34a' : 'var(--ink-faint)' }}>{i.opened_at ? 'Opened' : 'Not opened'}</span></>}
        {i.status === 'paid' && i.paid_at && <>Paid {new Date(i.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
        {i.status === 'part-paid' && <><span style={{ color: '#b45309', fontWeight: 600 }}>{money(i.paid_amount)} paid</span><br /><span style={{ color: 'var(--ink-faint)' }}>{money((parseFloat(i.total_value) || 0) - (parseFloat(i.paid_amount) || 0))} still due</span></>}
        {i.status === 'unsent' && <span style={{ color: 'var(--ink-faint)' }}>Ready to send</span>}
        {i.status === 'cancelled' && <span style={{ color: 'var(--ink-faint)' }}>—</span>}
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>{money(i.total_value)}</span>
      <div style={{ textAlign: 'right' }}><InvoiceStatusPill invoice={i} /></div>
    </div>
  );

  const Section = ({ title, list, tint }) => (
    <div style={{ marginBottom: 26 }}>
      {title && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: tint || 'var(--ink-soft)' }}>{title}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{list.length}</span>
      </div>}
      {list.length === 0 ? <div style={{ fontSize: 12, color: 'var(--ink-faint)', padding: '4px 2px' }}>None.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{list.map(i => <Row key={i.id} i={i} />)}</div>}
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="filter-bar">
        <span className="filter-label">Brand:</span>
        <select className="select-compact" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k => <option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
        {loading ? <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, textAlign: 'center', padding: 40 }}>Loading…</div> : (
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <Section title="Accepted — not yet sent" list={unsent} tint="#57534e" />
            <Section title="Outstanding" list={outstanding} tint="#185fa5" />
            <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 16 }}>
              <div onClick={() => setSettledOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <span style={{ fontSize: 12, color: 'var(--ink-faint)', transform: settledOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s', display: 'inline-block' }}>▶</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-soft)' }}>Settled</span>
                <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{settled.length}</span>
              </div>
              {settledOpen && <div style={{ marginTop: 12 }}><Section title="" list={settled} /></div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceModal({ invoiceId, onClose, onChanged }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editPercent, setEditPercent] = useState(false);
  const [percentValue, setPercentValue] = useState('');
  const [payUrl, setPayUrl] = useState('');
  const [payMode, setPayMode] = useState('');

  const load = useCallback(() => {
    api(`/api/invoices/${invoiceId}`).then(inv => { setInvoice(inv); setPercentValue(String(inv.deposit_percent || 50)); }).catch(() => {});
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  // Fetched on open rather than behind a button - it is wanted every time.
  useEffect(() => {
    let cancelled = false;
    api(`/api/invoices/${invoiceId}/pay-link`, { method: 'POST' })
      .then(r => { if (!cancelled) { setPayUrl(r.url); setPayMode(r.mode); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [invoiceId]);

  if (!invoice) return (
    <div className="modal-overlay"><div className="modal"><div className="mbody" style={{ alignItems: 'center' }}><span style={{ color: 'var(--ink-soft)' }}>Loading…</span></div></div></div>
  );

  const total = (invoice.items || []).reduce((s, it) => {
    const qty = parseFloat(it.quantity || 1);
    const base = (parseFloat(it.materials_cost || 0) + parseFloat(it.labour_cost || 0)) * qty;
    const markup = (it.markup_type === 'flat') ? parseFloat(it.markup_value || 0) : base * (parseFloat(it.markup_value || 0) / 100);
    const pre = base + markup;
    return s + pre + pre * (parseFloat(it.vat_percent == null ? 20 : it.vat_percent) / 100);
  }, 0);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) await showAlert(successMsg, { title: 'Done' }); load(); if (onChanged) onChanged(); }
    catch (e) { showAlert(e.message); }
    setBusy(false); setShowActions(false);
  };

  const send = () => act(async () => { await api(`/api/invoices/${invoiceId}/send`, { method: 'POST', body: JSON.stringify({}) }); }, 'Invoice sent to the customer.');
  const markPaid = async () => {
    const msg = outstanding < total
      ? `Record the remaining ${money(outstanding)} as paid?`
      : 'Mark this invoice as fully paid?';
    if (!(await showConfirm(msg, { title: 'Mark as paid', confirmLabel: 'Mark paid' }))) return;
    // No amount sent: the server settles whatever is still outstanding, so this
    // cannot double-count on an invoice that already has a deposit against it.
    act(() => api(`/api/invoices/${invoiceId}/mark-paid`, { method: 'POST', body: JSON.stringify({ method: 'manual' }) }), 'Marked as paid.');
  };
  const recordPayment = async () => {
    const raw = await showPrompt(`How much has been received? ${money(outstanding)} is outstanding.`, {
      title: 'Record a payment', defaultValue: outstanding.toFixed(2), placeholder: 'Amount in £',
    });
    if (raw == null) return;
    const value = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    if (!(value > 0)) { showAlert('Enter an amount greater than zero.'); return; }
    act(() => api(`/api/invoices/${invoiceId}/payments`, {
      method: 'POST', body: JSON.stringify({ amount: value, method: 'bank transfer' }),
    }), `${money(value)} recorded.`);
  };
  const cancel = async () => {
    if (!(await showConfirm('Mark this invoice as cancelled?', { title: 'Cancel invoice', danger: true, confirmLabel: 'Mark cancelled' }))) return;
    act(() => api(`/api/invoices/${invoiceId}/cancel`, { method: 'POST' }), 'Invoice cancelled.');
  };
  const del = async () => {
    if (!(await showConfirm('Delete this invoice permanently? This cannot be undone.', { title: 'Delete invoice', danger: true, confirmLabel: 'Delete' }))) return;
    setBusy(true);
    try { await api(`/api/invoices/${invoiceId}`, { method: 'DELETE' }); if (onChanged) onChanged(); onClose(); }
    catch (e) { showAlert(e.message); setBusy(false); }
  };
  const savePercent = () => {
    const p = parseFloat(percentValue);
    if (isNaN(p) || p <= 0 || p > 100) { showAlert('Enter a percentage between 1 and 100.'); return; }
    act(() => api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ deposit_percent: p }) }));
    setEditPercent(false);
  };

  if (preview) return <DocPreview {...preview} onClose={() => setPreview(null)} />;

  const custName = tileDisplayName(invoice.title, invoice.first_name, invoice.last_name) || invoice.name || '';
  const canEdit = invoice.status === 'unsent' || invoice.status === 'sent';
  const paidSoFar = (invoice.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const outstanding = Math.max(0, total - paidSoFar);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="mhead">
          <div className="mtitle">{invoice.type === 'deposit' ? 'Deposit Invoice' : (invoice.type === 'balance' ? 'Balance Invoice' : 'Invoice')} {invoice.reference}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InvoiceStatusPill invoice={invoice} />
            <button className="btn btn-sm" onClick={() => setPreview({
              title: `Invoice preview${invoice.reference ? ` · ${invoice.reference}` : ''}`,
              htmlUrl: `/api/invoices/${invoiceId}/preview-html`,
              pdfUrl: `/api/invoices/${invoiceId}/pdf`,
            })}>Preview &amp; PDF</button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-sm" onClick={() => setShowActions(s => !s)} disabled={busy}>Actions ▾</button>
              {showActions && (
                <div style={{ position: 'absolute', right: 0, top: '110%', background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 8, boxShadow: 'var(--shadow-md)', zIndex: 90, minWidth: 190, padding: 6 }}>
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && <button className="menu-item" onClick={recordPayment}>Record a payment…</button>}
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && <button className="menu-item" onClick={markPaid}>Mark as fully paid</button>}
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && <button className="menu-item" onClick={cancel}>Mark as cancelled</button>}
                  <button className="menu-item" onClick={del} style={{ color: 'var(--red)' }}>Delete</button>
                </div>
              )}
            </div>
            <button className="xcbtn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="mbody">
          <div style={{ display: 'flex', gap: 40, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 2 }}>Customer</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{custName}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 2 }}>Total</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{money(total)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 2 }}>Status</div>
              <div style={{ fontSize: 13 }}>
                {invoice.status === 'sent' && invoice.sent_at && <>Sent {new Date(invoice.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · <span style={{ color: invoice.opened_at ? '#16a34a' : 'var(--ink-faint)', fontWeight: 600 }}>{invoice.opened_at ? 'Opened' : 'Not opened yet'}</span></>}
                {invoice.status === 'unsent' && 'Not sent yet'}
                {invoice.status === 'part-paid' && <span style={{ color: '#b45309', fontWeight: 600 }}>{money(invoice.paid_amount)} of {money(total)} paid</span>}
                {invoice.status === 'paid' && <span style={{ color: '#16a34a', fontWeight: 600 }}>Paid</span>}
                {invoice.status === 'cancelled' && <span style={{ color: 'var(--red)' }}>Cancelled</span>}
              </div>
            </div>
          </div>

          {(invoice.payments || []).length > 0 && (
            <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 6 }}>Payments and refunds</div>
              {(invoice.payments || []).map(p => {
                const refund = p.source === 'stripe-refund';
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
                    <span style={{ fontWeight: 700, minWidth: 76, color: refund ? 'var(--red)' : undefined }}>{money(p.amount)}</span>
                    <span style={{ color: 'var(--ink-soft)' }}>{new Date(p.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span style={{ color: refund ? 'var(--red)' : 'var(--ink-faint)', fontWeight: refund ? 600 : 400 }}>
                      {refund ? 'Refunded to the customer' : `${p.method}${p.source === 'quickbooks' ? ' · from QuickBooks' : (p.source === 'stripe' ? ' · card' : '')}`}
                    </span>
                    {!refund && p.receipt_sent_at && (
                      <span style={{ fontSize: 10.5, color: '#16a34a', fontWeight: 600 }}>receipt sent</span>
                    )}
                    {!refund && (
                      <button className="btn btn-sm" style={{ padding: '0 7px', fontSize: 11 }}
                        onClick={() => setPreview({
                          title: `Receipt R${p.id}`,
                          htmlUrl: `/api/payments/${p.id}/receipt-preview-html`,
                          pdfUrl: `/api/payments/${p.id}/receipt.pdf`,
                        })}>Receipt</button>
                    )}
                    {!refund && p.source !== 'stripe' && p.source !== 'quickbooks' && !p.confirmed_at && (
                      <span title="Recorded here but not yet matched against this invoice in QuickBooks. It clears itself once the QuickBooks pull sees it."
                        style={{ fontSize: 10.5, color: '#b45309', fontWeight: 600, background: '#fef3c7',
                          border: '1px solid #fcd34d', borderRadius: 4, padding: '0 5px' }}>not matched in QuickBooks</span>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 10, fontSize: 12.5, paddingTop: 6, marginTop: 4, borderTop: '1px solid var(--line-soft)' }}>
                <span style={{ fontWeight: 700, minWidth: 76 }}>{money(outstanding)}</span>
                <span style={{ color: outstanding > 0 ? '#b45309' : '#16a34a', fontWeight: 600 }}>{outstanding > 0 ? 'still due' : 'settled in full'}</span>
              </div>
            </div>
          )}

          {invoice.type === 'deposit' && canEdit && (
            <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Deposit percentage:</span>
              {editPercent ? (
                <>
                  <input type="number" value={percentValue} onChange={e => setPercentValue(e.target.value)} style={{ width: 70 }} />
                  <button className="btn btn-sm btn-green" onClick={savePercent}>Save</button>
                  <button className="btn btn-sm" onClick={() => { setEditPercent(false); setPercentValue(String(invoice.deposit_percent || 50)); }}>Cancel</button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{invoice.deposit_percent || 50}%</span>
                  <button className="btn btn-sm" onClick={() => setEditPercent(true)}>Edit</button>
                </>
              )}
            </div>
          )}

          {canEdit && payUrl && (
            <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 6 }}>
                Stripe payment link{payMode === 'test' ? ' · TEST MODE, no real money' : ''}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input readOnly value={payUrl} onClick={e => e.target.select()} style={{ flex: 1, fontSize: 12 }} />
                <button className="btn btn-sm" onClick={() => window.open(payUrl, '_blank')}>Open</button>
              </div>
            </div>
          )}

          {canEdit && (
            <label style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={invoice.show_bank_details !== false}
                onChange={e => act(() => api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ show_bank_details: e.target.checked }) }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Show bank details on this invoice</span>
            </label>
          )}

          {canEdit && (
            <label style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!invoice.show_stripe_link}
                onChange={e => act(() => api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ show_stripe_link: e.target.checked }) }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Include a Stripe "pay online" button</span>
            </label>
          )}

          <div style={{ border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 18 }}>
            {(invoice.items || []).map((it, idx) => (
              <div key={it.id} style={{ padding: '10px 14px', borderTop: idx ? '1px solid var(--line-soft)' : 'none' }}>
                {it.title && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)' }}>{it.title}</div>}
                <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', whiteSpace: 'pre-wrap' }}>{it.description}</div>
              </div>
            ))}
          </div>

          {invoice.status === 'unsent' && <button className="btn btn-green" style={{ width: '100%' }} onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send to customer'}</button>}
          {invoice.status === 'sent' && <button className="btn" style={{ width: '100%' }} onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Re-send to customer'}</button>}
        </div>
      </div>
    </div>
  );
}

// Chase status for a sent quote.
//
// The flag is deliberately driven by reviewed_at (did they open the quote on our
// server) and never by opened_at (did a tracking pixel load). A customer reading
// the attached PDF with images blocked registers no open at all, so chasing on
// "not opened" would mean chasing people who have already read it.
const CHASE_AFTER_DAYS = 3;

function quoteChaseStatus(q) {
  if (!q.sent_at || q.status === 'accepted' || q.status === 'declined' || q.status === 'updated') return null;
  const shortDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  // A bounce outranks everything else: it did not arrive, so nothing else matters.
  if (q.bounced_at) {
    return { tone: 'bounce', text: `Email bounced ${shortDate(q.bounced_at)} - it never arrived${q.bounce_reason ? ` · ${String(q.bounce_reason).slice(0, 90)}` : ''}` };
  }
  if (q.reviewed_at) return { tone: 'good', text: `Quote viewed ${shortDate(q.reviewed_at)}` };
  const days = Math.floor((Date.now() - new Date(q.sent_at).getTime()) / 86400000);
  const seen = q.opened_at ? `email opened ${shortDate(q.opened_at)}` : 'no open recorded';
  if (days >= CHASE_AFTER_DAYS) {
    return { tone: 'chase', text: `Not viewed after ${days} day${days === 1 ? '' : 's'} · ${seen} · worth chasing` };
  }
  return { tone: 'quiet', text: `Not viewed yet · ${seen}` };
}

const CHASE_COLOURS = { good: '#16a34a', chase: '#b45309', quiet: 'var(--ink-faint)', bounce: '#b91c1c' };

function quoteChaseShort(q) {
  const st = quoteChaseStatus(q);
  if (!st) return null;
  const shortDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const days = Math.floor((Date.now() - new Date(q.sent_at).getTime()) / 86400000);
  const open = q.opened_at ? `opened ${shortDate(q.opened_at)}` : 'no open';
  if (st.tone === 'bounce') return { ...st, pill: 'Bounced', short: `Bounced ${shortDate(q.bounced_at)}` };
  if (st.tone === 'good') return { ...st, pill: 'Viewed', short: `Viewed ${shortDate(q.reviewed_at)}` };
  if (st.tone === 'chase') return { ...st, pill: 'Chase', short: `⚠ Not viewed ${days}d · ${open}` };
  return { ...st, pill: 'Sent', short: `Not viewed yet · ${open}` };
}
const CHASE_PILL = {
  good: { bg: '#e9f7ef', color: '#16a34a', border: '#c3ecd4' },
  chase: { bg: '#fff6e5', color: '#b45309', border: '#f6e0b5' },
  bounce: { bg: '#fdecec', color: '#b91c1c', border: '#f8d4d4' },
  quiet: { bg: '#f4f4f5', color: '#57534e', border: '#e7e5e4' },
};

function QuotesBoardView({ leads, onSelectLead, onOpenQuote }) {
  const { showAlert } = useDialog();
  const [filterBrand, setFilterBrand] = usePref('quotes.brand', 'all', isBrandPref);
  const [priorityFirst, setPriorityFirst] = usePref('quotes.priority', false, v => typeof v === 'boolean');
  const [mode, setMode] = usePref('quotes.mode', 'todo', oneOf('todo', 'quoted'));
  const [quoteCounts, setQuoteCounts] = useState({});

  // To quote: in the Quote stage with nothing sent, or nothing still open. Quoted: a
  // quote is with the customer and they have not answered - wherever the lead sits
  // before acceptance, because the sheet put some quoted leads in Lead.
  const inBrand = (l) => filterBrand === 'all' || l.brand === filterBrand;
  const quoteLeads = leads.filter(l => l.stage === 'Quote' && !quoteOut(l) && inBrand(l));
  const quotedLeads = leads
    .filter(l => ['Lead', 'Visit', 'Quote'].includes(l.stage) && quoteOut(l) && inBrand(l))
    .sort((a, b) => new Date(a.last_quote.sent_at) - new Date(b.last_quote.sent_at) || a.id - b.id);

  const sorted = [...quoteLeads].sort((a, b) => {
    if (priorityFirst) {
      const ap = a.quote_status === 'priority_quote' ? 0 : 1;
      const bp = b.quote_status === 'priority_quote' ? 0 : 1;
      if (ap !== bp) return ap - bp;
    }
    return new Date(a.created_at) - new Date(b.created_at);
  });

  useEffect(() => {
    Promise.all(quoteLeads.map(l => api(`/api/quotes?lead_id=${l.id}`).then(qs => [l.id, qs]).catch(() => [l.id, []])))
      .then(pairs => setQuoteCounts(Object.fromEntries(pairs)));
  }, [leads]);

  const createQuote = async (leadId, e) => {
    e.stopPropagation();
    try {
      const q = await api('/api/quotes', { method: 'POST', body: JSON.stringify({ lead_id: leadId }) });
      onOpenQuote(leadId, q.id);
    } catch (err) { showAlert(err.message); }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="filter-bar">
        <span className="filter-label">Brand:</span>
        <select className="select-compact" value={filterBrand} onChange={e => setFilterBrand(e.target.value)}>
          <option value="all">All brands</option>
          {BRAND_KEYS.map(k => <option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
        </select>
        <div className="tabs" style={{ marginLeft: 6 }}>
          <button className={`tab${mode === 'todo' ? ' active' : ''}`} onClick={() => setMode('todo')}>To quote ({quoteLeads.length})</button>
          <button className={`tab${mode === 'quoted' ? ' active' : ''}`} onClick={() => setMode('quoted')}>Quoted ({quotedLeads.length})</button>
        </div>
        {mode === 'todo' && (
          <button className="btn btn-sm" onClick={() => setPriorityFirst(v => !v)}
            style={priorityFirst ? { background: STATUS_BOX_COLORS.Quote.bg, color: STATUS_BOX_COLORS.Quote.text, borderColor: STATUS_BOX_COLORS.Quote.border } : {}}>
            {priorityFirst ? 'Priority first: On' : 'Priority first: Off'}
          </button>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginLeft: 'auto' }}>
          {mode === 'todo'
            ? `${sorted.length} to quote`
            : `${quotedLeads.length} with the customer · oldest first`}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
        {mode === 'quoted' && (
          <div className="qd-list">
            {quotedLeads.map(l => {
              const q = l.last_quote;
              const ch = quoteChaseShort(q) || { tone: 'quiet', pill: 'Sent', short: '' };
              const pc = CHASE_PILL[ch.tone] || CHASE_PILL.quiet;
              return (
                <div key={l.id} className="qd-row" onClick={() => onSelectLead(l.id)} title={ch.text || ''}>
                  <span className="job-num">{jobNumber(l.id)}</span>
                  <span className="qd-date">{fmtLongDate(q.sent_at)}</span>
                  <span className="qd-name">{tileDisplayName(l.title, l.first_name, l.last_name) || l.name}</span>
                  <BrandBadge brand={l.brand} />
                  <span className="qd-val">{fmt(l.quoted_value)}</span>
                  <span className="qd-pill" style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}>{ch.pill}</span>
                  <span className="qd-chase" style={{ color: CHASE_COLOURS[ch.tone] || 'var(--ink-faint)' }}>{ch.short}</span>
                  <button className="btn btn-sm" onClick={e => { e.stopPropagation(); onOpenQuote(l.id, q.id); }}>Open quote</button>
                </div>
              );
            })}
            {quotedLeads.length === 0 && <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, textAlign: 'center', padding: 40 }}>No quotes waiting on a customer.</div>}
          </div>
        )}
        {mode === 'todo' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {sorted.map(l => {
            const quotes = quoteCounts[l.id] || [];
            return (
              <div key={l.id} onClick={() => onSelectLead(l.id)} style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)',
                borderRadius: 'var(--radius)', padding: '12px 14px', cursor: 'pointer', transition: 'border-color .12s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#d4d4d8'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--line-soft)'}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="job-num">{jobNumber(l.id)}</span>
                  <BrandBadge brand={l.brand} />
                </div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{tileDisplayName(l.title, l.first_name, l.last_name) || l.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginBottom: 8, lineHeight: 1.4 }}>{l.address}</div>
                {l.quote_status === 'priority_quote' && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: STATUS_BOX_COLORS.Quote.text, background: STATUS_BOX_COLORS.Quote.bg,
                    border: `1px solid ${STATUS_BOX_COLORS.Quote.border}`, borderRadius: 4, padding: '2px 6px', display: 'inline-block', marginBottom: 8 }}>
                    Priority quote
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{quotes.length} version{quotes.length !== 1 ? 's' : ''}</span>
                  <button className="btn btn-sm btn-green" onClick={e => createQuote(l.id, e)}>+ New quote</button>
                </div>
                {quotes.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {quotes.slice(0, 3).map(q => (
                      <div key={q.id} onClick={e => { e.stopPropagation(); onOpenQuote(l.id, q.id); }}
                        style={{ fontSize: 11, color: 'var(--accent)', cursor: 'pointer' }}>
                        {q.reference ? `Quote #${q.reference}` : 'Draft'}
                        {!q.sent_at
                          ? <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 9,
                              background: 'var(--canvas-sunken)', color: 'var(--ink-soft)' }}>Draft quote</span>
                          : <span style={{ marginLeft: 6, color: 'var(--ink-faint)' }}>· {q.status}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {sorted.length === 0 && <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, gridColumn: '1/-1', textAlign: 'center', padding: 40 }}>Nothing waiting to be quoted.</div>}
        </div>
        )}
      </div>
    </div>
  );
}

// Phone navigation. Rendered always, shown only under the breakpoint, so there
// is no width-sniffing in JS to disagree with the CSS.
function MobileNav({ view, setView, todoCount, showLabour, showDayOrder }) {
  const [more, setMore] = useState(false);
  const goto = (v) => { setView(v); setMore(false); };
  const Item = ({ id, label, badge }) => (
    <button className={`mnav-item${view === id ? ' active' : ''}`} onClick={() => goto(id)}>
      {label}
      {badge > 0 && <span className="mnav-badge">{badge}</span>}
    </button>
  );
  // Anything reachable from More still highlights More, so the bar never shows
  // nothing selected.
  const inMore = ['list', 'map', 'quotes', 'invoicing', 'dayorder', 'reports', 'labour', 'settings'].includes(view);
  return (
    <>
      {more && <div className="mnav-back" onClick={() => setMore(false)}/>}
      {more && (
        <div className="mnav-sheet">
          {[['list','List'],['map','Map'],['quotes','Quotes'],['invoicing','Invoicing'],
            ...(showDayOrder?[['dayorder','Day order']]:[]),
            ...(showLabour?[['reports','Reports'],['labour','Labour']]:[]),['settings','Settings']].map(([v,label]) => (
            <button key={v} className={`mnav-sheet-item${view === v ? ' active' : ''}`} onClick={() => goto(v)}>{label}</button>
          ))}
        </div>
      )}
      <nav className="mnav">
        <Item id="diary" label="Diary"/>
        <Item id="board" label="Board"/>
        <Item id="jobs" label="Jobs"/>
        <Item id="todo" label="To-do" badge={todoCount}/>
        <button className={`mnav-item${more || inMore ? ' active' : ''}`} onClick={() => setMore(v => !v)}
          aria-label="More" title="More"><span className="mnav-burger"/></button>
      </nav>
    </>
  );
}

const ROLE_LABELS = {
  admin: 'Admin',
  ld: 'Full access',
  office: 'Office',
  pm: 'Project manager',
  surveyor: 'Surveyor (field)',
  team: 'Team (field)',
};

// The crews. A team owns a tablet; who is on it changes daily, and that daily
// roster is set from the tablet rather than here. This is only the standing list
// of who can be on it.
function CrewTeamsPanel({ me }) {
  const { showAlert } = useDialog();
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [openId, setOpenId] = useState(null);
  const isAdmin = !!me && me.role === 'admin';

  const load = async () => {
    try {
      const [t, u] = await Promise.all([api('/api/teams'), api('/api/users')]);
      setTeams(t || []);
      setPeople((u || []).filter(x => x.active));
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const run = async (fn) => {
    if (busy) return false;
    setBusy(true);
    let ok = false;
    try { await fn(); await load(); ok = true; } catch (e) { showAlert(e.message); }
    setBusy(false);
    return ok;
  };

  const add = async () => {
    if (!name.trim()) return;
    if (await run(() => api('/api/teams', { method: 'POST', body: JSON.stringify({ name }) }))) setName('');
  };

  const toggleMember = (team, userId) => {
    const ids = (team.members || []).map(m => m.id);
    const next = ids.includes(userId) ? ids.filter(x => x !== userId) : [...ids, userId];
    return run(() => api(`/api/teams/${team.id}/members`, { method: 'PUT', body: JSON.stringify({ user_ids: next }) }));
  };

  return (
    <div style={{ padding: 20, maxWidth: 820 }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>Teams</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 16 }}>
        A team owns a tablet. Who is actually on it is chosen each morning on the tablet
        and can be changed per job; this is the standing list of who can be picked.
        {!isAdmin && ' Only an admin can change these.'}
      </p>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <input value={name} placeholder="Team 1" style={{ maxWidth: 220 }}
            onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} />
          <button className="btn btn-sm btn-green" disabled={busy || !name.trim()} onClick={add}>+ Add team</button>
        </div>
      )}

      {teams.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ink-faint)' }}>No teams yet.</p>
      )}

      {teams.map(t => (
        <div key={t.id} className="lab-card">
          <div className="lab-card-head">
            <strong style={{ fontSize: 14 }}>{t.name}</strong>
            <span style={{ color: 'var(--ink-soft)' }}>
              {(t.members || []).length ? t.members.map(m => m.display_name).join(', ') : 'nobody on the list yet'}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {!t.active && <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>Inactive</span>}
              {isAdmin && <button className="btn btn-sm" onClick={() => setOpenId(openId === t.id ? null : t.id)}>
                {openId === t.id ? 'Done' : 'Who is on it'}</button>}
              {isAdmin && <button className="btn btn-sm" disabled={busy}
                onClick={() => run(() => api(`/api/teams/${t.id}`, { method: 'PUT', body: JSON.stringify({ active: !t.active }) }))}>
                {t.active ? 'Deactivate' : 'Reactivate'}</button>}
            </span>
          </div>
          {openId === t.id && (
            <div className="lab-card-body">
              {people.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-faint)' }}>No active logins to add.</div>}
              {people.map(p => {
                const on = (t.members || []).some(m => m.id === p.id);
                return (
                  <button key={p.id} className={`jg-person${on ? ' on' : ''}`} disabled={busy}
                    onClick={() => toggleMember(t, p.id)}>
                    <span>{p.display_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{ROLE_LABELS[p.role] || p.role}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Nobody is ever deleted: their name is on job days, quotes and activity going
// back months. Deactivating stops the login and takes them out of every picker.
function TeamPanel({ me }) {
  const { showAlert } = useDialog();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: '', display_name: '', role: 'team', password: '' });
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState('');
  const [payFor, setPayFor] = useState(null);
  const [tabFor, setTabFor] = useState(null);
  const [pin, setPin] = useState('');
  const [pay, setPay] = useState({ day_rate: '', rate_basis: 'gross', cis_percent: '20', pay_type: 'cis' });
  const isAdmin = !!me && me.role === 'admin';

  const load = async () => { try { setRows(await api('/api/users')); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const run = async (fn) => {
    if (busy) return false;
    setBusy(true);
    let ok = false;
    try { await fn(); await load(); ok = true; } catch (e) { showAlert(e.message); }
    setBusy(false);
    return ok;
  };

  const add = async () => {
    const ok = await run(() => api('/api/users', { method: 'POST', body: JSON.stringify(form) }));
    if (ok) { setForm({ username: '', display_name: '', role: 'team', password: '' }); setAdding(false); }
  };

  const update = (id, body) => run(() => api(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }));

  const setPassword = async (id) => {
    const ok = await update(id, { password: pw });
    if (ok) { setPwFor(null); setPw(''); }
  };

  return (
    <div style={{ padding: 20, maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>People</h2>
        {isAdmin && !adding && <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add person</button>}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 16 }}>
        Everyone who can log in. <b>Admin</b> and <b>Full access</b> and <b>Office</b> see the whole
        CRM; <b>Surveyor</b> and <b>Team</b> are field logins and see only their own day, as each
        part of the field app is built. <b>Tablet</b> sets whether someone can sign in on a company
        tablet with a PIN.
        {!isAdmin && ' Only an admin can change these.'}
      </p>

      {adding && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 16,
          background: 'var(--canvas-sunken)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
            <div className="fl"><label>Name</label>
              <input value={form.display_name} placeholder="Dave Smith"
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} /></div>
            <div className="fl"><label>Username</label>
              <input value={form.username} placeholder="dave" autoComplete="off"
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></div>
            <div className="fl"><label>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {Object.keys(ROLE_LABELS).map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select></div>
            <div className="fl"><label>Password</label>
              <input type="password" value={form.password} autoComplete="new-password"
                placeholder="at least 8 characters"
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-sm btn-green" disabled={busy} onClick={add}>Create login</button>
            <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="team-row team-head">
        <span>Name</span><span>Username</span><span>Role</span><span>Status</span><span/>
      </div>
      {rows.map(u => (
        <div key={u.id} className={`team-row${u.active ? '' : ' off'}`}>
          <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="jc-crew-chip" style={personChipStyle(u.colour)} title="How this person reads on a job">
              {crewInitials(u.display_name)}
            </span>
            {u.display_name}{me && me.id === u.id ? ' (you)' : ''}
            {isAdmin && (
              <>
                <input type="color" value={u.colour || '#9ca3af'} disabled={busy} title="Badge colour"
                  onChange={e => update(u.id, { colour: e.target.value })}
                  style={{ width: 26, height: 22, padding: 0, border: '1px solid var(--line)', borderRadius: 5, cursor: 'pointer' }}/>
                {u.colour && (
                  <button className="btn btn-sm" disabled={busy} onClick={() => update(u.id, { colour: '' })}
                    style={{ fontSize: 10.5, padding: '2px 6px' }}>Clear</button>
                )}
              </>
            )}
          </span>
          <span style={{ color: 'var(--ink-soft)' }}>{u.username}</span>
          <span>
            {isAdmin
              ? <select className="select-compact" value={u.role} disabled={busy}
                  onChange={e => update(u.id, { role: e.target.value })}>
                  {Object.keys(ROLE_LABELS).map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              : (ROLE_LABELS[u.role] || u.role)}
          </span>
          <span style={{ fontSize: 12, color: u.active ? 'var(--accent)' : 'var(--ink-faint)' }}>
            {u.active ? 'Active' : 'Deactivated'}
          </span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isAdmin && <button className="btn btn-sm" disabled={busy}
              onClick={() => update(u.id, { active: !u.active })}>
              {u.active ? 'Deactivate' : 'Reactivate'}
            </button>}
            {isAdmin && <button className="btn btn-sm" onClick={() => { setPwFor(u.id); setPw(''); }}>Password</button>}
            {isAdmin && <button className="btn btn-sm" onClick={() => { setTabFor(tabFor === u.id ? null : u.id); setPin(''); }}>
              Tablet{u.tablet_user && u.has_pin ? ' ✓' : ''}</button>}
            {isAdmin && <button className="btn btn-sm" onClick={() => {
              setPayFor(payFor === u.id ? null : u.id);
              setPay({
                day_rate: u.day_rate == null ? '' : String(u.day_rate),
                rate_basis: u.rate_basis || 'gross',
                cis_percent: u.cis_percent == null ? '20' : String(u.cis_percent),
                pay_type: u.pay_type || 'cis',
              });
            }}>Pay</button>}
          </span>
          {tabFor === u.id && (
            <span style={{ gridColumn: '1 / -1', paddingTop: 10, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                <input type="checkbox" checked={!!u.tablet_user} disabled={busy}
                  onChange={e => update(u.id, { tablet_user: e.target.checked })}/>
                Can sign in on tablets
              </label>
              <div className="fl" style={{ width: 150 }}><label>{u.has_pin ? 'New PIN' : 'PIN'} (4-6 digits)</label>
                <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, ''))}/></div>
              <button className="btn btn-sm btn-green" disabled={busy || pin.length < 4}
                onClick={async () => { if (await update(u.id, { pin })) setPin(''); }}>Save PIN</button>
              {u.has_pin && <button className="btn btn-sm" disabled={busy} onClick={() => update(u.id, { pin: '' })}>Remove PIN</button>}
              <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
                {u.has_pin ? 'Has a PIN.' : 'No PIN yet.'}
                {u.pin_locked_until && new Date(u.pin_locked_until) > new Date() ? ' Locked after wrong PINs - saving a new PIN unlocks it.' : ''}
              </span>
            </span>
          )}
          {payFor === u.id && (
            <span style={{ gridColumn: '1 / -1', paddingTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
                <div className="fl"><label>Day rate</label>
                  <input inputMode="decimal" value={pay.day_rate} placeholder="200"
                    onChange={e => setPay(p => ({ ...p, day_rate: e.target.value }))} /></div>
                <div className="fl"><label>That figure is</label>
                  <select value={pay.rate_basis} onChange={e => setPay(p => ({ ...p, rate_basis: e.target.value }))}>
                    <option value="gross">Gross (before CIS)</option>
                    <option value="net">Net (what they receive)</option>
                  </select></div>
                <div className="fl"><label>Paid as</label>
                  <select value={pay.pay_type} onChange={e => setPay(p => ({ ...p, pay_type: e.target.value }))}>
                    <option value="cis">Subcontractor (CIS)</option>
                    <option value="paye">Employee (PAYE)</option>
                  </select></div>
                <div className="fl"><label>CIS %</label>
                  <input inputMode="decimal" value={pay.cis_percent}
                    onChange={e => setPay(p => ({ ...p, cis_percent: e.target.value }))} /></div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '10px 0 0' }}>
                Jobs are costed at the <b>gross</b> figure, because that is what the day costs the
                business. A rate entered as net is grossed up by the CIS percentage
                {pay.rate_basis === 'net' && pay.day_rate && isFinite(Number(pay.day_rate))
                  ? ` - £${Number(pay.day_rate).toFixed(2)} net is £${(Number(pay.day_rate) / (1 - Math.min(Math.max(Number(pay.cis_percent) || 0, 0), 95) / 100)).toFixed(2)} gross`
                  : ''}.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn btn-sm btn-green" disabled={busy} onClick={async () => {
                  const ok = await update(u.id, {
                    day_rate: pay.day_rate === '' ? '' : Number(pay.day_rate),
                    rate_basis: pay.rate_basis, pay_type: pay.pay_type,
                    cis_percent: Number(pay.cis_percent),
                  });
                  if (ok) setPayFor(null);
                }}>Save pay</button>
                <button className="btn btn-sm" onClick={() => setPayFor(null)}>Cancel</button>
              </div>
            </span>
          )}
          {pwFor === u.id && (
            <span style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', paddingTop: 8 }}>
              <input type="password" value={pw} autoComplete="new-password"
                placeholder="new password, at least 8 characters"
                style={{ flex: 1, minWidth: 180 }} onChange={e => setPw(e.target.value)} />
              <button className="btn btn-sm btn-green" disabled={busy} onClick={() => setPassword(u.id)}>Set</button>
              <button className="btn btn-sm" onClick={() => { setPwFor(null); setPw(''); }}>Cancel</button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// A pg timestamp is an instant; the inputs edit the wall-clock time on the day
// the stint belongs to, which is how anyone correcting a timesheet thinks about it.
function timeInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function withTimeOfDay(iso, hhmm, fallbackDate) {
  if (!/^\d{2}:\d{2}$/.test(hhmm || '')) return null;
  const base = iso ? new Date(iso) : new Date(`${fallbackDate}T00:00:00`);
  const [h, m] = hhmm.split(':').map(Number);
  base.setHours(h, m, 0, 0);
  return base.toISOString();
}
const hhmm = (mins) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// One stint, with its times editable. An edited time is marked, because these
// hours decide what somebody is paid.
function StintRow({ s, onSave, onDelete, busy }) {
  const [start, setStart] = useState(timeInputValue(s.started_at));
  const [finish, setFinish] = useState(timeInputValue(s.finished_at));
  const dirty = start !== timeInputValue(s.started_at) || finish !== timeInputValue(s.finished_at);
  return (
    <div className="lab-stint">
      <div className="lab-times">
        <input type="time" value={start} onChange={e => setStart(e.target.value)} />
        <span style={{ color: 'var(--ink-faint)' }}>to</span>
        <input type="time" value={finish} onChange={e => setFinish(e.target.value)}
          style={!s.finished_at ? { borderColor: 'var(--amber)' } : undefined} />
        {!s.finished_at && <span className="lab-edited">never finished</span>}
        {s.auto_closed && <span className="lab-edited">finish assumed</span>}
        {s.edited_at && <span className="lab-edited" title={`edited by ${s.edited_by_name || 'office'}`}>edited</span>}
      </div>
      <span className="lab-num" style={{ color: 'var(--ink-soft)' }}>
        {s.finished_at ? hhmm(s.minutes) : '—'}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-sm btn-green" disabled={busy || !dirty}
          onClick={() => onSave(s, {
            started_at: withTimeOfDay(s.started_at, start, s.work_date),
            finished_at: finish ? withTimeOfDay(s.finished_at || s.started_at, finish, s.work_date) : null,
          })}>Save</button>
        <button className="btn btn-sm btn-red" disabled={busy} onClick={() => onDelete(s)}>Delete</button>
      </span>
    </div>
  );
}

// The office's screen: what needs fixing, then the day itself.
function TimesheetsPanel() {
  const { showAlert, showConfirm } = useDialog();
  const [date, setDate] = useState(todayStr());
  const [day, setDay] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [d, s, x] = await Promise.all([
          api(`/api/labour/day?date=${date}`),
          api(`/api/work-sessions?date=${date}`),
          api('/api/labour/exceptions'),
        ]);
        if (!live) return;
        setDay(d || []); setSessions(s || []); setExceptions(x || []);
      } catch (e) {}
    })();
    return () => { live = false; };
  }, [date, tick]);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); setTick(t => t + 1); } catch (e) { showAlert(e.message); }
    setBusy(false);
  };

  const saveStint = (s, body) => run(() => api(`/api/work-sessions/${s.id}`, { method: 'PUT', body: JSON.stringify(body) }));
  const deleteStint = async (s) => {
    const ok = await showConfirm('Delete this stint? The time it carries stops counting towards pay.');
    if (ok) run(() => api(`/api/work-sessions/${s.id}`, { method: 'DELETE' }));
  };

  const byUser = {};
  for (const s of sessions) (byUser[s.user_id] = byUser[s.user_id] || []).push(s);

  return (
    <div className="lab-wrap">
      <div className="lab-head">
        <label style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Day</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ width: 170 }} />
        <button className="btn btn-sm" onClick={() => setDate(todayStr())}>Today</button>
      </div>

      {exceptions.length > 0 && (
        <div className="lab-warn">
          <strong style={{ color: 'var(--amber)' }}>
            {exceptions.length} time{exceptions.length === 1 ? '' : 's'} to check
          </strong>
          <p style={{ margin: '4px 0 6px', fontSize: 12.5, color: 'var(--ink-soft)' }}>
            A stint nobody finished counts nothing, which quietly overstates the other jobs
            that day. Fix these before Friday.
          </p>
          {exceptions.slice(0, 25).map((x, i) => (
            <div className="lab-ex" key={i}>
              <span>
                <b>{x.display_name}</b> · {x.work_date} · {x.detail}
              </span>
              <button className="btn btn-sm" onClick={() => setDate(x.work_date)}>Open that day</button>
            </div>
          ))}
        </div>
      )}

      {day.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ink-faint)' }}>Nothing logged on this day.</p>
      )}

      {day.map(p => (
        <div key={p.user_id} className="lab-card">
          <div className="lab-card-head">
            <strong style={{ fontSize: 14 }}>{p.display_name}</strong>
            <span style={{ color: 'var(--ink-soft)' }}>{hhmm(p.minutes)} across {p.jobs.length} job{p.jobs.length === 1 ? '' : 's'}</span>
            <span style={{ marginLeft: 'auto' }} className="lab-num">
              {gbp(p.pay_value)}{p.pay_value_is_override ? ' (part day)' : ''}
            </span>
          </div>
          <div className="lab-card-body">
            {p.overlap && (
              <div style={{ fontSize: 12.5, color: 'var(--amber)', fontWeight: 600, marginBottom: 8 }}>
                Two stints overlap - the same minutes are being counted twice.
              </div>
            )}
            {(byUser[p.user_id] || []).map(s => (
              <StintRow key={s.id} s={s} busy={busy} onSave={saveStint} onDelete={deleteStint} />
            ))}
            <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 10 }}>
              {p.jobs.map(j => (
                <div key={j.job_id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
                  <span style={{ color: 'var(--ink-soft)' }}>Job {j.job_id}</span>
                  <span style={{ marginLeft: 'auto' }} className="lab-num">{hhmm(j.minutes)}</span>
                  <span className="lab-num" style={{ width: 78, fontWeight: 600 }}>{gbp(j.cost)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Friday's figures, for the project manager to approve.
function LabourWeekPanel() {
  const { showAlert } = useDialog();
  const [weekStart, setWeekStart] = useState(todayStr());
  const [wk, setWk] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const d = await api(`/api/labour/week?week_start=${weekStart}`);
        if (live) setWk(d);
      } catch (e) {}
    })();
    return () => { live = false; };
  }, [weekStart, tick]);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); setTick(t => t + 1); } catch (e) { showAlert(e.message); }
    setBusy(false);
  };

  const setDayValue = (userId, d, value, approve) =>
    run(() => api(`/api/work-days/${userId}/${d}`, {
      method: 'PUT',
      body: JSON.stringify({ pay_value: value === '' ? null : Number(value), approve: !!approve }),
    }));

  const shiftWeek = (n) => {
    const d = new Date(`${(wk ? wk.week_start : weekStart)}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n * 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  return (
    <div className="lab-wrap">
      <div className="lab-head">
        <button className="btn btn-sm" onClick={() => shiftWeek(-1)}>← Previous</button>
        <strong style={{ fontSize: 14 }}>{wk ? `${wk.week_start} to ${wk.week_end}` : '—'}</strong>
        <button className="btn btn-sm" onClick={() => shiftWeek(1)}>Next →</button>
        <button className="btn btn-sm" onClick={() => setWeekStart(todayStr())}>This week</button>
      </div>

      {wk && wk.exceptions.length > 0 && (
        <div className="lab-warn">
          <strong style={{ color: 'var(--amber)' }}>
            {wk.exceptions.length} time{wk.exceptions.length === 1 ? '' : 's'} in this week still need correcting
          </strong>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--ink-soft)' }}>
            These figures are not reliable until they are fixed, on the Timesheets tab.
          </p>
        </div>
      )}

      {wk && wk.people.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ink-faint)' }}>Nobody logged any time this week.</p>
      )}

      {wk && wk.people.length > 0 && (
        <div className="lab-card">
          <div className="lab-card-body">
            <div className="lab-week lab-week-head">
              <span>Person</span><span className="lab-num">Days</span><span className="lab-num">Hours</span>
              <span className="lab-num">Gross</span><span className="lab-num">CIS</span><span className="lab-num">Net</span><span/>
            </div>
            {wk.people.map(p => (
              <React.Fragment key={p.user_id}>
                <div className="lab-week">
                  <span style={{ fontWeight: 600 }}>
                    {p.display_name}
                    {p.days_unapproved > 0 && <span className="lab-edited" style={{ marginLeft: 6 }}>
                      {p.days_unapproved} to approve</span>}
                  </span>
                  <span className="lab-num">{p.days_worked}</span>
                  <span className="lab-num">{p.hours}</span>
                  <span className="lab-num">{gbp(p.gross)}</span>
                  <span className="lab-num">{gbp(p.cis)}</span>
                  <span className="lab-num" style={{ fontWeight: 700 }}>{gbp(p.net)}</span>
                  <span>
                    <button className="btn btn-sm" onClick={() => setOpenId(openId === p.user_id ? null : p.user_id)}>
                      {openId === p.user_id ? 'Hide' : 'Days'}
                    </button>
                  </span>
                </div>
                {openId === p.user_id && p.days.map(d => (
                  <div className="lab-day" key={d.work_date}>
                    <span>
                      {d.work_date}
                      <span style={{ color: 'var(--ink-faint)', marginLeft: 8 }}>{hhmm(d.minutes)}</span>
                      {d.note && <span style={{ color: 'var(--ink-faint)', marginLeft: 8 }}>· {d.note}</span>}
                    </span>
                    <span className="lab-num" style={{ color: 'var(--ink-faint)' }}>
                      of {gbp(d.full_day_rate_gross)}
                    </span>
                    <span>
                      <input inputMode="decimal" defaultValue={d.pay_value}
                        onBlur={e => {
                          if (Number(e.target.value) !== Number(d.pay_value)) {
                            setDayValue(p.user_id, d.work_date, e.target.value, false);
                          }
                        }} />
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {d.approved_at
                        ? <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>Approved</span>
                        : <button className="btn btn-sm btn-green" disabled={busy}
                            onClick={() => setDayValue(p.user_id, d.work_date, d.pay_value, true)}>Approve</button>}
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
            <div className="lab-week" style={{ borderBottom: 'none', borderTop: '2px solid var(--ink)', fontWeight: 700 }}>
              <span>Total</span><span/><span/>
              <span className="lab-num">{gbp(wk.total_gross)}</span><span/>
              <span className="lab-num">{gbp(wk.total_net)}</span><span/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LabourView() {
  const [tab, setTab] = usePref('labour.tab', 'timesheets', oneOf('timesheets', 'week'));
  return (
    <div className="view-scroll">
      <div style={{ padding: '10px 20px 0' }}>
        <div className="tabs">
          <button className={`tab${tab === 'timesheets' ? ' active' : ''}`} onClick={() => setTab('timesheets')}>Timesheets</button>
          <button className={`tab${tab === 'week' ? ' active' : ''}`} onClick={() => setTab('week')}>Week</button>
        </div>
      </div>
      {tab === 'timesheets' && <TimesheetsPanel />}
      {tab === 'week' && <LabourWeekPanel />}
    </div>
  );
}

function SettingsView({ me }) {
  const [section, setSection] = usePref('settings.section', 'branding',
    oneOf('company', 'branding', 'guarantees', 'default-items', 'email-templates', 'notifications', 'team', 'teams', 'tablets', 'websites'));
  return (
    <div className="settings-wrap" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
      <div className="settings-nav" style={{ width: 180, flexShrink: 0, borderRight: '1px solid var(--line-soft)', padding: '14px 10px', background: 'var(--surface)' }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 8px' }}>Settings</div>
        <button onClick={() => setSection('company')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'company' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'company' ? 600 : 400 }}>
          Company
        </button>
        <button onClick={() => setSection('branding')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'branding' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'branding' ? 600 : 400 }}>
          Branding
        </button>
        <button onClick={() => setSection('guarantees')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'guarantees' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'guarantees' ? 600 : 400 }}>
          Guarantees
        </button>
        <button onClick={() => setSection('default-items')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'default-items' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'default-items' ? 600 : 400 }}>
          Default items
        </button>
        <button onClick={() => setSection('email-templates')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'email-templates' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'email-templates' ? 600 : 400 }}>
          Email templates
        </button>
        <button onClick={() => setSection('notifications')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'notifications' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'notifications' ? 600 : 400 }}>
          Notifications
        </button>
        <button onClick={() => setSection('team')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'team' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'team' ? 600 : 400 }}>
          People
        </button>
        <button onClick={() => setSection('teams')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'teams' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'teams' ? 600 : 400 }}>
          Teams
        </button>
        <button onClick={() => setSection('tablets')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'tablets' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'tablets' ? 600 : 400 }}>
          Tablets
        </button>
        <button onClick={() => setSection('websites')}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 8px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: section === 'websites' ? 'var(--canvas-sunken)' : 'transparent', color: 'var(--ink)', fontWeight: section === 'websites' ? 600 : 400 }}>
          Websites
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {section === 'notifications' && <NotificationSettingsPanel />}
        {section === 'company' && <CompanySettingsPanel />}
        {section === 'branding' && <BrandingSettingsPanel />}
        {section === 'guarantees' && <GuaranteeTemplatesPanel />}
        {section === 'default-items' && <DefaultItemsPanel />}
        {section === 'email-templates' && <EmailTemplatesPanel />}
        {section === 'team' && <TeamPanel me={me} />}
        {section === 'teams' && <CrewTeamsPanel me={me} />}
        {section === 'tablets' && <TabletsPanel me={me} />}
        {section === 'websites' && <WebSitesPanel me={me} />}
      </div>
    </div>
  );
}

// A phone lead, and taps on that brand's phone number from ad visitors just before it was
// logged. One tap on "Use it" ties the lead to the ad click; nothing is attached otherwise.
function AdTapMatch({ leadId, onUsed }) {
  const { showAlert } = useDialog();
  const [taps, setTaps] = useState([]);
  useEffect(() => { api(`/api/leads/${leadId}/ad-taps`).then(r => setTaps(r || [])).catch(() => {}); }, [leadId]);
  if (!taps.length) return null;
  const use = async (t) => {
    try { await api(`/api/leads/${leadId}/ad-taps/${t.id}/use`, { method: 'POST' }); setTaps([]); onUsed && onUsed(); }
    catch (e) { showAlert(e.message); }
  };
  const tm = (x) => new Date(x).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="ad-source">
      <b>Did this caller come from an ad?</b> {taps.length === 1 ? 'An ad visitor tapped this brand\'s number' : `${taps.length} ad visitors tapped this brand's number`} just before this lead was logged:
      {taps.map(t => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <span>{tm(t.tapped_at)} · {t.gclid || t.gbraid || t.wbraid ? 'Google Ads' : t.utm_source}{t.utm_campaign ? ` · ${t.utm_campaign}` : ''}{t.utm_term ? ` · "${t.utm_term}"` : ''} · on {t.page || '-'}</span>
          <button className="btn btn-sm" onClick={() => use(t)}>Use it</button>
        </div>
      ))}
    </div>
  );
}

// Brand websites sending enquiries straight into the CRM (docs/ads-attribution-spec.md).
// The site's own switch decides whether it sends; this decides whether the CRM accepts.
// A site's key is set on the server, never shown here. Peterborough stays off.
function WebSitesPanel({ me }) {
  const { showAlert } = useDialog();
  const [rows, setRows] = useState([]);
  const isAdmin = !!me && me.role === 'admin';
  const load = () => api('/api/web-sites').then(r => setRows(r || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (r) => {
    try { await api(`/api/web-sites/${r.brand}`, { method: 'PUT', body: JSON.stringify({ enquiries_on: !r.enquiries_on }) }); load(); }
    catch (e) { showAlert(e.message); }
  };
  const when = (t) => t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
  return (
    <div style={{ padding: 20, maxWidth: 820 }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>Brand websites</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', margin: '6px 0 16px' }}>
        A connected website sends each enquiry form straight in as a lead - with the Google Ads click it came from, when the
        visitor allowed it - as well as emailing the inbox as before. Turning a brand off here stops leads being created;
        the website's emails are never affected.
      </p>
      <div className="team-row team-head" style={{ gridTemplateColumns: '1.6fr .8fr .8fr .8fr 1fr auto' }}>
        <span>Brand</span><span>Connected</span><span>Enquiries 30d</span><span>Ad taps 30d</span><span>Last enquiry</span><span/>
      </div>
      {rows.map(r => (
        <div key={r.brand} className={`team-row${r.enquiries_on ? '' : ' off'}`} style={{ gridTemplateColumns: '1.6fr .8fr .8fr .8fr 1fr auto' }}>
          <span style={{ fontWeight: 600 }}>{r.brand} - {BRANDS[r.brand] || r.brand}</span>
          <span>{r.has_key ? 'Yes' : 'Not yet'}</span>
          <span>{r.enquiries_30d}</span><span>{r.taps_30d}</span><span>{when(r.last_enquiry_at)}</span>
          <span>{isAdmin
            ? <button className={`btn btn-sm${r.enquiries_on ? ' btn-green' : ''}`} onClick={() => toggle(r)}>{r.enquiries_on ? 'On' : 'Off'}</button>
            : (r.enquiries_on ? 'On' : 'Off')}</span>
        </div>
      ))}
    </div>
  );
}

// Andrew, 18 Oct: tablets are just numbered 1, 2, 3. Stored as the number, shown as "Tablet 1".
const tabletLabel = (name) => /^\d+$/.test(String(name || '').trim()) ? `Tablet ${String(name).trim()}` : String(name || '');

// Company tablets (field app step 2). Register is done ON the tablet, signed in as an admin;
// the tablet then keeps its own key and shows the name + PIN sign-in from then on.
// Andrew, 30 Oct: run once on each tablet when it is set up. A refused location is otherwise
// silent - Start still works and simply records no position.
function TabletCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    const out = {};
    out.location = await new Promise(resolve => {
      if (!navigator.geolocation) return resolve(false);
      navigator.geolocation.getCurrentPosition(() => resolve(true), () => resolve(false), { enableHighAccuracy: true, timeout: 15000 });
    });
    const media = async (want) => {
      try { const st = await navigator.mediaDevices.getUserMedia(want); st.getTracks().forEach(t => t.stop()); return true; }
      catch (e) { return false; }
    };
    out.camera = await media({ video: true });
    out.microphone = await media({ audio: true });
    setRes(out); setBusy(false);
  };
  const rows = [['location', 'Location (Start and Finish times)'], ['camera', 'Camera (photos)'], ['microphone', 'Microphone (voice notes)']];
  return (
    <div className="offline-card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Tablet check</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginBottom: 10 }}>Run this once on each tablet, and choose Allow on every question it asks.</div>
      {res && rows.map(([k, label]) => (
        <div key={k} style={{ fontSize: 14, fontWeight: 600, padding: '3px 0', color: res[k] ? 'var(--accent)' : 'var(--red)' }}>{res[k] ? '✓' : '✗'} {label}</div>
      ))}
      {res && !rows.every(([k]) => res[k]) && <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', margin: '6px 0' }}>
        For anything with a cross: in Chrome, tap the icon left of the address, then Permissions, set it to Allow, and run this again. For location, also check it is on in the tablet's Settings.</div>}
      <button className="btn btn-sm btn-green" style={{ marginTop: 8 }} disabled={busy} onClick={run}>{busy ? 'Checking…' : res ? 'Check again' : 'Check this tablet'}</button>
    </div>
  );
}
function TabletsPanel({ me }) {
  const { showAlert, showConfirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const isAdmin = !!me && me.role === 'admin';
  const load = () => api('/api/devices').then(r => setRows(r || [])).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);
  const here = rows.find(r => r.this_device && !r.revoked_at);
  // The next number not used by a tablet that is still registered.
  const used = new Set(rows.filter(r => !r.revoked_at).map(r => String(r.name).trim()));
  let nextNo = 1; while (used.has(String(nextNo))) nextNo++;
  useEffect(() => { if (!name) setName(String(nextNo)); }, [nextNo, name]);
  if (!isAdmin) return <div style={{ padding: 20, fontSize: 13, color: 'var(--ink-soft)' }}>Only an admin can register tablets.</div>;
  const register = async () => {
    setBusy(true);
    try { await api('/api/devices', { method: 'POST', body: JSON.stringify({ name }) }); setName(''); await load(); }
    catch (e) { showAlert(e.message); }
    setBusy(false);
  };
  const revoke = async (d) => {
    if (!(await showConfirm(`Stop ${tabletLabel(d.name)} being a company tablet? Its name-and-PIN sign-in stops working straight away. Use this for a lost tablet.`,
      { title: 'De-register tablet', confirmLabel: 'De-register', danger: true }))) return;
    try { await api(`/api/devices/${d.id}`, { method: 'DELETE' }); await load(); } catch (e) { showAlert(e.message); }
  };
  const when = (t) => t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
  return (
    <div style={{ padding: 20, maxWidth: 760 }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>Company tablets</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', margin: '6px 0 16px' }}>
        A registered tablet signs people in by tapping their name and entering their PIN (set under People).
        Register a tablet from the tablet itself, while you are signed in on it.
      </p>
      {here ? (
        <div className="uq-box" style={{ background: 'var(--accent-soft)', borderColor: 'var(--accent-soft-line)', color: 'var(--accent-hover)' }}>
          This device is registered as <b>{tabletLabel(here.name)}</b>.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="fl" style={{ width: 160 }}><label>Register this device as tablet</label>
            <input inputMode="numeric" value={name} onChange={e => setName(e.target.value.replace(/\D/g, ''))} /></div>
          <button className="btn btn-sm btn-green" disabled={busy || !name.trim()} onClick={register}>Register this device</button>
        </div>
      )}
      {here && <TabletCheck/>}
      <div className="team-row team-head" style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr auto' }}>
        <span>Tablet</span><span>Registered</span><span>Last sign-in</span><span>By</span><span/>
      </div>
      {rows.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', padding: '8px 0' }}>No tablets registered yet.</div>}
      {rows.map(d => (
        <div key={d.id} className={`team-row${d.revoked_at ? ' off' : ''}`} style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr auto' }}>
          <span style={{ fontWeight: 600 }}>{tabletLabel(d.name)}{d.this_device ? ' (this device)' : ''}{d.revoked_at ? ' - de-registered' : ''}</span>
          <span>{when(d.created_at)}</span><span>{when(d.last_seen_at)}</span><span>{d.last_user || '-'}</span>
          <span>{!d.revoked_at && <button className="btn btn-sm btn-red" onClick={() => revoke(d)}>De-register</button>}</span>
        </div>
      ))}
    </div>
  );
}

function EmailTemplatesPanel() {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', body: '', subject: '', purpose: '' });
  const [newForm, setNewForm] = useState({ name: '', body: '', subject: '', purpose: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => { api('/api/email-templates').then(setItems).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => { setEditingId(it.id); setEditForm({ name: it.name || '', body: it.body || '', subject: it.subject || '', purpose: it.purpose || '' }); };
  const saveEdit = async () => {
    try { await api(`/api/email-templates/${editingId}`, { method: 'PUT', body: JSON.stringify(editForm) }); setEditingId(null); load(); }
    catch (e) { showAlert(e.message); }
  };
  const remove = async (id) => {
    if (!(await showConfirm('Remove this template?', {danger:true, confirmLabel:'Remove'}))) return;
    try { await api(`/api/email-templates/${id}`, { method: 'DELETE' }); load(); } catch (e) { showAlert(e.message); }
  };
  const addNew = async () => {
    if (!newForm.name.trim()) { await showAlert('Give the template a name.'); return; }
    setAdding(true);
    try { await api('/api/email-templates', { method: 'POST', body: JSON.stringify(newForm) }); setNewForm({ name: '', body: '', subject: '', purpose: '' }); load(); }
    catch (e) { showAlert(e.message); }
    setAdding(false);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', maxWidth: 720 }}>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Add a template</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginBottom: 10 }}>
          These are reply bodies only — the subject always carries over from the original thread (with "Re:" added automatically), so there's no subject field here. You can use {'{{first_name}}'}, {'{{last_name}}'}, {'{{brand_name}}'}, {'{{brand_phone}}'}, {'{{brand_website}}'}, {'{{visit_date}}'}, {'{{visit_time}}'}, and {'{{visit_datetime}}'} (date and time together) — these fill in automatically from the lead when the template is selected.
        </div>
        <div className="fl"><label>Name</label><input type="text" value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Initial enquiry reply" /></div>
        <div className="fl" style={{ marginTop: 10 }}><label>Body</label><textarea rows={4} value={newForm.body} onChange={e => setNewForm(f => ({ ...f, body: e.target.value }))} /></div>
        <TemplatePurposeFields form={newForm} setForm={setNewForm}/>
        <button className="btn btn-green btn-sm" style={{ marginTop: 10 }} onClick={addNew} disabled={adding}>{adding ? 'Adding…' : '+ Add template'}</button>
      </div>

      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Library ({items.length})</div>
        {items.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>No templates yet.</div>}
        {items.map(it => (
          <div key={it.id} style={{ borderTop: '1px solid var(--line-soft)', padding: '10px 0' }}>
            {editingId === it.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" />
                <textarea rows={4} value={editForm.body} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))} placeholder="Body" />
                <TemplatePurposeFields form={editForm} setForm={setEditForm}/>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-green btn-sm" onClick={saveEdit}>Save</button>
                  <button className="btn btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {it.name}
                    {TEMPLATE_PURPOSE_BADGES[it.purpose] && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600,
                      background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent-soft-line)',
                      borderRadius: 4, padding: '1px 6px' }}>{TEMPLATE_PURPOSE_BADGES[it.purpose]}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-sm" onClick={() => startEdit(it)}>Edit</button>
                  <button className="btn btn-sm btn-red" onClick={() => remove(it.id)}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const TEMPLATE_PURPOSE_BADGES = {
  review: 'Review request', receipt: 'Receipt', visit_proposed: 'Proposed visit', visit_confirmed: 'Visit confirmation', quick_quote: 'Quick quote',
};

// Andrew, 14 Oct: confirming a visit asks whether to send the standard confirmation. Shown
// only when there is something to send - a template holding the visit_confirmed purpose and
// an email address on the lead - otherwise it closes itself without a word. The text is
// filled for THIS visit and stays editable; it goes through the same send as a typed reply,
// so it threads with the enquiry and lands in the lead's Messages.
function VisitConfirmEmailModal({ leadId, visitId, onClose }) {
  const { showAlert } = useDialog();
  const [to, setTo] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [lead, templates] = await Promise.all([api(`/api/leads/${leadId}`), api('/api/email-templates')]);
        const tpl = templates.find(t => t.purpose === 'visit_confirmed');
        if (!tpl || !lead.email) { if (live) closeRef.current(); return; }
        const filled = await api(`/api/leads/${leadId}/fill-template`, { method: 'POST',
          body: JSON.stringify({ subject: tpl.subject, body: tpl.body, visit_id: visitId }) });
        if (!live) return;
        setSubject(filled.subject); setBody(filled.body); setTo(lead.email);
      } catch (e) { if (live) closeRef.current(); }
    })();
    return () => { live = false; };
  }, [leadId, visitId]);
  if (!to) return null;
  const send = async () => {
    setSending(true);
    try {
      await api(`/api/leads/${leadId}/send-email`, { method: 'POST', body: JSON.stringify({ subject, body }) });
      onClose();
    } catch (e) { setSending(false); showAlert(`Not sent: ${e.message}`); }
  };
  return (
    <div className="modal-overlay" onClick={() => { if (!sending) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="mhead">
          <div><div className="mtitle">Send the visit confirmation?</div>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>To {to}</div></div>
          <button className="xcbtn" onClick={onClose} disabled={sending}>×</button>
        </div>
        <div className="mbody">
          <div className="fl"><label>Subject</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}/></div>
          <div className="fl"><label>Message</label>
            <textarea rows={9} value={body} onChange={e => setBody(e.target.value)}/></div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
            Changes here are for this email only. The standard wording is the Visit confirmation template in Settings.
          </div>
        </div>
        <div className="mfoot">
          <button className="btn btn-sm" onClick={onClose} disabled={sending}>Don't send</button>
          <button className="btn btn-green btn-sm" onClick={send} disabled={sending || !body.trim()}>{sending ? 'Sending…' : 'Send email'}</button>
        </div>
      </div>
    </div>
  );
}

// A template the CRM sends by itself needs its own subject - there is no thread
// for it to inherit one from.
function TemplatePurposeFields({ form, setForm }) {
  const [coverage, setCoverage] = useState(null);
  useEffect(() => {
    if (form.purpose !== 'review') { setCoverage(null); return; }
    api('/api/brand-settings').then(data => {
      const sends = [], silent = [];
      for (const k of BRAND_KEYS) {
        const s = data[k] || {};
        ((s.google_review_url || '').trim() ? sends : silent).push(BRANDS[k]);
      }
      setCoverage({ sends, silent });
    }).catch(() => setCoverage(null));
  }, [form.purpose]);

  return (
    <>
      <div className="fl" style={{ marginTop: 4, maxWidth: 320 }}>
        <label>Send this automatically as</label>
        <select value={form.purpose || ''} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}>
          <option value="">Nothing - I'll send it by hand</option>
          <option value="review">The review request, 24 hours after a job is paid for</option>
          <option value="receipt">The receipt, whenever a payment is received</option>
          <option value="visit_proposed">The proposed-visit reply, loaded when a visit is booked</option>
          <option value="visit_confirmed">The visit confirmation, offered when a visit is confirmed</option>
          <option value="quick_quote">The quick quote email, loaded in Quick quote on a lead</option>
        </select>
      </div>
      {form.purpose === 'visit_proposed' && (
        <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
          Loaded into the reply box, not sent on its own - booking a visit opens the lead on
          Messages with this already written, replying to the customer's own enquiry. Nothing
          goes out until you press Send.
        </div>
      )}
      {form.purpose === 'quick_quote' && (
        <>
          <div className="fl"><label>Subject</label>
            <input type="text" value={form.subject || ''} placeholder="Your quote from {{brand_name}}"
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}/></div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
            Loaded into Quick quote on a lead, filled in and editable; nothing goes out until you press Send by email.
            Any other template that uses {'{{price}}'} is offered there too, to pick instead.
            The quote reference is added to the subject. Tokens: {'{{first_name}}'}, {'{{job}}'}, {'{{price}}'} (ex VAT),
            {' '}{'{{total}}'} (with VAT), {'{{quote_button}}'} (on a line of its own - the accept button; leave it out for none), {'{{brand_name}}'}, {'{{brand_phone}}'}.
            Only one template can hold this.
          </div>
        </>
      )}
      {form.purpose === 'visit_confirmed' && (
        <>
          <div className="fl"><label>Subject</label>
            <input type="text" value={form.subject || ''} placeholder="Your visit is confirmed - {{visit_datetime}}"
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}/></div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
            Offered in a pop-up whenever a proposed visit is confirmed - from the lead, by Edit visit or by
            dragging the lead into Visit. The pop-up shows it filled in and editable, and nothing goes out until
            you press Send. It goes as a reply in the customer's thread. Tokens: {'{{first_name}}'},
            {' '}{'{{visit_date}}'}, {'{{visit_time}}'}, {'{{visit_datetime}}'}, {'{{brand_name}}'},
            {' '}{'{{brand_phone}}'}, {'{{brand_website}}'}. Only one template can hold this.
          </div>
        </>
      )}
      {form.purpose === 'receipt' && (
        <>
          <div className="fl"><label>Subject</label>
            <input type="text" value={form.subject || ''} placeholder="Receipt for your payment"
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}/></div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
            Sent on its own with the receipt attached as a PDF, so it needs a subject.
            Tokens: {'{{first_name}}'}, {'{{amount}}'}, {'{{invoice_reference}}'}, {'{{paid_on}}'},
            {' '}{'{{method}}'}, {'{{brand_name}}'}, {'{{brand_phone}}'}, {'{{brand_website}}'}.
            One payment, one receipt - a 50/50 job sends two. Refunds never send one.
            Only one template can hold this; choosing it here releases whichever had it before.
          </div>
        </>
      )}
      {form.purpose === 'review' && (
        <>
          <div className="fl"><label>Subject</label>
            <input type="text" value={form.subject || ''} placeholder="How did we do?"
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}/></div>
          <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
            This one is sent on its own rather than as a reply, so it needs a subject.
            Use {'{{review_link}}'} for the brand's Google review link, and {'{{review_qr}}'} to place the
            QR code - leave the QR token out and the code is added at the end. Only one template can hold
            this - ticking it here releases whichever template had it before.
          </div>
          {coverage && (
            <div style={{ fontSize: 11.5, marginTop: 8, padding: '9px 11px', borderRadius: 6,
              background: 'var(--canvas)', border: '1px solid var(--line-soft)' }}>
              <div style={{ fontWeight: 600 }}>
                Sends for {coverage.sends.length} of {coverage.sends.length + coverage.silent.length} brands.
              </div>
              {coverage.silent.length > 0 && (
                <div style={{ marginTop: 5, color: 'var(--ink-faint)' }}>
                  Nothing is sent for {coverage.silent.join(', ')} - no Google review link on those brands,
                  so a job can finish and be paid for without ever asking. Add a link under Branding and the
                  next sweep sends it, including for jobs already completed.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

// One guarantee per work type. Nothing is seeded here on purpose - the wording is a
// promise about work that was done, and it has to be the business's own.
function GuaranteeTemplatesPanel() {
  const { showAlert, showConfirm } = useDialog();
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', years: '', body: '' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api('/api/guarantee-templates').then(setItems).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  const blank = { name: '', years: '', body: '' };
  const startAdd = () => { setEditingId(null); setForm(blank); setAdding(true); };
  const startEdit = (it) => {
    setAdding(false);
    setEditingId(it.id);
    setForm({ name: it.name || '', years: it.years == null ? '' : String(it.years), body: it.body || '' });
  };
  const cancel = () => { setAdding(false); setEditingId(null); setForm(blank); };

  const save = async () => {
    setSaving(true);
    try {
      const body = JSON.stringify({ name: form.name, years: form.years === '' ? null : form.years, body: form.body });
      if (editingId) await api(`/api/guarantee-templates/${editingId}`, { method: 'PUT', body });
      else await api('/api/guarantee-templates', { method: 'POST', body });
      cancel(); load();
    } catch (e) { showAlert(e.message); }
    setSaving(false);
  };

  const remove = async (it) => {
    if (!(await showConfirm(`Remove the "${it.name}" guarantee? Guarantees already issued keep their own wording and are not affected.`,
      { confirmLabel: 'Remove' }))) return;
    try { await api(`/api/guarantee-templates/${it.id}`, { method: 'DELETE' }); load(); }
    catch (e) { showAlert(e.message); }
  };

  const editor = (
    <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
      <div className="row2">
        <div className="fl"><label>Work type</label>
          <input type="text" placeholder="New flat roof" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}/></div>
        <div className="fl"><label>Guarantee period, in years</label>
          <input type="number" min="0" placeholder="10" value={form.years}
            onChange={e => setForm(f => ({ ...f, years: e.target.value }))}/></div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
        Leave the period blank if the wording states it itself - nothing is printed separately then.
      </div>
      <div className="fl" style={{ marginTop: 10 }}><label>Wording</label>
        <textarea rows={10} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}/></div>
      <div style={{ fontSize: 11, color: 'var(--ink-faint)', lineHeight: 1.6, marginTop: 4 }}>
        Merged when the guarantee is issued: {'{{customer_name}}'}, {'{{first_name}}'}, {'{{address}}'},
        {' '}{'{{postcode}}'}, {'{{brand_name}}'}, {'{{brand_phone}}'}, {'{{brand_email}}'}, {'{{brand_website}}'},
        {' '}{'{{company_name}}'}, {'{{completed_on}}'}, {'{{issued_on}}'}, {'{{guarantee_years}}'},
        {' '}{'{{expires_on}}'}, {'{{quote_reference}}'}, {'{{guarantee_reference}}'}.
        The brand logo, address, customer details and the registration footer are added around it.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-green btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-sm" onClick={cancel}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', maxWidth: 780 }}>
      <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', lineHeight: 1.6, marginBottom: 14 }}>
        One per work type - a new flat roof is not the same promise as a pitched roof repair.
        When a job with &ldquo;guarantee needed&rdquo; is completed, the work type is chosen on the job
        card, previewed, and sent to the customer as a PDF. The wording is copied onto the job at
        that moment, so editing a guarantee here never changes one already issued.
      </div>

      {!adding && editingId === null && (
        <button className="btn btn-green btn-sm" style={{ marginBottom: 14 }} onClick={startAdd}>+ Add a guarantee</button>
      )}
      {(adding || editingId !== null) && editor}

      {items.length === 0 && !adding && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>
          Nothing set up yet. Paste in the guarantee wording you already use, one per work type.
        </div>
      )}

      {items.map(it => (
        <div key={it.id} style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)',
          borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {it.name}
                {it.years ? <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600,
                  background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent-soft-line)',
                  borderRadius: 4, padding: '1px 6px' }}>{it.years} year{it.years === 1 ? '' : 's'}</span> : null}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 4, whiteSpace: 'pre-wrap',
                maxHeight: 54, overflow: 'hidden' }}>{it.body}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-sm" onClick={() => startEdit(it)}>Edit</button>
              <button className="btn btn-sm btn-red" onClick={() => remove(it)}>Remove</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DefaultItemsPanel() {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [newForm, setNewForm] = useState({ title: '', description: '' });
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const shown = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    const terms = q.split(/\s+/);
    return items.filter(it => {
      const hay = ((it.title || '') + ' ' + (it.description || '')).toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [items, filter]);

  const load = useCallback(() => { api('/api/default-line-items').then(setItems).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => { setEditingId(it.id); setEditForm({ title: it.title || '', description: it.description || '' }); };
  const saveEdit = async () => {
    try { await api(`/api/default-line-items/${editingId}`, { method: 'PUT', body: JSON.stringify(editForm) }); setEditingId(null); load(); }
    catch (e) { showAlert(e.message); }
  };
  const remove = async (id) => {
    if (!(await showConfirm('Remove this default item?', {danger:true, confirmLabel:'Remove'}))) return;
    try { await api(`/api/default-line-items/${id}`, { method: 'DELETE' }); load(); } catch (e) { showAlert(e.message); }
  };
  const addNew = async () => {
    if (!newForm.title.trim() && !newForm.description.trim()) return;
    setAdding(true);
    try { await api('/api/default-line-items', { method: 'POST', body: JSON.stringify(newForm) }); setNewForm({ title: '', description: '' }); load(); }
    catch (e) { showAlert(e.message); }
    setAdding(false);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', maxWidth: 720 }}>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Add a default item</div>
        <div className="fl"><label>Title</label><input type="text" value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Scaffolding Safety & Access" /></div>
        <div className="fl" style={{ marginTop: 10 }}><label>Description</label><textarea rows={6} value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} placeholder={"One line per point, as it should read on the quote:\nRemove existing felt layers\nInstall new 3 layer torch on\n10 Year Guarantee Supplied"} /></div>
        <button className="btn btn-green btn-sm" style={{ marginTop: 10 }} onClick={addNew} disabled={adding}>{adding ? 'Adding…' : '+ Add to library'}</button>
      </div>

      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>
          Library ({filter.trim() ? `${shown.length} of ${items.length}` : items.length})
        </div>
        {items.length > 6 && (
          <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter the library"
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: '5px 8px', marginBottom: 4 }} />
        )}
        {items.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>No default items yet.</div>}
        {items.length > 0 && shown.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-faint)', padding: '8px 0' }}>Nothing matches "{filter}".</div>
        )}
        {shown.map(it => (
          <div key={it.id} style={{ borderTop: '1px solid var(--line-soft)', padding: '10px 0' }}>
            {editingId === it.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="text" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" />
                <textarea rows={Math.min(14, Math.max(4, (editForm.description || '').split('\n').length + 1))}
                  value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-green btn-sm" onClick={saveEdit}>Save</button>
                  <button className="btn btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  {it.title && <div style={{ fontWeight: 600, fontSize: 13 }}>{it.title}</div>}
                  <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 2, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{it.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-sm" onClick={() => startEdit(it)}>Edit</button>
                  <button className="btn btn-sm btn-red" onClick={() => remove(it.id)}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Five fields that decide whether every quote and invoice the business sends is
// lawful. Held on the company rather than on each brand, so there is one place to get
// them right and one place they can be wrong.
function CompanySettingsPanel() {
  const { showAlert } = useDialog();
  const [form, setForm] = useState({ legal_name: '', company_number: '', place_of_registration: '',
    registered_office: '', vat_number: '', zero_rate_note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api('/api/company-settings').then(s => setForm({
      legal_name: s.legal_name || '',
      company_number: s.company_number || '',
      place_of_registration: s.place_of_registration || '',
      registered_office: s.registered_office || '',
      vat_number: s.vat_number || '',
      zero_rate_note: s.zero_rate_note || '',
    })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api('/api/company-settings', { method: 'PUT', body: JSON.stringify(form) });
      load();
    } catch (e) { showAlert(e.message); }
    setSaving(false);
  };

  const REQUIRED = [
    ['legal_name', 'registered company name'],
    ['company_number', 'company number'],
    ['place_of_registration', 'where the company is registered'],
    ['registered_office', 'registered office address'],
    ['vat_number', 'VAT registration number'],
  ];
  const missing = REQUIRED.filter(([k]) => !String(form[k] || '').trim()).map(([, label]) => label);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', maxWidth: 720 }}>
      {missing.length > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', color: '#78350f',
          borderRadius: 'var(--radius)', padding: '10px 12px', fontSize: 12.5, lineHeight: 1.55, marginBottom: 14 }}>
          Quotes and invoices are going out without everything the law requires on them.
          Still to fill in: {missing.join(', ')}.
        </div>
      )}
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>The company behind the brands</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', lineHeight: 1.55, marginBottom: 12 }}>
          Every brand is a trading name of one company, so these are held once and printed at the
          foot of every invoice, receipt and guarantee - &ldquo;Royston Roofing is a trading name
          of {form.legal_name || 'the company'}&rdquo; followed by the registration and VAT details.
          Each brand's own address and phone number stay in Branding.
        </div>
        <div className="row2">
          <div className="fl"><label>Registered company name</label>
            <input type="text" placeholder="Roofing Network Ltd" value={form.legal_name} onChange={set('legal_name')} /></div>
          <div className="fl"><label>Company number</label>
            <input type="text" placeholder="12345678" value={form.company_number} onChange={set('company_number')} /></div>
        </div>
        <div className="fl" style={{ marginTop: 10, maxWidth: 280 }}><label>Where the company is registered</label>
          <select value={form.place_of_registration} onChange={set('place_of_registration')}>
            <option value="">Choose one</option>
            <option value="England and Wales">England and Wales</option>
            <option value="Scotland">Scotland</option>
            <option value="Northern Ireland">Northern Ireland</option>
          </select></div>
        <div className="fl" style={{ marginTop: 10 }}><label>Registered office address</label>
          <textarea rows={3} value={form.registered_office} onChange={set('registered_office')} /></div>
        <div className="fl" style={{ marginTop: 10, maxWidth: 280 }}><label>VAT registration number</label>
          <input type="text" placeholder="GB 123 4567 89" value={form.vat_number} onChange={set('vat_number')} /></div>
        <div className="fl" style={{ marginTop: 14 }}><label>Zero rate or exemption note</label>
          <textarea rows={2} value={form.zero_rate_note} onChange={set('zero_rate_note')} /></div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', lineHeight: 1.55, marginTop: 4 }}>
          Printed under the totals, and only when a line on that invoice is at 0% VAT. Leave it
          blank unless your accountant has given you the wording - a VAT invoice has to say why
          something is not standard rated, and the right wording depends on the work.
        </div>
      </div>
      <button className="btn btn-green" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
    </div>
  );
}

function BrandingSettingsPanel() {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [settings, setSettings] = useState({});
  const [selectedBrand, setSelectedBrand] = useState('R');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [mailbox, setMailbox] = useState(null);
  const [mbPass, setMbPass] = useState('');
  const [mbSaving, setMbSaving] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api('/api/brand-settings').then(data => {
      setSettings(data);
      const s = data[selectedBrand] || {};
      setForm({ phone: s.phone || '', pm_phone: s.pm_phone || '', email: s.email || '', website: s.website || '', address: s.address || '', intro_text: s.intro_text || '', terms_text: s.terms_text || '', area_code: s.area_code || '',
        bank_account_name: s.bank_account_name || '', bank_sort_code: s.bank_sort_code || '', bank_account_number: s.bank_account_number || '', payment_note: s.payment_note || '',
        google_review_url: s.google_review_url || '', public_base_url: s.public_base_url || '', colour: s.colour || '' });
    });
    setMbPass('');
    api('/api/mailboxes').then(m => setMailbox((m && m[selectedBrand]) || null)).catch(() => setMailbox(null));
  }, [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api(`/api/brand-settings/${selectedBrand}`, { method: 'PUT', body: JSON.stringify(form) });
      load();
    } catch (e) { showAlert(e.message); }
    setSaving(false);
  };

  // Each brand's row is a full upsert, so the other fields have to be sent back
  // exactly as they are - otherwise copying the terms would wipe a phone number.
  const copyTextToOtherBrands = async () => {
    const others = BRAND_KEYS.filter(k => k !== selectedBrand);
    const named = others.map(k => BRANDS[k]).join(', ');
    if (!(await showConfirm(`Copy this brand's intro and terms onto ${named}? Anything they have there now is replaced.`,
      { confirmLabel: 'Copy' }))) return;
    setCopying(true);
    const failed = [];
    for (const k of others) {
      const s = settings[k] || {};
      const body = {
        phone: s.phone || '', pm_phone: s.pm_phone || '', email: s.email || '', website: s.website || '',
        address: s.address || '', area_code: s.area_code || '',
        bank_account_name: s.bank_account_name || '', bank_sort_code: s.bank_sort_code || '',
        bank_account_number: s.bank_account_number || '', payment_note: s.payment_note || '',
        google_review_url: s.google_review_url || '',
        intro_text: form.intro_text || '', terms_text: form.terms_text || '',
      };
      try { await api(`/api/brand-settings/${k}`, { method: 'PUT', body: JSON.stringify(body) }); }
      catch (e) { failed.push(BRANDS[k]); }
    }
    setCopying(false);
    load();
    if (failed.length) showAlert(`Copied, except: ${failed.join(', ')}.`);
    else showAlert(`Copied to ${others.length} other brands.`);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    try {
      const r = await fetch(`/api/brand-settings/${selectedBrand}/logo`, { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) throw new Error((await r.json()).error || 'Upload failed');
      load();
    } catch (err) { showAlert(err.message); }
    e.target.value = '';
  };

  const saveMailbox = async () => {
    setMbSaving(true);
    try {
      const body = {
        email: (mailbox && mailbox.email) || '',
        imap_host: (mailbox && mailbox.imap_host) || '',
        imap_port: (mailbox && mailbox.imap_port) || 993,
      };
      if (mbPass) body.password = mbPass;
      const r = await api(`/api/mailboxes/${selectedBrand}`, { method: 'PUT', body: JSON.stringify(body) });
      setMbPass('');
      setMailbox(m => ({ ...(m || {}), has_password: !!(r && r.has_password) }));
    } catch (e) { showAlert(e.message); }
    setMbSaving(false);
  };

  const current = settings[selectedBrand] || {};
  // What the badge will look like with whatever is currently typed, not what is saved.
  const swatch = derivedBadge(form.colour) || BRAND_COLORS[selectedBrand] || BRAND_COLORS.R;
  const mbForm = {
    email: (mailbox && mailbox.email) || '',
    imap_host: (mailbox && mailbox.imap_host) || '',
    has_password: !!(mailbox && mailbox.has_password),
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', maxWidth: 720 }}>
      <div className="fl" style={{ marginBottom: 16 }}>
        <label>Brand</label>
        <select value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} style={{ maxWidth: 260 }}>
          {BRAND_KEYS.map(k => {
            const s = settings[k] || {};
            const bare = !(s.intro_text || '').trim() && !(s.terms_text || '').trim();
            return <option key={k} value={k}>{k} — {BRANDS[k]}{bare ? '  (no quote text)' : ''}</option>;
          })}
        </select>
      </div>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '20px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Logo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {current.logo_filename ? (
            <img src={`/uploads/brand-logos/${current.logo_filename}`} alt="" style={{ height: 110, maxWidth: 320, objectFit: 'contain' }} />
          ) : (
            <div style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>No logo uploaded yet</div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadLogo} />
          <button className="btn btn-sm" onClick={() => fileInputRef.current.click()}>Upload logo</button>
        </div>
      </div>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Contact details</div>
        <div className="row2">
          <div className="fl"><label>Office phone</label><input type="tel" value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
          <div className="fl"><label>Project manager's phone</label><input type="tel" value={form.pm_phone || ''} onChange={e => setForm(f => ({ ...f, pm_phone: e.target.value }))} /></div>
        </div>
        <div className="row2" style={{ marginTop: 10 }}>
          <div className="fl"><label>Email</label><input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          <div className="fl"><label>Website</label><input type="text" value={form.website || ''} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} /></div>
        </div>
        <div className="row2" style={{ marginTop: 10 }}>
          <div className="fl"><label>Brand colour</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.colour || '') ? form.colour : '#1d4ed8'}
                onChange={e => setForm(f => ({ ...f, colour: e.target.value }))}
                style={{ width: 44, height: 30, padding: 2, flexShrink: 0 }} />
              <input type="text" placeholder="#1d4ed8" value={form.colour || ''}
                onChange={e => setForm(f => ({ ...f, colour: e.target.value }))} style={{ flex: 1, minWidth: 0 }} />
              <span className="bbadge" style={{ background: swatch.bg, color: swatch.text, border: `1px solid ${swatch.border}`, flexShrink: 0 }}>{selectedBrand}</span>
            </div>
          </div>
          <div className="fl">
            <div style={{ fontSize: 11, color: 'var(--ink-faint)', paddingTop: 20, lineHeight: 1.45 }}>
              This brand's own colour, taken off its website. It colours the initial shown beside
              every lead, and the badge here is exactly what that will look like. Leave it blank to
              keep the built-in colour.
            </div>
          </div>
        </div>
        <div className="fl" style={{ marginTop: 10 }}><label>Address</label><textarea rows={2} value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
        <div className="fl" style={{ marginTop: 10, maxWidth: 160 }}><label>Quote area code</label><input type="text" value={form.area_code || ''} onChange={e => setForm(f => ({ ...f, area_code: e.target.value }))} /></div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>Used to build quote reference numbers for this brand.</div>
        <div className="fl" style={{ marginTop: 10 }}><label>Google review link</label>
          <input type="text" placeholder="https://g.page/r/..." value={form.google_review_url || ''}
            onChange={e => setForm(f => ({ ...f, google_review_url: e.target.value }))} /></div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
          Goes into the review request as {'{{review_link}}'}. No link here means no review request is sent for this brand.
        </div>
        <div className="fl" style={{ marginTop: 10 }}><label>Customer link domain</label>
          <input type="text" placeholder="https://quotes.roystonroofing.co.uk" value={form.public_base_url || ''}
            onChange={e => setForm(f => ({ ...f, public_base_url: e.target.value }))} /></div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
          The address customers see on quote, acceptance and payment links for this brand.
          Leave it blank until the subdomain exists and points at the CRM - blank means links
          keep using crm.roofingnetwork.ltd, which always works. Must be https.
        </div>
      </div>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Mailbox</div>
        <div className="row2">
          <div className="fl"><label>Mailbox address</label>
            <input type="email" placeholder="info@example.co.uk" value={mbForm.email}
              onChange={e => setMailbox(m => ({ ...(m || {}), email: e.target.value }))} /></div>
          <div className="fl"><label>IMAP host</label>
            <input type="text" placeholder="example.co.uk" value={mbForm.imap_host}
              onChange={e => setMailbox(m => ({ ...(m || {}), imap_host: e.target.value }))} /></div>
        </div>
        <div className="row2" style={{ marginTop: 10 }}>
          <div className="fl"><label>Password</label>
            <input type="password" autoComplete="new-password"
              placeholder={mbForm.has_password ? 'Set - type a new one to replace it' : 'Not set'}
              value={mbPass} onChange={e => setMbPass(e.target.value)} /></div>
          <div className="fl"><label>Status</label>
            <div style={{ fontSize: 12.5, paddingTop: 7, color: mbForm.has_password ? 'var(--accent)' : 'var(--ink-faint)' }}>
              {mbForm.has_password ? 'Password set' : 'No password yet'}
            </div></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-sm btn-green" disabled={mbSaving} onClick={saveMailbox}>
            {mbSaving ? 'Saving...' : 'Save mailbox'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 6 }}>
          The password is never shown again once saved - only whether one is set. Leave it blank to change the
          address or host without touching the stored password. Replies to this brand are polled from this mailbox
          and quotes are sent from it.
        </div>
      </div>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 10, paddingBottom: 0 }}>Bank details</div>
        <div className="fl"><label>Account name</label><input type="text" value={form.bank_account_name || ''} onChange={e => setForm(f => ({ ...f, bank_account_name: e.target.value }))} /></div>
        <div className="row2" style={{ marginTop: 10 }}>
          <div className="fl"><label>Sort code</label><input type="text" placeholder="00-00-00" value={form.bank_sort_code || ''} onChange={e => setForm(f => ({ ...f, bank_sort_code: e.target.value }))} /></div>
          <div className="fl"><label>Account number</label><input type="text" placeholder="00000000" value={form.bank_account_number || ''} onChange={e => setForm(f => ({ ...f, bank_account_number: e.target.value }))} /></div>
        </div>
        <div className="fl" style={{ marginTop: 10 }}><label>Payment note (optional)</label><textarea rows={2} value={form.payment_note || ''} onChange={e => setForm(f => ({ ...f, payment_note: e.target.value }))} /></div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>Shown on invoices for this brand. Leave blank and the payment box is left off entirely.</div>
      </div>
      <div style={{ background: 'var(--canvas)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 14 }}>
        <div className="sec" style={{ border: 'none', marginBottom: 4, paddingBottom: 0 }}>Default quote text</div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 10 }}>
          Filled in automatically on every new quote for this brand, and the terms carry through
          to any invoice raised from it. Either can still be edited on an individual quote, and
          editing it there never changes what is saved here.
        </div>
        <div className="fl"><label>Intro text</label><textarea rows={4} value={form.intro_text || ''} onChange={e => setForm(f => ({ ...f, intro_text: e.target.value }))}
          placeholder="Thank you for asking us to look at your roof works, here's our quote for the work." /></div>
        <div className="fl" style={{ marginTop: 10 }}><label>Terms</label><textarea rows={10} value={form.terms_text || ''} onChange={e => setForm(f => ({ ...f, terms_text: e.target.value }))}
          placeholder={"50% Booking Deposit Required\nBalance due on completion\nQuote valid for 28 days"} /></div>
        <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={copyTextToOtherBrands} disabled={copying}>
          {copying ? 'Copying…' : 'Copy this intro & terms to all other brands'}
        </button>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>
          Overwrites the intro and terms on the other eight brands. Nothing else about them is touched.
        </div>
      </div>
      <button className="btn btn-green" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
    </div>
  );
}

function KanbanBoard({ leads, onSelect, onStageChange, onStatusChange, sortMode, onBookVisitForLead, onShowAll }) {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const dragLeadId = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [confirmDragVisit, setConfirmDragVisit] = useState(null); // {leadId, visitId, stage}
  const [offerConfirm, setOfferConfirm] = useState(null); // {leadId, visitId}

  const handleDragStart = (e, leadId) => {
    dragLeadId.current = leadId;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(stage);
  };
  const handleDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const leadId = dragLeadId.current;
    dragLeadId.current = null;
    if (!leadId) return;
    moveLeadTo(leadId, stageOfColumn(stage));
  };
  // What a drop does, also reached from the "Move to" box that touch screens get instead
  // (a finger never fires dragstart - todo item 14).
  const moveLeadTo = async (leadId, stage) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage === stage) return;

    const wasInVisit = lead.stage === 'Visit';
    const movingIntoVisit = stage === 'Visit';

    if (movingIntoVisit) {
      let visits = [];
      try { visits = await api(`/api/visits?lead_id=${leadId}`); } catch (e) {}
      const confirmed = visits.find(v => v.status === 'confirmed');
      const proposed = visits.find(v => v.status === 'proposed');

      if (confirmed) {
        onStageChange(leadId, stage);
        return;
      }
      if (proposed) {
        setConfirmDragVisit({ leadId, visitId: proposed.id, stage });
        return;
      }
      // No visits at all — open booking flow instead of moving the card
      onBookVisitForLead(lead);
      return;
    }

    if (wasInVisit) {
      try { await api(`/api/leads/${leadId}/revert-visits`, { method: 'POST' }); } catch (e) {}
    }
    onStageChange(leadId, stage);
  };
  const handleDragEnd = () => { setDragOver(null); dragLeadId.current = null; };

  const confirmDragVisitYes = async () => {
    if (!confirmDragVisit) return;
    try {
      await api(`/api/visits/${confirmDragVisit.visitId}/confirm`, { method: 'POST' });
      onStageChange(confirmDragVisit.leadId, confirmDragVisit.stage);
      setOfferConfirm({ leadId: confirmDragVisit.leadId, visitId: confirmDragVisit.visitId });
    } catch (e) { showAlert(e.message); }
    setConfirmDragVisit(null);
  };
  const confirmDragVisitNo = () => { setConfirmDragVisit(null); };

  const sortCards = (cards, stage) => {
    return [...cards].sort((a,b)=>{
      if (sortMode==='smart' && stage==='Visit') {
        const av=a.next_visit_at?new Date(a.next_visit_at).getTime():Infinity;
        const bv=b.next_visit_at?new Date(b.next_visit_at).getTime():Infinity;
        if (av!==bv) return av-bv;
      }
      const diff = new Date(b.updated_at)-new Date(a.updated_at);
      return sortMode==='oldest' ? -diff : diff;
    });
  };

  return(
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden'}}>
    <div className="kanban">
      {BOARD_COLUMNS.map(stage=>{
        const allCards=sortCards(leads.filter(l=>boardColumnOf(l)===stage), stage);
        const cap=CAPPED_STAGES[stage];
        const cards=cap?allCards.slice(0,cap):allCards;
        return(
          <div key={stage} className="col">
            <div className="col-head"><span><span className="dot" style={{background:columnColour(stage)||'var(--accent)'}}/>{stage}</span><span style={{fontSize:10,color:'#a8a29e'}}>{allCards.length}</span></div>
            <div className={`col-body${dragOver===stage?' drag-over':''}`}
              onDragOver={e=>handleDragOver(e,stage)}
              onDrop={e=>handleDrop(e,stage)}
              onDragLeave={()=>setDragOver(null)}>
              {cards.length===0&&<div className="empty-col">—</div>}
              {cards.map(l=>{
                const c=brandColor(l.brand);
                return(
                  <div key={l.id}
                    className="card"
                    draggable={!(l.jobs||[]).length}
                    title={(l.jobs||[]).length?'This lead has accepted work - its stage follows the job. Change it in the Jobs or Invoicing tab.':undefined}
                    onDragStart={e=>handleDragStart(e,l.id)}
                    onDragEnd={handleDragEnd}
                    onClick={()=>onSelect(l.id)}>
                    <div className="card-top">
                      <span className="job-num">{jobNumber(l.id)}</span>
                      <BrandBadge brand={l.brand}/>
                      <span className="card-date" title="Date the lead was added">{fmtLongDate(l.created_at)}</span>
                      <span className={`owner-chip ${displayOwner(l)==='Andy'?'andy':'ld'}`} style={{marginLeft:'auto'}}>{displayOwner(l)}</span>
                    </div>
                    <div className="card-name-row">{tileDisplayName(l.title,l.first_name,l.last_name)||l.name}</div>
                    <div className="card-addr">{l.address}</div>
                    <div className="card-extra">
                    {(l.jobs||[]).map(j=>(
                      <JobPill key={j.id} pill={j.pill}/>
                    ))}
                    {(l.jobs||[]).some(j=>j.scheduled_date)&&(
                      <span style={{fontSize:10.5,color:'var(--ink-faint)'}}>
                        {(l.jobs||[]).filter(j=>j.scheduled_date).map(j=>
                          new Date(j.scheduled_date).toLocaleDateString('en-GB',{day:'numeric',month:'short'})).join(', ')}
                      </span>
                    )}
                    {!(l.jobs||[]).length&&(
                      <select className="select-compact card-move" value=""
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>{e.stopPropagation();if(e.target.value)moveLeadTo(l.id,e.target.value);}}>
                        <option value="">Move to…</option>
                        {['Lead','Visit','Quote','Dead'].filter(s=>s!==l.stage).map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {l.stage==='Lead'&&(
                      <select
                        value={l.lead_status||''}
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>{e.stopPropagation();onStatusChange(l.id,e.target.value,'lead_status');}}
                        className="select-compact"
                        style={{width:'100%',fontSize:10.5,height:26,padding:'2px 24px 2px 7px',background:l.lead_status?leadStatusBox(l.lead_status).bg:'var(--canvas)',
                          color:l.lead_status?leadStatusBox(l.lead_status).text:'var(--ink-faint)',borderColor:l.lead_status?leadStatusBox(l.lead_status).border:'var(--line-soft)'}}>
                        <option value=""></option>
                        {leadStatusOptions()}
                      </select>
                    )}
                    {stage==='To quote'&&(
                      <select
                        value={l.quote_status||''}
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>{e.stopPropagation();onStatusChange(l.id,e.target.value,'quote_status');}}
                        className="select-compact"
                        style={{width:'100%',fontSize:10.5,height:26,padding:'2px 24px 2px 7px',background:l.quote_status?STATUS_BOX_COLORS.Quote.bg:'var(--canvas)',
                          color:l.quote_status?STATUS_BOX_COLORS.Quote.text:'var(--ink-faint)',borderColor:l.quote_status?STATUS_BOX_COLORS.Quote.border:'var(--line-soft)'}}>
                        <option value=""></option>
                        <option value="priority_quote">Priority quote</option>
                      </select>
                    )}
                    </div>
                    <div className="card-foot">
                      <span className="card-val">{fmt(l.quoted_value)}</span>
                      <span className="age">{daysSince(l.created_at)}d</span>
                    </div>
                  </div>
                );
              })}
              {cap&&allCards.length>cap&&(
                <div onClick={()=>onShowAll&&onShowAll(stage)}
                  style={{fontSize:11.5,color:'var(--accent)',cursor:'pointer',padding:'8px 2px',textAlign:'center'}}>
                  Show all {allCards.length} →
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    {confirmDragVisit&&(
      <ConfirmModal
        title="Confirm this visit?"
        message="This lead has a proposed visit. Moving it into the Visit stage will confirm that visit. Continue?"
        confirmLabel="Confirm visit"
        onConfirm={confirmDragVisitYes}
        onCancel={confirmDragVisitNo}
      />
    )}
    {offerConfirm&&<VisitConfirmEmailModal leadId={offerConfirm.leadId} visitId={offerConfirm.visitId} onClose={()=>setOfferConfirm(null)}/>}
    </div>
  );
}

function AppInner() {
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [user,setUser]=useState(null);
  const [leads,setLeads]=useState([]);
  const [stats,setStats]=useState({});
  const [emailQueue,setEmailQueue]=useState([]);
  const [view,setView]=useState('board');
  const [reviewFocus,setReviewFocus]=useState(null);
  const [filterBrand,setFilterBrand]=useState('all');
  const [filterStage,setFilterStage]=useState('all');
  const [boardSortMode,setBoardSortMode]=useState('smart');
  const [filterOwner,setFilterOwner]=useState('all');
  const [filterStatus,setFilterStatus]=useState('all');
  const [searchTerm,setSearchTerm]=useState('');
  const [selectedId,setSelectedId]=useState(null);
  const [selectedInitialTab,setSelectedInitialTab]=useState(null);
  const [todoRefreshKey,setTodoRefreshKey]=useState(0);
  const [todoCount,setTodoCount]=useState(0);
  const [showNew,setShowNew]=useState(false);
  const [bookingLead,setBookingLead]=useState(null);
  const [composePurpose,setComposePurpose]=useState(null);
  const [quickBookLead,setQuickBookLead]=useState(null);
  const [openQuoteFor,setOpenQuoteFor]=useState(null);
  const [openInvoiceId,setOpenInvoiceId]=useState(null);
  const [invoiceRefreshKey,setInvoiceRefreshKey]=useState(0);
  const [listSort,setListSort]=useState({col:'added',dir:'asc'});
  const [listColorMode,setListColorMode]=useState(false);

  // Remembered views (see usePref). The app waits for this login's settings before it
  // shows anything, so nothing opens on the defaults and then jumps. Changes are sent
  // in one batch 0.8s after the last one, and straight away when the page is hidden or
  // closed, or on sign out.
  const [prefsLoaded,setPrefsLoaded]=useState(false);
  const prefsRef=useRef({});
  const prefQueue=useRef({});
  const prefTimer=useRef(null);
  const flushPrefs=useCallback((keepalive)=>{
    clearTimeout(prefTimer.current);
    const body=prefQueue.current;
    if(!Object.keys(body).length)return Promise.resolve();
    prefQueue.current={};
    return fetch('/api/prefs',{method:'POST',credentials:'include',keepalive:!!keepalive,
      headers:{'Content-Type':'application/json'},body:JSON.stringify({device:PREF_DEVICE,prefs:body})})
      .catch(()=>{});
  },[]);
  const savePrefs=useCallback((obj)=>{
    let changed=false;
    Object.keys(obj).forEach(k=>{
      if(JSON.stringify(prefsRef.current[k])===JSON.stringify(obj[k]))return;
      prefsRef.current[k]=obj[k];prefQueue.current[k]=obj[k];changed=true;
    });
    if(!changed)return;
    clearTimeout(prefTimer.current);
    prefTimer.current=setTimeout(()=>{flushPrefs(false);},800);
  },[flushPrefs]);
  const prefsStore=useMemo(()=>({get:(k)=>prefsRef.current[k],save:savePrefs}),[savePrefs]);
  useEffect(()=>{
    const hide=()=>{flushPrefs(true);};
    const vis=()=>{if(document.visibilityState==='hidden')hide();};
    window.addEventListener('pagehide',hide);
    document.addEventListener('visibilitychange',vis);
    return()=>{window.removeEventListener('pagehide',hide);document.removeEventListener('visibilitychange',vis);};
  },[flushPrefs]);
  // Every field is set, stored value or default, so signing in as someone else on the
  // same browser never inherits the last person's filters.
  useEffect(()=>{
    if(!user){setPrefsLoaded(false);prefsRef.current={};prefQueue.current={};return;}
    let gone=false;
    const apply=(p)=>{
      if(gone)return;
      const s=(p&&typeof p==='object'&&!Array.isArray(p))?p:{};
      prefsRef.current={...s};
      const pick=(k,def,ok)=>(s[k]!==undefined&&ok(s[k])?s[k]:def);
      const full=user.role==='admin'||user.role==='ld';
      setView(pick('view','board',v=>['board','list','diary','map','quotes','jobs','invoicing','todo','dayorder','settings'].includes(v)
        ||(full&&(v==='reports'||v==='labour'))));
      setFilterBrand(pick('board.brand','all',isBrandPref));
      setFilterStage(pick('list.stage','all',v=>v==='all'||BOARD_COLUMNS.includes(v)));
      setBoardSortMode(pick('board.sort','smart',oneOf('smart','newest','oldest')));
      setFilterOwner(pick('list.owner','all',oneOf('all','Andy','LD')));
      setFilterStatus(pick('board.status','all',isStatusFilter));
      setListSort(pick('list.sort',{col:'added',dir:'asc'},v=>!!v&&typeof v==='object'
        &&['added','updated','job','value','name'].includes(v.col)&&['asc','desc'].includes(v.dir)));
      setListColorMode(pick('list.colour',false,v=>typeof v==='boolean'));
      setPrefsLoaded(true);
    };
    api(`/api/prefs?device=${PREF_DEVICE}`).then(apply).catch(()=>apply({}));
    return()=>{gone=true;};
  },[user]);
  useEffect(()=>{
    if(!prefsLoaded)return;
    savePrefs({view,'board.brand':filterBrand,'list.stage':filterStage,'board.sort':boardSortMode,
      'list.owner':filterOwner,'board.status':filterStatus,'list.sort':listSort,'list.colour':listColorMode});
  },[prefsLoaded,savePrefs,view,filterBrand,filterStage,boardSortMode,filterOwner,filterStatus,listSort,listColorMode]);
  const listRef=useRef(null);
  const listScrollKey=useRef('');
  useEffect(()=>{
    if(view!=='list'){listScrollKey.current='';return;}
    if(!listRef.current||!leads.length)return;
    const key=[listSort.col,listSort.dir,filterStage,filterBrand,filterOwner,filterStatus].join('|');
    if(listScrollKey.current===key)return;
    listScrollKey.current=key;
    listRef.current.scrollTop=listRef.current.scrollHeight;
  },[view,listSort,filterStage,filterBrand,filterOwner,filterStatus,leads.length]);

  useEffect(() => {
    if (!user || !FULL_ACCESS_ROLES_CLIENT.includes(user.role)) return;
    const loadTodoCount = () => { api('/api/todo-items').then(items => setTodoCount(items.length)).catch(() => {}); };
    loadTodoCount();
    const interval = setInterval(loadTodoCount, 15000);
    return () => clearInterval(interval);
  }, [todoRefreshKey, user]);

  // Andrew, 14 Oct: the To-do count as the red number on the home-screen icon. iOS shows it
  // only for an app added to the home screen with notifications allowed; elsewhere the
  // Badging API is missing or ignored and this does nothing. Pushes set it while closed.
  useEffect(() => {
    if (!user || !('setAppBadge' in navigator)) return;
    (todoCount > 0 ? navigator.setAppBadge(todoCount) : navigator.clearAppBadge()).catch(() => {});
  }, [todoCount, user]);

  // The same count on the browser tab as a red number on the tab icon. Andrew, 3 Nov: the
  // title stays plain "Roofing Network" - the red number is enough. Logged out, or nothing
  // waiting, puts the plain icon back.
  useEffect(() => {
    const show = !!user && todoCount > 0;
    const n = todoCount > 99 ? '99+' : String(todoCount);
    document.title = 'Roofing Network';
    setTabBadge(show ? n : null);
  }, [todoCount, user]);

  const [diaryRefreshKey,setDiaryRefreshKey]=useState(0);
  const [loading,setLoading]=useState(true);
  const [boardError,setBoardError]=useState(null);

  // Andrew, 18 Oct: today's work kept on the phone for patchy signal. With no answer from
  // the server (an error that is not an HTTP reply, or nothing within 8 seconds) and a saved
  // copy on the phone, the app opens on that copy instead of the login screen.
  const [offlineMode,setOfflineMode]=useState(false);
  // Field app step 2: a tablet sign-in asks "Who's with you today?" for anyone on a team
  // who is not on today's list yet. Field logins then get their own home screen.
  const [onTablet,setOnTablet]=useState(false);
  const [fieldOffline,setFieldOffline]=useState(null);   // the last field login, when opened with no signal
  useEffect(()=>{if(user)rememberUser(user);},[user]);
  const [teamPick,setTeamPick]=useState(null);   // the /api/field/teams answer while choosing
  useEffect(()=>{
    if(!user)return;
    if(!onTablet&&FULL_ACCESS_ROLES_CLIENT.includes(user.role))return;
    api('/api/field/teams').then(d=>{if(d&&d.my_teams.length&&!d.my_team_today)setTeamPick(d);}).catch(()=>{});
  },[user,onTablet]);
  const [netDown,setNetDown]=useState(typeof navigator!=='undefined'&&navigator.onLine===false);
  useEffect(()=>{
    let settled=false;
    const toOffline=()=>{if(settled)return;settled=true;
      const lu=lastUser();
      if(lu&&!FULL_ACCESS_ROLES_CLIENT.includes(lu.role)&&readFieldCopy(lu.id))setFieldOffline(lu);
      else if(readOfflineCopy())setOfflineMode(true);
      setLoading(false);};
    const timer=setTimeout(toOffline,8000);
    api('/api/me').then(d=>{if(settled)return;settled=true;clearTimeout(timer);setUser(d.user);setOnTablet(!!d.tablet);setLoading(false);})
      .catch(e=>{clearTimeout(timer);if(e instanceof TypeError)toOffline();else if(!settled){settled=true;setLoading(false);}});
    return()=>clearTimeout(timer);
  },[]);
  // The shell has to be in the service worker's cache before the first time it is needed,
  // so the worker is registered for everyone, not only for those who turned push on.
  useEffect(()=>{if('serviceWorker' in navigator)navigator.serviceWorker.register('/sw.js').catch(()=>{});},[]);
  // Anything left in the upload queue (app closed, no signal) is sent once signed in and
  // again whenever the connection comes back.
  useEffect(()=>{
    if(!user)return;
    uqProcess();
    const go=()=>uqProcess();
    window.addEventListener('online',go);
    return()=>window.removeEventListener('online',go);
  },[user]);
  const uqItems=useUploadQueue(null);
  useEffect(()=>{
    const down=()=>setNetDown(true);
    const up=()=>{setNetDown(false);if(user)saveOfflineCopy();};
    window.addEventListener('offline',down);window.addEventListener('online',up);
    return()=>{window.removeEventListener('offline',down);window.removeEventListener('online',up);};
  },[user]);
  useEffect(()=>{
    if(!user||!FULL_ACCESS_ROLES_CLIENT.includes(user.role))return;   // field logins get their own copy in step 5
    saveOfflineCopy();
    const t=setInterval(saveOfflineCopy,10*60*1000);
    return()=>clearInterval(t);
  },[user]);

  // A push notification opens /?lead=123. Consumed once and removed from the address bar,
  // so a refresh afterwards does not keep re-opening the same lead.
  useEffect(()=>{
    if(!user)return;
    const m=/[?&]lead=(\d+)/.exec(window.location.search);
    if(!m)return;
    setSelectedId(Number(m[1]));
    window.history.replaceState({},'',window.location.pathname);
  },[user]);

  const [brandColourKey,setBrandColourKey]=useState(0);
  useEffect(()=>{
    if(!user||!FULL_ACCESS_ROLES_CLIENT.includes(user.role))return;
    api('/api/brand-settings').then(rows=>{
      const next={};
      Object.keys(rows||{}).forEach(k=>{ if(rows[k]&&rows[k].colour) next[k]=rows[k].colour; });
      BRAND_COLOUR_HEX=next;
      setBrandColourKey(n=>n+1);
    }).catch(()=>{});
  },[user]);

  const load=useCallback(async()=>{
    if(!user||!FULL_ACCESS_ROLES_CLIENT.includes(user.role))return;   // field logins: refused, so not asked
    const params=new URLSearchParams();
    if(searchTerm.trim())params.set('search',searchTerm.trim());
    const qs=params.toString()?'?'+params.toString():'';
    const [l,s,eq,board]=await Promise.all([
      api(`/api/leads${qs}`),api('/api/stats'),api('/api/email-queue'),
      api('/api/board').catch(e=>({__error:e&&e.message?e.message:'request failed'})),
    ]);
    setBoardError(board&&board.__error?board.__error:null);
    const rows=Array.isArray(board)?board:[];
    const jobsByLead=new Map(rows.map(b=>[b.id,b.jobs||[]]));
    setLeads((l||[]).map(x=>({...x,jobs:jobsByLead.get(x.id)||[]})));
    setStats(s);setEmailQueue(eq);
    setDiaryRefreshKey(k=>k+1);
  },[user,searchTerm]);

  useEffect(()=>{const t=setTimeout(load,250);return ()=>clearTimeout(t);},[load]);

  // Clicking between Board, Diary and Invoicing goes back to the server. Without this a
  // lead accepted on the customer's phone sits in the wrong column until F5. Both of
  // these must sit AFTER load is defined - a dep array is evaluated during render, so
  // naming a const declared further down throws a ReferenceError on the first one.
  useEffect(()=>{ if(user) load(); },[view,user,load]);
  useEffect(()=>{
    if(!user)return;
    const onBack=()=>{ if(document.visibilityState==='visible') load(); };
    document.addEventListener('visibilitychange',onBack);
    window.addEventListener('focus',onBack);
    return ()=>{ document.removeEventListener('visibilitychange',onBack); window.removeEventListener('focus',onBack); };
  },[user,load]);

  // Send the field that changed and nothing else. See the note above about a stale
  // board row overwriting what the server worked out.
  const handleStageChange=async(leadId,newStage)=>{
    const lead=leads.find(l=>l.id===leadId);
    if(!lead||lead.stage===newStage)return;
    try{await api(`/api/leads/${leadId}`,{method:'PUT',body:JSON.stringify({stage:newStage})});load();}catch(e){showAlert(e.message);}
  };

  const handleStatusChange=async(leadId,newStatus,field='lead_status')=>{
    const lead=leads.find(l=>l.id===leadId);
    if(!lead)return;
    try{await api(`/api/leads/${leadId}`,{method:'PUT',body:JSON.stringify({[field]:newStatus})});load();}catch(e){showAlert(e.message);}
  };

  const approveEmail=async(id)=>{try{await api(`/api/email-queue/${id}/approve`,{method:'POST'});load();}catch(e){showAlert('Send failed: '+e.message);}};
  const dismissEmail=async(id)=>{try{await api(`/api/email-queue/${id}/dismiss`,{method:'POST'});load();}catch(e){}};
  const logout=async()=>{await flushPrefs(false);await api('/api/logout',{method:'POST'});clearOfflineCopy();
    if(user){try{localStorage.removeItem(fieldCopyKey(user.id));}catch(e){}}rememberUser(null);setUser(null);};

  if(fieldOffline)return<><style>{css}</style><FieldHome user={fieldOffline} offline onChangeTeam={()=>{}} onLogout={()=>{}}/></>;
  if(offlineMode)return<><style>{css}</style><OfflineToday data={readOfflineCopy()}
    onBack={user?()=>setOfflineMode(false):null} onRetry={()=>window.location.reload()}/></>;
  if(loading)return<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'var(--ink-soft)'}}>Loading…</div>;
  if(!user)return<><style>{css}</style><Login onLogin={(u,tab)=>{setUser(u);setOnTablet(!!tab);}}/></>;
  if(teamPick)return<><style>{css}</style><TeamToday data={teamPick} me={user} onDone={()=>setTeamPick(null)}/></>;
  if(!FULL_ACCESS_ROLES_CLIENT.includes(user.role))return<><style>{css}</style>
    <FieldHome user={user} onChangeTeam={()=>api('/api/field/teams').then(setTeamPick).catch(()=>{})} onLogout={logout}/></>;
  if(!prefsLoaded)return<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'var(--ink-soft)'}}>Loading…</div>;

  const filtered=leads.filter(l=>{
    if(filterBrand!=='all'&&l.brand!==filterBrand)return false;
    if(filterStatus!=='all'&&shownStatus(l)!==(filterStatus==='none'?'':filterStatus))return false;
    if(view==='list'){
      if(filterStage!=='all'&&boardColumnOf(l)!==filterStage)return false;
      if(filterOwner!=='all'&&displayOwner(l)!==filterOwner)return false;
    }
    return true;
  });
  const handleBookVisit=(lead)=>{setSelectedId(null);setBookingLead(lead);setView('diary');};
  // Andrew's rule: a time proposed or confirmed is the moment to answer their enquiry,
  // so the lead opens on Messages with the proposed-visit template already written.
  // Only when there is somebody to reply to.
  const handleBookingComplete=(booked)=>{
    setBookingLead(null);load();
    if(booked&&booked.email){
      setSelectedInitialTab('messages');
      setComposePurpose('visit_proposed');
      setSelectedId(booked.id);
    }
  };

  return(
    <PrefsContext.Provider value={prefsStore}>
      <style>{css}</style>
      <div className="app" data-brand-colours={brandColourKey}>
        {uqItems.length>0&&(
          <div className="uq-pill" title="Photos and voice notes saved on this device and still to be sent">
            {uqItems.some(i=>i.error)?'⚠ ':''}{uqItems.length} waiting to upload
          </div>
        )}
        {netDown&&(
          <div className="offline-bar">
            <span>No signal - anything you change now will not save.</span>
            {readOfflineCopy()&&<button className="btn btn-sm" onClick={()=>setOfflineMode(true)}>Open today's saved copy</button>}
          </div>
        )}
        <div className="topbar">
          <div className="logo">RN <span>Pipeline</span></div>
          <input
            type="text"
            placeholder="Search name, phone, address…"
            value={searchTerm}
            onChange={e=>setSearchTerm(e.target.value)}
            style={{width:240,marginLeft:18}}
          />
          <div className="topbar-right">
            <div className="tabs">
              <button className={`tab${view==='board'?' active':''}`} onClick={()=>setView('board')}>Board</button>
              <button className={`tab${view==='list'?' active':''}`} onClick={()=>setView('list')}>List</button>
              <button className={`tab${view==='diary'?' active':''}`} onClick={()=>setView('diary')}>Diary</button>
              <button className={`tab${view==='map'?' active':''}`} onClick={()=>setView('map')}>Map</button>
              <button className={`tab${view==='quotes'?' active':''}`} onClick={()=>setView('quotes')}>Quotes</button>
              <button className={`tab${view==='jobs'?' active':''}`} onClick={()=>setView('jobs')}>Jobs</button>
              <button className={`tab${view==='invoicing'?' active':''}`} onClick={()=>setView('invoicing')}>Invoicing</button>
              {(user.role==='admin'||user.role==='ld')&&(
                <button className={`tab${view==='reports'?' active':''}`} onClick={()=>setView('reports')}>Reports</button>
              )}
              {(user.role==='admin'||user.role==='ld')&&(
                <button className={`tab${view==='labour'?' active':''}`} onClick={()=>setView('labour')}>Labour</button>
              )}
              {(user.role==='admin'||user.role==='ld')&&(
                <button className={`tab${view==='reviews'?' active':''}`} onClick={()=>{setReviewFocus(null);setView('reviews');}}>Reviews</button>
              )}
              {['admin','ld','pm'].includes(user.role)&&<button className={`tab${view==='dayorder'?' active':''}`} onClick={()=>setView('dayorder')}>Day order</button>}
              <button className={`tab${view==='todo'?' active':''}`} onClick={()=>setView('todo')} style={{position:'relative'}}>
                To-do
                {todoCount>0&&(
                  <span style={{position:'absolute',top:-6,right:-8,minWidth:16,height:16,padding:'0 4px',borderRadius:8,
                    background:'var(--red)',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>
                    {todoCount>99?'99+':todoCount}
                  </span>
                )}
              </button>
            </div>
            <button className="btn btn-green btn-sm" onClick={()=>setShowNew(true)}>+ New lead</button>
            <button
              onClick={()=>setView('settings')}
              title="Settings"
              style={{background:view==='settings'?'var(--canvas-sunken)':'transparent',border:'1px solid var(--line-soft)',
                borderRadius:6,width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',
                color:view==='settings'?'var(--ink)':'var(--ink-faint)',fontSize:20}}>
              ⚙
            </button>
            <span className="user-info">{user.display_name}</span>
            <button className="btn btn-sm" onClick={logout}>Sign out</button>
          </div>
        </div>

        <QboIssuesBanner refreshKey={invoiceRefreshKey}/>

        {boardError&&<div style={{margin:'0 0 12px',padding:'10px 14px',borderRadius:8,
          background:'#fef2f2',border:'1px solid #fecaca',color:'#991b1b',fontSize:13}}>
          Job status could not be loaded, so cards are missing their labels. An overdue or
          unsent invoice will not show here until this is fixed. ({boardError})
        </div>}

        {view!=='diary'&&<div className="dash">
          <div className="metric"><div className="metric-label">Active leads</div><div className="metric-val blue">{stats.active||0}</div></div>
          <div className="metric"><div className="metric-label">Pipeline value</div><div className="metric-val green">£{(stats.pipeline||0).toLocaleString()}</div></div>
          <div className="metric"><div className="metric-label">Won (30 days)</div><div className="metric-val green">£{(stats.won||0).toLocaleString()}</div></div>
          <div className="metric"><div className="metric-label">Pending emails</div><div className="metric-val amber">{stats.pending_emails||0}</div></div>
        </div>}

        {(view==='board'||view==='list')&&<div className="filter-bar">
          <span className="filter-label">Brand:</span>
          <select className="select-compact" value={filterBrand} onChange={e=>setFilterBrand(e.target.value)}>
            <option value="all">All brands</option>
            {BRAND_KEYS.map(k=><option key={k} value={k}>{k} — {BRANDS[k]}</option>)}
          </select>
          <span className="filter-label" style={{marginLeft:6}}>Status:</span>
          <select className="select-compact" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={filterStatus!=='all'&&filterStatus!=='none'?{background:(filterStatus in QUOTE_STATUS_LABELS?STATUS_BOX_COLORS.Quote:leadStatusBox(filterStatus)).bg}:{}}>
            <option value="all">All statuses</option>
            <option value="none">No status</option>
            <optgroup label="Lead">{Object.keys(LEAD_STATUS_LABELS).map(k=><option key={k} value={k}>{LEAD_STATUS_LABELS[k]}</option>)}</optgroup>
            <optgroup label="Quote">{Object.keys(QUOTE_STATUS_LABELS).map(k=><option key={k} value={k}>{QUOTE_STATUS_LABELS[k]}</option>)}</optgroup>
          </select>
          {view==='board'&&<>
            <span className="filter-label" style={{marginLeft:6}}>Sort:</span>
            <select className="select-compact" value={boardSortMode} onChange={e=>setBoardSortMode(e.target.value)}>
              <option value="smart">Soonest visit first</option>
              <option value="newest">Newest updated first</option>
              <option value="oldest">Oldest updated first</option>
            </select>
          </>}
          {view==='list'&&<>
            <span className="filter-label" style={{marginLeft:6}}>Stage:</span>
            <select className="select-compact" value={filterStage} onChange={e=>setFilterStage(e.target.value)}>
              <option value="all">All stages</option>
              {BOARD_COLUMNS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <span className="filter-label" style={{marginLeft:6}}>Owner:</span>
            <select className="select-compact" value={filterOwner} onChange={e=>setFilterOwner(e.target.value)}>
              <option value="all">All owners</option>
              <option value="Andy">Andy</option>
              <option value="LD">Lawrance</option>
            </select>
            <span className="filter-label" style={{marginLeft:6}}>Sort:</span>
            <select className="select-compact" value={`${listSort.col}-${listSort.dir}`} onChange={e=>{const [col,dir]=e.target.value.split('-');setListSort({col,dir});}}>
              <option value="added-asc">Date added (newest at the bottom)</option>
              <option value="added-desc">Date added (newest at the top)</option>
              <option value="updated-desc">Newest updated</option>
              <option value="updated-asc">Oldest updated</option>
              <option value="job-desc">Job # (highest first)</option>
              <option value="job-asc">Job # (lowest first)</option>
              <option value="value-desc">Value (highest first)</option>
              <option value="name-asc">Name (A–Z)</option>
            </select>
            <button className="btn btn-sm" style={{marginLeft:6}} onClick={()=>setListColorMode(v=>!v)}>
              {listColorMode?'Colour: On':'Colour: Off'}
            </button>
          </>}
        </div>}

        <div className="main">
          {view==='board'&&<KanbanBoard leads={filtered} onSelect={setSelectedId} onStageChange={handleStageChange} onStatusChange={handleStatusChange} sortMode={boardSortMode} onBookVisitForLead={setQuickBookLead}
            onShowAll={(stage)=>{setFilterStage(stage);setView('list');}}/>}
          {view==='list'&&(()=>{
            const sortKey=listSort.col, sortDir=listSort.dir;
            const getVal=(l,col)=>{
              switch(col){
                case 'job': return l.id;
                case 'name': return (tileDisplayName(l.title,l.first_name,l.last_name)||l.name||'').toLowerCase();
                case 'brand': return l.brand;
                case 'stage': return BOARD_COLUMNS.indexOf(boardColumnOf(l));
                case 'address': return (l.address||'').toLowerCase();
                case 'phone': return l.phone||'';
                case 'value': return Number(l.quoted_value)||0;
                case 'owner': return displayOwner(l);
                case 'status': return l.lead_status||l.quote_status||'';
                case 'updated': return new Date(l.updated_at).getTime();
                case 'added': return new Date(l.created_at).getTime()*1e4+l.id;
                case 'email': return (l.email||'').toLowerCase();
                case 'notes': return (l.notes||'').toLowerCase();
                default: return '';
              }
            };
            const sortedList=[...filtered].sort((a,b)=>{
              const av=getVal(a,sortKey), bv=getVal(b,sortKey);
              const cmp=av<bv?-1:av>bv?1:0;
              return sortDir==='asc'?cmp:-cmp;
            });
            const sortTh=(col,label)=>(
              <th onClick={()=>setListSort(s=>({col,dir:s.col===col&&s.dir==='asc'?'desc':'asc'}))} style={{cursor:'pointer'}}>
                {label}{sortKey===col?(sortDir==='asc'?' ↑':' ↓'):''}
              </th>
            );
            return(
            <div className="list-wrap" ref={listRef}>
              <table className="lead-list">
                <thead><tr>
                  {sortTh('job','Lead')}{sortTh('phone','Phone')}{sortTh('brand','Brand')}{sortTh('name','Name')}{sortTh('email','Email')}
                  {sortTh('address','Address')}{sortTh('notes','Notes')}{sortTh('stage','Stage')}{sortTh('status','Status')}
                  {sortTh('value','Value')}{sortTh('added','Added')}
                </tr></thead>
                <tbody>{sortedList.map(l=>{
                  const rowColor=listColorMode?columnColour(boardColumnOf(l)):null;
                  return(<tr key={l.id} onClick={()=>setSelectedId(l.id)} style={rowColor?{background:rowColor+'66'}:{}}>
                  <td className="job-num">{jobNumber(l.id)}</td>
                  <td>{l.phone}</td>
                  <td><BrandBadge brand={l.brand}/></td>
                  <td className="clip name" title={tileDisplayName(l.title,l.first_name,l.last_name)||l.name||''}>{tileDisplayName(l.title,l.first_name,l.last_name)||l.name}</td>
                  <td className="clip email" title={l.email||''} style={{color:'var(--ink-soft)'}}>{l.email}</td>
                  <td className="clip addr" title={l.address||''} style={{color:'var(--ink-soft)'}}>{l.address}</td>
                  <td className="clip notes" title={l.notes||''}>{(l.notes||'').replace(/\s*\n+\s*/g,' · ')}</td>
                  <td><span className="stage-chip">{boardColumnOf(l)}</span></td>
                  <td onClick={e=>e.stopPropagation()} style={{display:'table-cell',verticalAlign:'middle'}}>
                    {l.stage==='Lead' ? (
                      <select
                        value={l.lead_status||''}
                        onChange={e=>handleStatusChange(l.id,e.target.value,'lead_status')}
                        className="select-compact"
                        style={{
                          height:22,fontSize:11,padding:'2px 22px 2px 7px',width:170,
                          ...(l.lead_status?(listColorMode&&l.lead_status!=='pm_question'
                            ?{background:'#fff',color:'#3d2f5c',borderColor:'#b8a8d4'}
                            :{background:leadStatusBox(l.lead_status).bg,color:leadStatusBox(l.lead_status).text,borderColor:leadStatusBox(l.lead_status).border})
                            :{})
                        }}>
                        <option value=""></option>
                        {leadStatusOptions()}
                      </select>
                    ) : (l.stage==='Quote'&&quoteOut(l)) ? (
                      <span className="quoted-box" style={{width:170,height:22,background:QUOTED_BOX.bg,color:QUOTED_BOX.text,borderColor:QUOTED_BOX.border}}>Quoted</span>
                    ) : l.stage==='Quote' ? (
                      <select
                        value={l.quote_status||''}
                        onChange={e=>handleStatusChange(l.id,e.target.value,'quote_status')}
                        className="select-compact"
                        style={{
                          height:22,fontSize:11,padding:'2px 22px 2px 7px',width:170,
                          ...(l.quote_status?(listColorMode
                            ?{background:'#fff',color:'#7a6300',borderColor:'#e6c200'}
                            :{background:STATUS_BOX_COLORS.Quote.bg,color:STATUS_BOX_COLORS.Quote.text,borderColor:STATUS_BOX_COLORS.Quote.border})
                            :{})
                        }}>
                        <option value=""></option>
                        <option value="priority_quote">Priority quote</option>
                      </select>
                    ) : (
                      <span style={{fontSize:11,color:'var(--ink-faint)'}}>—</span>
                    )}
                  </td>
                  <td style={{color:'var(--accent)',fontWeight:600}}>{fmt(l.quoted_value)}</td>
                  <td style={{color:'var(--ink-soft)'}}>{fmtLongDate(l.created_at)}</td>
                </tr>);})}</tbody>
              </table>
            </div>
            );
          })()}
          {view==='diary'&&<DiaryView onSelectLead={(id)=>setSelectedId(id)} bookingLead={bookingLead} onBookingComplete={handleBookingComplete} onCancelBooking={()=>setBookingLead(null)} onVisitChanged={load} refreshKey={diaryRefreshKey}/>}
          {view==='map'&&<MapOverviewView leads={leads}/>}
          {view==='quotes'&&<QuotesBoardView leads={leads} onSelectLead={(id)=>setSelectedId(id)} onOpenQuote={(leadId,quoteId)=>setOpenQuoteFor({leadId,quoteId})}/>}
          {view==='jobs'&&<JobsView onSelectLead={(id)=>setSelectedId(id)} refreshKey={invoiceRefreshKey}
            onChanged={()=>setInvoiceRefreshKey(k=>k+1)}/>}
          {view==='invoicing'&&<InvoicingHub leads={leads} onSelectLead={(id)=>setSelectedId(id)} onOpenInvoice={(id)=>setOpenInvoiceId(id)}
            refreshKey={invoiceRefreshKey} onChanged={()=>setInvoiceRefreshKey(k=>k+1)}/>}
          {view==='dayorder'&&<DayOrder/>}
          {view==='todo'&&<ToDoView onSelectLead={(id)=>{setSelectedId(id);setSelectedInitialTab('messages');}} refreshKey={todoRefreshKey}
            onOpenReview={(user.role==='admin'||user.role==='ld')?(id)=>{setReviewFocus(id);setView('reviews');}:null}/>}
          {view==='reviews'&&(user.role==='admin'||user.role==='ld')&&<ReviewsView focusId={reviewFocus} onSelectLead={(id)=>setSelectedId(id)}/>}
          {view==='labour'&&(user.role==='admin'||user.role==='ld')&&<LabourView/>}
          {view==='reports'&&(user.role==='admin'||user.role==='ld')&&<ReportsView/>}
          {view==='settings'&&<SettingsView me={user}/>}
        </div>

        <MobileNav showDayOrder={['admin','ld','pm'].includes(user.role)} view={view} setView={setView} todoCount={todoCount}
          showLabour={user.role==='admin'||user.role==='ld'}/>

        {emailQueue.length>0&&(
          <div className="eq-bar">
            <span className="eq-label">✉ Pending emails ({emailQueue.length})</span>
            {emailQueue.map(eq=>(<div key={eq.id} className="eq-item">
              <span>{eq.trigger_stage} → {eq.lead_name} ({eq.to_email})</span>
              <button className="eq-approve" onClick={()=>approveEmail(eq.id)}>Approve &amp; send</button>
              <button className="eq-dismiss" onClick={()=>dismissEmail(eq.id)}>×</button>
            </div>))}
          </div>
        )}

        {selectedId&&<LeadModal leadId={selectedId} user={user} composePurpose={composePurpose} onClose={()=>{const wasFromTodo=selectedInitialTab==='messages';setSelectedId(null);setSelectedInitialTab(null);setComposePurpose(null);if(wasFromTodo)setTodoRefreshKey(k=>k+1);}} onSaved={load} onBookVisit={handleBookVisit} onOpenQuote={(qId)=>setOpenQuoteFor({leadId:selectedId,quoteId:qId})} onOpenInvoice={(id)=>setOpenInvoiceId(id)} invoiceRefreshKey={invoiceRefreshKey} initialTab={selectedInitialTab}/>}
        {showNew&&<NewLeadModal onClose={()=>setShowNew(false)} onSaved={(newLead)=>{load();if(newLead)handleBookVisit(newLead);}}/>}
        {quickBookLead&&<BookVisitForLeadModal lead={quickBookLead} onClose={()=>setQuickBookLead(null)} onSaved={()=>{setQuickBookLead(null);load();}}/>}
        {openQuoteFor&&<QuoteBuilderModal leadId={openQuoteFor.leadId} quoteId={openQuoteFor.quoteId} onClose={()=>setOpenQuoteFor(null)} onSaved={load} onSentAndClose={()=>{setOpenQuoteFor(null);setSelectedId(null);}}/>}
        {openInvoiceId&&<InvoiceModal invoiceId={openInvoiceId} onClose={()=>setOpenInvoiceId(null)} onChanged={()=>setInvoiceRefreshKey(k=>k+1)}/>}
      </div>
    </PrefsContext.Provider>
  );
}

export default function App() {
  return (
    <DialogProvider>
      <AppInner />
    </DialogProvider>
  );
}
